/* injected */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import {
  determineStatusCategory,
  inferManualStatusFromLastFlight,
normaliseTamStatus,
  shouldHighlightGrounded,
} from "./status-rules.js";
import {
  cleanText,
  canonicalIdent,
  getLookupKeys,
  getPrimaryIdent,
  getStatusIdent,
  normaliseManualLocation,
  normaliseRegistration,
} from "./ident-utils.js";
import {
  ON_WATCH_STORAGE_KEY,
  buildWatchStoragePayload,
  normaliseWatchSnapshotEntry,
  parseStoredWatchEntries,
} from "./watch-storage.js";
import { assessStatusReliability } from "./reliability.js";
import { normaliseLocalDateTimeInput, toTimestamp } from "./datetime.js";
import { summariseLastFlightawareQuery } from "./cron-health.js";

const config = window.__CONFIG__ || {
  API_BASE: window.location.origin,
  MODE: "development",
};

const runtimeMode = (config.RUNTIME_MODE || config.MODE || "development").toLowerCase();
const visualMode = (config.VISUAL_MODE || config.MODE || runtimeMode || "development").toLowerCase();
const isProdMode = visualMode.startsWith("prod");
const isDevMode = !isProdMode;
// Exposed from index.html for deployment/debugging (cache bust verification).
const buildId = cleanText(window.__ACTRACKER_BUILD_ID__) || null;
const devToolsEnabled =
  !isProdMode && parseBoolean(config.DEV_TOOLS_ENABLED, !runtimeMode.startsWith("prod"));

const EXPORT_HEADER_COLOR = "#B81818";
const EXPORT_REPORT_TITLE = "Aircraft Tracking";

function runAutoTable(doc, options) {
  if (!doc || !options) {
    return;
  }
  const candidate = typeof autoTable === "function" ? autoTable : autoTable?.default;
  if (typeof candidate === "function") {
    candidate(doc, options);
    return;
  }
  if (typeof doc.autoTable === "function") {
    doc.autoTable(options);
    return;
  }
  throw new Error("jsPDF autoTable plugin is not available");
}

function parsePositiveInteger(value) {
  if (value === undefined || value === null) {
    return null;
  }
  const parsed = Number.parseInt(String(value), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return parsed;
}

function parseBoolean(value, defaultValue = false) {
  if (value === undefined || value === null) {
    return defaultValue;
  }
  if (typeof value === "boolean") {
    return value;
  }
  const text = String(value).trim().toLowerCase();
  if (!text) {
    return defaultValue;
  }
  if (["1", "true", "yes", "y", "on"].includes(text)) {
    return true;
  }
  if (["0", "false", "no", "n", "off"].includes(text)) {
    return false;
  }
  return defaultValue;
}

function normaliseRegistrationValue(value) {
  const normalised = normaliseRegistration(value);
  return normalised || null;
}

function normaliseManualLocationValue(value) {
  const normalised = normaliseManualLocation(value);
  return normalised || null;
}

function normaliseMsnValue(value) {
  const cleaned = cleanText(value);
  if (!cleaned) {
    return null;
  }
  return cleaned.toUpperCase();
}

function applyAircraftNormalisation(aircraft) {
  if (!aircraft || typeof aircraft !== "object") {
    return aircraft;
  }
  const next = { ...aircraft };
  if (Object.prototype.hasOwnProperty.call(next, "registration")) {
    const normalisedRegistration = normaliseRegistrationValue(next.registration);
    next.registration = normalisedRegistration;
  }
  if (Object.prototype.hasOwnProperty.call(next, "msn")) {
    const normalisedMsn = normaliseMsnValue(next.msn);
    next.msn = normalisedMsn;
  }
  if (Object.prototype.hasOwnProperty.call(next, "manual_status_location")) {
    next.manual_status_location = normaliseManualLocationValue(next.manual_status_location);
  }
  return next;
}

function invalidateStatusCaches(previousAircraft, updatedAircraft) {
  if (!previousAircraft && !updatedAircraft) {
    return;
  }
  const previousKeys = previousAircraft ? getLookupKeys(previousAircraft) : [];
  const nextKeys = updatedAircraft ? getLookupKeys(updatedAircraft) : [];
  const nextKeySet = new Set(nextKeys);
  previousKeys.forEach((key) => {
    if (!nextKeySet.has(key)) {
      state.statuses.delete(key);
    }
  });
  const previousIdent = previousAircraft ? canonicalIdent(getStatusIdent(previousAircraft)) : null;
  const nextIdent = updatedAircraft ? canonicalIdent(getStatusIdent(updatedAircraft)) : null;
  if (previousIdent && previousIdent !== nextIdent) {
    state.statuses.delete(previousIdent);
    state.statusFetchedAt.delete(previousIdent);
    state.statusErrorStreak.delete(previousIdent);
  }
  if (nextIdent && previousIdent !== nextIdent) {
    state.statusFetchedAt.delete(nextIdent);
    state.statusErrorStreak.delete(nextIdent);
    state.statuses.delete(nextIdent);
    const targetId = updatedAircraft?.id || previousAircraft?.id || null;
    logEvent("Status cache cleared after registration change", {
      aircraftId: targetId,
      previousIdent: previousIdent || "n/a",
      nextIdent,
    });
    if (updatedAircraft?.id) {
      refreshStatuses({ aircraftIds: [updatedAircraft.id], force: true }).catch((error) => {
        logEvent("Registration refresh failed", {
          aircraftId: updatedAircraft.id,
          error: error.message,
        });
      });
    }
  }
}

function toDatetimeLocalInput(isoTimestamp) {
  if (!isoTimestamp) return "";
  const parsed = new Date(isoTimestamp);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }
  const pad = (value) => String(value).padStart(2, "0");
  const year = parsed.getFullYear();
  const month = pad(parsed.getMonth() + 1);
  const day = pad(parsed.getDate());
  return `${year}-${month}-${day}`;
}

function fromDatetimeLocalInput(value) {
  return normaliseLocalDateTimeInput(value);
}

function xmlEscape(value) {
  if (value === undefined || value === null) {
    return "";
  }
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function htmlEscape(value) {
  if (value === undefined || value === null) {
    return "";
  }
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function encodeBase64(text) {
  if (typeof text !== "string") {
    return "";
  }
  if (typeof TextEncoder === "function" && typeof btoa === "function") {
    const encoder = new TextEncoder();
    const bytes = encoder.encode(text);
    let binary = "";
    const chunkSize = 0x8000;
    for (let index = 0; index < bytes.length; index += chunkSize) {
      const chunk = bytes.subarray(index, index + chunkSize);
      binary += String.fromCharCode(...chunk);
    }
    return btoa(binary);
  }
  try {
    return window.btoa(unescape(encodeURIComponent(text)));
  } catch (error) {
    console.warn("Unable to base64 encode text", error);
    return "";
  }
}

const DEFAULT_TAM_USER = config.TAM_DEFAULT_USER || "ops";
const DEFAULT_TAM_SOURCE = config.TAM_DEFAULT_SOURCE || "ui";

const configAircraftLimit = parsePositiveInteger(config.AIRCRAFT_LIMIT);
const configDevLimit = parsePositiveInteger(config.DEV_AIRCRAFT_LIMIT);
const resolvedAircraftLimit =
  configAircraftLimit || (isDevMode ? configDevLimit || 10 : null);
const dataReliabilityEnabled = parseBoolean(config.DATA_RELIABILITY_ENABLED, isDevMode);
const dataReliabilityStaleMinutes =
  parsePositiveInteger(config.DATA_RELIABILITY_STALE_MINUTES) || (isProdMode ? 30 : 15);
const UPCOMING_LOOKAHEAD_MS = 30 * 24 * 60 * 60 * 1000;
const UPCOMING_PAST_GRACE_MS = 5 * 60 * 1000;

const defaultOwnerFilter =
  resolvedAircraftLimit > 0
    ? ""
    : isDevMode
    ? cleanText(config.DEV_OWNER_FILTER || "al") || ""
    : "";

const defaultEmailTransportState = {
  mode: null,
  disabled: true,
  message: "Sign in to email reports.",
  lastChecked: null,
  error: null,
};

const state = {
  aircraft: [],
  statuses: new Map(),
  statusFetchedAt: new Map(),
  statusErrorStreak: new Map(),
  comments: new Map(),
  statusHistory: new Map(),
  activeCommentDialog: null,
  activeDebugDialog: null,
  activeStatusDetail: null,
  loadingAircraft: false,
  loadingCachedStatuses: false,
  pendingAircraftReload: false,
  loadingStatuses: false,
  lastRefreshTs: null,
  refreshErrors: 0,
  flightawarePauseUntil: 0,
  ownerOptions: [],
  ownerFilter: defaultOwnerFilter,
  operatorOptions: [],
  operatorFilter: "",
  regionOptions: [],
  regionFilter: "",
  tamFilter: "",
  meFleetFilter: "",
  operatorDirectory: [],
  operatorDirectoryLoaded: false,
  loadingOperators: false,
  sortKey: "msn",
  sortDirection: "asc",
  sessionUser: null,
  flightawareDebug: new Map(),
  aircraftLoadedAt: null,
  importReport: null,
  activeApiBase: null,
  apiBaseDefault: null,
  apiBaseFallback: null,
  apiBaseOverride: null,
  apiBaseSource: "config",
  apiBaseIssue: null,
  aircraftLimit: resolvedAircraftLimit || null,
  aircraftLimitActive: Boolean(resolvedAircraftLimit),
  reliabilitySummary: null,
  dbTables: [],
  dbTablesLoading: false,
  dbTableError: null,
  dbTableData: new Map(),
  activeDbTable: null,
  aircraftSnapshot: new Map(),
  aircraftSnapshotDirty: false,
  statusChanges: [],
  statusChangesLastChecked: null,
  statusChangesDismissed: new Set(),
  aircraftSnapshotLoaded: false,
  onWatch: new Map(),
  onWatchSnapshotLoaded: false,
  onWatchSnapshotDirty: false,
  emailSending: false,
  lastEmailRecipients: [],
  lastEmailStatusCheck: null,
  adminTaskLogs: [],
  adminTaskLogsLoaded: false,
  adminTaskLogsSkipped: false,
  adminTaskLogTimer: null,
  adminBackupInFlight: false,
  adminTaskLogError: null,
  adminWatchSummary: { loading: false, error: null, entries: [], includeInactive: false },
  autoTamPending: new Set(),
  autoTamHistory: new Map(),
  heatmapLastSummary: "",
  emailTransport: { ...defaultEmailTransportState },
};

let watchSyncLogged = false;

const TABLE_LABELS = [
  "MSN",
  "Operator",
  "Owner",
  "Status",
  "Time on ground",
  "Last location",
  "Source",
  "Next flight scheduled",
  "TAM status",
  "Comments",
  "On watch",
  "Links",
];

const TABLE_HEADERS = [
  { label: "MSN", sortKey: "msn", className: "col-msn" },
  { label: "Operator", sortKey: "operator", className: "col-operator" },
  { label: "Owner", sortKey: "owner", className: "col-owner" },
  { label: "Status", sortKey: "status", className: "col-status" },
  { label: "Time on ground", sortKey: "time", className: "col-time" },
  { label: "Last location", sortKey: "location", className: "col-location" },
  {
    label: "Source",
    sortKey: "locationSource",
    className: "col-location-source",
    title:
      "Which system last wrote last_known_location to the database (FA = FlightAware ME report path, FR24 = verification update). Live status still comes from the FlightAware cache.",
  },
  { label: "Next flight scheduled", sortKey: "nextFlight", className: "col-next-flight" },
  { label: "TAM status", sortKey: "tam", className: "col-tam" },
  { label: "Comments", sortKey: "comments", className: "col-comments" },
  { label: "On watch", sortKey: "onWatch", className: "col-on-watch" },
  { label: "Links", sortKey: "links", className: "col-links" },
];

const dom = {
  appShell: document.getElementById("app-shell"),
  modeChip: document.getElementById("mode-chip"),
  refreshIndicator: document.getElementById("refresh-indicator"),
  lastUpdate: document.getElementById("last-update"),
  refreshButton: document.getElementById("refresh-button"),
  searchInput: document.getElementById("search-input"),
  ownerFilter: document.getElementById("owner-filter"),
  operatorFilter: document.getElementById("operator-filter"),
  regionFilter: document.getElementById("region-filter"),
  tamFilter: document.getElementById("tam-filter"),
  meFleetFilter: document.getElementById("me-fleet-filter"),
  autoRefreshNote: document.getElementById("auto-refresh-note"),
  cronRestartButton: document.getElementById("cron-restart-button"),
  heatmapCard: document.getElementById("fleet-heatmap-card"),
  heatmapContainer: document.getElementById("fleet-heatmap"),
  heatmapStatus: document.getElementById("fleet-heatmap-status"),
  exportButton: document.getElementById("export-button"),
  exportPdfButton: document.getElementById("export-pdf-button"),
  emailExportButton: document.getElementById("email-export-button"),
  faUsageButton: document.getElementById("fa-usage-button"),
  fr24UsageButton: document.getElementById("fr24-usage-button"),
  adminCard: document.getElementById("admin-utilities"),
  adminBackupButton: document.getElementById("admin-backup-button"),
  adminRefreshLogs: document.getElementById("admin-refresh-logs"),
  adminCronInstallButton: document.getElementById("admin-cron-install-button"),
  adminCronRunButton: document.getElementById("admin-cron-run-button"),
  adminCronRefreshButton: document.getElementById("admin-cron-refresh-button"),
  adminCronRunDialog: document.getElementById("admin-cron-run-dialog"),
  adminCronRunDialogForm: document.getElementById("admin-cron-run-dialog-form"),
  adminCronRunDialogTitle: document.getElementById("admin-cron-run-dialog-title"),
  adminCronRunDialogClose: document.getElementById("admin-cron-run-dialog-close"),
  adminCronRunDialogCancel: document.getElementById("admin-cron-run-dialog-cancel"),
  adminCronRunDialogSubmit: document.getElementById("admin-cron-run-dialog-submit"),
  adminCronRunDialogError: document.getElementById("admin-cron-run-dialog-error"),
  adminCronRunOwnersCheckboxes: document.getElementById("admin-cron-run-owners-checkboxes"),
  adminCronRunTestMode: document.getElementById("admin-cron-run-test-mode"),
  adminCronRunTestEmail: document.getElementById("admin-cron-run-test-email"),
  adminCronRunTestEmailField: document.getElementById("admin-cron-run-test-email-field"),
  adminCronRunSendEmail: document.getElementById("admin-cron-run-send-email"),
  adminWatchSummary: document.getElementById("admin-watch-summary"),
  adminTaskLog: document.getElementById("admin-task-log"),
  adminUsersCard: document.getElementById("admin-users"),
  adminUsersBody: document.getElementById("admin-users-body"),
  adminUsersAdd: document.getElementById("admin-users-add"),
  adminUsersRefresh: document.getElementById("admin-users-refresh"),
  adminUserDialog: document.getElementById("admin-user-dialog"),
  adminUserDialogForm: document.getElementById("admin-user-dialog-form"),
  adminUserDialogTitle: document.getElementById("admin-user-dialog-title"),
  adminUserDialogClose: document.getElementById("admin-user-dialog-close"),
  adminUserDialogCancel: document.getElementById("admin-user-dialog-cancel"),
  adminUserDialogSubmit: document.getElementById("admin-user-dialog-submit"),
  adminUserDialogError: document.getElementById("admin-user-dialog-error"),
  adminUserUsername: document.getElementById("admin-user-username"),
  adminUserDisplayName: document.getElementById("admin-user-display-name"),
  adminUserInitials: document.getElementById("admin-user-initials"),
  adminUserEmail: document.getElementById("admin-user-email"),
  adminUserPassword: document.getElementById("admin-user-password"),
  adminUserPasswordHint: document.getElementById("admin-user-password-hint"),
  adminUserIsAdmin: document.getElementById("admin-user-is-admin"),
  adminUserOwnerCodes: document.getElementById("admin-user-owner-codes"),
  adminUserActive: document.getElementById("admin-user-active"),
  adminOperatorsCard: document.getElementById("admin-operators"),
  adminOperatorsBody: document.getElementById("admin-operators-body"),
  adminOperatorsRefresh: document.getElementById("admin-operators-refresh"),
  adminOperatorsDialog: document.getElementById("admin-operators-dialog"),
  adminOperatorsDialogForm: document.getElementById("admin-operators-dialog-form"),
  adminOperatorsDialogTitle: document.getElementById("admin-operators-dialog-title"),
  adminOperatorsDialogClose: document.getElementById("admin-operators-dialog-close"),
  adminOperatorsDialogCancel: document.getElementById("admin-operators-dialog-cancel"),
  adminOperatorsDialogSubmit: document.getElementById("admin-operators-dialog-submit"),
  adminOperatorsDialogError: document.getElementById("admin-operators-dialog-error"),
  adminOperatorsDialogSummary: document.getElementById("admin-operators-dialog-summary"),
  adminOperatorsContacts: document.getElementById("admin-operators-contacts"),
  adminOperatorsAddContact: document.getElementById("admin-operators-add-contact"),
  adminAircraftCard: document.getElementById("admin-aircraft"),
  adminAircraftBody: document.getElementById("admin-aircraft-body"),
  adminAircraftAdd: document.getElementById("admin-aircraft-add"),
  adminAircraftRefresh: document.getElementById("admin-aircraft-refresh"),
  adminAircraftDialog: document.getElementById("admin-aircraft-dialog"),
  adminAircraftDialogForm: document.getElementById("admin-aircraft-dialog-form"),
  adminAircraftDialogTitle: document.getElementById("admin-aircraft-dialog-title"),
  adminAircraftDialogClose: document.getElementById("admin-aircraft-dialog-close"),
  adminAircraftDialogCancel: document.getElementById("admin-aircraft-dialog-cancel"),
  adminAircraftDialogSubmit: document.getElementById("admin-aircraft-dialog-submit"),
  adminAircraftDialogError: document.getElementById("admin-aircraft-dialog-error"),
  adminAircraftIdent: document.getElementById("admin-aircraft-ident"),
  adminAircraftRegistration: document.getElementById("admin-aircraft-registration"),
  adminAircraftType: document.getElementById("admin-aircraft-type"),
  adminAircraftMsn: document.getElementById("admin-aircraft-msn"),
  adminAircraftTail: document.getElementById("admin-aircraft-tail"),
  adminAircraftOperator: document.getElementById("admin-aircraft-operator"),
  adminAircraftOwner: document.getElementById("admin-aircraft-owner"),
  adminAircraftUrl: document.getElementById("admin-aircraft-url"),
  adminAircraftFlightawareUrl: document.getElementById("admin-aircraft-flightaware-url"),
  adminAircraftRegion: document.getElementById("admin-aircraft-region"),
  adminAircraftNotes: document.getElementById("admin-aircraft-notes"),
  adminAircraftExpectedRelease: document.getElementById("admin-aircraft-expected-release"),
  adminAircraftActive: document.getElementById("admin-aircraft-active"),
  operatorOutreachDialog: document.getElementById("operator-outreach-dialog"),
  operatorOutreachClose: document.getElementById("operator-outreach-dialog-close"),
  operatorOutreachCancel: document.getElementById("operator-outreach-dialog-cancel"),
  operatorOutreachMarkSent: document.getElementById("operator-outreach-mark-sent"),
  operatorOutreachContact: document.getElementById("operator-outreach-contact"),
  operatorOutreachSubject: document.getElementById("operator-outreach-subject"),
  operatorOutreachBody: document.getElementById("operator-outreach-body"),
  operatorOutreachError: document.getElementById("operator-outreach-dialog-error"),
  statusChangeAlert: null, // Will be created dynamically
  cronLogsDialog: document.getElementById("cron-logs-dialog"),
  cronLogsDialogTitle: document.getElementById("cron-logs-dialog-title"),
  cronLogsDialogContent: document.getElementById("cron-logs-dialog-content"),
  cronLogsDialogClose: document.getElementById("cron-logs-dialog-close"),
  usageDialog: document.getElementById("usage-dialog"),
  usageDialogTitle: document.getElementById("usage-dialog-title"),
  usageDialogContent: document.getElementById("usage-dialog-content"),
  usageDialogClose: document.getElementById("usage-dialog-close"),
  tableBody: document.getElementById("aircraft-body"),
  dataReport: document.getElementById("data-report"),
  dataReportStamp: document.getElementById("data-report-stamp"),
  dataReportSummary: document.getElementById("data-report-summary"),
  dataReportMetrics: document.getElementById("data-report-metrics"),
  dataReportReliability: document.getElementById("data-report-reliability"),
  dataReportReliabilitySummary: document.getElementById("data-report-reliability-summary"),
  dataReportReliabilityList: document.getElementById("data-report-reliability-list"),
  dataReportReliabilityInstructions: document.getElementById("data-report-reliability-instructions"),
  dataReportReliabilitySteps: document.getElementById("data-report-reliability-steps"),
  devConsole: document.getElementById("dev-console"),
  devStream: document.getElementById("dev-log-stream"),
  devTableTools: document.getElementById("dev-table-tools"),
  devTableControls: document.getElementById("dev-table-controls"),
  devTableOutput: document.getElementById("dev-table-output"),
  devTableClear: document.getElementById("dev-table-clear"),
  devInterpretationGuide: document.getElementById("dev-interpretation-guide"),
  loginShell: document.getElementById("login-shell"),
  loginForm: document.getElementById("login-form"),
  loginUser: document.getElementById("login-user"),
  loginPassword: document.getElementById("login-password"),
  loginError: document.getElementById("login-error"),
  loginSubmit: document.getElementById("login-submit"),
  sessionChip: document.getElementById("session-chip"),
  sessionUser: document.getElementById("session-user"),
  logoutButton: document.getElementById("logout-button"),
  commentDialog: document.getElementById("comment-dialog"),
  commentDialogForm: document.getElementById("comment-dialog-form"),
  commentDialogStatus: document.getElementById("comment-dialog-status"),
  commentDialogComment: document.getElementById("comment-dialog-comment"),
  commentDialogTitle: document.getElementById("comment-dialog-title"),
  commentDialogAircraft: document.getElementById("comment-dialog-aircraft"),
  commentDialogRegistration: document.getElementById("comment-dialog-registration"),
  commentDialogMsn: document.getElementById("comment-dialog-msn"),
  commentDialogOperator: document.getElementById("comment-dialog-operator"),
  commentDialogExpectedReleaseField: document.getElementById("comment-dialog-expected-release-field"),
  commentDialogExpectedRelease: document.getElementById("comment-dialog-expected-release"),
  commentDialogExpectedReleaseHint: document.getElementById("comment-dialog-expected-release-hint"),
  commentDialogCancel: document.getElementById("comment-dialog-cancel"),
  commentDialogClose: document.getElementById("comment-dialog-close"),
  commentDialogSubmit: document.getElementById("comment-dialog-submit"),
  commentDialogError: document.getElementById("comment-dialog-error"),
  commentDialogHint: document.getElementById("comment-dialog-hint"),
  commentDialogStatusHistory: document.getElementById("comment-dialog-status-history"),
  commentDialogStatusHistoryList: document.getElementById("comment-dialog-status-history-list"),
  commentDialogStatusHistoryMeta: document.getElementById("comment-dialog-status-history-meta"),
  commentDialogStatusHistoryEmpty: document.getElementById("comment-dialog-status-history-empty"),
  commentDialogCommentsHistory: document.getElementById("comment-dialog-comments-history"),
  commentDialogCommentsHistoryList: document.getElementById("comment-dialog-comments-history-list"),
  commentDialogCommentsHistoryMeta: document.getElementById("comment-dialog-comments-history-meta"),
  commentDialogCommentsHistoryEmpty: document.getElementById("comment-dialog-comments-history-empty"),
  manualStatusPanel: document.getElementById("manual-status-panel"),
  manualLastFlight: document.getElementById("manual-last-flight"),
  manualLastLocation: document.getElementById("manual-last-location"),
  manualStatusPreview: document.getElementById("manual-status-preview"),
  manualStatusHint: document.getElementById("manual-status-hint"),
  debugDialog: document.getElementById("debug-dialog"),
  debugDialogTitle: document.getElementById("debug-dialog-title"),
  debugDialogClose: document.getElementById("debug-dialog-close"),
  debugDialogContent: document.getElementById("debug-dialog-content"),
  statusDialog: document.getElementById("status-dialog"),
  statusDialogTitle: document.getElementById("status-dialog-title"),
  statusDialogClose: document.getElementById("status-dialog-close"),
  statusDialogContent: document.getElementById("status-dialog-content"),
};

const TASK_CHECKLIST = [
  {
    request: "Re-evaluate status after registration changes",
    action: "Invalidate cached FlightAware lookups and trigger a targeted refresh whenever a registration is updated.",
    status: "completed",
  },
  {
    request: "Persist uppercase registration and last location",
    action: "Normalise both fields to uppercase before saving and when loading data into the dashboard.",
    status: "completed",
  },
  {
    request: "Excel export button missing",
    action: "Keep the Download Excel control visible in the filters bar and surface usage guidance in the dev log.",
    status: "completed",
  },
  {
    request: "Manual override editor losing values",
    action: "Normalise and retain manual override fields after refreshes, filters, and dialog submissions.",
    status: "completed",
  },
  {
    request: "Operator edition feedback",
    action: "Synchronise operator and registration updates immediately in the dialog and table for quick verification.",
    status: "completed",
  },
  {
    request: "TAM status change requires comment",
    action: "Allow saving TAM status selections without forcing a comment and honour the operator's choice even if the label stays the same.",
    status: "completed",
  },
  {
    request: "Manual status override missing last flight",
    action: "Auto-calculate and persist the override status when only the last flight is provided so time on ground updates correctly.",
    status: "completed",
  },
  {
    request: "Flag repeated FlightAware retrieval errors",
    action: "Retry each fetch once automatically and mark the table with a red indicator when both attempts fail so the record can be reviewed.",
    status: "completed",
  },
  {
    request: "Manual refresh snapshots disappear after reload",
    action: "Persist every manual FlightAware refresh directly into the cache so filters, resorting, and full reloads keep the latest status.",
    status: "completed",
  },
  {
    request: "Last flight overrides missing when time omitted",
    action: "Accept date-only entries by defaulting to midnight so the grounded duration can be recalculated reliably.",
    status: "completed",
  },
  {
    request: "Manual refresh snapshots tracked by every alias",
    action:
      "Save FlightAware snapshots for every aircraft alias (registration, tail, ident, MSN) and prefer the freshest timestamp before writing to the cache.",
    status: "completed",
  },
  {
    request: "Allow MSN edits in the status dialog",
    action: "Expose the MSN field in the update dialog, require a value, and persist changes through the aircraft profile API.",
    status: "completed",
  },
];

function extractAllowedOwnersFromUser(user) {
  if (!user) {
    return null;
  }
  if (user.allowedOwners === null || user.allowedOwners === undefined) {
    return null;
  }
  const list = Array.isArray(user.allowedOwners) ? user.allowedOwners : [];
  return list.map((value) => cleanText(value)).filter(Boolean);
}

function getAllowedOwners() {
  if (!state.sessionUser) {
    return null;
  }
  const allowed = state.sessionUser.allowedOwners;
  if (allowed === null || allowed === undefined) {
    return null;
  }
  if (!Array.isArray(allowed)) {
    return [];
  }
  return allowed.map((value) => cleanText(value)).filter(Boolean);
}

/** True when the row's owner column matches the user's allowed owner codes (same scope as the fleet list). */
function aircraftOwnerInSessionScope(aircraft, snapshot = null) {
  const fromRow = (cleanText(aircraft?.owner) || "").toUpperCase();
  const fromSnap = (cleanText(snapshot?.owner) || "").toUpperCase();
  const acOwner = fromRow || fromSnap;
  const allowed = getAllowedOwners();
  if (!Array.isArray(allowed)) {
    return true;
  }
  if (!allowed.length || !acOwner) {
    return false;
  }
  return allowed.some((o) => (cleanText(o) || "").toUpperCase() === acOwner);
}

function isAdminUser() {
  return Boolean(state.sessionUser?.isAdmin);
}

function isOwnerRestricted() {
  const allowed = getAllowedOwners();
  return Array.isArray(allowed);
}

function ensureOwnerFilterIsValid() {
  const allowed = getAllowedOwners();
  if (!Array.isArray(allowed)) {
    return;
  }
  if (!allowed.length) {
    state.ownerFilter = "";
    return;
  }
  const current = cleanText(state.ownerFilter);
  if (!current && allowed.length === 1) {
    state.ownerFilter = allowed[0];
  }
}

function getOwnerFilterLabel() {
  const allowed = getAllowedOwners();
  if (!Array.isArray(allowed)) {
    return state.ownerFilter || "All owners";
  }
  if (!allowed.length) {
    return "No assigned owners";
  }
  if (!state.ownerFilter) {
    return allowed.length === 1 ? allowed[0] : "All assigned owners";
  }
  return state.ownerFilter;
}

const API_BASE_STORAGE_KEY = "actracker.apiBaseOverride";
const AIRCRAFT_SNAPSHOT_STORAGE_KEY = "actracker.aircraftSnapshot.v1";
let onWatchPersistTimer = null;
// Legacy-style fast preload: fetch all cached statuses in one request.
// This restores the "instant" initial render behavior for ~180 aircraft.
const CACHED_STATUS_BATCH_SIZE = 250;
const DB_TABLE_FETCH_LIMIT = 20;
const RELEVANT_TABLE_PATTERN = /(flightaware|aircraft|tracking)/i;
const FLIGHTAWARE_GUIDE_STEPS = [
  "Call GET https://aeroapi.flightaware.com/aeroapi/flights/{REG}?ident_type=registration once per tail and include x-apikey.",
  "Use flights[0] for the most recent activity. The aircraft is airborne when actual_off exists and actual_on is still null (optionally confirm with last_position altitude).",
  "Find the most recent flight with actual_on to determine the last landing time and airport (destination.code/name).",
  "When the aircraft is on the ground, prefer last_position for coordinates; otherwise fall back to the last destination airport.",
  "Upcoming flights are flights with scheduled_out or scheduled_off in the future and no actual_off yet; filter by timestamps you need to monitor.",
  "Timestamps are UTC (ISO-8601). Convert using origin.timezone or destination.timezone when you need local times, and derive status labels from the same payload (scheduled/airborne/arrived/cancelled).",
];

function safeReadLocalStorage(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    console.debug("[ui] localStorage read failed", { error: error.message });
    return null;
  }
}

function safeWriteLocalStorage(key, value) {
  try {
    if (value === null || value === undefined) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch (error) {
    console.debug("[ui] localStorage write failed", { error: error.message });
  }
}

function getWatchEntry(id) {
  const numeric = Number(id);
  if (!Number.isFinite(numeric)) {
    return null;
  }
  return state.onWatch.get(numeric) || null;
}

function persistOnWatchSnapshot() {
  if (!state.onWatchSnapshotLoaded) {
    return;
  }
  try {
    const payload = buildWatchStoragePayload(state.onWatch);
    if (!payload) {
      safeWriteLocalStorage(ON_WATCH_STORAGE_KEY, null);
      state.onWatchSnapshotDirty = false;
      if (devToolsEnabled) {
        logEvent("Cleared on watch snapshot", {});
      }
      return;
    }
    safeWriteLocalStorage(ON_WATCH_STORAGE_KEY, JSON.stringify(payload));
    state.onWatchSnapshotDirty = false;
    if (devToolsEnabled) {
      logEvent("Persisted on watch snapshot", { entries: payload.entries.length });
    }
  } catch (error) {
    logEvent("Failed to persist on watch snapshot", { error: error.message });
  }
}

function scheduleOnWatchSnapshotPersist() {
  if (!state.onWatchSnapshotLoaded) {
    return;
  }
  state.onWatchSnapshotDirty = true;
  if (onWatchPersistTimer) {
    clearTimeout(onWatchPersistTimer);
  }
  onWatchPersistTimer = window.setTimeout(() => {
    onWatchPersistTimer = null;
    if (state.onWatchSnapshotDirty) {
      persistOnWatchSnapshot();
    }
  }, 250);
}

function updateWatchEntryInState(aircraftId, watch = null) {
  const numeric = Number(aircraftId);
  if (!Number.isFinite(numeric)) {
    return;
  }
  const entry = normaliseWatchSnapshotEntry(watch);
  if (entry) {
    state.onWatch.set(numeric, entry);
  } else {
    state.onWatch.delete(numeric);
  }

  const target = state.aircraft.find((item) => Number(item.id) === numeric);
  if (target) {
    target.watch_on = entry?.onWatch || false;
    target.watch_reason = entry?.reason || null;
    target.watch_source = entry?.source || null;
    target.watch_scheduled_departure = entry?.scheduledDepartureAt || null;
    target.watch_scheduled_context = entry?.scheduledContext || null;
    target.watch_triggered_at = entry?.triggeredAt || null;
    target.watch_payload = entry?.payload || {};
    target.watch_updated_at = entry?.updatedAt || null;
    target.watch_created_at = entry?.createdAt || null;
  }

  scheduleOnWatchSnapshotPersist();
}

function syncWatchState() {
  const previous = new Map(state.onWatch);
  state.onWatch.clear();
  state.aircraft.forEach((aircraft) => {
    const watch = {
      onWatch: aircraft.watch_on,
      reason: aircraft.watch_reason,
      source: aircraft.watch_source,
      scheduledDepartureAt: aircraft.watch_scheduled_departure,
      scheduledContext: aircraft.watch_scheduled_context,
      triggeredAt: aircraft.watch_triggered_at,
      payload: aircraft.watch_payload,
      updatedAt: aircraft.watch_updated_at,
      createdAt: aircraft.watch_created_at,
    };
    updateWatchEntryInState(aircraft.id, watch);
  });
  const tracked = state.onWatch.size;
  let activeCount = 0;
  state.onWatch.forEach((entry) => {
    if (entry?.onWatch) {
      activeCount += 1;
    }
  });
  const previousTracked = previous.size;
  let previousActive = 0;
  previous.forEach((entry) => {
    if (entry?.onWatch) {
      previousActive += 1;
    }
  });
  if (!watchSyncLogged || previousTracked !== tracked || previousActive !== activeCount) {
    logEvent("Watch state synchronised", { tracked, active: activeCount });
    watchSyncLogged = true;
  }
  scheduleOnWatchSnapshotPersist();
}

async function setAircraftOnWatch(aircraft, enabled, toggle = null) {
  if (!state.sessionUser) {
    handleUnauthenticated("Session expired. Please sign in again.");
    if (toggle) {
      toggle.checked = !enabled;
    }
    return;
  }
  const numericId = Number(aircraft?.id);
  if (!Number.isFinite(numericId)) {
    return;
  }
  const previousEntry = getWatchEntry(numericId);
  const previousState = Boolean(previousEntry?.onWatch);
  const label =
    cleanText(aircraft?.registration) ||
    cleanText(aircraft?.ident) ||
    cleanText(aircraft?.tail) ||
    `Aircraft ${numericId}`;

  if (toggle) {
    toggle.disabled = true;
    toggle.setAttribute("aria-busy", "true");
  }

  try {
    const response = await fetchJson(apiUrl(`/api/aircraft/${numericId}/watch`), {
      method: "POST",
      body: JSON.stringify({ onWatch: enabled, reason: "manual_toggle" }),
    });
    const watch = response?.watch || response;
    updateWatchEntryInState(numericId, watch);
    const meta = {
      aircraftId: numericId,
      onWatch: enabled,
      source: "api",
    };
    if (watch?.reason) {
      meta.reason = watch.reason;
    }
    if (watch?.scheduledDepartureAt) {
      meta.scheduledDepartureAt = watch.scheduledDepartureAt;
    }
    if (watch?.triggeredAt) {
      meta.triggeredAt = watch.triggeredAt;
    }
    logEvent("On watch updated", meta);
    renderTable();
    if (isAdminUser()) {
      loadAdminWatchSummary({ reason: "toggle" }).catch((error) => {
        logEvent("Watch summary refresh failed", { error: error.message });
      });
    }
    const confirmation = enabled
      ? `${label} added to watch list.`
      : `${label} removed from watch list.`;
    setRefreshIndicator(confirmation);
  } catch (error) {
    logEvent("Failed to update on watch", { aircraftId: numericId, error: error.message });
    if (toggle) {
      toggle.checked = previousState;
    }
    setRefreshIndicator(`On watch update failed for ${label}: ${error.message}`);
  } finally {
    if (toggle) {
      toggle.disabled = false;
      toggle.removeAttribute("aria-busy");
    }
  }
}

function renderAdminWatchSummary() {
  if (!dom.adminWatchSummary) {
    return;
  }
  if (!isAdminUser()) {
    dom.adminWatchSummary.hidden = true;
    dom.adminWatchSummary.textContent = "";
    return;
  }
  const summary = state.adminWatchSummary;
  const parts = [];
  let activeCount = 0;
  if (summary.loading) {
    parts.push("Loading on watch list…");
  }
  if (summary.error) {
    parts.push(`Error: ${summary.error}`);
  }
  if (!summary.loading && !summary.error) {
    activeCount = summary.entries.filter((entry) => entry.watch?.onWatch).length;
    parts.push(`${activeCount} aircraft on watch`);
    if (summary.includeInactive) {
      parts.push(`${summary.entries.length} total entries`);
    }
  }
  if (!parts.length) {
    dom.adminWatchSummary.hidden = true;
    dom.adminWatchSummary.textContent = "";
    return;
  }
  dom.adminWatchSummary.hidden = false;
  dom.adminWatchSummary.textContent = parts.join(" · ");
  dom.adminWatchSummary.dataset.loading = summary.loading ? "true" : "false";
  dom.adminWatchSummary.dataset.error = summary.error ? "true" : "false";
  dom.adminWatchSummary.dataset.active = String(activeCount);
  dom.adminWatchSummary.dataset.total = String(summary.entries?.length || 0);
  dom.adminWatchSummary.dataset.includeInactive = summary.includeInactive ? "true" : "false";
}

async function loadAdminWatchSummary({ includeInactive = false, reason = "auto" } = {}) {
  if (!isAdminUser()) {
    return;
  }
  state.adminWatchSummary = {
    ...state.adminWatchSummary,
    loading: true,
    error: null,
    includeInactive,
  };
  renderAdminWatchSummary();
  try {
    const query = includeInactive ? "?includeInactive=true" : "";
    const response = await fetchJson(apiUrl(`/api/aircraft/watch${query}`));
    const entries = Array.isArray(response?.entries) ? response.entries : [];
    state.adminWatchSummary = {
      loading: false,
      error: null,
      entries,
      includeInactive,
    };
    renderAdminWatchSummary();
    logEvent("Watch summary refreshed", { reason, count: entries.length, includeInactive });
  } catch (error) {
    state.adminWatchSummary = {
      ...state.adminWatchSummary,
      loading: false,
      error: error.message || "Unable to load watch summary",
    };
    renderAdminWatchSummary();
    logEvent("Watch summary failed", { error: error.message });
  }
}

function normaliseBaseForCompare(value) {
  if (!value) return "";
  try {
    const url = new URL(value, window.location.origin);
    return url.toString().replace(/\/+$/, "");
  } catch (_error) {
    return String(value).trim();
  }
}

function chunkArray(list, size) {
  const result = [];
  const safeSize = Math.max(1, Number(size) || 1);
  for (let index = 0; index < list.length; index += safeSize) {
    result.push(list.slice(index, index + safeSize));
  }
  return result;
}

const debounce = (fn, ms = 120) => {
  let timer = null;
  return (...args) => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => fn(...args), ms);
  };
};

function setHeaderHeightVar() {
  const header =
    document.querySelector("[data-app-header]") || document.querySelector(".app-header");
  if (!header) {
    return;
  }
  const rect = header.getBoundingClientRect();
  const height = Math.max(0, Math.round(rect.height));
  if (height > 0) {
    document.documentElement.style.setProperty("--app-header-height", `${height}px`);
  }
}

const updateHeaderVars = debounce(setHeaderHeightVar, 120);
window.addEventListener("resize", updateHeaderVars);

if (document.fonts && document.fonts.ready) {
  document.fonts.ready.then(setHeaderHeightVar).catch(() => {});
}

const headerElement =
  document.querySelector("[data-app-header]") || document.querySelector(".app-header");
if (headerElement && typeof MutationObserver === "function") {
  const observer = new MutationObserver(() => updateHeaderVars());
  observer.observe(headerElement, { subtree: true, childList: true, attributes: true });
}

function hasMeaningfulValue(value) {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === "string") {
    return value.trim() !== "";
  }
  return true;
}

function serialiseAircraftSnapshot(aircraft) {
  if (!aircraft || typeof aircraft !== "object") {
    return null;
  }
  const numericId = Number(aircraft.id);
  if (!Number.isFinite(numericId)) {
    return null;
  }
  return {
    id: numericId,
    registration: cleanText(aircraft.registration) || null,
    msn: cleanText(aircraft.msn) || null,
    tam_status: cleanText(aircraft.tam_status) || null,
    tam_status_source: cleanText(aircraft.tam_status_source) || null,
    tam_status_user: cleanText(aircraft.tam_status_user) || null,
    tam_status_updated_at: aircraft.tam_status_updated_at || null,
    operator_id: Number.isFinite(Number(aircraft.operator_id))
      ? Number(aircraft.operator_id)
      : null,
    operator: cleanText(aircraft.operator) || null,
    operator_contact_email: cleanText(aircraft.operator_contact_email) || null,
    operator_contacts: Array.isArray(aircraft.operator_contacts) ? aircraft.operator_contacts : null,
    expected_release_date: cleanText(aircraft.expected_release_date) || null,
    owner: cleanText(aircraft.owner) || null,
  };
}

const persistAircraftSnapshotToStorage = debounce(() => {
  const entries = Array.from(state.aircraftSnapshot.values());
  const payload = {
    savedAt: new Date().toISOString(),
    entries,
  };
  safeWriteLocalStorage(AIRCRAFT_SNAPSHOT_STORAGE_KEY, JSON.stringify(payload));
  state.aircraftSnapshotDirty = false;
}, 400);

function scheduleAircraftSnapshotPersist() {
  state.aircraftSnapshotDirty = true;
  persistAircraftSnapshotToStorage();
}

function recordAircraftSnapshot(aircraft, options = {}) {
  if (!aircraft || typeof aircraft !== "object") {
    return;
  }
  const numericId = Number(aircraft.id);
  if (!Number.isFinite(numericId)) {
    return;
  }
  const snapshot = serialiseAircraftSnapshot(aircraft);
  if (!snapshot) {
    return;
  }
  state.aircraftSnapshot.set(numericId, snapshot);
  if (!options.skipPersist) {
    scheduleAircraftSnapshotPersist();
  }
}

function getAircraftSnapshot(idOrAircraft) {
  const numericId = Number(
    typeof idOrAircraft === "object" ? idOrAircraft?.id : idOrAircraft
  );
  if (!Number.isFinite(numericId)) {
    return null;
  }
  return state.aircraftSnapshot.get(numericId) || null;
}

function mergeAircraftWithSnapshot(aircraft, options = {}) {
  if (!aircraft || typeof aircraft !== "object") {
    return { merged: aircraft, restoredFields: 0 };
  }
  const merged = { ...aircraft };
  const numericId = Number(merged.id);
  if (!Number.isFinite(numericId)) {
    return { merged, restoredFields: 0 };
  }
  const snapshot = state.aircraftSnapshot.get(numericId);
  let restoredFields = 0;
  if (snapshot) {
    const preservedKeys = [
      "registration",
      "msn",
      "tam_status",
      "tam_status_source",
      "tam_status_user",
      "tam_status_updated_at",
      "operator_id",
      "operator",
      "operator_contact_email",
      "operator_contacts",
      "expected_release_date",
      "owner",
    ];
    preservedKeys.forEach((key) => {
      if (!hasMeaningfulValue(merged[key]) && hasMeaningfulValue(snapshot[key])) {
        merged[key] = snapshot[key];
        restoredFields += 1;
      }
    });
  }
  recordAircraftSnapshot(merged, { skipPersist: options.skipPersist });
  return { merged, restoredFields };
}

function hydrateAircraftSnapshotFromStorage() {
  if (state.aircraftSnapshotLoaded) {
    return;
  }
  const raw = safeReadLocalStorage(AIRCRAFT_SNAPSHOT_STORAGE_KEY);
  if (!raw) {
    state.aircraftSnapshotLoaded = true;
    return;
  }
  try {
    const parsed = JSON.parse(raw);
    const entries = Array.isArray(parsed?.entries)
      ? parsed.entries
      : Array.isArray(parsed)
      ? parsed
      : [];
    entries.forEach((entry) => {
      const numericId = Number(entry?.id);
      if (!Number.isFinite(numericId)) {
        return;
      }
      const snapshot = {
        id: numericId,
        registration: cleanText(entry.registration) || null,
        msn: cleanText(entry.msn) || null,
        tam_status: cleanText(entry.tam_status) || null,
        tam_status_source: cleanText(entry.tam_status_source) || null,
        tam_status_user: cleanText(entry.tam_status_user) || null,
        tam_status_updated_at: entry.tam_status_updated_at || null,
        operator_id: Number.isFinite(Number(entry.operator_id))
          ? Number(entry.operator_id)
          : null,
        operator: cleanText(entry.operator) || null,
        operator_contact_email: cleanText(entry.operator_contact_email) || null,
        operator_contacts: Array.isArray(entry.operator_contacts) ? entry.operator_contacts : null,
        expected_release_date: cleanText(entry.expected_release_date) || null,
        owner: cleanText(entry.owner) || null,
      };
      state.aircraftSnapshot.set(numericId, snapshot);
    });
    state.aircraftSnapshotLoaded = true;
    if (devToolsEnabled) {
      logEvent("Restored aircraft snapshot", {
        entries: state.aircraftSnapshot.size,
      });
    }
  } catch (error) {
    state.aircraftSnapshot.clear();
    state.aircraftSnapshotLoaded = true;
    logEvent("Failed to restore aircraft snapshot", { error: error.message });
    safeWriteLocalStorage(AIRCRAFT_SNAPSHOT_STORAGE_KEY, null);
  }
}

function hydrateOnWatchFromStorage() {
  if (state.onWatchSnapshotLoaded) {
    return;
  }
  const raw = safeReadLocalStorage(ON_WATCH_STORAGE_KEY);
  state.onWatchSnapshotLoaded = true;
  state.onWatchSnapshotDirty = false;
  if (!raw) {
    return;
  }
  const { entries, error } = parseStoredWatchEntries(raw);
  if (error) {
    logEvent("Failed to restore on watch snapshot", { error: error.message });
    safeWriteLocalStorage(ON_WATCH_STORAGE_KEY, null);
    state.onWatch.clear();
    return;
  }
  if (!entries.length) {
    return;
  }
  entries.forEach(([id, entry]) => {
    state.onWatch.set(id, entry);
  });
  if (devToolsEnabled) {
    logEvent("Restored on watch snapshot", { restored: entries.length });
  }
}

function resetOnWatchSnapshotStorage(reason, meta = {}) {
  state.onWatchSnapshotLoaded = true;
  state.onWatchSnapshotDirty = false;
  state.onWatch.clear();
  safeWriteLocalStorage(ON_WATCH_STORAGE_KEY, null);
  const detail = { reason, ...meta };
  if (devToolsEnabled) {
    logEvent("On watch snapshot reset", detail);
  } else {
    console.warn("[ui] On watch snapshot reset", detail);
  }
}

function tryHydrateOnWatchSnapshot() {
  try {
    if (typeof hydrateOnWatchFromStorage !== "function") {
      resetOnWatchSnapshotStorage("helper_missing");
      return false;
    }
    hydrateOnWatchFromStorage();
    return true;
  } catch (error) {
    const message = error && error.message ? error.message : String(error);
    resetOnWatchSnapshotStorage("hydrate_failed", { error: message });
    return false;
  }
}

const fallbackApiBase = resolveApiBase(config.FALLBACK_API_BASE || window.location.origin);
const defaultConfigApiBase = config.RAW_API_BASE
  ? resolveApiBase(config.RAW_API_BASE)
  : config.API_BASE
  ? resolveApiBase(config.API_BASE)
  : fallbackApiBase;
// Production always uses config / same-origin API — never a saved localhost override.
if (isProdMode) {
  safeWriteLocalStorage(API_BASE_STORAGE_KEY, null);
}
const storedApiBaseOverride = isProdMode
  ? null
  : safeReadLocalStorage(API_BASE_STORAGE_KEY);
let currentApiBase = resolveApiBase(
  storedApiBaseOverride || config.API_BASE || fallbackApiBase
);

state.activeApiBase = currentApiBase;
state.apiBaseDefault = defaultConfigApiBase;
state.apiBaseFallback = fallbackApiBase;
state.apiBaseSource = storedApiBaseOverride ? "override" : "config";
state.apiBaseOverride = storedApiBaseOverride ? currentApiBase : null;
updateApiBaseStatus();

const STATUS_REQUEST_DELAY_MS = 250; // Optimized to 4 requests/second (250ms each)
const STATUS_CACHE_TTL_MS = isProdMode ? 15 * 60 * 1000 : 5 * 60 * 1000;
const STATUS_ERROR_RETRY_MS = 5 * 60 * 1000;
const STATUS_FETCH_MAX_ATTEMPTS = 2;
const STATUS_RETRY_DELAY_MS = isProdMode ? 2000 : 800;
const RATE_LIMIT_BACKOFF_MS = isProdMode ? 15 * 60 * 1000 : 10 * 60 * 1000;
const MAX_LOG_ENTRIES = 400;
const DATA_RELIABILITY_STALE_MS = Math.max(
  dataReliabilityStaleMinutes * 60 * 1000,
  STATUS_CACHE_TTL_MS
);
const RELIABILITY_BASE_STEPS = [
  "Step 1 — open the FlightAware (FA) link in the Links column.",
  "Step 2 — compare the aircraft's status, times, and airport codes on FlightAware with this dashboard.",
  "Step 3 — press Manual Refresh and update the TAM status or leave a comment if anything is different.",
];
const CATEGORY_CLASS_MAP = {
  "in-service": "success",
  "on-ground": "warning",
  storage: "info",
  unknown: "muted",
};

const TAM_STATUS_CLASS_MAP = new Map([
  ["IN SERVICE", "tam-chip--status-in-service"],
  ["IN STORAGE", "tam-chip--status-storage"],
  ["STORAGE", "tam-chip--status-storage"],
  ["AOG", "tam-chip--status-aog"],
  ["AWAITING OPERATOR FEEDBACK", "tam-chip--status-awaiting"],
  ["CHECK WITH FM", "tam-chip--status-check-fm"],
  ["CHECK WITH DM", "tam-chip--status-check-fm"],
  ["MAINTENANCE", "tam-chip--status-maintenance"],
  ["TRANSITION", "tam-chip--status-transition"],
  ["INCIDENT", "tam-chip--status-incident"],
  ["UNKNOWN", "tam-chip--status-unknown"],
]);

const HEATMAP_STATUS_CLASS_MAP = new Map([
  ["IN SERVICE", "heatmap-tile--status-in-service"],
  ["AOG", "heatmap-tile--status-aog"],
  ["AWAITING OPERATOR FEEDBACK", "heatmap-tile--status-awaiting"],
  ["CHECK WITH FM", "heatmap-tile--status-check-fm"],
  ["CHECK WITH DM", "heatmap-tile--status-check-fm"],
  ["IN STORAGE", "heatmap-tile--status-storage"],
  ["STORAGE", "heatmap-tile--status-storage"],
  ["MAINTENANCE", "heatmap-tile--status-maintenance"],
  ["TRANSITION", "heatmap-tile--status-transition"],
  ["INCIDENT", "heatmap-tile--status-incident"],
  ["UNKNOWN", "heatmap-tile--status-unknown"],
]);

const HEATMAP_CATEGORY_CLASS_MAP = {
  "in-service": "heatmap-tile--status-in-service",
  storage: "heatmap-tile--status-storage",
  aog: "heatmap-tile--status-aog",
  "on-ground": "heatmap-tile--status-grounded",
  unknown: "heatmap-tile--status-unknown",
};

const HEATMAP_STATUS_SORT_ORDER = new Map([
  ["heatmap-tile--status-aog", 0],
  ["heatmap-tile--status-incident", 1],
  ["heatmap-tile--status-maintenance", 2],
  ["heatmap-tile--status-transition", 3],
  ["heatmap-tile--status-awaiting", 4],
  ["heatmap-tile--status-check-fm", 5],
  ["heatmap-tile--status-grounded", 6],
  ["heatmap-tile--status-storage", 7],
  ["heatmap-tile--status-in-service", 8],
  ["heatmap-tile--status-unknown", 9],
]);

const HEATMAP_STATUS_FALLBACK_CLASS = "heatmap-tile--status-unknown";

const EMAIL_HEATMAP_STATUS_STYLE_MAP = new Map([
  ["heatmap-tile--status-in-service", { background: "#2BAA66", color: "#FFFFFF" }],
  ["heatmap-tile--status-storage", { background: "#1F6FEB", color: "#FFFFFF" }],
  ["heatmap-tile--status-aog", { background: "#8E0404", color: "#FFFFFF" }],
  ["heatmap-tile--status-awaiting", { background: "#D6922D", color: "#1E1F24" }],
  ["heatmap-tile--status-grounded", { background: "#D6922D", color: "#1E1F24" }],
  ["heatmap-tile--status-check-fm", { background: "#0891B2", color: "#FFFFFF" }],
  ["heatmap-tile--status-maintenance", { background: "#7A2BA7", color: "#FFFFFF" }],
  ["heatmap-tile--status-transition", { background: "#DB2777", color: "#FFFFFF" }],
  ["heatmap-tile--status-incident", { background: "#B91C1C", color: "#FFFFFF" }],
  ["heatmap-tile--status-unknown", { background: "#1E1F24", color: "#FFFFFF" }],
]);

const EMAIL_HEATMAP_DISABLED_STYLE = { background: "#F0F1F4", color: "#5B6168" };

const EMAIL_HEATMAP_STATUS_COLORS = new Map(
  Array.from(EMAIL_HEATMAP_STATUS_STYLE_MAP.entries()).map(([key, value]) => [
    key,
    { background: value.background, text: value.color },
  ])
);

const EMAIL_HEATMAP_LEGEND_ITEMS = [
  { label: "In service", statusClass: "heatmap-tile--status-in-service" },
  { label: "Awaiting feedback / >7 days", statusClass: "heatmap-tile--status-awaiting" },
  { label: "Check with FM", statusClass: "heatmap-tile--status-check-fm" },
  { label: "Storage", statusClass: "heatmap-tile--status-storage" },
  { label: "Maintenance", statusClass: "heatmap-tile--status-maintenance" },
  { label: "Transition", statusClass: "heatmap-tile--status-transition" },
  { label: "Incident", statusClass: "heatmap-tile--status-incident" },
  { label: "AOG", statusClass: "heatmap-tile--status-aog" },
  { label: "Unknown", statusClass: "heatmap-tile--status-unknown" },
];

const EMAIL_HEATMAP_LEGEND = EMAIL_HEATMAP_LEGEND_ITEMS.map((item) => {
  const colors = EMAIL_HEATMAP_STATUS_STYLE_MAP.get(item.statusClass) || {
    background: "#1E1F24",
    color: "#FFFFFF",
  };
  return {
    label: item.label,
    swatchColor: colors.background,
  };
});

const HEATMAP_REGION_KEYS = [
  "region",
  "global_region",
  "globalRegion",
  "market_region",
  "marketRegion",
  "area",
  "theatre",
  "theater",
  "zone",
  "territory",
  "owner_region",
  "ownerRegion",
];

const logBuffer = [];
const MAX_COMMENT_CONCURRENCY = 3;
const commentQueue = [];
const commentInFlight = new Set();
let activeCommentFetches = 0;
const DAY_MS = 24 * 60 * 60 * 1000;
const TAM_STATUS_OPTIONS = [
  "AOG",
  "AWAITING OPERATOR FEEDBACK",
  "CHECK WITH FM",
  "IN SERVICE",
  "INCIDENT",
  "MAINTENANCE",
  "STORAGE",
  "TRANSITION",
  "UNKNOWN",
].sort((a, b) => a.localeCompare(b));

function resolveApiBase(rawValue) {
  const fallback = window.location.origin.replace(/\/$/, "");
  if (rawValue === undefined || rawValue === null) {
    return fallback;
  }
  const candidate = String(rawValue).trim();
  if (!candidate) {
    return fallback;
  }
  try {
    const resolved = new URL(candidate, window.location.origin);
    return resolved.toString().replace(/\/$/, "");
  } catch (_error) {
    return fallback;
  }
}

function getActiveApiBase() {
  return state.activeApiBase || currentApiBase;
}

function getFallbackApiBase() {
  return state.apiBaseFallback || fallbackApiBase;
}

function getDefaultApiBase() {
  return state.apiBaseDefault || defaultConfigApiBase || getFallbackApiBase();
}

function updateApiBaseStatus() {
  // The simplified login screen surfaces connection errors through the main error banner.
}

function setActiveApiBase(nextBase, source = "config") {
  if (!nextBase) {
    return getActiveApiBase();
  }
  const resolved = resolveApiBase(nextBase);
  const previous = getActiveApiBase();
  const previousNormalised = normaliseBaseForCompare(previous);
  const nextNormalised = normaliseBaseForCompare(resolved);
  if (previousNormalised === nextNormalised && state.apiBaseSource === source) {
    updateApiBaseStatus();
    return resolved;
  }
  currentApiBase = resolved;
  state.activeApiBase = resolved;
  state.apiBaseSource = source;
  state.apiBaseOverride = source === "override" ? resolved : null;
  if (source === "override" && !isProdMode) {
    safeWriteLocalStorage(API_BASE_STORAGE_KEY, resolved);
  } else {
    safeWriteLocalStorage(API_BASE_STORAGE_KEY, null);
  }
  logEvent("API base updated", { from: previous, to: resolved, source });
  updateApiBaseStatus();
  return resolved;
}

function flagApiConnectivityIssue(message) {
  const detail = typeof message === "string" ? message.trim() : "";
  const fallback = getFallbackApiBase();
  const defaultBase = getDefaultApiBase();
  const active = getActiveApiBase();
  const repeatIssue = state.apiBaseIssue && state.apiBaseIssue.message === detail;
  const attemptedFallback = repeatIssue && state.apiBaseIssue.attemptedFallback;
  state.apiBaseIssue = {
    message: detail,
    at: new Date().toISOString(),
    attemptedFallback: Boolean(attemptedFallback),
  };
  if (!repeatIssue) {
    logEvent("API connectivity issue", {
      message: detail,
      active,
      fallback,
      defaultBase,
      source: state.apiBaseSource,
    });
  }
  const displayMessage = detail || "Unable to reach the server. Retrying…";
  if (!state.sessionUser) {
    showLoginError(displayMessage);
  }
  const canTryFallback =
    fallback &&
    normaliseBaseForCompare(fallback) !== normaliseBaseForCompare(active) &&
    !state.apiBaseIssue.attemptedFallback &&
    state.apiBaseSource !== "override-pending";

  if (canTryFallback) {
    state.apiBaseIssue.attemptedFallback = true;
    applyFallbackApiBase().catch((error) => {
      logEvent("Fallback API apply failed", { error: error?.message || "unknown" });
    });
  }
}

function clearApiBaseIssue() {
  if (!state.apiBaseIssue) return;
  state.apiBaseIssue = null;
  updateApiBaseStatus();
}

async function applyFallbackApiBase() {
  const fallback = getFallbackApiBase();
  if (!fallback) return;
  const active = getActiveApiBase();
  if (normaliseBaseForCompare(fallback) === normaliseBaseForCompare(active)) {
    return;
  }
  const previousBase = active;
  const previousSource = state.apiBaseSource;
  setActiveApiBase(fallback, "override-pending");
  if (!state.sessionUser) {
    showLoginError("Trying the detected server address…");
  }
  try {
    const sessionOk = await loadSession();
    if (!sessionOk) {
      throw new Error("Fallback API session check failed");
    }
    setActiveApiBase(fallback, "override");
    if (!state.sessionUser) {
      showLoginError("");
    }
    logEvent("Fallback API override confirmed", { base: fallback });
  } catch (error) {
    setActiveApiBase(previousBase, previousSource);
    const message = error?.message || "Fallback API session check failed";
    const rolledBack =
      normaliseBaseForCompare(getActiveApiBase()) === normaliseBaseForCompare(previousBase);
    logEvent("Fallback API check failed", { error: message, attempted: fallback });
    logEvent("Fallback API override rolled back", {
      attempted: fallback,
      restored: previousBase,
      rolledBack,
    });
    if (!state.sessionUser) {
      showLoginError(message);
    }
    console.assert(
      rolledBack,
      "Fallback API override should roll back to the previous server when the session check fails."
    );
    throw error;
  }
}

function formatNetworkError(error, targetUrl) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return "No internet connection detected. Reconnect and try again.";
  }
  const urlString = targetUrl || "";
  if (window.location.protocol === "https:" && urlString.startsWith("http://")) {
    return `The browser blocked an insecure request to ${urlString}. Update the API base to use HTTPS or serve the UI over HTTP.`;
  }
  let origin = null;
  try {
    origin = new URL(urlString).origin;
  } catch (_error) {
    try {
      origin = new URL(getActiveApiBase()).origin;
    } catch (_inner) {
      origin = getActiveApiBase();
    }
  }
  const detail = error && typeof error.message === "string" ? error.message.trim() : "";
  const suffix = detail && detail !== "Failed to fetch" ? ` (${detail})` : "";
  if (origin) {
    return `Unable to reach the API at ${origin}. Confirm the backend is running or update config.js.${suffix}`;
  }
  return `Unable to reach the API server. Confirm the backend is running or update config.js.${suffix}`;
}

function apiUrl(path = "/") {
  const base = getActiveApiBase().replace(/\/+$/, "");
  const suffix = String(path || "/");
  const normalisedPath = suffix.startsWith("/") ? suffix : `/${suffix}`;
  if (/^\/api(\/|$)/i.test(normalisedPath) && /\/api$/i.test(base)) {
    return `${base}${normalisedPath.replace(/^\/api/i, "")}`;
  }
  return `${base}${normalisedPath}`;
}

function expandSearchTokens(value) {
  const cleaned = cleanText(value);
  if (!cleaned) return [];
  const lower = cleaned.toLowerCase();
  const tokens = [lower];
  const compact = lower.replace(/[-\s]/g, "");
  if (compact && compact !== lower) {
    tokens.push(compact);
  }
  return tokens;
}

function compareTextValues(a, b) {
  const left = cleanText(a) || "";
  const right = cleanText(b) || "";
  if (left === right) return 0;
  return left < right ? -1 : 1;
}

function compareNumberValues(a, b) {
  const left = Number.isFinite(a) ? a : Number.NEGATIVE_INFINITY;
  const right = Number.isFinite(b) ? b : Number.NEGATIVE_INFINITY;
  if (left === right) return 0;
  return left < right ? -1 : 1;
}

function getActiveUserLabel() {
  if (!state.sessionUser) return null;
  return cleanText(state.sessionUser.displayName) || cleanText(state.sessionUser.username) || null;
}

function ensureAppShellVisible() {
  if (!dom.appShell) {
    return;
  }
  dom.appShell.hidden = false;
  dom.appShell.setAttribute("aria-hidden", "false");
}

function ensureSessionVisibility() {
  const signedIn = Boolean(state.sessionUser);
  ensureAppShellVisible();
  if (dom.loginShell) {
    dom.loginShell.hidden = signedIn;
    dom.loginShell.setAttribute("aria-hidden", signedIn ? "true" : "false");
  }
  if (dom.sessionChip) {
    dom.sessionChip.hidden = !signedIn;
  }
  if (dom.logoutButton) {
    dom.logoutButton.disabled = !signedIn;
    dom.logoutButton.hidden = !signedIn;
  }
  if (dom.refreshButton) {
    dom.refreshButton.disabled = !signedIn;
    dom.refreshButton.title = signedIn
      ? "Refresh aircraft statuses manually"
      : "Sign in to refresh aircraft data";
  }
  if (dom.exportButton) {
    dom.exportButton.disabled = !signedIn;
    dom.exportButton.title = signedIn
      ? "Download the visible rows as an Excel spreadsheet"
      : "Sign in to download the Excel report";
  }
  if (dom.exportPdfButton) {
    dom.exportPdfButton.disabled = !signedIn;
    dom.exportPdfButton.title = signedIn
      ? "Download the visible rows as a styled PDF report"
      : "Sign in to download the PDF report";
  }
  updateEmailExportAvailability();
  if (signedIn) {
    setHeaderHeightVar();
  }
}

function updateSessionChip() {
  if (!dom.sessionUser) return;
  const label = getActiveUserLabel();
  dom.sessionUser.textContent = label || "";
}

function setSessionUser(user) {
  if (user) {
    const allowedOwners = extractAllowedOwnersFromUser(user);
    const email = cleanText(user.email) || null;
    const isAdmin =
      user.isAdmin === true || allowedOwners === null || allowedOwners === undefined;
    state.sessionUser = {
      username: cleanText(user.username) || cleanText(user.name) || null,
      displayName: cleanText(user.displayName) || cleanText(user.fullName) || cleanText(user.username) || null,
      allowedOwners,
      isAdmin,
      email,
    };
    if (email) {
      state.lastEmailRecipients = [email];
    }
    state.adminTaskLogError = null;
    if (Array.isArray(allowedOwners)) {
      if (!allowedOwners.length) {
        state.ownerFilter = "";
      } else if (!cleanText(state.ownerFilter) && allowedOwners.length === 1) {
        state.ownerFilter = allowedOwners[0];
      }
    } else if (!isAdmin && !state.aircraftLimitActive && !cleanText(state.ownerFilter)) {
      // Only set default owner filter for non-admin users
      state.ownerFilter = defaultOwnerFilter;
    } else if (isAdmin) {
      // Admin users should see all aircraft - clear any owner filter
      state.ownerFilter = "";
    }
    ensureOwnerFilterIsValid();
    populateOwnerFilter();
    clearApiBaseIssue();
    setEmailTransportState(
      {
        mode: null,
        disabled: false,
        message: "Email Excel Report",
        error: null,
      },
      { reset: true }
    );
  } else {
    state.sessionUser = null;
    setEmailTransportState(
      {
        mode: null,
        disabled: true,
        message: "Sign in to email reports.",
        error: null,
        lastChecked: null,
      },
      { reset: true }
    );
  }
  updateSessionChip();
  ensureSessionVisibility();
  updateAdminVisibility();
  updateLastUpdatedDisplay();
}

function setEmailTransportState(update = {}, options = {}) {
  const base = options.reset ? { ...defaultEmailTransportState } : { ...state.emailTransport };
  const next = { ...base, ...update };
  state.emailTransport = next;
  updateEmailExportAvailability();
  return next;
}

function updateEmailExportAvailability() {
  if (!dom.emailExportButton) {
    return;
  }
  const baseTitle =
    "Email the visible rows as an Excel attachment with a styled summary";
  const transport = state.emailTransport || defaultEmailTransportState;
  let disabled = false;
  let title = baseTitle;

  if (!state.sessionUser) {
    disabled = true;
    title = "Sign in to email reports.";
  } else if (state.emailSending) {
    disabled = true;
    title = "Email export in progress.";
  } else if (transport.disabled) {
    disabled = true;
    title = transport.message || "Email delivery disabled.";
  }

  dom.emailExportButton.disabled = disabled;
  dom.emailExportButton.setAttribute("aria-disabled", disabled ? "true" : "false");
  dom.emailExportButton.title = title;

  const transportDisabledWhileSignedIn = Boolean(state.sessionUser) && transport.disabled;

  if (state.emailSending) {
    dom.emailExportButton.textContent = "Sending…";
  } else if (transportDisabledWhileSignedIn) {
    dom.emailExportButton.textContent = "Email disabled";
  } else {
    dom.emailExportButton.textContent = "Email Excel Report";
  }
}

async function refreshEmailTransport({ reason = "bootstrap" } = {}) {
  if (!dom.emailExportButton) {
    return;
  }
  if (!state.sessionUser) {
    setEmailTransportState(
      {
        mode: null,
        disabled: true,
        message: "Sign in to email reports.",
        error: null,
        lastChecked: null,
      },
      { reset: true }
    );
    return;
  }

  if (!isAdminUser()) {
    setEmailTransportState({ disabled: false, error: null });
    return;
  }

  setEmailTransportState({ message: "Checking email configuration…" });

  try {
    const result = await fetchJson(apiUrl("/api/admin/email/verify"));
    const lastChecked = new Date().toISOString();

    if (result?.disabled) {
      setEmailTransportState(
        {
          mode: result.mode || null,
          disabled: true,
          message: result.message || "Email delivery disabled.",
          error: null,
          lastChecked,
        },
        { reset: false }
      );
      logEvent("Email transport disabled", {
        mode: result.mode || "unknown",
        reason,
        message: result.message || "disabled",
      });
      return;
    }

    if (result?.ok) {
      setEmailTransportState(
        {
          mode: result.mode || "smtp",
          disabled: false,
          message: "Email Excel Report",
          error: null,
          lastChecked,
        },
        { reset: false }
      );
      logEvent("Email transport verified", {
        mode: result.mode || "smtp",
        tookMs: result.tookMs || null,
        reason,
      });
      return;
    }

    const errorMessage = result?.error || "Email verification failed.";
    setEmailTransportState(
      {
        mode: result?.mode || "smtp",
        disabled: true,
        message: errorMessage,
        error: errorMessage,
        lastChecked,
      },
      { reset: false }
    );
    logEvent("Email transport verification failed", {
      error: errorMessage,
      mode: result?.mode || "smtp",
      reason,
    });
  } catch (error) {
    const message =
      error?.message ||
      "Unable to verify email transport.";
    setEmailTransportState(
      {
        disabled: true,
        message,
        error: message,
        lastChecked: new Date().toISOString(),
      },
      { reset: false }
    );
    logEvent("Email transport check failed", { error: message, reason });
  }
}

function clearAdminTaskLogTimer() {
  if (state.adminTaskLogTimer) {
    clearInterval(state.adminTaskLogTimer);
    state.adminTaskLogTimer = null;
  }
}

function renderAdminTaskLogs() {
  if (!dom.adminTaskLog) {
    return;
  }
  dom.adminTaskLog.innerHTML = "";
  if (!isAdminUser()) {
    return;
  }
  if (state.adminTaskLogError) {
    const errorLine = document.createElement("p");
    errorLine.className = "admin-log__error";
    errorLine.textContent = state.adminTaskLogError;
    dom.adminTaskLog.appendChild(errorLine);
    return;
  }
  if (!state.adminTaskLogsLoaded) {
    const loading = document.createElement("p");
    loading.className = "admin-log__hint";
    loading.textContent = "Loading automated task history…";
    dom.adminTaskLog.appendChild(loading);
    return;
  }
  if (state.adminTaskLogsSkipped) {
    const skipped = document.createElement("p");
    skipped.className = "admin-log__hint";
    skipped.textContent = "Task logging is disabled because the API database connection is unavailable.";
    dom.adminTaskLog.appendChild(skipped);
    return;
  }
  if (!Array.isArray(state.adminTaskLogs) || !state.adminTaskLogs.length) {
    const empty = document.createElement("p");
    empty.className = "admin-log__hint";
    empty.textContent = "No automated tasks have been recorded yet.";
    dom.adminTaskLog.appendChild(empty);
    return;
  }
  const list = document.createElement("ol");
  list.className = "admin-log__list";
  state.adminTaskLogs.forEach((entry) => {
    const item = document.createElement("li");
    item.className = "admin-log__item";
    const header = document.createElement("div");
    header.className = "admin-log__item-header";
    const title = document.createElement("span");
    title.className = "admin-log__title";
    title.textContent = `${(entry.taskType || "task").toUpperCase()} · ${entry.summary || "No summary"}`;
    const statusBadge = document.createElement("span");
    statusBadge.className = "admin-log__status";
    const status = cleanText(entry.status) || "unknown";
    statusBadge.dataset.status = status.toLowerCase();
    statusBadge.textContent = status.toUpperCase();
    header.appendChild(title);
    header.appendChild(statusBadge);
    const meta = document.createElement("dl");
    meta.className = "admin-log__meta";
    const timestamp = cleanText(entry.createdAt) || null;
    if (timestamp) {
      const timeLabel = document.createElement("dt");
      timeLabel.textContent = "Created";
      const timeValue = document.createElement("dd");
      timeValue.textContent = formatExportTimestamp(timestamp);
      meta.appendChild(timeLabel);
      meta.appendChild(timeValue);
    }
    if (entry.triggeredBy) {
      const userLabel = document.createElement("dt");
      userLabel.textContent = "Triggered by";
      const userValue = document.createElement("dd");
      userValue.textContent = entry.triggeredBy.toUpperCase();
      meta.appendChild(userLabel);
      meta.appendChild(userValue);
    }
    if (entry.details && typeof entry.details === "object") {
      const detailsLabel = document.createElement("dt");
      detailsLabel.textContent = "Details";
      const detailsValue = document.createElement("dd");
      detailsValue.textContent = JSON.stringify(entry.details);
      meta.appendChild(detailsLabel);
      meta.appendChild(detailsValue);
    }
    item.appendChild(header);
    item.appendChild(meta);
    list.appendChild(item);
  });
  dom.adminTaskLog.appendChild(list);
}

function formatUsd(value) {
  if (value === undefined || value === null) return "—";
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return `$${num.toFixed(2)}`;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function buildFlightawareUsageHtml(payload, { label = "Totals" } = {}) {
  const raw = payload?.raw && typeof payload.raw === "object" ? payload.raw : {};
  const details = Array.isArray(raw.resource_details) ? raw.resource_details : [];
  const totalCalls = raw.total_calls ?? null;
  const totalCost = raw.total_cost ?? null;
  const totalSuccess = raw.total_successful_calls ?? null;
  const totalFailed = raw.total_failed_calls ?? null;

  const rows = details
    .map((row) => ({
      operation: cleanText(row?.operation) || "unknown",
      calls: Number(row?.total_resource_calls) || 0,
      cost: Number(row?.resource_cost) || 0,
      ok: Number(row?.successful_resource_calls) || 0,
      failed: Number(row?.failed_resource_calls) || 0,
    }))
    .sort((a, b) => b.cost - a.cost)
    .slice(0, 15);

  const tableRows = rows
    .map(
      (r) => `<tr>
  <td style="text-align:left; padding: 8px; border-bottom: 1px solid #e5e7eb;">${escapeHtml(r.operation)}</td>
  <td style="text-align:right; padding: 8px; border-bottom: 1px solid #e5e7eb;">${r.calls}</td>
  <td style="text-align:right; padding: 8px; border-bottom: 1px solid #e5e7eb;">${formatUsd(r.cost)}</td>
  <td style="text-align:right; padding: 8px; border-bottom: 1px solid #e5e7eb;">${r.ok}</td>
  <td style="text-align:right; padding: 8px; border-bottom: 1px solid #e5e7eb;">${r.failed}</td>
</tr>`
    )
    .join("");

  return `
<div style="display:flex; flex-direction:column; gap: 14px;">
  <div>
    <p style="margin:0; color:#475569;"><strong>${escapeHtml(label)}</strong> · as of <strong>${escapeHtml(payload?.fetchedAt || "")}</strong></p>
  </div>
  <div style="display:grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px;">
    <div style="background:#f8fafc; border:1px solid #e2e8f0; border-radius:10px; padding:12px;">
      <div style="font-size:0.85rem; color:#64748b;">Total calls</div>
      <div style="font-size:1.2rem; font-weight:700;">${totalCalls ?? "—"}</div>
    </div>
    <div style="background:#f8fafc; border:1px solid #e2e8f0; border-radius:10px; padding:12px;">
      <div style="font-size:0.85rem; color:#64748b;">Total cost</div>
      <div style="font-size:1.2rem; font-weight:700;">${formatUsd(totalCost)}</div>
    </div>
    <div style="background:#f8fafc; border:1px solid #e2e8f0; border-radius:10px; padding:12px;">
      <div style="font-size:0.85rem; color:#64748b;">Successful</div>
      <div style="font-size:1.2rem; font-weight:700;">${totalSuccess ?? "—"}</div>
    </div>
    <div style="background:#f8fafc; border:1px solid #e2e8f0; border-radius:10px; padding:12px;">
      <div style="font-size:0.85rem; color:#64748b;">Failed</div>
      <div style="font-size:1.2rem; font-weight:700;">${totalFailed ?? "—"}</div>
    </div>
  </div>
  <div>
    <h3 style="margin: 0 0 8px 0;">Top items by cost</h3>
    <div style="overflow:auto; border:1px solid #e5e7eb; border-radius:10px;">
      <table style="width:100%; border-collapse: collapse; font-size: 0.92rem;">
        <thead>
          <tr style="background:#f1f5f9;">
            <th style="text-align:left; padding: 10px;">What</th>
            <th style="text-align:right; padding: 10px;">Calls</th>
            <th style="text-align:right; padding: 10px;">Cost</th>
            <th style="text-align:right; padding: 10px;">OK</th>
            <th style="text-align:right; padding: 10px;">Fail</th>
          </tr>
        </thead>
        <tbody>
          ${tableRows || `<tr><td colspan="5" style="padding:12px;">No resource breakdown returned.</td></tr>`}
        </tbody>
      </table>
    </div>
  </div>
</div>`;
}

function buildFlightawareUsageCombinedHtml({ allTime, monthToDate }) {
  const allBlock = allTime
    ? buildFlightawareUsageHtml(allTime, { label: "All-time" })
    : `<p style="color:#b91c1c;">Failed to load all-time totals.</p>`;
  const mtdBlock = monthToDate
    ? buildFlightawareUsageHtml(monthToDate, { label: "Month-to-date" })
    : `<p style="color:#b91c1c;">Failed to load month-to-date totals.</p>`;
  return `
<div style="display:flex; flex-direction:column; gap: 18px;">
  ${allBlock}
  <hr style="border:0; border-top:1px solid #e5e7eb;" />
  ${mtdBlock}
</div>`;
}

function buildFlightawareTriggersHtml(payload) {
  const rows = Array.isArray(payload?.rows) ? payload.rows : [];
  if (!rows.length) {
    return `<p style="margin:0; color:#64748b;">No FlightAware calls recorded for this window.</p>`;
  }
  function formatHourUtc(value) {
    if (!value) return "—";
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      // YYYY-MM-DD HH:00 UTC
      return `${d.toISOString().slice(0, 13).replace("T", " ")}:00 UTC`;
    } catch {
      return String(value);
    }
  }
  const body = rows
    .map((row) => {
      const hour = escapeHtml(formatHourUtc(row.hour_utc));
      const source = escapeHtml(row.trigger_source || "unknown");
      const user = escapeHtml(row.username || "unknown");
      const op = escapeHtml(row.operation || "—");
      const calls = Number(row.calls) || 0;
      const ok = Number(row.ok_calls) || 0;
      const failed = Number(row.failed_calls) || 0;
      const cost = row.costUsd === undefined || row.costUsd === null ? "—" : formatUsd(row.costUsd);
      return `<tr>
  <td style="text-align:left; padding: 8px; border-bottom: 1px solid #e5e7eb;">${hour}</td>
  <td style="text-align:left; padding: 8px; border-bottom: 1px solid #e5e7eb;">${source}</td>
  <td style="text-align:left; padding: 8px; border-bottom: 1px solid #e5e7eb;">${user}</td>
  <td style="text-align:left; padding: 8px; border-bottom: 1px solid #e5e7eb;">${op}</td>
  <td style="text-align:right; padding: 8px; border-bottom: 1px solid #e5e7eb;">${calls}</td>
  <td style="text-align:right; padding: 8px; border-bottom: 1px solid #e5e7eb;">${cost}</td>
  <td style="text-align:right; padding: 8px; border-bottom: 1px solid #e5e7eb;">${ok}</td>
  <td style="text-align:right; padding: 8px; border-bottom: 1px solid #e5e7eb;">${failed}</td>
</tr>`;
    })
    .join("");
  const unknownCount = rows.filter((r) => (r.trigger_source || "unknown") === "unknown").reduce((sum, r) => sum + (Number(r.calls) || 0), 0);
  return `
<div style="margin-top: 14px;">
  <h3 style="margin: 0 0 8px 0;">What triggered the calls</h3>
  <div style="overflow:auto; border:1px solid #e5e7eb; border-radius:10px;">
    <table style="width:100%; border-collapse: collapse; font-size: 0.92rem;">
      <thead>
        <tr style="background:#f1f5f9;">
          <th style="text-align:left; padding: 10px;">Hour (UTC)</th>
          <th style="text-align:left; padding: 10px;">Trigger</th>
          <th style="text-align:left; padding: 10px;">User</th>
          <th style="text-align:left; padding: 10px;">Type</th>
          <th style="text-align:right; padding: 10px;">Calls</th>
          <th style="text-align:right; padding: 10px;">Cost</th>
          <th style="text-align:right; padding: 10px;">OK</th>
          <th style="text-align:right; padding: 10px;">Fail</th>
        </tr>
      </thead>
      <tbody>${body}</tbody>
    </table>
  </div>
  <p style="margin:10px 0 0 0; color:#64748b; font-size:0.9rem;">
    ${unknownCount ? `Note: ${unknownCount} calls show as “unknown” because they were logged before trigger-tracking was enabled.` : ""}
  </p>
</div>`;
}

function buildFr24UsageHtml(payload) {
  const periods = payload?.periods && typeof payload.periods === "object" ? payload.periods : {};
  const rows = ["day", "week", "month", "year"]
    .map((key) => {
      const calls = periods?.[key]?.calls ?? 0;
      const cost = periods?.[key]?.costUsd;
      return `<tr>
  <td style="text-align:left; padding: 8px; border-bottom: 1px solid #e5e7eb;">${escapeHtml(key)}</td>
  <td style="text-align:right; padding: 8px; border-bottom: 1px solid #e5e7eb;">${calls}</td>
  <td style="text-align:right; padding: 8px; border-bottom: 1px solid #e5e7eb;">${cost === undefined || cost === null ? "—" : formatUsd(cost)}</td>
</tr>`;
    })
    .join("");
  const totalCalls = payload?.totalCallsSinceStart ?? 0;
  const note =
    totalCalls > 0
      ? payload?.note || ""
      : "No FR24 calls recorded since the last API restart. FR24 does not provide an API endpoint to query billing/credits; this screen is a local estimate based on what this server process has called.";
  return `
<div style="display:flex; flex-direction:column; gap: 12px;">
  <p style="margin:0; color:#475569;">As of <strong>${escapeHtml(payload?.asOf || "")}</strong></p>
  <div style="display:grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px;">
    <div style="background:#f8fafc; border:1px solid #e2e8f0; border-radius:10px; padding:12px;">
      <div style="font-size:0.85rem; color:#64748b;">Process started</div>
      <div style="font-size:1.05rem; font-weight:700;">${escapeHtml(payload?.startedAt || "—")}</div>
    </div>
    <div style="background:#f8fafc; border:1px solid #e2e8f0; border-radius:10px; padding:12px;">
      <div style="font-size:0.85rem; color:#64748b;">Calls since start</div>
      <div style="font-size:1.2rem; font-weight:700;">${payload?.totalCallsSinceStart ?? 0}</div>
    </div>
  </div>
  <div>
    <h3 style="margin: 0 0 8px 0;">Windowed counts (local estimate)</h3>
    <div style="overflow:auto; border:1px solid #e5e7eb; border-radius:10px;">
      <table style="width:100%; border-collapse: collapse; font-size: 0.92rem;">
        <thead>
          <tr style="background:#f1f5f9;">
            <th style="text-align:left; padding: 10px;">Period</th>
            <th style="text-align:right; padding: 10px;">Calls</th>
            <th style="text-align:right; padding: 10px;">Cost</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
    <p style="margin:10px 0 0 0; color:#64748b; font-size:0.9rem;">${escapeHtml(note)}</p>
  </div>
</div>`;
}

function openUsageDialog({ title, html }) {
  if (!dom.usageDialog) return;
  if (dom.usageDialogTitle) {
    dom.usageDialogTitle.textContent = title || "API costs & usage";
  }
  if (dom.usageDialogContent) {
    dom.usageDialogContent.innerHTML = html || "";
  }
  dom.usageDialog.hidden = false;
  dom.usageDialog.setAttribute("aria-hidden", "false");
}

function closeUsageDialog() {
  if (!dom.usageDialog) return;
  dom.usageDialog.hidden = true;
  dom.usageDialog.setAttribute("aria-hidden", "true");
}

async function fetchAndShowFlightawareUsage({ range = "all" } = {}) {
  if (!isAdminUser()) return;
  openUsageDialog({
    title: "FlightAware costs",
    html: "<p>Loading…</p>",
  });
  try {
    const [allResp, mtdResp] = await Promise.all([
      fetchJson(apiUrl(`/api/admin/flightaware/usage?range=all`)),
      fetchJson(apiUrl(`/api/admin/flightaware/usage?range=mtd`)),
    ]);
    const todayResp = await fetchJson(apiUrl(`/api/admin/flightaware/usage?range=today`));
    if (!allResp?.ok) throw new Error(allResp?.error || "Unable to fetch FlightAware totals.");
    if (!mtdResp?.ok) throw new Error(mtdResp?.error || "Unable to fetch FlightAware month-to-date totals.");
    if (!todayResp?.ok) throw new Error(todayResp?.error || "Unable to fetch FlightAware today totals.");
    openUsageDialog({
      title: "FlightAware costs",
      html: `
<div style="display:flex; flex-direction:column; gap: 18px;">
  <div class="admin-actions" style="margin: 0 0 6px 0;">
    <button type="button" class="primary" id="fa-costs-see-movements" style="padding: 8px 14px; font-size: 0.9rem;">See movements</button>
    <button type="button" class="ghost" id="fa-costs-back-summary" hidden>Back to summary</button>
    <select id="fa-costs-movements-range" style="padding: 8px 10px; border-radius: 10px; border: 1px solid #e2e8f0;">
      <option value="today">Today</option>
      <option value="last24h">Last 24 hours</option>
      <option value="mtd">Month-to-date</option>
    </select>
  </div>
  <div id="fa-costs-movements-output" hidden></div>
  ${buildFlightawareUsageHtml(todayResp, { label: "Today (UTC)" })}
  <hr style="border:0; border-top:1px solid #e5e7eb;" />
  ${buildFlightawareUsageHtml(mtdResp, { label: "Month-to-date" })}
  <hr style="border:0; border-top:1px solid #e5e7eb;" />
  ${buildFlightawareUsageHtml(allResp, { label: "All-time" })}
</div>`,
    });

    // Wire the "See movements" buttons after render.
    const btn = document.getElementById("fa-costs-see-movements");
    const out = document.getElementById("fa-costs-movements-output");
    const back = document.getElementById("fa-costs-back-summary");
    const rangeSelect = document.getElementById("fa-costs-movements-range");
    async function loadMovements(rangeKey) {
      if (!out) return;
      out.hidden = false;
      out.innerHTML = "<p>Loading movements…</p>";
      if (back) back.hidden = false;
      const resp = await fetchJson(apiUrl(`/api/admin/flightaware/usage-triggers?range=${encodeURIComponent(rangeKey)}`));
      if (!resp?.ok) {
        throw new Error(resp?.error || "Unable to load movements.");
      }
      out.innerHTML = buildFlightawareTriggersHtml(resp);
    }
    if (btn) {
      btn.onclick = () => {
        const rangeKey = rangeSelect?.value || "today";
        loadMovements(rangeKey).catch((error) => {
          if (out) {
            out.hidden = false;
            out.innerHTML = `<p style="color:#b91c1c;">Failed to load movements: ${escapeHtml(error.message)}</p>`;
          }
        });
      };
    }
    if (back) {
      back.onclick = () => {
        if (out) {
          out.hidden = true;
          out.innerHTML = "";
        }
        back.hidden = true;
      };
    }
  } catch (error) {
    openUsageDialog({
      title: "FlightAware costs",
      html: `<p style="color:#b91c1c;">Failed to load: ${escapeHtml(error.message)}</p>`,
    });
  }
}

async function fetchAndShowFr24Usage() {
  if (!isAdminUser()) return;
  openUsageDialog({ title: "FR24 costs", html: "<p>Loading FR24 usage…</p>" });
  try {
    const response = await fetchJson(apiUrl("/api/admin/fr24/usage"));
    if (!response?.ok) {
      throw new Error(response?.error || "Unable to fetch FR24 usage.");
    }
    openUsageDialog({ title: "FR24 costs", html: buildFr24UsageHtml(response) });
  } catch (error) {
    openUsageDialog({
      title: "FR24 costs",
      html: `<p style="color:#b91c1c;">Failed to load: ${escapeHtml(error.message)}</p>`,
    });
  }
}

function ensureAdminTaskLogTimer() {
  if (!isAdminUser()) {
    clearAdminTaskLogTimer();
    return;
  }
  const intervalMs = isDevMode ? 30_000 : 90_000;
  if (!state.adminTaskLogTimer) {
    state.adminTaskLogTimer = setInterval(() => {
      loadAdminTaskLogs({ reason: "auto" }).catch((error) => {
        logEvent("Admin log poll failed", { error: error.message });
      });
    }, intervalMs);
  }
}

async function loadAdminTaskLogs({ reason = "manual" } = {}) {
  if (!isAdminUser()) {
    return;
  }
  try {
    const limit = 25;
    const response = await fetchJson(apiUrl(`/api/admin/task-logs?limit=${limit}`));
    state.adminTaskLogs = Array.isArray(response.logs) ? response.logs : [];
    state.adminTaskLogsLoaded = true;
    state.adminTaskLogsSkipped = Boolean(response.skipped);
    state.adminTaskLogError = null;
    renderAdminTaskLogs();
    logEvent("Admin task logs refreshed", {
      reason,
      count: state.adminTaskLogs.length,
      skipped: state.adminTaskLogsSkipped,
    });
  } catch (error) {
    state.adminTaskLogError = error.message || "Unable to load task history.";
    state.adminTaskLogsLoaded = true;
    renderAdminTaskLogs();
    logEvent("Admin task log fetch failed", { error: error.message });
  }
}

function updateAdminVisibility() {
  const admin = isAdminUser();
  if (dom.adminCard) {
    dom.adminCard.hidden = !admin;
    dom.adminCard.setAttribute("aria-hidden", admin ? "false" : "true");
  }
  if (dom.adminUsersCard) {
    dom.adminUsersCard.hidden = !admin;
    dom.adminUsersCard.setAttribute("aria-hidden", admin ? "false" : "true");
    // Add id for anchor links
    if (!dom.adminUsersCard.id) {
      dom.adminUsersCard.id = "admin-users";
    }
  }
  if (dom.adminOperatorsCard) {
    dom.adminOperatorsCard.hidden = !admin;
    dom.adminOperatorsCard.setAttribute("aria-hidden", admin ? "false" : "true");
    if (!dom.adminOperatorsCard.id) {
      dom.adminOperatorsCard.id = "admin-operators";
    }
  }
  if (dom.adminAircraftCard) {
    // Show the admin aircraft table for Add/Delete functionality
    dom.adminAircraftCard.hidden = !admin;
    dom.adminAircraftCard.setAttribute("aria-hidden", admin ? "false" : "true");
    // Add id for anchor links
    if (!dom.adminAircraftCard.id) {
      dom.adminAircraftCard.id = "admin-aircraft";
    }
  }
  if (dom.adminCard) {
    dom.adminCard.hidden = !admin;
    dom.adminCard.setAttribute("aria-hidden", admin ? "false" : "true");
    // Add id for anchor links
    if (!dom.adminCard.id || dom.adminCard.id === "admin-utilities") {
      // Already has id
    }
  }
  if (admin) {
    // Add admin navigation banner if it doesn't exist
    if (!document.getElementById("admin-nav-banner")) {
      const adminNav = document.createElement("div");
      adminNav.id = "admin-nav-banner";
      adminNav.className = "admin-nav-banner";
      adminNav.style.cssText = "background: #f0f9ff; border: 2px solid #0ea5e9; border-radius: 12px; padding: 16px; margin: 20px 0; text-align: center; box-shadow: 0 2px 8px rgba(14, 165, 233, 0.1);";
      adminNav.innerHTML = `
        <div style="font-size: 1.1rem; font-weight: 600; margin-bottom: 8px; color: #0c4a6e;">🔧 Admin Panel Available</div>
        <div style="display: flex; justify-content: center; gap: 16px; flex-wrap: wrap;">
          <a href="#admin-users" style="color: #0ea5e9; text-decoration: underline; font-weight: 500;">👥 Manage Users</a>
          <span style="color: #94a3b8;">|</span>
          <a href="#admin-operators" style="color: #0ea5e9; text-decoration: underline; font-weight: 500;">🏢 Operators</a>
          <span style="color: #94a3b8;">|</span>
          <a href="#admin-aircraft" style="color: #0ea5e9; text-decoration: underline; font-weight: 500;">✈️ Manage Aircraft</a>
          <span style="color: #94a3b8;">|</span>
          <a href="#admin-utilities" style="color: #0ea5e9; text-decoration: underline; font-weight: 500;">🔧 Admin Utilities</a>
        </div>
      `;
      // Insert before the first admin card
      const firstAdminCard = dom.adminUsersCard || dom.adminOperatorsCard || dom.adminAircraftCard || dom.adminCard;
      if (firstAdminCard && firstAdminCard.parentNode) {
        firstAdminCard.parentNode.insertBefore(adminNav, firstAdminCard);
      }
    }
    loadAdminUsers().catch((error) => logEvent("Failed to load admin users", { error: error.message }));
    loadAdminOperatorsPage().catch((error) => logEvent("Failed to load admin operators", { error: error.message }));
    loadAdminAircraft().catch((error) => logEvent("Failed to load admin aircraft", { error: error.message }));
  }
  if (dom.adminBackupButton) {
    dom.adminBackupButton.hidden = !admin;
  }
  if (dom.adminRefreshLogs) {
    dom.adminRefreshLogs.disabled = !admin;
    dom.adminRefreshLogs.hidden = !admin;
  }
  if (dom.faUsageButton) {
    dom.faUsageButton.disabled = !admin;
    dom.faUsageButton.hidden = !admin;
  }
  if (dom.fr24UsageButton) {
    dom.fr24UsageButton.disabled = !admin;
    dom.fr24UsageButton.hidden = !admin;
  }
  if (dom.adminCronInstallButton) {
    dom.adminCronInstallButton.disabled = !admin;
    dom.adminCronInstallButton.hidden = !admin;
  }
  if (dom.adminCronRunButton) {
    dom.adminCronRunButton.disabled = !admin;
    dom.adminCronRunButton.hidden = !admin;
  }
  if (dom.adminCronRefreshButton) {
    dom.adminCronRefreshButton.disabled = !admin;
    dom.adminCronRefreshButton.hidden = !admin;
  }
  if (!admin) {
    clearAdminTaskLogTimer();
    state.adminTaskLogs = [];
    state.adminTaskLogsLoaded = false;
    state.adminTaskLogsSkipped = false;
    state.adminTaskLogError = null;
    if (dom.adminTaskLog) {
      dom.adminTaskLog.innerHTML = "";
    }
    return;
  }
  renderAdminTaskLogs();
  updateBackupButtonState(state.adminBackupInFlight);
  if (!state.adminTaskLogsLoaded) {
    loadAdminTaskLogs({ reason: "login" }).catch((error) => {
      logEvent("Admin task log bootstrap failed", { error: error.message });
      state.adminTaskLogError = error.message || "Unable to load task history.";
      state.adminTaskLogsLoaded = true;
      renderAdminTaskLogs();
    });
  }
  ensureAdminTaskLogTimer();
}

function updateBackupButtonState(running) {
  if (!dom.adminBackupButton) {
    return;
  }
  const disabled = running || !isAdminUser();
  dom.adminBackupButton.disabled = disabled;
  dom.adminBackupButton.textContent = running ? "Running backup…" : "Run DB backup";
}

async function triggerManualBackup() {
  if (!isAdminUser()) {
    setRefreshIndicator("Admin access required to run backups.");
    return;
  }
  if (state.adminBackupInFlight) {
    setRefreshIndicator("A backup is already in progress.");
    return;
  }
  state.adminBackupInFlight = true;
  updateBackupButtonState(true);
  try {
    const response = await fetchJson(apiUrl("/api/admin/db-backup"), {
      method: "POST",
      body: JSON.stringify({}),
    });
    const fileName = cleanText(response.fileName) || "database-backup";
    const message = `Backup completed: ${fileName}`;
    setRefreshIndicator(message);
    logEvent("Manual backup completed", {
      file: fileName,
      mode: response.mode || null,
      bytes: response.bytes || null,
    });
    await loadAdminTaskLogs({ reason: "backup" });
  } catch (error) {
    const message = error?.message || "Unable to run backup.";
    setRefreshIndicator(`Backup failed: ${message}`);
    logEvent("Manual backup failed", { error: message });
  } finally {
    state.adminBackupInFlight = false;
    updateBackupButtonState(false);
  }
}

function showLoginError(message) {
  if (!dom.loginError) return;
  dom.loginError.textContent = message || "";
}

function resetDataState() {
  state.aircraft = [];
  state.statuses = new Map();
  state.statusFetchedAt = new Map();
  state.statusErrorStreak = new Map();
  state.comments = new Map();
  state.statusHistory = new Map();
  state.flightawareDebug = new Map();
  state.activeCommentDialog = null;
  state.activeDebugDialog = null;
  state.loadingAircraft = false;
  state.pendingAircraftReload = false;
  state.loadingStatuses = false;
  state.lastRefreshTs = null;
  state.refreshErrors = 0;
  state.flightawarePauseUntil = 0;
  state.ownerOptions = [];
  state.operatorOptions = [];
  state.ownerFilter = defaultOwnerFilter;
  state.operatorFilter = "";
  state.aircraftLoadedAt = null;
  state.importReport = null;
  state.dbTables = [];
  state.dbTablesLoading = false;
  state.dbTableError = null;
  state.dbTableData = new Map();
  state.activeDbTable = null;
  state.emailSending = false;
  state.adminBackupInFlight = false;
  state.adminTaskLogs = [];
  state.adminTaskLogsLoaded = false;
  state.adminTaskLogsSkipped = false;
  state.adminTaskLogError = null;
  state.lastEmailRecipients = [];
  state.statusChanges = [];
  state.statusChangesLastChecked = null;
  state.statusChangesDismissed = new Set();
  if (state.adminTaskLogTimer) {
    clearInterval(state.adminTaskLogTimer);
    state.adminTaskLogTimer = null;
  }
  commentQueue.length = 0;
  commentInFlight.clear();
  activeCommentFetches = 0;
  renderDataReport();
  updateLastUpdatedDisplay();
  populateOwnerFilter();
  populateOperatorFilter();
  populateRegionFilter();
  populateTamFilter();
  populateMeFleetFilter();
}

function handleUnauthenticated(reason) {
  setSessionUser(null);
  resetDataState();
  closeCommentDialog();
  closeDebugDialog();
  dom.loginForm?.reset();
  showLoginError(reason || "");
  setRefreshIndicator(reason ? reason : "Sign in to load data.");
  renderTable();
}

const STATUS_SORT_SEQUENCE = [
  "AOG",
  "ON GROUND > 24HRS",
  "1 DAY",
  "2 DAYS",
  "5 DAYS",
  "1W",
  "2W",
  "3W",
  "1MO",
  "2MO",
  "3MO",
  "4MO",
  "5MO",
  "6MO",
  "1YR",
  "IN STORAGE",
  "UNKNOWN",
  "IN SERVICE",
];

const STATUS_SORT_ORDER = new Map(
  STATUS_SORT_SEQUENCE.map((label, index) => [label, index])
);

const STATUS_SORT_FALLBACK_WEIGHT = STATUS_SORT_SEQUENCE.length + 5;

function getStatusSortWeight(label) {
  const normalized = cleanText(label)?.toUpperCase() || "";
  if (!normalized) {
    return STATUS_SORT_FALLBACK_WEIGHT;
  }
  if (STATUS_SORT_ORDER.has(normalized)) {
    return STATUS_SORT_ORDER.get(normalized);
  }
  if (normalized.includes("STORAGE")) {
    return STATUS_SORT_ORDER.get("IN STORAGE");
  }
  if (normalized === "IN SERVICE") {
    return STATUS_SORT_ORDER.get("IN SERVICE");
  }
  if (normalized === "UNKNOWN") {
    return STATUS_SORT_ORDER.get("UNKNOWN");
  }
  return STATUS_SORT_FALLBACK_WEIGHT - 1;
}

const SORTERS = {
  msn: (a, b) => compareTextValues(a.aircraft.msn, b.aircraft.msn),
  registration: (a, b) =>
    compareTextValues(
      cleanText(a.aircraft.registration) || cleanText(a.aircraft.tail) || cleanText(a.aircraft.ident),
      cleanText(b.aircraft.registration) || cleanText(b.aircraft.tail) || cleanText(b.aircraft.ident)
    ),
  aircraft_type: (a, b) => compareTextValues(cleanText(a.aircraft.aircraft_type), cleanText(b.aircraft.aircraft_type)),
  operator: (a, b) => compareTextValues(cleanText(a.aircraft.operator), cleanText(b.aircraft.operator)),
  owner: (a, b) =>
    compareTextValues(cleanText(a.aircraft.owner), cleanText(b.aircraft.owner)),
  status: (a, b) => {
    const leftWeight = getStatusSortWeight(a.model.categoryLabel);
    const rightWeight = getStatusSortWeight(b.model.categoryLabel);
    if (leftWeight !== rightWeight) {
      return leftWeight - rightWeight;
    }
    return compareTextValues(a.model.categoryLabel, b.model.categoryLabel);
  },
  time: (a, b) =>
    compareNumberValues(
      Number.isFinite(a.model.groundedMinutes) ? a.model.groundedMinutes : -1,
      Number.isFinite(b.model.groundedMinutes) ? b.model.groundedMinutes : -1
    ),
  location: (a, b) => compareTextValues(a.model.lastLocation, b.model.lastLocation),
  locationSource: (a, b) => compareTextValues(a.model.locationSourceLabel, b.model.locationSourceLabel),
  nextFlight: (a, b) => {
    const leftHas = a.model.hasUpcomingFlight ? 1 : 0;
    const rightHas = b.model.hasUpcomingFlight ? 1 : 0;
    if (leftHas !== rightHas) {
      return leftHas - rightHas;
    }
    return compareNumberValues(
      Number.isFinite(a.model.nextDepartureTs) ? a.model.nextDepartureTs : Number.POSITIVE_INFINITY,
      Number.isFinite(b.model.nextDepartureTs) ? b.model.nextDepartureTs : Number.POSITIVE_INFINITY
    );
  },
  tam: (a, b) => compareTextValues(a.model.tamDisplay, b.model.tamDisplay),
  comments: (a, b) => compareNumberValues(a.model.commentCount, b.model.commentCount),
  onWatch: (a, b) => Number(b.model.onWatch ? 1 : 0) - Number(a.model.onWatch ? 1 : 0),
  links: (a, b) => compareNumberValues(a.model.linkCount, b.model.linkCount),
  lastLanding: (a, b) =>
    compareNumberValues(toTimestamp(a.model.lastLandingIso), toTimestamp(b.model.lastLandingIso)),
};

function getCategoryClass(key) {
  return CATEGORY_CLASS_MAP[key] || CATEGORY_CLASS_MAP.unknown;
}

function getTamChipClass(status) {
  const normalised = normaliseTamStatus(status);
  if (!normalised) {
    return "";
  }
  return TAM_STATUS_CLASS_MAP.get(normalised) || "";
}

const TAM_STATUSES_WITH_EXPECTED_RELEASE = new Set(["MAINTENANCE", "STORAGE", "INCIDENT", "IN STORAGE"]);

function isExpectedReleaseTamStatus(status) {
  const t = normaliseTamStatus(status);
  return Boolean(t && TAM_STATUSES_WITH_EXPECTED_RELEASE.has(t));
}

function classifyExpectedReleaseDateForUi(ymd) {
  const s = cleanText(ymd);
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return "tam-expected-release--future";
  }
  const [y, m, d] = s.split("-").map((x) => Number(x));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
    return "tam-expected-release--future";
  }
  const day = new Date(y, m - 1, d);
  const today = new Date();
  const a = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const b = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
  return b <= a ? "tam-expected-release--due" : "tam-expected-release--future";
}

function buildFlightawareUrl(ident) {
  const cleaned = cleanText(ident);
  if (!cleaned) {
    return null;
  }
  return `https://flightaware.com/live/flight/${encodeURIComponent(cleaned)}`;
}

function buildFr24Url(registration) {
  const cleaned = normaliseRegistration(registration);
  if (!cleaned) return null;
  return `https://www.flightradar24.com/data/aircraft/${encodeURIComponent(cleaned)}`;
}

function summarisePayload(value, depth = 0) {
  if (value === null || value === undefined) {
    return value;
  }
  if (typeof value === "string") {
    if (value.length > 160) {
      return `${value.slice(0, 160)} ⟪truncated⟫`;
    }
    return value;
  }
  if (typeof value !== "object") {
    return value;
  }
  if (depth > 2) {
    return { summary: Array.isArray(value) ? `array(${value.length})` : "object" };
  }
  if (Array.isArray(value)) {
    if (value.length > 12) {
      return { type: "array", length: value.length };
    }
    return value.map((item) => summarisePayload(item, depth + 1));
  }
  const keys = Object.keys(value);
  if (keys.length > 12) {
    return { type: "object", keys: keys.length };
  }
  const summary = {};
  keys.forEach((key) => {
    summary[key] = summarisePayload(value[key], depth + 1);
  });
  return summary;
}

function prepareLogMeta(meta = {}) {
  if (!meta || typeof meta !== "object") {
    return meta;
  }
  const prepared = { ...meta };
  if ("payload" in prepared) {
    prepared.payload = summarisePayload(prepared.payload);
  }
  if ("response" in prepared) {
    prepared.response = summarisePayload(prepared.response);
  }
  return prepared;
}

function logEvent(message, meta = {}) {
  const preparedMeta = prepareLogMeta(meta);
  const safeMeta = sanitiseLogMeta(preparedMeta);
  const entry = {
    ts: new Date(),
    message,
    meta: safeMeta,
  };
  logBuffer.push(entry);
  while (logBuffer.length > MAX_LOG_ENTRIES) {
    logBuffer.shift();
  }
  if (!devToolsEnabled) {
    console.log(`[ui] ${message}`, meta);
    return;
  }
  if (dom.devConsole) {
    dom.devConsole.classList.add("active");
    dom.devConsole.removeAttribute("hidden");
  }
  if (dom.devStream) {
    const line = document.createElement("div");
    line.className = "log-entry";
    const time = document.createElement("time");
    time.textContent = entry.ts.toISOString();
    const content = document.createElement("span");
    const metaText = safeMeta && typeof safeMeta === "object" && Object.keys(safeMeta).length
      ? ` ${JSON.stringify(safeMeta)}`
      : "";
    content.textContent = `${message}${metaText}`;
    line.appendChild(time);
    line.appendChild(content);
    dom.devStream.appendChild(line);
    dom.devStream.scrollTop = dom.devStream.scrollHeight;
  }
  console.log(`[ui] ${message}`, meta);
}

function logTaskSummary() {
  TASK_CHECKLIST.forEach((item, index) => {
    logEvent(`Request ${index + 1}: ${item.request}`, {
      action: item.action,
      status: item.status,
    });
  });
  logEvent("Excel export guidance", {
    hint: "Use Download Excel (.xls) in the Search & Filters card to export the visible table with the current filters.",
  });
}

function renderInterpretationGuide() {
  if (!devToolsEnabled || !dom.devInterpretationGuide) {
    return;
  }
  dom.devInterpretationGuide.innerHTML = "";
  const heading = document.createElement("h4");
  heading.textContent = "FlightAware payload interpretation (single-call playbook)";
  dom.devInterpretationGuide.appendChild(heading);
  const intro = document.createElement("p");
  intro.textContent =
    "These notes summarise how the dashboard maps GET /flights/{REG}?ident_type=registration responses to status fields.";
  dom.devInterpretationGuide.appendChild(intro);
  const list = document.createElement("ol");
  FLIGHTAWARE_GUIDE_STEPS.forEach((step) => {
    const item = document.createElement("li");
    item.textContent = step;
    list.appendChild(item);
  });
  dom.devInterpretationGuide.appendChild(list);
}

function renderDbTableOutput() {
  if (!devToolsEnabled || !dom.devTableOutput) {
    return;
  }
  dom.devTableOutput.innerHTML = "";
  const tableName = state.activeDbTable;
  const hasCache = state.dbTableData && state.dbTableData.size > 0;
  if (!tableName) {
    dom.devTableOutput.appendChild(
      createElement(
        "div",
        "dev-table-placeholder",
        hasCache
          ? "Select a table to refresh the preview."
          : "Select a table to preview its latest rows."
      )
    );
    if (dom.devTableClear) {
      dom.devTableClear.hidden = !hasCache;
    }
    return;
  }

  const payload = state.dbTableData.get(tableName);
  if (!payload) {
    dom.devTableOutput.appendChild(
      createElement(
        "div",
        "dev-table-placeholder",
        "No cached preview yet. Click the table button again to load data."
      )
    );
    if (dom.devTableClear) {
      dom.devTableClear.hidden = false;
    }
    return;
  }

  if (payload.ok === false) {
    dom.devTableOutput.appendChild(
      createElement("div", "dev-table-error", payload.error || "Unable to load table data.")
    );
    if (Array.isArray(payload.hints) && payload.hints.length) {
      dom.devTableOutput.appendChild(
        createElement("div", "dev-table-hint", payload.hints.join(" · "))
      );
    }
    if (dom.devTableClear) {
      dom.devTableClear.hidden = false;
    }
    return;
  }

  const summaryParts = [];
  if (typeof payload.rowCount === "number") {
    summaryParts.push(`${payload.rowCount} rows fetched`);
  }
  if (Array.isArray(payload.rows)) {
    summaryParts.push(`showing up to ${payload.rows.length} rows`);
  }
  const summaryText = summaryParts.length ? summaryParts.join(" · ") : "Preview loaded.";
  dom.devTableOutput.appendChild(
    createElement("div", "dev-table-summary", `${tableName} — ${summaryText}`)
  );
  const pre = document.createElement("pre");
  pre.textContent = JSON.stringify(payload.rows ?? [], null, 2);
  dom.devTableOutput.appendChild(pre);
  if (Array.isArray(payload.hints) && payload.hints.length) {
    dom.devTableOutput.appendChild(
      createElement("div", "dev-table-hint", payload.hints.join(" · "))
    );
  }
  if (dom.devTableClear) {
    dom.devTableClear.hidden = false;
  }
}

function renderDbTableTools() {
  if (!devToolsEnabled || !dom.devTableTools) {
    return;
  }
  dom.devTableTools.hidden = false;
  renderInterpretationGuide();
  if (dom.devTableControls) {
    dom.devTableControls.innerHTML = "";
    if (state.dbTablesLoading) {
      dom.devTableControls.appendChild(
        createElement("div", "dev-table-placeholder", "Loading table list…")
      );
    } else if (state.dbTableError) {
      dom.devTableControls.appendChild(
        createElement("div", "dev-table-error", state.dbTableError)
      );
    } else {
      const tables = Array.isArray(state.dbTables) ? state.dbTables : [];
      let relevant = tables.filter((table) =>
        RELEVANT_TABLE_PATTERN.test(String(table.name || ""))
      );
      if (!relevant.length) {
        relevant = tables.slice(0, 5);
      }
      if (!relevant.length) {
        dom.devTableControls.appendChild(
          createElement(
            "div",
            "dev-table-placeholder",
            "No tables reported yet. Load data to populate the cache."
          )
        );
      } else {
        relevant.forEach((table) => {
          const button = document.createElement("button");
          button.type = "button";
          const rowCount =
            typeof table.rowCount === "number" ? table.rowCount : "?";
          button.textContent = `${table.name} (${rowCount})`;
          if (state.activeDbTable === table.name) {
            button.classList.add("active");
          }
          button.addEventListener("click", (event) => {
            const target = event.currentTarget;
            handleDevTablePreview(table.name, target instanceof HTMLButtonElement ? target : null);
          });
          dom.devTableControls.appendChild(button);
        });
      }
    }
  }
  renderDbTableOutput();
}

async function loadDbTables() {
  if (!devToolsEnabled || state.dbTablesLoading) {
    return;
  }
  state.dbTablesLoading = true;
  state.dbTableError = null;
  renderDbTableTools();
  try {
    const data = await fetchJson(apiUrl("/api/health/db/tables"), {
      skipAuthHandling: true,
    });
    state.dbTables = Array.isArray(data?.tables) ? data.tables : [];
    state.dbTableError = data?.error || null;
    logEvent("DB table list loaded", { tables: state.dbTables.length });
  } catch (error) {
    state.dbTableError = error?.message || "Unable to load tables";
    logEvent("DB table list failed", { error: state.dbTableError });
  } finally {
    state.dbTablesLoading = false;
    renderDbTableTools();
  }
}

async function handleDevTablePreview(tableName, button) {
  if (!devToolsEnabled || !tableName) {
    return;
  }
  const label = String(tableName);
  let previousText = null;
  if (button) {
    previousText = button.textContent;
    button.disabled = true;
    button.textContent = `Loading ${label}…`;
  }
  try {
    const response = await fetchJson(
      apiUrl(`/api/health/db/table/${encodeURIComponent(label)}?limit=${DB_TABLE_FETCH_LIMIT}`),
      { skipAuthHandling: true }
    );
    state.dbTableData.set(label, response);
    state.activeDbTable = label;
    logEvent("DB table preview loaded", {
      table: label,
      rows: response?.rowCount,
    });
  } catch (error) {
    state.dbTableData.set(label, { ok: false, error: error.message, table: label });
    state.activeDbTable = label;
    logEvent("DB table preview failed", { table: label, error: error.message });
  } finally {
    if (button) {
      button.disabled = false;
      if (previousText !== null) {
        button.textContent = previousText;
      }
    }
    renderDbTableTools();
  }
}

function clearDbTableOutput() {
  if (!devToolsEnabled) {
    return;
  }
  state.activeDbTable = null;
  if (state.dbTableData) {
    state.dbTableData.clear();
  }
  renderDbTableTools();
  logEvent("DB table preview cleared");
}

function setModeChip() {
  if (!dom.modeChip) return;
  const mode = visualMode || "development";
  const productionChip = mode.startsWith("prod");
  dom.modeChip.hidden = productionChip;
  dom.modeChip.textContent = productionChip ? "" : mode;
  dom.modeChip.classList.remove("dev", "prod");
  dom.modeChip.title = `Runtime mode: ${runtimeMode || "development"}${buildId ? ` | Build ${buildId}` : ""}`;
  dom.modeChip.setAttribute("aria-hidden", productionChip ? "true" : "false");
  dom.modeChip.classList.add(productionChip ? "prod" : "dev");
}

function setRefreshIndicator(text) {
  if (dom.refreshIndicator) {
    dom.refreshIndicator.textContent = text;
  }
}

function formatTime(value) {
  if (!value) return "—";
  try {
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) {
      return value;
    }
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  } catch (error) {
    return value;
  }
}

function formatRelative(value) {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.round(diffMs / 60000);
  if (Math.abs(diffMinutes) < 1) return "just now";
  if (diffMinutes > 0) {
    if (diffMinutes < 60) return `${diffMinutes} min ago`;
    const hours = Math.round(diffMinutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    return `${days}d ago`;
  }
  const futureMin = Math.abs(diffMinutes);
  if (futureMin < 60) return `in ${futureMin} min`;
  const futureHours = Math.round(futureMin / 60);
  if (futureHours < 24) return `in ${futureHours}h`;
  const futureDays = Math.round(futureHours / 24);
  return `in ${futureDays}d`;
}

let absoluteFormatter;

function formatDisplayTimestamp(value) {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) {
    return "";
  }
  try {
    if (!absoluteFormatter) {
      absoluteFormatter = new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      });
    }
    return absoluteFormatter.format(date);
  } catch (error) {
    try {
      return date.toLocaleString();
    } catch (_inner) {
      return date.toISOString();
    }
  }
}

function summariseReliabilityIssue(issue) {
  if (!issue || typeof issue !== "object") {
    return "Manual verification required.";
  }

  if (issue.type === "missing") {
    return "No FlightAware data has been fetched yet.";
  }

  if (issue.type === "stale") {
    const thresholdMinutes = Math.round(((issue.thresholdMs ?? DATA_RELIABILITY_STALE_MS) / 60000) || 0);
    const relative = formatRelative(issue.fetchedAt);
    const absolute = formatDisplayTimestamp(issue.fetchedAt);
    const timeLabel = relative
      ? absolute && absolute !== relative
        ? `${relative} (${absolute})`
        : relative
      : absolute || "earlier";
    const thresholdText = thresholdMinutes ? `Threshold ${thresholdMinutes} min.` : "";
    return `Last FlightAware sync ${timeLabel}. ${thresholdText}`.trim();
  }

  if (issue.type === "mismatch") {
    const tam = issue.tamStatus || "TAM";
    const fa = issue.autoDisplay || issue.autoStatus || "FlightAware";
    return `TAM shows ${tam} while FlightAware suggests ${fa}.`;
  }

  if (issue.type === "error") {
    const message = issue.errorMessage ? issue.errorMessage : "Unknown error";
    return `FlightAware error: ${message}.`;
  }

  return "Manual verification required.";
}

function buildReliabilityViewModel(assessment) {
  if (!assessment) {
    return null;
  }

  const issues = Array.isArray(assessment.issues) ? assessment.issues : [];
  const messages = issues.map(summariseReliabilityIssue);
  const issueCount = issues.length;
  const label = issueCount
    ? `Manual check required (${issueCount} ${issueCount === 1 ? "issue" : "issues"})`
    : "FlightAware data verified";
  const hasError = issues.some((issue) => issue.type === "error");
  const baseInstruction = "Follow the verification steps in the Data reliability panel before updating this aircraft.";
  const instruction = hasError
    ? `${baseInstruction} If the FlightAware error repeats after Manual Refresh, capture a screenshot and notify the digital support team.`
    : baseInstruction;

  return {
    ...assessment,
    messages,
    label,
    instruction,
    hasError,
  };
}

function computeReliabilitySummary(maxExamples = 5) {
  const summary = {
    enabled: dataReliabilityEnabled,
    total: state.aircraft.length,
    assessed: 0,
    issues: 0,
    missing: 0,
    stale: 0,
    mismatch: 0,
    error: 0,
    examples: [],
    hasError: false,
  };

  if (!dataReliabilityEnabled || !state.aircraft.length) {
    return summary;
  }

  const seen = new Set();

  state.aircraft.forEach((aircraft) => {
    const model = composeStatusModel(aircraft);
    if (!model?.reliability) {
      return;
    }
    summary.assessed += 1;
    const reliability = model.reliability;
    if (reliability.isReliable) {
      return;
    }
    summary.issues += 1;
    reliability.issues.forEach((issue) => {
      if (issue.type === "missing") summary.missing += 1;
      if (issue.type === "stale") summary.stale += 1;
      if (issue.type === "mismatch") summary.mismatch += 1;
      if (issue.type === "error") summary.error += 1;
    });
    if (reliability.hasError) {
      summary.hasError = true;
    }
    const identLabel =
      cleanText(getStatusIdent(aircraft)) ||
      cleanText(aircraft.registration) ||
      cleanText(aircraft.ident) ||
      cleanText(aircraft.tail) ||
      `Aircraft ${aircraft.id}`;
    if (!seen.has(identLabel) && summary.examples.length < maxExamples) {
      seen.add(identLabel);
      summary.examples.push({
        ident: identLabel,
        severity: reliability.severity,
        messages: reliability.messages,
      });
    }
  });

  return summary;
}

function renderReliabilitySteps(summary) {
  if (!dom.dataReportReliabilitySteps) {
    return;
  }
  dom.dataReportReliabilitySteps.replaceChildren();
  if (!dataReliabilityEnabled || !state.aircraft.length) {
    return;
  }
  const steps = [...RELIABILITY_BASE_STEPS];
  if (summary?.hasError) {
    steps.push(
      "Step 4 — if the FlightAware error repeats after Manual Refresh, capture a screenshot and notify the digital support team."
    );
  }
  steps.forEach((step) => {
    const li = document.createElement("li");
    li.textContent = step;
    dom.dataReportReliabilitySteps.appendChild(li);
  });
}

function renderReliabilityReport() {
  if (!dom.dataReportReliability) {
    return;
  }
  if (!dataReliabilityEnabled || !state.aircraft.length) {
    dom.dataReportReliability.hidden = true;
    state.reliabilitySummary = null;
    return;
  }

  const summary = computeReliabilitySummary();
  state.reliabilitySummary = summary;

  dom.dataReportReliability.hidden = false;

  if (dom.dataReportReliabilitySummary) {
    if (!summary.assessed) {
      dom.dataReportReliabilitySummary.textContent =
        "Reliability checks will populate after the first FlightAware status loads.";
    } else if (!summary.issues) {
      dom.dataReportReliabilitySummary.textContent =
        "All loaded aircraft have recent FlightAware data. Still cross-check with the official site.";
    } else {
      const parts = [];
      const detail = [];
      if (summary.missing) detail.push(`${summary.missing} missing`);
      if (summary.stale) detail.push(`${summary.stale} stale`);
      if (summary.mismatch) detail.push(`${summary.mismatch} mismatched`);
      if (summary.error) detail.push(`${summary.error} error${summary.error === 1 ? "" : "s"}`);
      parts.push(`${summary.issues} aircraft need manual verification${detail.length ? ` (${detail.join(" · ")})` : ""}.`);
      dom.dataReportReliabilitySummary.textContent = parts.join(" ");
    }
  }

  if (dom.dataReportReliabilityList) {
    dom.dataReportReliabilityList.replaceChildren();
    if (!summary.issues) {
      const item = document.createElement("li");
      item.className = "data-report__reliability-item";
      item.textContent =
        "No issues detected. Still compare every aircraft against FlightAware before making decisions.";
      dom.dataReportReliabilityList.appendChild(item);
    } else if (summary.examples.length) {
      summary.examples.forEach((example) => {
        const item = document.createElement("li");
        item.className = `data-report__reliability-item data-report__reliability-item--${example.severity}`;
        const messagesText = example.messages && example.messages.length
          ? example.messages.join(" · ")
          : "Manual verification required.";
        item.textContent = `${example.ident} — ${messagesText}`;
        dom.dataReportReliabilityList.appendChild(item);
      });
    } else {
      const fallback = document.createElement("li");
      fallback.className = "data-report__reliability-item data-report__reliability-item--warning";
      fallback.textContent =
        "Several aircraft need verification. Review the table for Manual check required notices.";
      dom.dataReportReliabilityList.appendChild(fallback);
    }
  }

  if (dom.dataReportReliabilityInstructions) {
    dom.dataReportReliabilityInstructions.textContent =
      "Always complete these checks before updating TAM or sharing updates with customers.";
  }

  renderReliabilitySteps(summary);
}

function computeImportReport(list = []) {
  const items = Array.isArray(list) ? list : [];
  const total = items.length;
  const ownerCounts = new Map();
  const operatorCounts = new Map();
  const statusCounts = new Map();
  let activeCount = 0;

  for (const item of items) {
    const owner = cleanText(item?.owner) || "Unknown";
    ownerCounts.set(owner, (ownerCounts.get(owner) || 0) + 1);

    const operator = cleanText(item?.operator) || "Unknown";
    operatorCounts.set(operator, (operatorCounts.get(operator) || 0) + 1);

    const status = cleanText(item?.tam_status) || "Unknown";
    statusCounts.set(status, (statusCounts.get(status) || 0) + 1);

    if (item?.active === false) {
      continue;
    }
    activeCount += 1;
  }

  const toSortedEntries = (counts) =>
    Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => {
        if (b.count === a.count) {
          return a.name.localeCompare(b.name);
        }
        return b.count - a.count;
      });

  return {
    total,
    active: activeCount,
    inactive: total - activeCount,
    ownerCount: ownerCounts.size,
    operatorCount: operatorCounts.size,
    statusCount: statusCounts.size,
    owners: toSortedEntries(ownerCounts),
    operators: toSortedEntries(operatorCounts),
    statuses: toSortedEntries(statusCounts),
    generatedAt: new Date().toISOString(),
  };
}

function formatTopEntries(entries = [], limit = 3) {
  if (!Array.isArray(entries) || entries.length === 0) {
    return "";
  }
  return entries
    .slice(0, limit)
    .map(({ name, count }) => `${name || "Unknown"} (${count})`)
    .join(", ");
}

function updateLastUpdatedDisplay() {
  if (!dom.lastUpdate) return;
  if (!state.sessionUser) {
    dom.lastUpdate.textContent = "Last status update: sign in to begin.";
    return;
  }
  if (!state.lastRefreshTs) {
    dom.lastUpdate.textContent = "Last status update: awaiting first refresh.";
    return;
  }
  const absolute = formatDisplayTimestamp(state.lastRefreshTs);
  const relative = formatRelative(state.lastRefreshTs);
  dom.lastUpdate.textContent = `Last status update: ${absolute}${relative ? ` (${relative})` : ""}`;
}

function renderDataReport() {
  if (!dom.dataReport) {
    return;
  }

  if (isProdMode) {
    dom.dataReport.hidden = true;
    if (dom.dataReportReliability) {
      dom.dataReportReliability.hidden = true;
    }
    state.reliabilitySummary = null;
    return;
  }

  if (!state.sessionUser || !state.importReport || !state.aircraftLoadedAt) {
    dom.dataReport.hidden = true;
    if (dom.dataReportReliability) {
      dom.dataReportReliability.hidden = true;
    }
    state.reliabilitySummary = null;
    dom.dataReportMetrics?.replaceChildren();
    dom.dataReportSummary?.replaceChildren();
    if (!state.sessionUser && dom.dataReportStamp) {
      dom.dataReportStamp.textContent = "Sign in to load the data snapshot.";
    }
    return;
  }

  dom.dataReport.hidden = false;
  renderReliabilityReport();

  if (dom.dataReportStamp) {
    const absolute = formatDisplayTimestamp(state.aircraftLoadedAt);
    const relative = formatRelative(state.aircraftLoadedAt);
    dom.dataReportStamp.textContent = `Dataset refreshed ${absolute}${relative ? ` (${relative})` : ""}.`;
  }

  if (dom.dataReportSummary) {
    dom.dataReportSummary.replaceChildren();
    const ownerLine = document.createElement("div");
    const ownerLabel = getOwnerFilterLabel();
    ownerLine.textContent = state.aircraftLimitActive
      ? `Owner filter: ${ownerLabel} (locked while showing the first ${state.aircraftLimit} aircraft)`
      : `Owner filter: ${ownerLabel}`;
    dom.dataReportSummary.appendChild(ownerLine);
    const operatorLine = document.createElement("div");
    const operatorLabel = state.operatorFilter || "All operators";
    operatorLine.textContent = state.aircraftLimitActive
      ? `Operator filter: ${operatorLabel} (locked)`
      : `Operator filter: ${operatorLabel}`;
    dom.dataReportSummary.appendChild(operatorLine);
    if (state.aircraftLimitActive) {
      const limitLine = document.createElement("div");
      limitLine.textContent = `Development dataset limit active: showing the first ${state.aircraftLimit} aircraft.`;
      dom.dataReportSummary.appendChild(limitLine);
    }
  }

  if (dom.dataReportMetrics) {
    dom.dataReportMetrics.replaceChildren();
    const report = state.importReport;
    const metrics = [
      {
        title: "Total aircraft",
        value: report.total,
        detail:
          report.total > 0
            ? `Active ${report.active}${report.inactive ? ` · Inactive ${report.inactive}` : ""}`
            : "No aircraft in this import.",
      },
      {
        title: "Unique owners",
        value: report.ownerCount,
        detail: formatTopEntries(report.owners),
      },
      {
        title: "Unique operators",
        value: report.operatorCount,
        detail: formatTopEntries(report.operators),
      },
      {
        title: "TAM status",
        value: report.statusCount,
        detail: formatTopEntries(report.statuses),
      },
    ];

    metrics.forEach((metric) => {
      const metricCard = document.createElement("div");
      metricCard.className = "data-report__metric";

      const title = document.createElement("p");
      title.className = "data-report__metric-title";
      title.textContent = metric.title;
      metricCard.appendChild(title);

      const value = document.createElement("div");
      value.className = "data-report__metric-value";
      value.textContent = String(metric.value ?? 0);
      metricCard.appendChild(value);

      if (metric.detail) {
        const detail = document.createElement("p");
        detail.className = "data-report__metric-detail";
        detail.textContent = metric.detail;
        metricCard.appendChild(detail);
      }

      dom.dataReportMetrics.appendChild(metricCard);
    });
  }
}

function stringifyForDebug(value, limit = 1600) {
  if (value === null) {
    return "null";
  }
  if (value === undefined) {
    return "undefined";
  }
  try {
    const json = JSON.stringify(value, null, 2);
    if (!json) {
      return "";
    }
    if (json.length <= limit) {
      return json;
    }
    return `${json.slice(0, limit)}\n… (truncated)`;
  } catch (error) {
    const text = String(value);
    if (text.length <= limit) {
      return text;
    }
    return `${text.slice(0, limit)}…`;
  }
}

function createElement(tag, className, textContent) {
  const el = document.createElement(tag);
  if (className) {
    el.className = className;
  }
  if (textContent !== undefined) {
    el.textContent = textContent;
  }
  return el;
}

function formatGroundDuration(minutes) {
  if (!Number.isFinite(minutes)) {
    return "—";
  }
  if (minutes < 60) {
    return `${minutes} min`;
  }
  if (minutes < 24 * 60) {
    const hours = Math.floor(minutes / 60);
    const remainder = minutes - hours * 60;
    return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
  }
  if (minutes < 7 * 24 * 60) {
    const days = Math.floor(minutes / (24 * 60));
    const hours = Math.floor((minutes - days * 24 * 60) / 60);
    return hours ? `${days}d ${hours}h` : `${days}d`;
  }
  if (minutes < 30 * 24 * 60) {
    const weeks = Math.floor(minutes / (7 * 24 * 60));
    const days = Math.floor((minutes - weeks * 7 * 24 * 60) / (24 * 60));
    return days ? `${weeks}w ${days}d` : `${weeks}w`;
  }
  const months = Math.floor(minutes / (30 * 24 * 60));
  const weeks = Math.floor((minutes - months * 30 * 24 * 60) / (7 * 24 * 60));
  return weeks ? `${months}mo ${weeks}w` : `${months}mo`;
}

function toTitleCaseWords(value) {
  if (!value) {
    return "";
  }
  return String(value)
    .split(/[\s_/,-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function resolveRegionLabel(aircraft = {}) {
  for (const key of HEATMAP_REGION_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(aircraft, key)) {
      continue;
    }
    const candidate = cleanText(aircraft[key]);
    if (candidate) {
      if (candidate === candidate.toUpperCase()) {
        return toTitleCaseWords(candidate);
      }
      return candidate;
    }
  }
  return "Unassigned";
}

function truncatePlainText(value, maxLength = 120) {
  if (value === undefined || value === null) {
    return "";
  }
  const text = cleanText(value);
  if (!text) {
    return "";
  }
  if (text.length <= maxLength) {
    return text;
  }
  if (maxLength <= 1) {
    return text.slice(0, maxLength);
  }
  return `${text.slice(0, maxLength - 1)}…`;
}

function formatOwnerInitials(owner, fallback = "—") {
  const cleaned = cleanText(owner);
  if (!cleaned) {
    return fallback;
  }
  const compact = cleaned.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
  if (!compact) {
    return fallback;
  }
  if (compact.startsWith("ADMIN")) {
    return "ADMIN";
  }
  if (compact.length === 1) {
    return compact;
  }
  return compact.slice(0, 2);
}

function formatHeatmapTime(model) {
  if (!model) {
    return "—";
  }
  if (model.isAirborne) {
    if (Number.isFinite(model.airborneMinutes)) {
      return `Airborne ${formatGroundDuration(model.airborneMinutes)}`;
    }
    const label = cleanText(model.timeLabel);
    if (label) {
      return label.replace(/^AIRBORNE\s*[·\-]?\s*/i, "Airborne ").trim();
    }
    return "Airborne";
  }
  if (Number.isFinite(model.groundedMinutes)) {
    return `On ground ${formatGroundDuration(model.groundedMinutes)}`;
  }
  const label = cleanText(model.timeLabel);
  if (label) {
    return label;
  }
  return "—";
}

function getHeatmapStatusClass(model, aircraft) {
  const displayStatus = model?.tamDisplay && model.tamDisplay !== "—" ? model.tamDisplay : null;
  const tamSource = displayStatus || aircraft?.tam_status;
  const tamNormalised = normaliseTamStatus(tamSource);
  if (tamNormalised && tamNormalised !== "—" && HEATMAP_STATUS_CLASS_MAP.has(tamNormalised)) {
    return HEATMAP_STATUS_CLASS_MAP.get(tamNormalised);
  }
  const aircraftTam = normaliseTamStatus(aircraft?.tam_status);
  if (aircraftTam && aircraftTam !== "—" && HEATMAP_STATUS_CLASS_MAP.has(aircraftTam)) {
    return HEATMAP_STATUS_CLASS_MAP.get(aircraftTam);
  }
  if (model?.categoryKey && HEATMAP_CATEGORY_CLASS_MAP[model.categoryKey]) {
    return HEATMAP_CATEGORY_CLASS_MAP[model.categoryKey];
  }
  return "";
}

function getHeatmapStatusSortValue(aircraft, model, statusClassOverride) {
  const statusClass = statusClassOverride || getHeatmapStatusClass(model, aircraft) || HEATMAP_STATUS_FALLBACK_CLASS;
  if (HEATMAP_STATUS_SORT_ORDER.has(statusClass)) {
    return HEATMAP_STATUS_SORT_ORDER.get(statusClass);
  }
  return HEATMAP_STATUS_SORT_ORDER.size;
}

function summariseHeatmapComment(comment) {
  if (!comment) {
    return "";
  }
  if (typeof comment === "string") {
    return truncatePlainText(comment, 140);
  }
  if (typeof comment === "object") {
    const body = truncatePlainText(comment.body || comment.comment || comment.text || "", 140);
    const author = truncatePlainText(comment.author || comment.user || "", 40);
    const relative = comment.created_at ? formatRelative(comment.created_at) : "";
    const metaParts = [author, relative].filter(Boolean);
    if (body && metaParts.length) {
      return `${body} (${metaParts.join(" · ")})`;
    }
    if (body) {
      return body;
    }
    if (metaParts.length) {
      return metaParts.join(" · ");
    }
  }
  return "";
}

function buildHeatmapTooltip({ operatorLabel, statusLabel, aircraft, model }) {
  const parts = [];
  const operatorText = operatorLabel || cleanText(aircraft?.operator) || "Unknown operator";
  if (operatorText) {
    parts.push(operatorText);
  }
  const statusText = truncatePlainText(statusLabel || model?.categoryLabel || "", 80);
  if (statusText) {
    parts.push(`Status ${statusText}`);
  }
  const commentSummary = summariseHeatmapComment(model?.lastComment || aircraft?.last_comment);
  if (commentSummary) {
    parts.push(`Comment ${commentSummary}`);
  }
  const notesSummary = truncatePlainText(model?.notes, 120);
  if (notesSummary) {
    parts.push(`Notes ${notesSummary}`);
  }
  return parts.join(" · ");
}

function createHeatmapTile(aircraft, model) {
  const tile = document.createElement("button");
  tile.type = "button";
  tile.className = "heatmap-tile";
  const statusClass = getHeatmapStatusClass(model, aircraft) || HEATMAP_STATUS_FALLBACK_CLASS;
  if (statusClass) {
    tile.classList.add(statusClass);
  }
  const msn = cleanText(aircraft.msn) || "—";
  const registration =
    cleanText(aircraft.registration) ||
    cleanText(aircraft.ident) ||
    cleanText(aircraft.tail) ||
    "—";
  let ownerInitials = formatOwnerInitials(aircraft.owner, "");
  if (!ownerInitials) {
    ownerInitials = formatOwnerInitials(aircraft.operator, "");
  }
  if (!ownerInitials) {
    ownerInitials = "—";
  }
  const location = cleanText(model.lastLocation) || "—";
  const timeText = formatHeatmapTime(model);

  const primary = createElement(
    "span",
    "heatmap-tile__line heatmap-tile__line--primary",
    `${msn} – ${registration}`
  );
  const secondary = createElement(
    "span",
    "heatmap-tile__line heatmap-tile__line--secondary",
    `${location}${timeText && timeText !== "—" ? ` · ${timeText}` : ""}`
  );
  const meta = createElement("span", "heatmap-tile__line heatmap-tile__line--meta", ownerInitials);

  tile.appendChild(primary);
  tile.appendChild(secondary);
  tile.appendChild(meta);

  const displayStatusLabel = model.tamDisplay && model.tamDisplay !== "—" ? model.tamDisplay : "";
  const statusLabel = normaliseTamStatus(displayStatusLabel) || model.categoryLabel || "Unknown";
  tile.dataset.status = statusLabel;
  tile.dataset.aircraftId = String(aircraft.id || "");
  const operatorLabel = cleanText(aircraft.operator) || "Unknown operator";
  const tamLabelForTitle = displayStatusLabel || statusLabel;
  const tooltipText = buildHeatmapTooltip({
    operatorLabel,
    statusLabel: tamLabelForTitle,
    aircraft,
    model,
  });
  if (tooltipText) {
    tile.title = tooltipText;
  }
  tile.dataset.statusClass = statusClass;
  tile.dataset.statusOrder = String(getHeatmapStatusSortValue(aircraft, model, statusClass));

  const flightawareUrl = model.flightawareUrl || cleanText(aircraft.flightaware_url);
  if (flightawareUrl) {
    tile.addEventListener("click", () => {
      window.open(flightawareUrl, "_blank", "noopener");
      logEvent("Heatmap tile opened", {
        aircraftId: aircraft.id,
        msn: msn || "n/a",
        registration: registration || "n/a",
      });
    });
  } else {
    tile.disabled = true;
    tile.classList.add("heatmap-tile--disabled");
  }

  const labelParts = [
    msn !== "—" ? `MSN ${msn}` : null,
    registration !== "—" ? `Registration ${registration}` : null,
    location !== "—" ? `Location ${location}` : null,
    timeText && timeText !== "—" ? timeText : null,
  ];
  tile.setAttribute("aria-label", labelParts.filter(Boolean).join(", "));

  return tile;
}

function renderHeatmap(rows, { loading = false, reason = "" } = {}) {
  if (!dom.heatmapCard || !dom.heatmapContainer || !dom.heatmapStatus) {
    return;
  }
  const container = dom.heatmapContainer;
  container.innerHTML = "";

  let statusMessage = "";

  if (loading) {
    statusMessage = "Loading fleet data for the heatmap…";
    container.appendChild(createElement("div", "heatmap-empty", "Fetching aircraft data."));
  } else if (!state.sessionUser) {
    statusMessage = "Sign in to view the fleet heatmap.";
    container.appendChild(createElement("div", "heatmap-empty", "Sign in to load aircraft tiles."));
  } else if (!rows.length) {
    statusMessage =
      reason === "filtered"
        ? "No aircraft match the current filters. Adjust filters to populate the heatmap."
        : "No aircraft available for the heatmap.";
    container.appendChild(createElement("div", "heatmap-empty", statusMessage));
  } else {
    const grouped = new Map();
    rows.forEach(({ aircraft, model }) => {
      const region = resolveRegionLabel(aircraft);
      if (!grouped.has(region)) {
        grouped.set(region, new Map());
      }
      const operator = cleanText(aircraft.operator) || "Unknown operator";
      const regionGroup = grouped.get(region);
      if (!regionGroup.has(operator)) {
        regionGroup.set(operator, []);
      }
      regionGroup.get(operator).push({ aircraft, model });
    });

    const regionEntries = Array.from(grouped.entries()).sort((left, right) => {
      const leftTotal = Array.from(left[1].values()).reduce((sum, list) => sum + list.length, 0);
      const rightTotal = Array.from(right[1].values()).reduce((sum, list) => sum + list.length, 0);
      if (leftTotal !== rightTotal) {
        return rightTotal - leftTotal;
      }
      return left[0].localeCompare(right[0]);
    });

    let operatorCount = 0;

    regionEntries.forEach(([region, operators]) => {
      operatorCount += operators.size;
      const totalAircraft = Array.from(operators.values()).reduce(
        (sum, list) => sum + list.length,
        0
      );
      const regionSection = createElement("section", "heatmap-region");
      const regionHeader = createElement("div", "heatmap-region__title");
      regionHeader.appendChild(createElement("span", "heatmap-region__label", `${region}`));
      regionHeader.appendChild(
        createElement("span", "heatmap-region__count", `${totalAircraft} aircraft`)
      );
      regionSection.appendChild(regionHeader);

      const operatorsContainer = createElement("div", "heatmap-operators");
      const operatorEntries = Array.from(operators.entries()).sort((left, right) => {
        if (right[1].length !== left[1].length) {
          return right[1].length - left[1].length;
        }
        return left[0].localeCompare(right[0]);
      });
      operatorEntries.forEach(([operator, items]) => {
        const operatorBlock = createElement("article", "heatmap-operator");
        const operatorHeader = createElement("div", "heatmap-operator__title");
        operatorHeader.appendChild(createElement("span", "heatmap-operator__label", operator));
        operatorHeader.appendChild(
          createElement("span", "heatmap-operator__count", `${items.length} msn`)
        );
        operatorBlock.appendChild(operatorHeader);

        const tiles = createElement("div", "heatmap-tiles");
        items
          .slice()
          .sort((a, b) => {
            const statusOrderA = getHeatmapStatusSortValue(a.aircraft, a.model);
            const statusOrderB = getHeatmapStatusSortValue(b.aircraft, b.model);
            if (statusOrderA !== statusOrderB) {
              return statusOrderA - statusOrderB;
            }
            const msnCompare = compareTextValues(
              cleanText(a.aircraft.msn),
              cleanText(b.aircraft.msn)
            );
            if (msnCompare !== 0) {
              return msnCompare;
            }
            return compareTextValues(
              cleanText(a.aircraft.registration),
              cleanText(b.aircraft.registration)
            );
          })
          .forEach(({ aircraft, model }) => {
            tiles.appendChild(createHeatmapTile(aircraft, model));
          });
        operatorBlock.appendChild(tiles);
        operatorsContainer.appendChild(operatorBlock);
      });

      regionSection.appendChild(operatorsContainer);
      container.appendChild(regionSection);
    });

    statusMessage = `${rows.length} aircraft · ${regionEntries.length} region${
      regionEntries.length === 1 ? "" : "s"
    } · ${operatorCount} operator${operatorCount === 1 ? "" : "s"}`;

    if (state.heatmapLastSummary !== statusMessage) {
      state.heatmapLastSummary = statusMessage;
      logEvent("Heatmap updated", { summary: statusMessage });
    }
  }

  dom.heatmapStatus.textContent = statusMessage;
}

function preferIata(value) {
  const code = cleanText(value);
  if (!code) return null;
  const upper = code.toUpperCase();
  if (/^[A-Z]{3}$/.test(upper)) {
    return upper;
  }
  if (/^[A-Z]{4}$/.test(upper) && /^[KC]/.test(upper)) {
    return upper.slice(1);
  }
  return null;
}

function normaliseLocationLabel(value) {
  if (!value) return null;
  if (typeof value === "string") {
    return preferIata(value) || cleanText(value);
  }
  if (typeof value === "object") {
    const iata =
      preferIata(value.iata) ||
      preferIata(value.code_iata) ||
      preferIata(value.airport_code) ||
      preferIata(value.code) ||
      null;
    if (iata) {
      return iata;
    }
    const derived = preferIata(value.icao) || preferIata(value.code_icao) || null;
    if (derived) {
      return derived;
    }
    const code =
      cleanText(value.code) ||
      cleanText(value.icao) ||
      cleanText(value.airport_code) ||
      cleanText(value.ident) ||
      null;
    if (code) {
      return code.toUpperCase();
    }
    const name = cleanText(value.name) || cleanText(value.airport_name) || cleanText(value.city);
    if (name) {
      return name;
    }
  }
  return null;
}

function sanitiseLogMeta(meta, depth = 0) {
  if (!meta || typeof meta !== "object") {
    return meta;
  }
  if (depth > 2) {
    return { summary: "depth limit" };
  }
  if (Array.isArray(meta)) {
    if (meta.length > 10) {
      return { type: "array", length: meta.length };
    }
    return meta.map((item) => sanitiseLogMeta(item, depth + 1));
  }
  const result = {};
  Object.entries(meta).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      if (value.length > 10) {
        result[key] = { type: "array", length: value.length };
      } else {
        result[key] = value.map((item) => sanitiseLogMeta(item, depth + 1));
      }
      return;
    }
    if (value && typeof value === "object") {
      const keys = Object.keys(value);
      if (keys.length > 10) {
        result[key] = { keys: keys.length };
      } else {
        result[key] = sanitiseLogMeta(value, depth + 1);
      }
      return;
    }
    if (typeof value === "string" && value.length > 160) {
      result[key] = `${value.slice(0, 160)} ⟪truncated⟫`;
      return;
    }
    result[key] = value;
  });
  return result;
}

function shouldRefreshStatusForAircraft(aircraft, now = Date.now()) {
  const ident = canonicalIdent(getStatusIdent(aircraft));
  if (!ident) {
    return false;
  }
  const keys = getLookupKeys(aircraft);
  const hasStatus = keys.some((key) => state.statuses.has(key));
  const lastFetched = state.statusFetchedAt.get(ident) ?? 0;
  const streak = state.statusErrorStreak.get(ident) || 0;
  const interval = streak > 0 ? STATUS_ERROR_RETRY_MS : STATUS_CACHE_TTL_MS;
  if (!hasStatus) {
    return true;
  }
  return now - lastFetched >= interval;
}

function applyStatusToAircraft(identKey, status, fetchedStamp) {
  if (!identKey || !status) {
    return;
  }
  const timestamp = Number.isFinite(fetchedStamp) ? fetchedStamp : Date.now();
  const existingStamp = state.statusFetchedAt.get(identKey);
  if (Number.isFinite(existingStamp) && existingStamp > timestamp) {
    return;
  }
  const isoTimestamp = new Date(timestamp).toISOString();
  if (!status.updated_at) {
    status.updated_at = isoTimestamp;
  }
  state.statuses.set(identKey, status);
  const matching = state.aircraft.filter((aircraft) => canonicalIdent(getStatusIdent(aircraft)) === identKey);
  if (!matching.length) {
    state.statusFetchedAt.set(identKey, timestamp);
    state.statusErrorStreak.delete(identKey);
    return;
  }
  matching.forEach((aircraft) => {
    const keys = getLookupKeys(aircraft);
    keys.forEach((key) => state.statuses.set(key, status));
    state.statusFetchedAt.set(identKey, timestamp);
    state.statusErrorStreak.delete(identKey);
    updateStatusDetailIfActive(aircraft.id);
  });
}

function applyErrorStatusToAircraft(identKey, errorMessage, fetchedStamp) {
  if (!identKey || !errorMessage) {
    return;
  }
  const timestamp = Number.isFinite(fetchedStamp) ? fetchedStamp : Date.now();
  const existingStamp = state.statusFetchedAt.get(identKey);
  if (Number.isFinite(existingStamp) && existingStamp > timestamp) {
    return;
  }
  const isoTimestamp = new Date(timestamp).toISOString();
  const payload = {
    ident: identKey,
    phase: "unknown",
    status_text: null,
    error: errorMessage,
    updated_at: isoTimestamp,
  };
  state.statuses.set(identKey, payload);
  const matching = state.aircraft.filter((aircraft) => canonicalIdent(getStatusIdent(aircraft)) === identKey);
  matching.forEach((aircraft) => {
    const keys = getLookupKeys(aircraft);
    keys.forEach((key) => state.statuses.set(key, payload));
    updateStatusDetailIfActive(aircraft.id);
  });
  state.statusFetchedAt.set(identKey, timestamp);
  state.statusErrorStreak.set(identKey, (state.statusErrorStreak.get(identKey) || 0) + 1);
}

async function persistStatusSnapshot(entry = {}) {
  if (!state.sessionUser) {
    return;
  }
  const ident = canonicalIdent(entry.ident);
  if (!ident) {
    return;
  }
  const statusPayload = entry.status && typeof entry.status === "object" ? entry.status : null;
  const fetchedAt =
    normaliseLocalDateTimeInput(entry.fetchedAt || entry.fetched_at || statusPayload?.updated_at) ||
    new Date().toISOString();
  const responseStatus = Number.isFinite(entry.responseStatus)
    ? Number(entry.responseStatus)
    : Number.isFinite(entry.status)
    ? Number(entry.status)
    : null;
  const responseError = cleanText(entry.responseError || entry.error);
  const source = cleanText(entry.source || entry.origin || entry.context) || "ui-sync";
  const aliasInputs = Array.isArray(entry.aliases) ? entry.aliases : [];
  const aliasSet = new Set(aliasInputs.map((value) => canonicalIdent(value)).filter(Boolean));
  aliasSet.add(ident);
  const aliasList = Array.from(aliasSet);
  if (!aliasList.length) {
    return;
  }
  const payloadEntries = aliasList.map((aliasIdent, index) => ({
    ident: aliasIdent,
    lookup: aliasIdent,
    status: statusPayload,
    fetchedAt,
    responseStatus,
    responseError,
    source: index === 0 ? source : `${source}-alias`,
  }));
  try {
    await fetchJson(apiUrl("/api/flightaware/status/cache/save"), {
      method: "POST",
      body: JSON.stringify({ entries: payloadEntries }),
    });
    logEvent("Persisted FlightAware snapshot", {
      ident,
      source,
      aliasCount: Math.max(0, aliasList.length - 1),
    });
  } catch (error) {
    logEvent("Snapshot persistence failed", { ident, error: error.message });
  }
}

function composeStatusModel(aircraft) {
  const canonicalLookup = canonicalIdent(getStatusIdent(aircraft));
  const fetchedStamp = canonicalLookup ? state.statusFetchedAt.get(canonicalLookup) : null;
  const fetchedAt = Number.isFinite(fetchedStamp) ? new Date(fetchedStamp).toISOString() : null;
  const lookupKeys = getLookupKeys(aircraft);
  let status = null;
  for (const key of lookupKeys) {
    if (state.statuses.has(key)) {
      status = state.statuses.get(key);
      break;
    }
  }
  const raw = status || null;
  const watchEntry = getWatchEntry(aircraft.id);
  const watchPayload =
    watchEntry?.payload && typeof watchEntry.payload === "object" ? watchEntry.payload : {};
  const watchScheduledTs = toTimestamp(watchEntry?.scheduledDepartureAt);
  const watchScheduledIso = Number.isFinite(watchScheduledTs)
    ? new Date(watchScheduledTs).toISOString()
    : null;
  const watchTriggeredTs = toTimestamp(watchEntry?.triggeredAt);
  const watchTriggeredIso = Number.isFinite(watchTriggeredTs)
    ? new Date(watchTriggeredTs).toISOString()
    : null;
  const watchUpdatedTs = toTimestamp(watchEntry?.updatedAt);
  const watchUpdatedIso = Number.isFinite(watchUpdatedTs)
    ? new Date(watchUpdatedTs).toISOString()
    : null;
  const watchCreatedTs = toTimestamp(watchEntry?.createdAt);
  const watchCreatedIso = Number.isFinite(watchCreatedTs)
    ? new Date(watchCreatedTs).toISOString()
    : null;
  const watchReason = cleanText(watchEntry?.reason) || null;
  const watchSource = cleanText(watchEntry?.source) || null;
  const watchScheduledContext = cleanText(watchEntry?.scheduledContext) || null;
  const watchOn = Boolean(watchEntry?.onWatch);
  const watchSearchValues = [];
  if (watchReason) {
    watchSearchValues.push(watchReason);
  }
  if (watchSource) {
    watchSearchValues.push(watchSource);
  }
  if (watchScheduledContext) {
    watchSearchValues.push(watchScheduledContext);
  }
  if (watchEntry?.scheduledDepartureAt) {
    watchSearchValues.push(watchEntry.scheduledDepartureAt);
  }
  if (watchScheduledIso) {
    const formattedSchedule = formatDisplayTimestamp(watchScheduledIso);
    if (formattedSchedule) {
      watchSearchValues.push(formattedSchedule);
    }
  }
  if (watchTriggeredIso) {
    const formattedTrigger = formatDisplayTimestamp(watchTriggeredIso);
    if (formattedTrigger) {
      watchSearchValues.push(formattedTrigger);
    }
  }
  if (watchPayload && typeof watchPayload.route === "string") {
    watchSearchValues.push(watchPayload.route);
  }
  const watchSummaryParts = [];
  if (watchReason) {
    watchSummaryParts.push(`Reason: ${watchReason}`);
  }
  if (watchScheduledIso) {
    const scheduledSegments = [];
    if (watchScheduledContext) {
      scheduledSegments.push(watchScheduledContext);
    }
    const scheduledAbsolute = formatDisplayTimestamp(watchScheduledIso);
    if (scheduledAbsolute) {
      scheduledSegments.push(scheduledAbsolute);
    }
    const scheduledRelative = formatRelative(watchScheduledIso);
    if (scheduledRelative && scheduledRelative !== scheduledAbsolute) {
      scheduledSegments.push(scheduledRelative);
    }
    if (scheduledSegments.length) {
      watchSummaryParts.push(`Scheduled ${scheduledSegments.join(" · ")}`);
    }
  }
  if (watchTriggeredIso) {
    const triggeredAbsolute = formatDisplayTimestamp(watchTriggeredIso);
    const triggeredRelative = formatRelative(watchTriggeredIso);
    const triggeredSegments = [];
    if (triggeredAbsolute) {
      triggeredSegments.push(triggeredAbsolute);
    }
    if (triggeredRelative && triggeredRelative !== triggeredAbsolute) {
      triggeredSegments.push(triggeredRelative);
    }
    if (triggeredSegments.length) {
      watchSummaryParts.push(`Triggered ${triggeredSegments.join(" · ")}`);
    }
  }
  if (watchSource) {
    watchSummaryParts.push(`Source: ${watchSource}`);
  }
  const watchSummary = watchSummaryParts.join(" · ") || null;
  const watch = watchEntry
    ? {
        onWatch: watchOn,
        reason: watchReason,
        source: watchSource,
        scheduledDepartureAt: watchEntry.scheduledDepartureAt || null,
        scheduledDepartureIso: watchScheduledIso,
        scheduledContext: watchScheduledContext,
        triggeredAt: watchEntry.triggeredAt || null,
        triggeredAtIso: watchTriggeredIso,
        updatedAt: watchEntry.updatedAt || null,
        updatedAtIso: watchUpdatedIso,
        createdAt: watchEntry.createdAt || null,
        createdAtIso: watchCreatedIso,
        payload: watchPayload,
        summary: watchSummary,
        searchValues: watchSearchValues,
      }
    : null;
  const phaseText =
    cleanText(raw?.status_text) || cleanText(raw?.phase) || cleanText(aircraft.status_text) || "unknown";
  const phaseLower = phaseText.toLowerCase();
  const phaseSuggestsAirborne =
    phaseLower.includes("enroute") ||
    phaseLower.includes("en route") ||
    phaseLower.includes("airborne") ||
    phaseLower.includes("depart");
  const now = Date.now();
  // CRITICAL: Keep timestamp extraction aligned with server-side deriveMovementAt().
  // FlightAware often uses actual_arrival/actual_departure instead of on/off.
  const landingCandidates = [
    raw?.on,
    raw?.actual_on,
    raw?.actual_in,
    raw?.block_in,
    raw?.actual_arrival,
    raw?.actualArrival,
  ];
  const takeoffCandidates = [
    raw?.off,
    raw?.actual_off,
    raw?.actual_out,
    raw?.block_out,
    raw?.actual_departure,
    raw?.actualDeparture,
  ];

  let lastLandingTs = null;
  for (const candidate of landingCandidates) {
    const parsed = toTimestamp(candidate);
    if (parsed) {
      lastLandingTs = parsed;
      break;
    }
  }

  let lastLandingIso = lastLandingTs !== null ? new Date(lastLandingTs).toISOString() : null;

  let lastTakeoffTs = null;
  for (const candidate of takeoffCandidates) {
    const parsed = toTimestamp(candidate);
    if (parsed) {
      lastTakeoffTs = parsed;
      break;
    }
  }

  // If FlightAware didn't provide on/off but did include a flights array,
  // derive the latest landing/takeoff from it (mirrors server-side behavior).
  if ((lastLandingTs === null || lastTakeoffTs === null) && Array.isArray(raw?.flights)) {
    for (const flight of raw.flights) {
      if (!flight || typeof flight !== "object") continue;
      if (lastLandingTs === null) {
        const candidateLanding = toTimestamp(flight.on || flight.actual_arrival || flight.actual_on || flight.actual_in);
        if (Number.isFinite(candidateLanding)) {
          lastLandingTs = candidateLanding;
          lastLandingIso = new Date(candidateLanding).toISOString();
        }
      }
      if (lastTakeoffTs === null) {
        const candidateTakeoff = toTimestamp(flight.off || flight.actual_departure || flight.actual_off || flight.actual_out);
        if (Number.isFinite(candidateTakeoff)) {
          lastTakeoffTs = candidateTakeoff;
        }
      }
      if (lastLandingTs !== null && lastTakeoffTs !== null) break;
    }
  }

  // Display-only fallback: when we have NO FlightAware data (raw === null), use manual last flight
  // date only to compute grounded time for the 12h AOG rule. Does not affect position, status
  // updates, or any server data; only the displayed TAM category and time-on-ground label.
  if (lastLandingTs === null && !phaseSuggestsAirborne) {
    const manualTs = toTimestamp(aircraft.manual_status_last_flight);
    if (Number.isFinite(manualTs)) {
      lastLandingTs = manualTs;
      lastLandingIso = new Date(manualTs).toISOString();
    }
  }

  const hasLandingAfterTakeoff =
    lastLandingTs !== null &&
    lastTakeoffTs !== null &&
    lastLandingTs >= lastTakeoffTs - 60 * 1000;

  let airborneMinutes = null;
  if (lastTakeoffTs !== null && (!lastLandingTs || !hasLandingAfterTakeoff)) {
    airborneMinutes = Math.max(0, Math.round((now - lastTakeoffTs) / 60000));
  }

  let isAirborne = phaseSuggestsAirborne;
  if (!isAirborne && airborneMinutes !== null && airborneMinutes <= 36 * 60) {
    isAirborne = true;
  }
  if (isAirborne && hasLandingAfterTakeoff && lastLandingTs !== null) {
    if (now - lastLandingTs > 5 * 60 * 1000) {
      isAirborne = false;
      airborneMinutes = null;
    }
  }

  let timeLabel = "—";
  let since = null;
  let diffMinutes = null;
  let timeSummaryLabel = "Time on ground";
  let timeContextLabel = "Last landing";

  const recentEvents = [
    raw?.off,
    raw?.actual_off,
    raw?.actual_departure,
    raw?.on,
    raw?.actual_on,
    raw?.actual_arrival,
    raw?.block_out,
    raw?.block_in,
  ]
    .map(toTimestamp)
    .filter((value) => typeof value === "number");
  const hasRecentFlight = recentEvents.some((value) => now - value <= DAY_MS);

  const upcomingCandidates = [
    { value: raw?.etd, label: "ETD" },
    { value: raw?.scheduled_out, label: "Scheduled out" },
    { value: raw?.scheduled_off, label: "Scheduled off" },
    { value: raw?.estimated_out, label: "Estimated out" },
    { value: raw?.estimated_off, label: "Estimated off" },
    { value: raw?.filed_departure_time, label: "Filed departure" },
    { value: raw?.eta, label: "ETA" },
    { value: raw?.scheduled_in, label: "Scheduled in" },
    { value: raw?.scheduled_on, label: "Scheduled on" },
    { value: raw?.estimated_in, label: "Estimated in" },
    { value: raw?.estimated_on, label: "Estimated on" },
    { value: raw?.filed_arrival_time, label: "Filed arrival" },
  ];
  const upcomingEvents = upcomingCandidates
    .map(({ value, label }) => ({ ts: toTimestamp(value), label }))
    .filter((item) => typeof item.ts === "number");
  const futureEvents = upcomingEvents
    .filter((item) => item.ts >= now - UPCOMING_PAST_GRACE_MS)
    .sort((a, b) => a.ts - b.ts);
  let nextEvent = futureEvents.length ? futureEvents[0] : null;
  let nextDepartureTs = nextEvent ? nextEvent.ts : null;
  const hasUpcomingFromEvents = futureEvents.some((item) => item.ts - now <= UPCOMING_LOOKAHEAD_MS);
  const manualDepartureCandidates = [
    { value: aircraft.next_scheduled_departure, label: "Scheduled departure" },
    { value: aircraft.nextScheduledDeparture, label: "Scheduled departure" },
    { value: aircraft.next_scheduled_departure_at, label: "Scheduled departure" },
    { value: aircraft.nextScheduledDepartureAt, label: "Scheduled departure" },
    { value: aircraft.next_flight_departure, label: "Next flight departure" },
    { value: aircraft.nextFlightDeparture, label: "Next flight departure" },
    { value: aircraft.next_flight_departure_at, label: "Next flight departure" },
    { value: aircraft.nextFlightDepartureAt, label: "Next flight departure" },
    { value: aircraft.next_departure, label: "Next departure" },
    { value: aircraft.nextDeparture, label: "Next departure" },
    { value: aircraft.next_departure_at, label: "Next departure" },
    { value: aircraft.nextDepartureAt, label: "Next departure" },
    { value: aircraft.manual_next_departure, label: "Manual departure" },
    { value: aircraft.manualNextDeparture, label: "Manual departure" },
    { value: aircraft.manual_next_departure_at, label: "Manual departure" },
    { value: aircraft.manualNextDepartureAt, label: "Manual departure" },
  ];
  let fallbackDepartureTs = null;
  let fallbackLabel = null;
  for (const candidate of manualDepartureCandidates) {
    const ts = toTimestamp(candidate.value);
    if (!Number.isFinite(ts)) {
      continue;
    }
    if (ts < now - UPCOMING_PAST_GRACE_MS) {
      continue;
    }
    if (ts - now > UPCOMING_LOOKAHEAD_MS) {
      continue;
    }
    fallbackDepartureTs = ts;
    fallbackLabel = candidate.label;
    break;
  }
  let hasUpcomingFlight = hasUpcomingFromEvents;
  if (fallbackDepartureTs !== null) {
    hasUpcomingFlight = true;
    if (!nextDepartureTs || fallbackDepartureTs < nextDepartureTs) {
      nextDepartureTs = fallbackDepartureTs;
      if (!nextEvent) {
        nextEvent = { ts: fallbackDepartureTs, label: fallbackLabel };
      }
    }
  }
  const nextEventLabel = nextEvent?.label || fallbackLabel || null;

  if (isAirborne) {
    timeSummaryLabel = "Time airborne";
    timeContextLabel = "Departed";
    if (airborneMinutes !== null && lastTakeoffTs !== null) {
      timeLabel = `AIRBORNE · ${formatGroundDuration(airborneMinutes)}`;
      since = new Date(lastTakeoffTs).toISOString();
    } else {
      timeLabel = "AIRBORNE";
      if (lastTakeoffTs !== null) {
        since = new Date(lastTakeoffTs).toISOString();
      }
    }
    diffMinutes = null;
  } else if (lastLandingTs !== null) {
    diffMinutes = Math.max(0, Math.round((now - lastLandingTs) / 60000));
    since = new Date(lastLandingTs).toISOString();
    timeLabel = formatGroundDuration(diffMinutes);
  }
  
  if (diffMinutes === null && nextDepartureTs !== null) {
    const minutesUntilDeparture = Math.max(0, Math.round((nextDepartureTs - now) / 60000));
    timeLabel = minutesUntilDeparture
      ? `DEPARTS IN ${formatGroundDuration(minutesUntilDeparture)}`
      : "DEPARTING";
    timeSummaryLabel = "Next departure";
    timeContextLabel = nextEventLabel || "ETD";
    since = new Date(nextDepartureTs).toISOString();
  }

  // Only treat as manual override when user explicitly set TAM status text (not just last flight date/location)
  const hasManualOverride = Boolean(cleanText(aircraft.manual_status_text));
  const ownerForRule = aircraft?.owner != null ? String(cleanText(aircraft.owner) || "").trim().toUpperCase() : "";
  const manualBaselineTs = toTimestamp(aircraft.manual_status_last_flight);
  const hasMovementEvidence = Boolean(
    Number.isFinite(lastLandingTs) || Number.isFinite(lastTakeoffTs) || Number.isFinite(manualBaselineTs)
  );
  const statusCategory = determineStatusCategory({
    groundedMinutes: diffMinutes,
    isAirborne,
    hasRecentFlight,
    hasUpcomingFlight,
    hasMovementEvidence,
    tamStatus: aircraft?.tam_status,
    hasManualOverride,
    owner: ownerForRule || undefined,
    isMeFleet: Boolean(aircraft?.is_me_fleet),
  });
  const categoryKey = statusCategory.key;
  const categoryLabel = statusCategory.label;

  const originCode = normaliseLocationLabel(raw?.origin);
  const destinationCode = normaliseLocationLabel(raw?.destination);
  const route = originCode || destinationCode ? `${originCode || "?"} → ${destinationCode || "?"}` : null;
  const timeline = [];
  if (raw?.eta) timeline.push(`ETA ${formatRelative(raw.eta)}`);
  if (raw?.etd) timeline.push(`ETD ${formatRelative(raw.etd)}`);
  if (raw?.off) timeline.push(`Off ${formatRelative(raw.off)}`);
  if (raw?.on) timeline.push(`On ${formatRelative(raw.on)}`);
  if (fetchedAt) {
    timeline.push(`Fetched ${formatRelative(fetchedAt)} (${formatDisplayTimestamp(fetchedAt)})`);
  }

  const manualLastFlightTs = toTimestamp(aircraft.manual_status_last_flight);
  const manualLastFlightIso = Number.isFinite(manualLastFlightTs)
    ? new Date(manualLastFlightTs).toISOString()
    : null;
  const manualStatusText = cleanText(aircraft.manual_status_text);
  const manualLastLocation = cleanText(aircraft.manual_status_location);
  const manualUpdatedTs = toTimestamp(aircraft.manual_status_updated_at);
  const manualUpdatedAt = Number.isFinite(manualUpdatedTs)
    ? new Date(manualUpdatedTs).toISOString()
    : null;
  const manualUpdatedBy = cleanText(aircraft.manual_status_updated_by);
  const manualOverrideActive = Boolean(
    manualStatusText || manualLastFlightIso || manualLastLocation
  );

  let positionText = null;
  if (
    raw?.position &&
    raw.position.lat !== undefined &&
    raw.position.lat !== null &&
    raw.position.lon !== undefined &&
    raw.position.lon !== null
  ) {
    const latValue = Number(raw.position.lat);
    const lonValue = Number(raw.position.lon);
    const altValue = Number(raw.position.alt_ft);
    if (Number.isFinite(latValue) && Number.isFinite(lonValue)) {
      const parts = [`Lat ${latValue.toFixed(2)}`, `Lon ${lonValue.toFixed(2)}`];
      if (Number.isFinite(altValue)) {
        parts.push(`${Math.round(altValue)} ft`);
      }
      const positionTimestamp = cleanText(raw.position.ts);
      if (positionTimestamp) {
        parts.push(`${formatRelative(positionTimestamp)} (${formatDisplayTimestamp(positionTimestamp)})`);
      }
      positionText = parts.join(" · ");
    }
  }

  const positionAirport = normaliseLocationLabel(raw?.position);
  const baseLocation = normaliseLocationLabel(aircraft.last_known_location) || normaliseLocationLabel(aircraft.base);
  let lastLocation = positionAirport || null;
  const indicatesArrival = phaseLower.includes("arriv") || phaseLower.includes("park");
  const persistedSource = (cleanText(aircraft.last_location_source) || "").toUpperCase();
  const originLooksLikeGps = /^L\s*-?\d+(\.\d+)?\s+-?\d+(\.\d+)?$/i.test(String(originCode || ""));
  const destinationLooksLikeGps = /^L\s*-?\d+(\.\d+)?\s+-?\d+(\.\d+)?$/i.test(String(destinationCode || ""));

  if (isAirborne) {
    // If the DB says FR24 last wrote last_known_location, prefer the persisted airport-level route
    // over FlightAware-derived GPS routes like `L 22... → OMDW`.
    if (
      persistedSource === "FR24" &&
      baseLocation &&
      (originLooksLikeGps || destinationLooksLikeGps || String(route || "").includes("?"))
    ) {
      lastLocation = baseLocation;
    } else if (originCode && destinationCode) {
      lastLocation = `${originCode} → ${destinationCode}`;
    } else {
      lastLocation = originCode || destinationCode || route || baseLocation || lastLocation;
    }
  } else if (!lastLocation && (indicatesArrival || Number.isFinite(diffMinutes))) {
    lastLocation = destinationCode || baseLocation || null;
  }

  if (!lastLocation && hasUpcomingFlight) {
    lastLocation = originCode || route || baseLocation || null;
  }

  if (!lastLocation) {
    lastLocation = route || baseLocation || positionText || "—";
  }

  if (manualOverrideActive) {
    // CRITICAL: Manual override location should take precedence when manual override is active
    // This ensures the location the user entered is always displayed
    if (manualLastLocation) {
      lastLocation = manualLastLocation;
    }
    if (!isAirborne && manualLastFlightIso) {
      const manualDiff = Math.max(
        0,
        Math.round((now - new Date(manualLastFlightIso).getTime()) / 60000)
      );
      diffMinutes = manualDiff;
      since = manualLastFlightIso;
      timeLabel = manualDiff ? formatGroundDuration(manualDiff) : "—";
      timeSummaryLabel = "Time on ground";
      timeContextLabel = "Last flight";
      lastLandingIso = manualLastFlightIso;
    }
  }

  const tamNormalised = normaliseTamStatus(aircraft.tam_status);
  // When there is no explicit TAM override, show:
  // - Manual TAM text if set (IN STORAGE, MAINTENANCE, etc.)
  // - AOG when auto AOG rule applies (ME owner, >= 12h)
  // - IN SERVICE for normal operational aircraft (including long ground times)
  // NEVER use the time-on-ground label (\"5 DAYS\", \"ON GROUND > 24HRS\") as the TAM status itself.
  let autoTamDisplay;
  if (categoryKey === "aog") {
    autoTamDisplay = "AOG";
  } else if (tamNormalised) {
    autoTamDisplay = tamNormalised;
  } else if (categoryKey === "in-service" || categoryKey === "on-ground") {
    autoTamDisplay = "IN SERVICE";
  } else {
    autoTamDisplay = "—";
  }
  const tamDisplay = hasManualOverride
    ? (tamNormalised || (categoryKey === "in-service" ? "IN SERVICE" : "—"))
    : autoTamDisplay;
  // Source reflects where last_known_location was last persisted (FA/FR24 cron), not whether
  // manual TAM fields exist. "Ops" = ops form explicitly saved location (last_location_source MANUAL).
  const locationSourceLabel = (() => {
    const src = (cleanText(aircraft.last_location_source) || "").toUpperCase();
    if (src === "FR24") return "FR24";
    if (src === "MANUAL") return "Ops";
    if (src === "FA") return "FA";
    // Legacy rows before last_location_source existed: show Ops only if still clearly ops-only
    if (hasManualOverride && manualLastLocation) return "Ops";
    return "FA";
  })();
  const storageHold = categoryKey === "storage";
  const trackerUrl = cleanText(aircraft.tracker_url) || cleanText(aircraft.url);
  const flightawareUrl =
    cleanText(aircraft.flightaware_url) || buildFlightawareUrl(getPrimaryIdent(aircraft));
  const fr24Url = buildFr24Url(aircraft.registration || aircraft.tail || aircraft.ident);
  const linkCount = (trackerUrl ? 1 : 0) + (flightawareUrl ? 1 : 0) + (fr24Url ? 1 : 0);
  const rawError = cleanText(raw?.error);
  const statusError =
    rawError && !/ident query parameter is required/i.test(rawError)
      ? rawError
      : null;
  const highlightGrounded = shouldHighlightGrounded(diffMinutes, categoryKey);
  const groundedMinutesValue = !isAirborne && Number.isFinite(diffMinutes) ? diffMinutes : null;
  const reliabilityAssessment = dataReliabilityEnabled
    ? assessStatusReliability({
        fetchedAt,
        statusError,
        tamStatus: tamNormalised,
        autoCategory: categoryLabel,
        staleThresholdMs: DATA_RELIABILITY_STALE_MS,
      })
    : null;
  const reliability = reliabilityAssessment ? buildReliabilityViewModel(reliabilityAssessment) : null;
  const errorStreak = canonicalLookup ? state.statusErrorStreak.get(canonicalLookup) || 0 : 0;
  const statusCheckFailed = errorStreak >= STATUS_FETCH_MAX_ATTEMPTS;

  return {
    raw,
    lookupKeys,
    categoryLabel,
    categoryKey,
    categoryClass: getCategoryClass(categoryKey),
    timeLabel,
    since,
    timeSummaryLabel,
    timeContextLabel,
    isAirborne,
    route,
    timeline: timeline.join(" · "),
    positionText,
    lastLocation,
    locationSourceLabel,
    tamDisplay,
    storageHold,
    tamUpdated: aircraft.tam_status_updated_at ? new Date(aircraft.tam_status_updated_at).toISOString() : null,
    tamSource: cleanText(aircraft.tam_status_source),
    tamUser: cleanText(aircraft.tam_status_user),
    tamAgeSeconds: Number.isFinite(aircraft.tam_status_age_seconds)
      ? aircraft.tam_status_age_seconds
      : null,
    tamRecordId: aircraft.tam_status_record_id ?? null,
    phase: (manualStatusText || phaseText || "unknown").toUpperCase(),
    notes: cleanText(aircraft.notes),
    flightawareUrl,
    fr24Url,
    trackerUrl,
    commentCount: Number(aircraft.comment_count || 0),
    lastComment: aircraft.last_comment || null,
    airborneMinutes:
      isAirborne && Number.isFinite(airborneMinutes) ? airborneMinutes : null,
    groundedMinutes: groundedMinutesValue,
    linkCount,
    statusError,
    statusErrorAttempts: errorStreak,
    statusCheckFailed,
    highlightGrounded,
    fetchedAt,
    reliability,
    hasRecentFlight,
    hasUpcomingFlight,
    nextDepartureTs,
    nextDepartureIso: nextDepartureTs ? new Date(nextDepartureTs).toISOString() : null,
    nextEventLabel,
    manualOverrideActive,
    manualStatusText,
    manualLastFlightIso,
    manualLastLocation,
    manualUpdatedAt,
    manualUpdatedBy,
    lastLandingIso,
    onWatch: watchOn,
    watch,
    watchSummary,
    expectedReleaseDate: cleanText(aircraft.expected_release_date) || null,
  };
}

async function fetchJson(url, options = {}) {
  const { skipAuthHandling, ...fetchOptions } = options;
  const started = performance.now();
  const headers = new Headers(fetchOptions.headers || {});
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  if (fetchOptions.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  let response;
  try {
    response = await fetch(url, {
      ...fetchOptions,
      headers,
      credentials: "include",
    });
  } catch (networkError) {
    const friendly = formatNetworkError(networkError, url);
    logEvent("fetch failed", { url, error: friendly });
    flagApiConnectivityIssue(friendly);
    const wrapped = new Error(friendly);
    wrapped.status = 0;
    wrapped.cause = networkError;
    wrapped.url = url;
    throw wrapped;
  }
  const elapsed = Math.round(performance.now() - started);
  let payload = null;
  try {
    payload = await response.json();
  } catch (error) {
    logEvent("Failed to parse JSON", { url, status: response.status, error: error.message });
  }
  const payloadSummary = summarisePayload(payload);
  logEvent("fetch", { url, status: response.status, elapsed, payload: payloadSummary });
  if (!config.MODE || !config.MODE.toLowerCase().startsWith("prod")) {
    console.debug("[ui] fetch payload", { url, status: response.status, elapsed, payload });
  }
  if (!response.ok) {
    const message = payload?.error || payload?.message || `Request failed (${response.status})`;
    const error = new Error(message);
    error.status = response.status;
    error.payload = payload;
    if (response.status === 401 && !skipAuthHandling) {
      handleUnauthenticated(message);
    }
    throw error;
  }
  if (state.apiBaseIssue) {
    clearApiBaseIssue();
  }
  return payload;
}

function upsertOperatorDirectory(record) {
  if (!record || record.id === undefined || record.id === null) {
    return;
  }
  const id = Number(record.id);
  if (!Number.isFinite(id)) {
    return;
  }
  const contacts = Array.isArray(record.contacts)
    ? record.contacts
        .map((c) => ({
          name: cleanText(c?.name) || "Operator contact",
          email: cleanText(c?.email) || "",
        }))
        .filter((c) => c.email)
    : [];
  const entry = {
    id,
    name: cleanText(record.name) || `Operator ${id}`,
    slug: record.slug || null,
    contact_email: cleanText(record.contact_email) || "",
    contacts,
  };
  const existingIndex = state.operatorDirectory.findIndex((item) => Number(item.id) === id);
  if (existingIndex >= 0) {
    state.operatorDirectory[existingIndex] = entry;
  } else {
    state.operatorDirectory.push(entry);
  }
  state.operatorDirectory.sort((a, b) => a.name.localeCompare(b.name));
  state.operatorOptions = state.operatorDirectory.map((operator) => operator.name);
  populateOperatorFilter();
}

async function loadOperatorDirectory(force = false) {
  if (!state.sessionUser) {
    state.operatorDirectory = [];
    state.operatorOptions = [];
    state.operatorDirectoryLoaded = false;
    return [];
  }
  if (!force && state.operatorDirectoryLoaded && state.operatorDirectory.length) {
    return state.operatorDirectory;
  }
  if (state.loadingOperators) {
    return state.operatorDirectory;
  }
  state.loadingOperators = true;
  try {
    const response = await fetchJson(apiUrl("/api/operators"));
    const list = Array.isArray(response?.operators) ? response.operators : [];
    state.operatorDirectory = list
      .map((entry) => ({
        id: Number(entry.id),
        name: cleanText(entry.name) || `Operator ${entry.id}`,
        slug: entry.slug || null,
        contact_email: cleanText(entry.contact_email) || "",
        contacts: Array.isArray(entry.contacts)
          ? entry.contacts
              .map((c) => ({
                name: cleanText(c?.name) || "Operator contact",
                email: cleanText(c?.email) || "",
              }))
              .filter((c) => c.email)
          : [],
        aircraft_count: Number(entry.aircraft_count) || 0,
      }))
      .filter((entry) => Number.isFinite(entry.id))
      .sort((a, b) => a.name.localeCompare(b.name));
    state.operatorDirectoryLoaded = true;
    state.operatorOptions = state.operatorDirectory.map((operator) => operator.name);
    populateOperatorFilter();
    return state.operatorDirectory;
  } catch (error) {
    logEvent("operator directory load failed", { error: error.message });
    state.operatorDirectoryLoaded = false;
    return state.operatorDirectory;
  } finally {
    state.loadingOperators = false;
  }
}

async function submitAircraftProfileUpdate(aircraftId, payload) {
  if (!payload || Object.keys(payload).length === 0) {
    return null;
  }
  const payloadToSend = { ...payload };
  if (Object.prototype.hasOwnProperty.call(payloadToSend, "registration")) {
    payloadToSend.registration = normaliseRegistrationValue(payloadToSend.registration);
  }
  if (Object.prototype.hasOwnProperty.call(payloadToSend, "msn")) {
    const normalisedMsn = normaliseMsnValue(payloadToSend.msn);
    payloadToSend.msn = normalisedMsn;
  }
  const response = await fetchJson(apiUrl(`/api/aircraft/${aircraftId}/profile`), {
    method: "PUT",
    body: JSON.stringify(payloadToSend),
  });
  if (response?.operator) {
    upsertOperatorDirectory(response.operator);
  }
  if (response?.aircraft) {
    const index = state.aircraft.findIndex((item) => Number(item.id) === Number(response.aircraft.id));
    if (index >= 0) {
      const previous = state.aircraft[index];
      const merged = applyAircraftNormalisation({
        ...previous,
        ...response.aircraft,
      });
      state.aircraft[index] = merged;
      recordAircraftSnapshot(merged);
      invalidateStatusCaches(previous, merged);
      renderTable();
      renderDataReport();
    }
  }
  return response;
}

async function fetchAircraftProfileById(aircraftId, options = {}) {
  if (!state.sessionUser) {
    return null;
  }
  const numericId = Number(aircraftId);
  if (!Number.isFinite(numericId) || numericId <= 0) {
    return null;
  }
  const skipRender = Boolean(options.skipRender);
  try {
    if (devToolsEnabled) {
      logEvent("Aircraft profile refresh requested", { aircraftId: numericId, source: options.source || "ui" });
    }
    const response = await fetchJson(apiUrl(`/api/aircraft/${numericId}`));
    const record = response?.aircraft || null;
    if (!record) {
      return null;
    }
    const normalised = applyAircraftNormalisation(record);
    const existingIndex = state.aircraft.findIndex((item) => Number(item.id) === numericId);
    if (existingIndex >= 0) {
      const previous = state.aircraft[existingIndex];
      const merged = { ...previous, ...normalised };
      state.aircraft[existingIndex] = merged;
      recordAircraftSnapshot(merged);
      invalidateStatusCaches(previous, merged);
      if (!skipRender) {
        renderTable();
        renderDataReport();
      }
      scheduleAircraftSnapshotPersist();
      if (devToolsEnabled) {
        logEvent("Aircraft profile refreshed", {
          aircraftId: numericId,
          msn: merged.msn || null,
          registration: merged.registration || null,
        });
      }
      return merged;
    }
    state.aircraft.push(normalised);
    recordAircraftSnapshot(normalised);
    if (!skipRender) {
      renderTable();
      renderDataReport();
    }
    scheduleAircraftSnapshotPersist();
    if (devToolsEnabled) {
      logEvent("Aircraft profile loaded (new entry)", {
        aircraftId: numericId,
        msn: normalised.msn || null,
        registration: normalised.registration || null,
      });
    }
    return normalised;
  } catch (error) {
    logEvent("Aircraft profile refresh failed", {
      aircraftId: numericId,
      error: error.message,
    });
    if (error.status === 404) {
      return null;
    }
    throw error;
  }
}

async function loadSession() {
  try {
    const data = await fetchJson(apiUrl("/api/session"), { skipAuthHandling: true });
    if (data?.user) {
      showLoginError("");
      setSessionUser(data.user);
      return true;
    }
    handleUnauthenticated("");
    return false;
  } catch (error) {
    if (error.status === 401) {
      handleUnauthenticated("");
      return false;
    }
    const message =
      error.status === 0
        ? error.message || formatNetworkError(error, apiUrl("/api/session"))
        : error.message;
    const serverLikelyIssue = error.status === 0 || (error.status ?? 0) >= 500;
    const helpHint = "docker compose logs --tail=200 api";
    const baseMessage = message || "Unable to confirm session.";
    logEvent("Session check failed", {
      error: baseMessage,
      status: error.status ?? null,
      sshHint: serverLikelyIssue ? helpHint : undefined,
    });
    const note =
      serverLikelyIssue && devToolsEnabled
        ? `${baseMessage}. SSH check: ${helpHint}`
        : baseMessage;
    handleUnauthenticated(note);
    if (serverLikelyIssue) {
      setRefreshIndicator(
        `Session check failed with a server error. Gather API logs via SSH (${helpHint}) and run the health curls.`
      );
    }
    return false;
  }
}

async function signIn(username, password) {
  const cleanUser = cleanText(username);
  if (!cleanUser || !password) {
    showLoginError("Username and password are required.");
    return false;
  }
  try {
    showLoginError("");
    const response = await fetchJson(apiUrl("/api/session"), {
      method: "POST",
      body: JSON.stringify({ username: cleanUser, password }),
      skipAuthHandling: true,
    });
    if (response?.user) {
      setSessionUser(response.user);
      showLoginError("");
      // After successful login, go straight to v2 UI.
      // v2 will reuse the same session cookie.
      try {
        const returnTo = localStorage.getItem("actracker.returnToV2");
        localStorage.removeItem("actracker.returnToV2");
        window.location.href = returnTo || "/v2";
      } catch {
        window.location.href = "/v2";
      }
      return true;
    }
    showLoginError("Invalid username or password.");
    return false;
  } catch (error) {
    if (error.status === 401) {
      showLoginError("Invalid username or password.");
      return false;
    }
    const message =
      error.status === 0
        ? error.message || formatNetworkError(error, apiUrl("/api/session"))
        : error.message;
    const serverLikelyIssue = error.status === 0 || (error.status ?? 0) >= 500;
    const helpHint =
      "docker compose logs --tail=200 api";
    const suggestion =
      serverLikelyIssue && devToolsEnabled
        ? `${message || "Unable to sign in."} · SSH check: ${helpHint}`
        : message || "Unable to sign in.";
    logEvent("Login failed", {
      error: message || error.message,
      status: error.status ?? null,
      sshHint: serverLikelyIssue ? helpHint : undefined,
    });
    showLoginError(suggestion);
    if (serverLikelyIssue) {
      setRefreshIndicator(
        `Login request hit a server error. Collect API logs via SSH (${helpHint}) and rerun the health curls.`
      );
    }
    return false;
  }
}

async function signOut() {
  try {
    await fetchJson(apiUrl("/api/session"), { method: "DELETE", skipAuthHandling: true });
  } catch (error) {
    logEvent("Logout failed", { error: error.message });
  } finally {
    handleUnauthenticated("");
  }
}

function renderTableSkeleton(labels) {
  if (!dom.tableBody) return;
  dom.tableBody.innerHTML = "";
  for (let i = 0; i < 3; i += 1) {
    const row = document.createElement("tr");
    row.className = "skeleton-row";
    labels.forEach((label) => {
      const cell = document.createElement("td");
      cell.dataset.label = label;
      const pill = document.createElement("div");
      pill.className = "skeleton-pill";
      pill.style.width = `${60 + Math.random() * 30}%`;
      cell.appendChild(pill);
      row.appendChild(cell);
    });
    dom.tableBody.appendChild(row);
  }
}

function buildVisibleRows() {
  if (!state.sessionUser) {
    return [];
  }

  const dataset = state.aircraft.map((aircraft) => ({
    aircraft,
    model: composeStatusModel(aircraft),
  }));

  const searchTerm = dom.searchInput?.value?.trim().toLowerCase() || "";
  const filtersDisabled = state.aircraftLimitActive;
  const ownerFilter = filtersDisabled ? "" : state.ownerFilter;
  const operatorFilter = filtersDisabled ? "" : state.operatorFilter;
  const regionFilter = filtersDisabled ? "" : state.regionFilter;
  const tamFilter = filtersDisabled ? "" : state.tamFilter;
  const meFleetFilter = filtersDisabled ? "" : state.meFleetFilter;
  const normalisedTamFilter = tamFilter ? normaliseTamStatus(tamFilter) : "";

  const filtered = dataset.filter(({ aircraft, model }) => {
    if (operatorFilter) {
      const operatorValue = cleanText(aircraft.operator);
      if (operatorValue !== operatorFilter) {
        return false;
      }
    }
    if (ownerFilter) {
      const ownerValue = cleanText(aircraft.owner) || cleanText(aircraft.operator);
      if (ownerValue !== ownerFilter) {
        return false;
      }
    }
    if (regionFilter) {
      const regionValue = cleanText(aircraft.region);
      if (regionValue !== regionFilter) {
        return false;
      }
    }
    if (normalisedTamFilter) {
      const rowTam = normaliseTamStatus(model.tamDisplay || "");
      if (rowTam !== normalisedTamFilter) {
        return false;
      }
    }
    if (meFleetFilter === "me") {
      if (!aircraft.is_me_fleet) {
        return false;
      }
    }
    if (!searchTerm) {
      return true;
    }
    const haystackValues = [
      aircraft.ident,
      aircraft.registration,
      aircraft.aircraft_type,
      aircraft.tail,
      aircraft.msn,
      aircraft.owner,
      aircraft.operator,
      aircraft.base,
      aircraft.notes,
      aircraft.last_known_location,
      model.categoryLabel,
      model.phase,
      model.tamDisplay,
      model.timeline,
      model.nextEventLabel,
      model.hasUpcomingFlight ? "yes" : "no",
      model.onWatch ? "on watch" : "",
      model.watchSummary,
      ...(Array.isArray(model.watch?.searchValues) ? model.watch.searchValues : []),
    ]
      .map((value) => expandSearchTokens(value))
      .flat();
    return haystackValues.some((value) => value.includes(searchTerm));
  });

  const sorter = SORTERS[state.sortKey] || SORTERS.registration;
  return filtered
    .slice()
    .sort((left, right) => {
      const result = sorter(left, right);
      if (result === 0) {
        return compareTextValues(left.aircraft.ident, right.aircraft.ident);
      }
      return state.sortDirection === "asc" ? result : -result;
    });
}

function summariseActiveFilters() {
  const filters = [];
  const searchValue = cleanText(dom.searchInput?.value);
  if (searchValue) {
    filters.push(`Search: "${searchValue}"`);
  }
  const ownerLabel = cleanText(getOwnerFilterLabel());
  if (ownerLabel) {
    filters.push(`Owner: ${ownerLabel}`);
  }
  const operatorLabel = cleanText(state.operatorFilter) || "All operators";
  filters.push(`Operator: ${operatorLabel}`);
  if (state.aircraftLimitActive) {
    const limitLabel = state.aircraftLimit
      ? `Development limit: first ${state.aircraftLimit} aircraft`
      : "Development dataset limit active";
    filters.push(limitLabel);
  }
  if (!filters.length) {
    return "Filters: none (all aircraft visible)";
  }
  return `Filters: ${filters.join(" · ")}`;
}

let exportTimestampFormatter;

function hexToRgbTuple(hex) {
  if (!hex) {
    return [0, 0, 0];
  }
  let normalized = String(hex).trim();
  if (normalized.startsWith("#")) {
    normalized = normalized.slice(1);
  }
  if (normalized.length === 3) {
    normalized = normalized
      .split("")
      .map((char) => char + char)
      .join("");
  }
  if (normalized.length !== 6) {
    return [0, 0, 0];
  }
  const numeric = Number.parseInt(normalized, 16);
  if (!Number.isFinite(numeric)) {
    return [0, 0, 0];
  }
  const r = (numeric >> 16) & 0xff;
  const g = (numeric >> 8) & 0xff;
  const b = numeric & 0xff;
  return [r, g, b];
}

function formatExportTimestamp(value) {
  if (!value) {
    return "";
  }
  try {
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) {
      return "";
    }
    if (!exportTimestampFormatter) {
      exportTimestampFormatter = new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
        timeZoneName: "short",
      });
    }
    return exportTimestampFormatter.format(date);
  } catch (error) {
    try {
      return new Date(value).toLocaleString();
    } catch (_inner) {
      return String(value);
    }
  }
}

function formatDownloadStamp(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}${month}${day}`;
}

function resolveLastUpdateTimestamp(aircraft, model, comments = []) {
  // Keep Excel export aligned with what operators see in the UI row ("Updated ..."),
  // which comes from TAM/manual/comment business updates, not raw fetch timestamps.
  const candidates = [
    model?.tamUpdated,
    model?.manualUpdatedAt,
    model?.manualLastFlightIso,
    aircraft?.manual_status_updated_at,
    aircraft?.tam_status_updated_at,
    aircraft?.last_comment?.created_at,
  ];
  if (Array.isArray(comments) && comments.length) {
    const lastComment = comments[comments.length - 1];
    if (lastComment?.created_at) {
      candidates.push(lastComment.created_at);
    }
  }
  const numeric = candidates
    .map((value) => toTimestamp(value))
    .filter((value) => Number.isFinite(value));
  if (!numeric.length) {
    return null;
  }
  return new Date(Math.max(...numeric)).toISOString();
}

async function buildTabularExportContext() {
  const visibleRows = buildVisibleRows();
  if (!visibleRows.length) {
    return { ok: false, reason: "empty" };
  }

  const aircraftIds = visibleRows
    .map(({ aircraft }) => Number(aircraft.id))
    .filter((value) => Number.isFinite(value));

  let commentHistory = new Map();
  if (aircraftIds.length) {
    try {
      const response = await fetchJson(apiUrl("/api/aircraft/comments/bulk"), {
        method: "POST",
        body: JSON.stringify({ ids: aircraftIds }),
      });
      if (response?.comments && typeof response.comments === "object") {
        commentHistory = new Map(
          Object.entries(response.comments).map(([key, value]) => [
            Number(key),
            Array.isArray(value)
              ? value.map((entry) => ({
                  author: cleanText(entry.author) || "system",
                  body: entry.body || "",
                  created_at: entry.created_at || null,
                }))
              : [],
          ])
        );
      }
    } catch (error) {
      logEvent("Comment history export failed", { error: error.message });
      commentHistory = new Map();
    }
  }

  const tamSortWeight = (tam) => {
    const value = (cleanText(tam) || "").toUpperCase();
    if (value === "AOG") return 0;
    if (value === "IN SERVICE") return 1;
    if (value === "STORAGE") return 2;
    if (value === "MAINTENANCE") return 3;
    return 4;
  };

  const formatMeEmailDuration = (minutes) => {
    if (!Number.isFinite(minutes) || minutes < 0) return "—";
    if (minutes === 0) return "AIRBORNE";
    if (minutes < 60) return `${minutes} min`;
    if (minutes < 24 * 60) {
      const h = Math.floor(minutes / 60);
      const m = minutes - h * 60;
      return m ? `${h}h ${m}m` : `${h}h`;
    }
    const d = Math.floor(minutes / (24 * 60));
    const h = Math.floor((minutes - d * 24 * 60) / 60);
    if (d < 30) return h ? `${d}d ${h}h` : `${d}d`;
    if (d < 365) {
      const months = Math.floor(d / 30);
      const weeks = Math.floor((d - months * 30) / 7);
      return weeks ? `${months}mo ${weeks}w` : `${months}mo`;
    }
    const years = Math.floor(d / 365);
    const months = Math.floor((d - years * 365) / 30);
    return months ? `${years}yr ${months}mo` : `${years}yr`;
  };

  // Always export with the same schema as the UI table for line-by-line parity.
  const headers = [
    "MSN",
    "Registration",
    "Type",
    "Operator",
    "Owner",
    "Status",
    "Time on ground",
    "Last location",
    "Source (FA/FR24/Ops)",
    "Next flight scheduled",
    "TAM status",
    "Comments history",
    "Last update (local time)",
    "Last landing (local time)",
    "On watch",
  ];

  const dataRows = visibleRows.map(({ aircraft, model }) => {
    const registration =
      cleanText(aircraft.registration) || cleanText(aircraft.tail) || cleanText(aircraft.ident) || "";
    const statusLabel =
      model.manualOverrideActive && model.manualStatusText
        ? model.manualStatusText.toUpperCase()
        : model.categoryLabel || "";
    const groundedMinutes = Number.isFinite(model.groundedMinutes) ? model.groundedMinutes : null;
    const timeCell = model.isAirborne
      ? "AIRBORNE"
      : groundedMinutes !== null
      ? formatMeEmailDuration(groundedMinutes)
      : "—";

    const nextFlight = model.hasUpcomingFlight ? "YES" : "NO";

    const commentEntries = commentHistory.get(Number(aircraft.id)) || [];
    const commentText = commentEntries.length
      ? commentEntries
          .map((entry) => {
            const timestamp = formatExportTimestamp(entry.created_at);
            const authorLabel = (cleanText(entry.author) || "system").toUpperCase();
            const body = entry.body === undefined || entry.body === null ? "" : String(entry.body);
            return `${timestamp ? `${timestamp} – ` : ""}${authorLabel}: ${body}`;
          })
          .join("\n")
      : "—";

    const lastUpdateIso = resolveLastUpdateTimestamp(aircraft, model, commentEntries);
    const lastUpdateText = lastUpdateIso ? formatExportTimestamp(lastUpdateIso) : "—";

    const lastLandingText = model.lastLandingIso ? formatExportTimestamp(model.lastLandingIso) : "—";

    const lastLocation =
      model.lastLocation || cleanText(aircraft.last_known_location) || cleanText(aircraft.base) || "—";

    const sourceExport = model.locationSourceLabel || "FA";

    return [
      cleanText(aircraft.msn) || "",
      registration,
      cleanText(aircraft.aircraft_type) || "",
      cleanText(aircraft.operator) || "",
      cleanText(aircraft.owner) || "",
      statusLabel,
      timeCell,
      lastLocation,
      sourceExport,
      nextFlight,
      model.tamDisplay || "—",
      commentText,
      lastUpdateText,
      lastLandingText,
      model.onWatch ? "YES" : "NO",
    ];
  });

  return {
    ok: true,
    headers,
    rows: dataRows,
    rowCount: dataRows.length,
    reportGeneratedIso: new Date().toISOString(),
    filterSummary: summariseActiveFilters(),
  };
}

function buildEmailReportInlineStyles() {
  return [
    "body{margin:0;padding:0;background:#f5f6f7;color:#1e1f24;font-family:'Inter',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;line-height:1.45;}",
    ".email-report{max-width:960px;margin:0 auto;padding:24px 20px;}",
    ".email-report__card{background:#ffffff;border-radius:16px;border:1px solid #d8dbe0;padding:24px;box-shadow:0 4px 12px rgba(12,22,44,0.08);}",
    ".email-report h1{margin:0 0 12px;font-size:20px;line-height:1.2;}",
    ".email-report dl{display:flex;flex-wrap:wrap;margin:0;padding:0;gap:8px 18px;font-size:13px;}",
    ".email-report dt{font-weight:600;margin:0;}",
    ".email-report dd{margin:0;}",
    ".email-report table{width:100%;border-collapse:collapse;margin-top:20px;font-size:13px;background:#ffffff;}",
    ".email-report th{background:#B81818;color:#ffffff;text-transform:uppercase;letter-spacing:0.05em;padding:10px;border:1px solid #d8dbe0;font-size:12px;}",
    ".email-report td{padding:10px;border:1px solid #d8dbe0;vertical-align:top;}",
    ".email-report tr:nth-child(even) td{background:#f7f9fc;}",
    ".email-report footer{margin-top:20px;font-size:12px;color:#5b6168;}",
    ".email-heatmap{margin-bottom:24px;border-radius:18px;border:1px solid #d8dbe0;background:#ffffff;overflow:hidden;}",
    ".email-heatmap__inner{padding:24px;}",
    ".email-heatmap__header{display:block;margin-bottom:16px;}",
    ".email-heatmap__title{margin:0;font-size:18px;}",
    ".email-heatmap__summary{margin:8px 0 0;color:#5b6168;font-size:13px;}",
    ".email-heatmap__legend{width:100%;border-collapse:collapse;margin:16px 0 0;}",
    ".email-heatmap__legend-item{padding:6px 10px;white-space:nowrap;font-size:12px;font-weight:600;color:#1e1f24;border-radius:999px;background:#eef1f7;}",
    ".email-heatmap__legend-swatch{display:inline-block;width:12px;height:12px;border-radius:4px;margin-right:6px;}",
    ".email-heatmap__legend-swatch--in-service{background:#2BAA66;}",
    ".email-heatmap__legend-swatch--awaiting{background:#D6922D;}",
    ".email-heatmap__legend-swatch--storage{background:#1F6FEB;}",
    ".email-heatmap__legend-swatch--maintenance{background:#7A2BA7;}",
    ".email-heatmap__legend-swatch--transition{background:#A850DF;}",
    ".email-heatmap__legend-swatch--incident{background:#B91C1C;}",
    ".email-heatmap__legend-swatch--aog{background:#8E0404;}",
    ".email-heatmap__legend-swatch--unknown{background:#1E1F24;}",
    ".email-heatmap__region{margin-top:18px;padding:18px;border:1px solid #e2e5ec;border-radius:14px;}",
    ".email-heatmap__region-header{display:flex;justify-content:space-between;align-items:center;gap:12px;margin-bottom:14px;}",
    ".email-heatmap__region-label{font-weight:600;font-size:14px;}",
    ".email-heatmap__region-count{color:#5b6168;font-size:12px;}",
    ".email-heatmap__operator{margin-top:12px;border:1px solid #d8dbe0;border-radius:12px;padding:14px;}",
    ".email-heatmap__operator:first-of-type{margin-top:0;}",
    ".email-heatmap__operator-header{display:flex;justify-content:space-between;align-items:center;gap:10px;margin-bottom:12px;}",
    ".email-heatmap__operator-label{font-weight:600;font-size:13px;}",
    ".email-heatmap__operator-count{text-transform:uppercase;font-size:11px;letter-spacing:0.08em;color:#5b6168;}",
    ".email-heatmap__tiles{width:100%;border-collapse:separate;border-spacing:16px 16px;margin:0;}",
    ".email-heatmap__tiles td{width:50%;padding:0;margin:0;vertical-align:top;}",
    ".email-heatmap__tile{border-radius:12px;padding:14px 16px;min-height:116px;box-shadow:0 6px 18px rgba(0,0,0,0.12);}",
    ".email-heatmap__tile-primary{font-size:14px;font-weight:600;}",
    ".email-heatmap__tile-secondary{margin-top:6px;font-size:12px;line-height:1.35;}",
    ".email-heatmap__tile-meta{margin-top:10px;font-size:11px;letter-spacing:0.12em;text-transform:uppercase;opacity:0.8;}",
    ".email-heatmap__tile-status{margin-top:8px;font-size:11px;font-weight:600;letter-spacing:0.05em;text-transform:uppercase;opacity:0.85;}",
    "@media (max-width:640px){.email-heatmap__tiles td{width:100%;display:block;} .email-heatmap__tiles{border-spacing:12px 12px;} .email-heatmap__tile{min-height:auto;}}",
  ].join("");
}

function buildEmailHeatmapTileHtml(tile) {
  if (!tile) {
    return "";
  }
  const colors = tile.colors || EMAIL_HEATMAP_STATUS_COLORS.get(HEATMAP_STATUS_FALLBACK_CLASS);
  const background = colors?.background || "#1E1F24";
  const textColor = colors?.text || "#FFFFFF";
  const pieces = [];
  pieces.push(
    `<div className="email-heatmap__tile" style="background:${background};color:${textColor}">`
  );
  pieces.push(`<div className="email-heatmap__tile-primary">${htmlEscape(tile.primary)}</div>`);
  if (tile.secondary) {
    pieces.push(
      `<div className="email-heatmap__tile-secondary">${htmlEscape(tile.secondary)}</div>`
    );
  }
  if (tile.meta) {
    pieces.push(`<div className="email-heatmap__tile-meta">${htmlEscape(tile.meta)}</div>`);
  }
  if (tile.statusLabel) {
    pieces.push(
      `<div className="email-heatmap__tile-status">${htmlEscape(tile.statusLabel)}</div>`
    );
  }
  pieces.push("</div>");
  return pieces.join("");
}

function buildEmailHeatmapSection(heatmap) {
  if (!heatmap || !heatmap.totalAircraft) {
    return "";
  }
  const pieces = [];
  pieces.push('<section className="email-heatmap"><div className="email-heatmap__inner">');
  pieces.push('<div className="email-heatmap__header">');
  pieces.push('<h2 className="email-heatmap__title">Fleet activity heatmap</h2>');
  if (heatmap.summary) {
    pieces.push(
      `<p className="email-heatmap__summary">${htmlEscape(heatmap.summary)}</p>`
    );
  }
  pieces.push("</div>");

  const legendRows = chunkArray(EMAIL_HEATMAP_LEGEND, 3);
  pieces.push('<table role="presentation" className="email-heatmap__legend">');
  legendRows.forEach((row) => {
    pieces.push("<tr>");
    row.forEach((item) => {
      pieces.push(
        `<td className="email-heatmap__legend-item"><span className="email-heatmap__legend-swatch" style="background:${item.swatchColor}"></span>${htmlEscape(item.label)}</td>`
      );
    });
    if (row.length < 3) {
      for (let index = row.length; index < 3; index += 1) {
        pieces.push('<td className="email-heatmap__legend-item" style="opacity:0"></td>');
      }
    }
    pieces.push("</tr>");
  });
  pieces.push("</table>");

  heatmap.regions.forEach((region) => {
    pieces.push('<div className="email-heatmap__region">');
    pieces.push('<div className="email-heatmap__region-header">');
    pieces.push(
      `<span className="email-heatmap__region-label">${htmlEscape(region.region)}</span>`
    );
    pieces.push(
      `<span className="email-heatmap__region-count">${region.totalAircraft} aircraft</span>`
    );
    pieces.push("</div>");

    region.operators.forEach((operator) => {
      pieces.push('<div className="email-heatmap__operator">');
      pieces.push('<div className="email-heatmap__operator-header">');
      pieces.push(
        `<span className="email-heatmap__operator-label">${htmlEscape(operator.operator)}</span>`
      );
      pieces.push(
        `<span className="email-heatmap__operator-count">${operator.count} msn</span>`
      );
      pieces.push("</div>");

      const tileRows = chunkArray(operator.tiles, 2);
      pieces.push('<table role="presentation" className="email-heatmap__tiles">');
      tileRows.forEach((tileRow) => {
        pieces.push("<tr>");
        tileRow.forEach((tile) => {
          pieces.push(`<td>${buildEmailHeatmapTileHtml(tile)}</td>`);
        });
        if (tileRow.length < 2) {
          for (let index = tileRow.length; index < 2; index += 1) {
            pieces.push('<td></td>');
          }
        }
        pieces.push("</tr>");
      });
      pieces.push("</table>");
      pieces.push("</div>");
    });

    pieces.push("</div>");
  });

  pieces.push("</div></section>");
  return pieces.join("");
}

function buildExcelExportPayload(context) {
  const { headers, rows, rowCount, reportGeneratedIso, filterSummary } = context;
  const columnWidths = [
    60,
    110,
    150,
    140,
    130,
    120,
    140,
    110,
    120,
    320,
    160,
    150,
    90,
  ];
  const columnStyleIds = [
    "CellCenter",
    "CellCenter",
    "CellCenter",
    "CellCenter",
    "CellCenter",
    "CellCenter",
    "CellCenter",
    "CellCenter",
    "CellCenter",
    "CellWrap",
    "Cell",
    "Cell",
    "CellCenter",
  ];
  const headerColor = EXPORT_HEADER_COLOR;
  const reportDateLabel = formatExportTimestamp(reportGeneratedIso);
  const totalAircraftLabel = rowCount === 1 ? "1 aircraft" : `${rowCount} aircraft`;
  const metaRows = [
    `<Row><Cell ss:StyleID="Title" ss:MergeAcross="${headers.length - 1}"><Data ss:Type="String">${xmlEscape(
      EXPORT_REPORT_TITLE
    )}</Data></Cell></Row>`,
    `<Row><Cell ss:StyleID="Meta" ss:MergeAcross="${headers.length - 1}"><Data ss:Type="String">${xmlEscape(
      `Report date: ${reportDateLabel}`
    )}</Data></Cell></Row>`,
    `<Row><Cell ss:StyleID="Meta" ss:MergeAcross="${headers.length - 1}"><Data ss:Type="String">${xmlEscape(
      filterSummary
    )}</Data></Cell></Row>`,
    `<Row><Cell ss:StyleID="Meta" ss:MergeAcross="${headers.length - 1}"><Data ss:Type="String">${xmlEscape(
      `Visible aircraft: ${totalAircraftLabel}`
    )}</Data></Cell></Row>`,
  ];

  const xmlParts = [];
  xmlParts.push("<?xml version=\"1.0\"?>");
  xmlParts.push("<?mso-application progid=\"Excel.Sheet\"?>");
  xmlParts.push(
    '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet" xmlns:html="http://www.w3.org/TR/REC-html40">'
  );
  xmlParts.push("<Styles>");
  xmlParts.push(
    `<Style ss:ID="Header"><Font ss:Bold="1" ss:Color="#FFFFFF"/><Interior ss:Color="${headerColor}" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:Vertical="Center"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/></Borders></Style>`
  );
  xmlParts.push(
    '<Style ss:ID="Cell"><Alignment ss:Vertical="Top"/><Font ss:Color="#1E1F24"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/></Borders></Style>'
  );
  xmlParts.push(
    '<Style ss:ID="CellCenter"><Alignment ss:Horizontal="Center" ss:Vertical="Top"/><Font ss:Color="#1E1F24"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/></Borders></Style>'
  );
  xmlParts.push(
    '<Style ss:ID="CellWrap"><Alignment ss:Vertical="Top" ss:WrapText="1"/><Font ss:Color="#1E1F24"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/><Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/></Borders></Style>'
  );
  xmlParts.push(
    `<Style ss:ID="Title"><Font ss:Bold="1" ss:Size="16" ss:Color="${headerColor}"/><Alignment ss:Horizontal="Left" ss:Vertical="Center"/></Style>`
  );
  xmlParts.push(
    '<Style ss:ID="Meta"><Alignment ss:Horizontal="Left" ss:Vertical="Center"/><Font ss:Color="#1E1F24"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/></Borders></Style>'
  );
  xmlParts.push("</Styles>");
  xmlParts.push("<Worksheet ss:Name=\"Aircraft\">");
  const totalRows = rows.length + 1 + metaRows.length;
  xmlParts.push(
    `<Table ss:ExpandedColumnCount="${headers.length}" ss:ExpandedRowCount="${totalRows}" x:FullColumns="1" x:FullRows="1">`
  );
  columnWidths.forEach((width) => {
    xmlParts.push(`<Column ss:AutoFitWidth="1" ss:Width="${width}"/>`);
  });
  metaRows.forEach((rowXml) => {
    xmlParts.push(rowXml);
  });
  xmlParts.push("<Row>");
  headers.forEach((header) => {
    xmlParts.push(
      `<Cell ss:StyleID="Header"><Data ss:Type="String">${xmlEscape(header)}</Data></Cell>`
    );
  });
  xmlParts.push("</Row>");
  rows.forEach((row) => {
    xmlParts.push("<Row>");
    row.forEach((value, index) => {
      const styleId = columnStyleIds[index] || "Cell";
      xmlParts.push(
        `<Cell ss:StyleID="${styleId}"><Data ss:Type="String">${xmlEscape(value)}</Data></Cell>`
      );
    });
    xmlParts.push("</Row>");
  });
  xmlParts.push("</Table>");
  const headerRowIndex = metaRows.length + 1;
  const lastRowIndex = headerRowIndex + rows.length;
  xmlParts.push(
    `<AutoFilter x:Range="R${headerRowIndex}C1:R${lastRowIndex}C${headers.length}"/>`
  );
  xmlParts.push(
    '<WorksheetOptions xmlns="urn:schemas-microsoft-com:office:excel"><Selected/><ProtectObjects>False</ProtectObjects><ProtectScenarios>False</ProtectScenarios><FreezePanes/><FrozenNoSplit/><SplitHorizontal>5</SplitHorizontal><TopRowBottomPane>5</TopRowBottomPane><ActivePane>2</ActivePane></WorksheetOptions>'
  );
  xmlParts.push("</Worksheet>");
  xmlParts.push("</Workbook>");

  const stampDate = reportGeneratedIso ? new Date(reportGeneratedIso) : new Date();
  const fileName = `AC Tracking-${formatDownloadStamp(stampDate)}.xls`;
  return {
    xml: xmlParts.join("\n"),
    fileName,
    rowCount,
    reportDateLabel,
    filterSummary,
    totalAircraftLabel,
    headers,
    rows,
  };
}

function buildEmailHeatmapLegendHtml() {
  const legendEntries = [
    { className: "heatmap-legend__swatch--in-service", label: "In service" },
    { className: "heatmap-legend__swatch--awaiting", label: "Awaiting feedback / >7 days" },
    { className: "heatmap-legend__swatch--storage", label: "Storage" },
    { className: "heatmap-legend__swatch--maintenance", label: "Maintenance" },
    { className: "heatmap-legend__swatch--transition", label: "Transition" },
    { className: "heatmap-legend__swatch--incident", label: "Incident" },
    { className: "heatmap-legend__swatch--aog", label: "AOG" },
    { className: "heatmap-legend__swatch--unknown", label: "Unknown" },
  ];
  const items = legendEntries
    .map(({ className, label }) => {
      const swatchClass = `heatmap-legend__swatch ${className}`;
      return `\
<li className="heatmap-legend__item"><span className="${swatchClass}" aria-hidden="true"></span><span className="heatmap-legend__label">${htmlEscape(
        label
      )}</span></li>`;
    })
    .join("");
  return `<ul className="heatmap-legend" aria-label="TAM status color guide">${items}</ul>`;
}

function buildEmailHeatmapTileMarkup(aircraft, model) {
  const classes = ["heatmap-tile"];
  const statusClass = getHeatmapStatusClass(model, aircraft) || HEATMAP_STATUS_FALLBACK_CLASS;
  if (statusClass) {
    classes.push(statusClass);
  }

  const msn = cleanText(aircraft.msn) || "—";
  const registration =
    cleanText(aircraft.registration) || cleanText(aircraft.ident) || cleanText(aircraft.tail) || "—";

  let ownerInitials = formatOwnerInitials(aircraft.owner, "");
  if (!ownerInitials) {
    ownerInitials = formatOwnerInitials(aircraft.operator, "");
  }
  if (!ownerInitials) {
    ownerInitials = "—";
  }

  const location = cleanText(model.lastLocation) || "—";
  const timeText = formatHeatmapTime(model);
  const secondaryText =
    timeText && timeText !== "—" ? `${location} · ${timeText}` : location;

  const lines = [
    `<span className="heatmap-tile__line heatmap-tile__line--primary">${htmlEscape(
      `${msn} – ${registration}`
    )}</span>`,
    `<span className="heatmap-tile__line heatmap-tile__line--secondary">${htmlEscape(secondaryText)}</span>`,
    `<span className="heatmap-tile__line heatmap-tile__line--meta">${htmlEscape(ownerInitials)}</span>`,
  ];

  const displayStatusLabel = model.tamDisplay && model.tamDisplay !== "—" ? model.tamDisplay : "";
  const statusLabel = normaliseTamStatus(displayStatusLabel) || model.categoryLabel || "Unknown";

  return `\
<div className="${classes.join(" ")}" data-status="${htmlEscape(statusLabel)}" data-aircraft-id="${htmlEscape(
    String(aircraft.id ?? "")
  )}">${lines.join("")}</div>`;
}

function buildEmailHeatmapMarkup(rows = []) {
  const entries = Array.isArray(rows) ? rows : [];
  const legendHtml = buildEmailHeatmapLegendHtml();

  if (!entries.length) {
    const statusMessage = "No aircraft available for the heatmap.";
    const pieces = [];
    pieces.push('<section className="card card--heatmap">');
    pieces.push('<div className="heatmap-header">');
    pieces.push('<div className="heatmap-header__titles">');
    pieces.push('<h2>Fleet activity heatmap</h2>');
    pieces.push('<p className="heatmap-status">' + htmlEscape(statusMessage) + '</p>');
    pieces.push('</div>');
    pieces.push(legendHtml);
    pieces.push('</div>');
    pieces.push('<div className="fleet-heatmap">');
    pieces.push('<div className="heatmap-empty">' + htmlEscape(statusMessage) + '</div>');
    pieces.push('</div>');
    pieces.push('</section>');
    return pieces.join("");
  }

  const grouped = new Map();
  entries.forEach(({ aircraft, model }) => {
    const region = resolveRegionLabel(aircraft);
    if (!grouped.has(region)) {
      grouped.set(region, new Map());
    }
    const operator = cleanText(aircraft.operator) || "Unknown operator";
    const regionGroup = grouped.get(region);
    if (!regionGroup.has(operator)) {
      regionGroup.set(operator, []);
    }
    regionGroup.get(operator).push({ aircraft, model });
  });

  const regionEntries = Array.from(grouped.entries()).sort((left, right) => {
    const leftTotal = Array.from(left[1].values()).reduce((sum, list) => sum + list.length, 0);
    const rightTotal = Array.from(right[1].values()).reduce((sum, list) => sum + list.length, 0);
    if (leftTotal !== rightTotal) {
      return rightTotal - leftTotal;
    }
    return left[0].localeCompare(right[0]);
  });

  let operatorCount = 0;
  const regionMarkup = regionEntries
    .map(([region, operators]) => {
      operatorCount += operators.size;
      const totalAircraft = Array.from(operators.values()).reduce((sum, list) => sum + list.length, 0);

      const operatorEntries = Array.from(operators.entries()).sort((left, right) => {
        if (right[1].length !== left[1].length) {
          return right[1].length - left[1].length;
        }
        return left[0].localeCompare(right[0]);
      });

      const operatorsMarkup = operatorEntries
        .map(([operator, items]) => {
          const tiles = items
            .slice()
            .sort((a, b) => {
              const statusOrderA = getHeatmapStatusSortValue(a.aircraft, a.model);
              const statusOrderB = getHeatmapStatusSortValue(b.aircraft, b.model);
              if (statusOrderA !== statusOrderB) {
                return statusOrderA - statusOrderB;
              }
              const msnCompare = compareTextValues(cleanText(a.aircraft.msn), cleanText(b.aircraft.msn));
              if (msnCompare !== 0) {
                return msnCompare;
              }
              return compareTextValues(
                cleanText(a.aircraft.registration),
                cleanText(b.aircraft.registration)
              );
            })
            .map(({ aircraft, model }) => buildEmailHeatmapTileMarkup(aircraft, model))
            .join("");

          const pieces = [];
          pieces.push('<article className="heatmap-operator">');
          pieces.push('<div className="heatmap-operator__title">');
          pieces.push('<span className="heatmap-operator__label">' + htmlEscape(operator) + '</span>');
          pieces.push('<span className="heatmap-operator__count">' + items.length + ' msn</span>');
          pieces.push('</div>');
          pieces.push('<div className="heatmap-tiles">' + tiles + '</div>');
          pieces.push('</article>');
          return pieces.join("");
        })
        .join("");

      const pieces = [];
      pieces.push('<section className="heatmap-region">');
      pieces.push('<div className="heatmap-region__title">');
      pieces.push('<span className="heatmap-region__label">' + htmlEscape(region) + '</span>');
      pieces.push('<span className="heatmap-region__count">' + totalAircraft + ' aircraft</span>');
      pieces.push('</div>');
      pieces.push('<div className="heatmap-operators">' + operatorsMarkup + '</div>');
      pieces.push('</section>');
      return pieces.join("");
    })
    .join("");

  const statusMessage = entries.length + ' aircraft · ' + regionEntries.length + ' region' + (regionEntries.length === 1 ? '' : 's') + ' · ' + operatorCount + ' operator' + (operatorCount === 1 ? '' : 's');

  const pieces = [];
  pieces.push('<section className="card card--heatmap">');
  pieces.push('<div className="heatmap-header">');
  pieces.push('<div className="heatmap-header__titles">');
  pieces.push('<h2>Fleet activity heatmap</h2>');
  pieces.push('<p className="heatmap-status">' + htmlEscape(statusMessage) + '</p>');
  pieces.push('</div>');
  pieces.push(legendHtml);
  pieces.push('</div>');
  pieces.push('<div className="fleet-heatmap">');
  pieces.push(regionMarkup);
  pieces.push('</div>');
  pieces.push('</section>');
  return pieces.join("");
}

function buildEmailHtmlReport(context, meta, statusChanges = []) {
  // #region agent log
  console.error('[CRITICAL-DEBUG] buildEmailHtmlReport ENTRY - VERSION b8e78de - HEATMAP REMOVED');
  // #endregion
  const { headers, rows, filterSummary } = context;
  const { reportDateLabel, totalAircraftLabel } = meta;
  const modeLabel = isProdMode ? "Production" : "Development";
  const style = `
body{font-family:Inter,system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#f5f6f7;color:#1e1f24;padding:24px;}
h1{margin:0 0 8px;font-size:20px;}
p{margin:4px 0;}
dl{display:flex;flex-wrap:wrap;margin:8px 0 0;padding:0;}
dt{font-weight:600;margin-right:6px;}
dd{margin:0 12px 0 0;}
table{width:100%;border-collapse:collapse;margin-top:16px;background:#fff;}
th,td{border:1px solid #d8dbe0;padding:8px 10px;font-size:13px;vertical-align:top;}
th{background:${EXPORT_HEADER_COLOR};color:#fff;text-transform:uppercase;font-size:12px;letter-spacing:0.05em;}
td{background:#fff;}
tr:nth-child(even) td{background:#f7f9fc;}
small{color:#5b6168;}
footer{margin-top:20px;font-size:12px;color:#5b6168;}
.card{background:#fff;border-radius:10px;box-shadow:0 1px 2px rgba(0,0,0,0.08),0 8px 24px rgba(0,0,0,0.04);border:1px solid #d8dbe0;padding:20px 24px;display:flex;flex-direction:column;gap:16px;margin-top:20px;}
.card--heatmap{gap:20px;}
.heatmap-header{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;flex-wrap:wrap;}
.heatmap-header__titles{display:flex;flex-direction:column;gap:6px;}
.heatmap-header h2{margin:0;font-size:18px;}
.heatmap-status{margin:0;font-size:13px;color:#5b6168;font-weight:500;}
.heatmap-legend{display:flex;flex-wrap:wrap;gap:10px;margin:2px 0 0;padding:0;list-style:none;}
.heatmap-legend__item{display:inline-flex;align-items:center;gap:8px;padding:6px 10px;border-radius:999px;background:#ecedef;font-size:12px;font-weight:600;color:#3a3f45;}
.heatmap-legend__swatch{width:14px;height:14px;border-radius:5px;box-shadow:inset 0 0 0 1px rgba(0,0,0,0.08);display:block;}
.heatmap-legend__swatch--in-service{background:rgba(43,170,102,0.92);}
.heatmap-legend__swatch--awaiting{background:rgba(214,146,45,0.9);}
.heatmap-legend__swatch--storage{background:rgba(31,111,235,0.9);}
.heatmap-legend__swatch--maintenance{background:rgba(122,43,167,0.92);}
.heatmap-legend__swatch--transition{background:rgba(168,80,223,0.9);}
.heatmap-legend__swatch--aog{background:rgba(142,4,4,0.92);}
.heatmap-legend__swatch--unknown{background:rgba(30,31,36,0.95);}
.fleet-heatmap{display:flex;flex-direction:column;gap:18px;}
.heatmap-empty{padding:32px 24px;text-align:center;border:1px dashed #d8dbe0;border-radius:12px;background:#f5f6f7;color:#5b6168;font-weight:500;}
.heatmap-region{background:#f5f6f7;border:1px solid #d8dbe0;border-radius:12px;padding:16px 18px;display:flex;flex-direction:column;gap:14px;}
.heatmap-region__title{display:flex;justify-content:space-between;align-items:baseline;gap:12px;font-weight:600;color:#3a3f45;}
.heatmap-region__count{font-size:13px;color:#5b6168;}
.heatmap-operators{display:flex;flex-wrap:wrap;gap:16px;}
.heatmap-operator{flex:1 1 clamp(260px,32%,420px);min-width:clamp(240px,45%,420px);background:#fff;border:1px solid #d8dbe0;border-radius:12px;padding:14px;display:flex;flex-direction:column;gap:12px;box-shadow:0 1px 2px rgba(0,0,0,0.08),0 8px 24px rgba(0,0,0,0.04);}
.heatmap-operator__title{display:flex;justify-content:space-between;align-items:baseline;gap:8px;font-weight:600;color:#3a3f45;}
.heatmap-operator__count{font-size:12px;color:#5b6168;text-transform:uppercase;letter-spacing:0.08em;}
.heatmap-tiles{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));grid-auto-flow:dense;gap:12px;}
.heatmap-tile{border-radius:12px;padding:14px 16px;min-height:116px;display:flex;flex-direction:column;gap:6px;background:#585858;color:#fff;text-align:left;box-shadow:0 6px 18px rgba(0,0,0,0.14);}
.heatmap-tile__line{font-size:13px;line-height:1.4;}
.heatmap-tile__line--primary{font-size:15px;font-weight:600;letter-spacing:0.01em;}
.heatmap-tile__line--meta{font-size:12px;letter-spacing:0.12em;text-transform:uppercase;color:rgba(255,255,255,0.75);}
.heatmap-tile--status-in-service{background:rgba(43,170,102,0.92);}
.heatmap-tile--status-storage{background:rgba(31,111,235,0.9);}
.heatmap-tile--status-aog{background:rgba(142,4,4,0.92);}
.heatmap-tile--status-awaiting,.heatmap-tile--status-grounded{background:rgba(214,146,45,0.9);color:#1e1f24;}
.heatmap-tile--status-maintenance{background:rgba(122,43,167,0.92);}
.heatmap-tile--status-unknown{background:rgba(30,31,36,0.95);}
.heatmap-tile--status-transition{background:rgba(168,80,223,0.9);}
`;
  const pieces = [];
  pieces.push("<!doctype html><html><head><meta charset=\"utf-8\">");
  pieces.push(`<style>${style}</style></head><body><div className="email-report">`);
  pieces.push(`<header className="email-report__header"><h1>${htmlEscape(EXPORT_REPORT_TITLE)}</h1></header>`);
  
  // Filter out status changes to "unknown" - only show real status changes
  const meaningfulStatusChanges = (statusChanges || []).filter((change) => {
    const newStatus = (change.newStatus || "").toLowerCase();
    return newStatus && !newStatus.includes("unknown") && newStatus !== "";
  });
  
  // Add status changes section at top if there are meaningful changes
  if (meaningfulStatusChanges && meaningfulStatusChanges.length > 0) {
    pieces.push('<section className="status-changes-section" style="background:#FEF3C7;border:1px solid #F59E0B;border-radius:8px;padding:16px;margin:16px 0;">');
    pieces.push(`<h2 style="margin:0 0 12px;font-size:18px;color:#92400E;">🔄 Status Changes (New Activity Detected)</h2>`);
    pieces.push('<ul style="margin:0;padding-left:20px;list-style-type:disc;">');
    meaningfulStatusChanges.forEach((change) => {
      // Format: Bold MSN only, rest normal text
      const msnPart = change.msn ? `<strong>${htmlEscape(change.msn)}</strong>` : "";
      const registrationPart = change.registration ? htmlEscape(change.registration) : "";
      const operatorPart = change.operator ? `(${htmlEscape(change.operator)})` : "";
      
      const parts = [msnPart, registrationPart, operatorPart].filter(Boolean);
      const aircraftLabel = parts.length ? parts.join(" ") : `Aircraft ${change.aircraftId}`;
      
      const previousStatus = change.previousStatus || "MANUAL OVERRIDE";
      const newStatus = change.newStatus || "ACTIVE";
      const flightInfo = change.flightIdent ? `Flight ${htmlEscape(change.flightIdent)}` : "New activity";
      
      // Use flight date (actual flight time), not created date (when status change was recorded)
      // Format: dd-mm-yyyy HH:MM (no seconds)
      let flightDate = "recently";
      if (change.flightDate) {
        try {
          const date = new Date(change.flightDate);
          if (Number.isFinite(date.getTime())) {
            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, "0");
            const minutes = String(date.getMinutes()).padStart(2, "0");
            flightDate = `${day}-${month}-${year} ${hours}:${minutes}`;
          }
        } catch (e) {
          // Use fallback
        }
      } else if (change.createdAt) {
        try {
          const date = new Date(change.createdAt);
          if (Number.isFinite(date.getTime())) {
            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, "0");
            const minutes = String(date.getMinutes()).padStart(2, "0");
            flightDate = `${day}-${month}-${year} ${hours}:${minutes}`;
          }
        } catch (e) {
          // Use fallback
        }
      }
      
      let text = `${aircraftLabel} changed status from ${htmlEscape(previousStatus)} to ${htmlEscape(newStatus)} as ${flightInfo} was recorded on ${htmlEscape(flightDate)}.`;
      if (change.watchRemoved) {
        text += " No longer on watch.";
      }
      
      pieces.push(`<li style="margin:8px 0;">${text}</li>`);
    });
    pieces.push('</ul>');
    pieces.push('</section>');
  } else if (statusChanges && statusChanges.length === 0) {
    // No status changes - show green success message
    pieces.push('<section className="status-changes-section" style="background:#D1FAE5;border:1px solid #10B981;border-radius:8px;padding:16px;margin:16px 0;">');
    pieces.push(`<p style="margin:0;font-size:16px;color:#065F46;">✓ No changes in status since last update</p>`);
    pieces.push('</section>');
  }
  
  pieces.push('<section className="email-report__meta"><dl className="email-report__meta-list">');
  pieces.push(`<dt>Report date</dt><dd>${htmlEscape(reportDateLabel)}</dd>`);
  pieces.push(`<dt>Visible aircraft</dt><dd>${htmlEscape(totalAircraftLabel)}</dd>`);
  if (filterSummary) {
    pieces.push(`<dt>Filters</dt><dd>${htmlEscape(filterSummary)}</dd>`);
  }
  pieces.push("</dl>");
  // Heatmap removed from email template - no longer included
  pieces.push("<table><thead><tr>");
  headers.forEach((header) => {
    pieces.push(`<th scope="col">${htmlEscape(header)}</th>`);
  });
  pieces.push('</tr></thead><tbody>');
  rows.forEach((row) => {
    pieces.push('<tr>');
    row.forEach((value) => {
      const safe = htmlEscape(value || "—").replace(/\n/g, "<br />");
      pieces.push(`<td>${safe}</td>`);
    });
    pieces.push('</tr>');
  });
  pieces.push("</tbody></table></section>");
  pieces.push(
    `<footer className="email-report__footer">Generated automatically from the Aircraft Tracking dashboard (${modeLabel} mode).</footer>`
  );
  pieces.push("</div></body></html>");
  return pieces.join("");
}

async function exportVisibleDataToExcel() {
  if (!state.sessionUser) {
    handleUnauthenticated("Sign in to export the current view.");
    return;
  }
  if (state.loadingAircraft) {
    setRefreshIndicator("Please wait for aircraft to finish loading before exporting.");
    return;
  }

  const context = await buildTabularExportContext();
  if (!context.ok) {
    if (context.reason === "empty") {
      setRefreshIndicator("No aircraft match the current filters to export.");
    } else {
      setRefreshIndicator("Export is currently unavailable. Try again in a moment.");
    }
    return;
  }

  const payload = buildExcelExportPayload(context);
  const blob = new Blob([payload.xml], { type: "application/vnd.ms-excel" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = payload.fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 1000);

  logEvent("Export visible data", { format: "excel", count: payload.rowCount, file: payload.fileName });
  setRefreshIndicator(`Exported ${payload.rowCount} aircraft to Excel.`);
}

function summariseRecipientLabel(recipients) {
  if (!Array.isArray(recipients) || !recipients.length) {
    return "recipients";
  }
  if (recipients.length === 1) {
    return recipients[0];
  }
  return `${recipients.length} recipients`;
}

async function emailVisibleDataReport() {
  console.error('[CRITICAL-DEBUG] emailVisibleDataReport ENTRY - VERSION c4ee9c1 - NEW CODE IS RUNNING');
  console.log('[DEBUG-FIX-VERIFY] emailVisibleDataReport called - NEW CODE VERSION a147244');
  if (!state.sessionUser) {
    handleUnauthenticated("Sign in to export the current view.");
    return;
  }
  if (state.emailTransport?.disabled) {
    const reason = state.emailTransport.message || "Email delivery is disabled.";
    setRefreshIndicator(`Email disabled: ${reason}`);
    logEvent("email export blocked", { reason });
    return;
  }
  if (state.loadingAircraft) {
    setRefreshIndicator("Please wait for aircraft to finish loading before exporting.");
    return;
  }
  if (state.emailSending) {
    setRefreshIndicator("An email export is already in progress.");
    return;
  }

  const defaultRecipients = Array.isArray(state.lastEmailRecipients)
    ? state.lastEmailRecipients.join(", ")
    : cleanText(state.sessionUser.email) || "";
  const promptMessage =
    "Enter the recipient email addresses separated by commas. These will receive the Excel file and HTML summary.";
  const input = window.prompt(promptMessage, defaultRecipients || "");
  if (input === null) {
    setRefreshIndicator("Email export cancelled.");
    return;
  }
  const recipients = input
    .split(/[,;\s]/)
    .map((entry) => cleanText(entry))
    .filter(Boolean);
  if (!recipients.length) {
    setRefreshIndicator("Email export cancelled because no valid recipients were provided.");
    return;
  }

  state.lastEmailRecipients = recipients;
  state.emailSending = true;
  updateEmailExportAvailability();

  try {
    const context = await buildTabularExportContext();
    if (!context.ok) {
      throw new Error(
        context.reason === "empty"
          ? "No aircraft match the current filters to export."
          : "Export is currently unavailable. Try again."
      );
    }
    
    // Query status changes since last email (or last 24 hours if first time)
    let statusChanges = [];
    try {
      const since = state.lastEmailStatusCheck || new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const statusResponse = await fetchJson(apiUrl(`/api/status-changes?since=${encodeURIComponent(since)}&limit=50`));
      if (statusResponse?.ok && Array.isArray(statusResponse.changes)) {
        statusChanges = statusResponse.changes;
        state.lastEmailStatusCheck = new Date().toISOString();
      }
    } catch (error) {
      // Non-critical - continue without status changes
      if (devToolsEnabled) {
        logEvent("Status changes fetch failed for email", { error: error.message });
      }
    }
    
    const payload = buildExcelExportPayload(context);
    const htmlReport = buildEmailHtmlReport(context, payload, statusChanges);
    const excelBase64 = encodeBase64(payload.xml);
    if (!excelBase64) {
      throw new Error("Unable to prepare the Excel attachment.");
    }
    const subject = `${EXPORT_REPORT_TITLE} — ${payload.reportDateLabel}`;
    const summary = `${payload.totalAircraftLabel}. ${context.filterSummary}`;
    const response = await fetchJson(apiUrl("/api/reports/email"), {
      method: "POST",
      body: JSON.stringify({
        recipients,
        subject,
        summary,
        html: htmlReport,
        excel: {
          base64: excelBase64,
          fileName: payload.fileName,
        },
      }),
    });
    const label = summariseRecipientLabel(response.recipients || recipients);
    setRefreshIndicator(`Report emailed to ${label}.`);
    logEvent("Export visible data", {
      format: "email",
      recipients: response.recipients || recipients,
      file: payload.fileName,
      transport: response.transport || "console",
    });
    if (isAdminUser()) {
      await loadAdminTaskLogs({ reason: "email" });
    }
  } catch (error) {
    const message = error?.message || "Unable to send the report email.";
    setRefreshIndicator(`Email export failed: ${message}`);
    logEvent("email export failed", { error: message });
  } finally {
    state.emailSending = false;
    updateEmailExportAvailability();
  }
}

async function exportVisibleDataToPdf() {
  if (!state.sessionUser) {
    handleUnauthenticated("Sign in to export the current view.");
    return;
  }
  if (state.loadingAircraft) {
    setRefreshIndicator("Please wait for aircraft to finish loading before exporting.");
    return;
  }

  const context = await buildTabularExportContext();
  if (!context.ok) {
    if (context.reason === "empty") {
      setRefreshIndicator("No aircraft match the current filters to export.");
    } else {
      setRefreshIndicator("Export is currently unavailable. Try again in a moment.");
    }
    return;
  }

  const {
    headers,
    rows,
    rowCount,
    reportGeneratedIso,
    filterSummary,
  } = context;

  const { reportDateLabel, totalAircraftLabel } = buildExcelExportPayload(context);
  const headerRgb = hexToRgbTuple(EXPORT_HEADER_COLOR);
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
  const margin = 48;
  const bodyTextColor = [30, 31, 36];
  const secondaryTextColor = [120, 123, 133];

  runAutoTable(doc, {
    head: [headers],
    body: rows,
    startY: 130,
    margin: { top: 130, bottom: 60, left: margin, right: margin },
    styles: {
      fontSize: 9,
      cellPadding: 6,
      overflow: "linebreak",
      valign: "top",
      textColor: bodyTextColor,
    },
    headStyles: {
      fillColor: headerRgb,
      textColor: [255, 255, 255],
      fontStyle: "bold",
      halign: "center",
      valign: "middle",
    },
    alternateRowStyles: { fillColor: [247, 249, 252] },
    columnStyles: {
      0: { halign: "center" },
      1: { halign: "center" },
      2: { halign: "center" },
      3: { halign: "center" },
      4: { halign: "center" },
      5: { halign: "center" },
      6: { halign: "center" },
      7: { halign: "center" },
      8: { halign: "center" },
      12: { halign: "center" },
    },
    tableWidth: "auto",
    didDrawPage(data) {
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFontSize(18);
      doc.setTextColor(...headerRgb);
      doc.text(EXPORT_REPORT_TITLE, margin, 60);
      doc.setFontSize(11);
      doc.setTextColor(...bodyTextColor);
      doc.text(`Report date: ${reportDateLabel}`, margin, 80);
      doc.text(`Visible aircraft: ${totalAircraftLabel}`, margin, 96);
      const filterLines = doc.splitTextToSize(filterSummary, pageWidth - margin * 2);
      doc.text(filterLines, margin, 112);
      doc.setFontSize(9);
      doc.setTextColor(...secondaryTextColor);
      const pageLabel = `Page ${data.pageNumber} of ${doc.internal.getNumberOfPages()}`;
      doc.text(pageLabel, pageWidth - margin, pageHeight - 30, { align: "right" });
      doc.text("Report generated with live dashboard styling.", margin, pageHeight - 30);
    },
  });

  const downloadName = `AC Tracking-${formatDownloadStamp()}.pdf`;
  doc.save(downloadName);

  logEvent("Export visible data", { format: "pdf", count: rowCount, file: downloadName });
  setRefreshIndicator(`Exported ${rowCount} aircraft to PDF.`);
}

function renderStatusCell(aircraft, model) {
  const container = createElement("div", "status-stack");
  const chipClasses = ["status-chip"];
  if (model.categoryClass) {
    chipClasses.push(model.categoryClass);
  }
  const chipLabel = model.manualOverrideActive && model.manualStatusText
    ? model.manualStatusText.toUpperCase()
    : model.categoryLabel;
  const chip = createElement("span", chipClasses.join(" "), chipLabel);
  container.appendChild(chip);
  if (!isProdMode) {
    const phase = createElement("div", "status-phase", model.phase);
    container.appendChild(phase);
    if (model.route) {
      container.appendChild(createElement("div", "status-route", model.route));
    }
    if (model.timeline) {
      container.appendChild(createElement("div", "status-meta", model.timeline));
    }
    if (model.manualOverrideActive) {
      const manualParts = [];
      if (model.manualStatusText) {
        manualParts.push(`Override: ${model.manualStatusText}`);
      }
      if (model.manualLastFlightIso) {
        manualParts.push(`Last flight ${formatDisplayTimestamp(model.manualLastFlightIso)}`);
      }
      if (manualParts.length) {
        container.appendChild(createElement("div", "status-meta", manualParts.join(" · ")));
      }
      if (model.manualLastLocation) {
        container.appendChild(
          createElement(
            "div",
            "status-meta",
            `Location override: ${model.manualLastLocation}`
          )
        );
      }
      if (model.manualUpdatedAt) {
        const updatedParts = [];
        if (model.manualUpdatedBy) {
          updatedParts.push(`by ${model.manualUpdatedBy}`);
        }
        updatedParts.push(formatRelative(model.manualUpdatedAt));
        container.appendChild(
          createElement(
            "div",
            "status-meta",
            `Manual update ${updatedParts.join(" · ")}`
          )
        );
      }
    }
    if (model.statusError) {
      container.appendChild(createElement("div", "status-error", model.statusError));
    }
    const actions = createElement("div", "status-actions");
    const viewButton = document.createElement("button");
    viewButton.type = "button";
    viewButton.className = "status-action status-action--details";
    viewButton.textContent = "View data";
    viewButton.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      openStatusDetailDialog(aircraft, model);
    });
    actions.appendChild(viewButton);
    container.appendChild(actions);
  }
  if (!isProdMode && model.reliability && !model.reliability.isReliable) {
    const box = document.createElement("div");
    box.className = `status-reliability status-reliability--${model.reliability.severity}`;
    const title = createElement("p", "status-reliability__title", model.reliability.label);
    box.appendChild(title);
    if (model.reliability.messages && model.reliability.messages.length) {
      const list = document.createElement("ul");
      list.className = "status-reliability__list";
      model.reliability.messages.forEach((message) => {
        const item = document.createElement("li");
        item.textContent = message;
        list.appendChild(item);
      });
      box.appendChild(list);
    }
    if (model.reliability.instruction) {
      box.appendChild(createElement("p", "status-reliability__instruction", model.reliability.instruction));
    }
    container.appendChild(box);
  }
  return container;
}

function renderTimeCell(model) {
  const container = createElement("div", "time-stack");
  let displayLabel = "—";
  if (model.isAirborne) {
    displayLabel = "AIRBORNE";
  } else if (Number.isFinite(model.groundedMinutes)) {
    displayLabel = formatGroundDuration(model.groundedMinutes);
  }
  const timeValue = createElement("div", "time-value", displayLabel);
  if (model.highlightGrounded && !model.isAirborne) {
    timeValue.classList.add("time-value-critical");
  }
  container.appendChild(timeValue);
  return container;
}

function renderLocationCell(model) {
  const container = createElement("div", "location-stack");
  container.appendChild(createElement("div", "location-main", model.lastLocation || "—"));
  if (model.positionText && model.positionText !== model.lastLocation) {
    container.appendChild(createElement("div", "location-meta", model.positionText));
  }
  if (
    !isProdMode &&
    model.manualOverrideActive &&
    model.manualLastLocation &&
    model.manualLastLocation !== model.lastLocation
  ) {
    container.appendChild(
      createElement("div", "location-meta", `Manual override: ${model.manualLastLocation}`)
    );
  }
  return container;
}

function renderSourceCell(model) {
  const label = cleanText(model.locationSourceLabel) || "FA";
  const pillClass =
    label === "Ops" ? "ops" : label.toLowerCase();
  const span = createElement("span", `location-source-pill location-source-pill--${pillClass}`, label);
  if (label === "Ops") {
    span.title =
      "Location last saved from the ops override form. FlightAware/FR24 refreshes still show as FA or FR24 when they last wrote the location.";
  } else if (label === "FR24") {
    span.title = "last_known_location was last persisted from the FR24 verification job.";
  } else {
    span.title =
      "last_known_location was last persisted from the automated ME/FlightAware pipeline (or not yet tagged).";
  }
  return span;
}

function renderNextFlightCell(model) {
  const container = createElement("div", "next-flight-stack");
  const yesNo = model.hasUpcomingFlight ? "YES" : "NO";
  const chip = createElement(
    "span",
    `next-flight-chip next-flight-chip--${model.hasUpcomingFlight ? "yes" : "no"}`,
    yesNo
  );
  if (model.nextDepartureIso || model.nextEventLabel) {
    const detailParts = [];
    if (model.nextEventLabel) {
      detailParts.push(model.nextEventLabel);
    }
    if (model.nextDepartureIso) {
      detailParts.push(formatDisplayTimestamp(model.nextDepartureIso) || "");
    }
    const tooltip = detailParts.filter(Boolean).join(" · ");
    if (tooltip) {
      chip.title = tooltip;
    }
  }
  container.appendChild(chip);
  return container;
}

function renderOnWatchCell(aircraft, model) {
  const container = createElement("div", "on-watch-stack");
  container.dataset.state = model.onWatch ? "active" : "inactive";
  const toggle = document.createElement("input");
  toggle.type = "checkbox";
  toggle.className = "on-watch-toggle";
  toggle.checked = Boolean(model.onWatch);
  const label =
    cleanText(aircraft.registration) ||
    cleanText(aircraft.ident) ||
    cleanText(aircraft.tail) ||
    `Aircraft ${aircraft.id}`;
  toggle.setAttribute("aria-label", `Toggle on watch for ${label}`);
  const infoLines = [];
  const watch = model.watch || null;
  if (watch?.reason) {
    infoLines.push(`Reason: ${watch.reason}`);
  }
  if (watch?.scheduledDepartureIso) {
    const scheduledParts = [];
    if (watch.scheduledContext) {
      scheduledParts.push(watch.scheduledContext);
    }
    const scheduledAbsolute = formatDisplayTimestamp(watch.scheduledDepartureIso);
    if (scheduledAbsolute) {
      scheduledParts.push(scheduledAbsolute);
    }
    const scheduledRelative = formatRelative(watch.scheduledDepartureIso);
    if (scheduledRelative && scheduledRelative !== scheduledAbsolute) {
      scheduledParts.push(scheduledRelative);
    }
    if (scheduledParts.length) {
      infoLines.push(scheduledParts.join(" · "));
    }
  }
  if (watch?.payload?.route) {
    infoLines.push(`Route: ${watch.payload.route}`);
  }
  if (!model.onWatch && watch?.triggeredAtIso) {
    const triggeredAbsolute = formatDisplayTimestamp(watch.triggeredAtIso);
    const triggeredRelative = formatRelative(watch.triggeredAtIso);
    const triggeredParts = [];
    if (triggeredAbsolute) {
      triggeredParts.push(triggeredAbsolute);
    }
    if (triggeredRelative && triggeredRelative !== triggeredAbsolute) {
      triggeredParts.push(triggeredRelative);
    }
    if (triggeredParts.length) {
      infoLines.push(`Triggered ${triggeredParts.join(" · ")}`);
    }
  }
  if (model.onWatch && watch?.updatedAtIso) {
    const updatedAbsolute = formatDisplayTimestamp(watch.updatedAtIso);
    const updatedRelative = formatRelative(watch.updatedAtIso);
    const updatedParts = [];
    if (updatedAbsolute) {
      updatedParts.push(updatedAbsolute);
    }
    if (updatedRelative && updatedRelative !== updatedAbsolute) {
      updatedParts.push(updatedRelative);
    }
    if (updatedParts.length) {
      infoLines.push(`Updated ${updatedParts.join(" · ")}`);
    }
  }
  if (watch?.source) {
    infoLines.push(`Source: ${watch.source}`);
  }
  const tooltipParts = [];
  if (watch?.summary) {
    tooltipParts.push(watch.summary);
  }
  if (!tooltipParts.length && infoLines.length) {
    tooltipParts.push(infoLines.join(" · "));
  }
  const tooltip = tooltipParts.join(" · ");
  if (tooltip) {
    toggle.title = tooltip;
    container.title = tooltip;
  } else {
    toggle.removeAttribute("title");
    container.removeAttribute("title");
  }
  toggle.addEventListener("change", () => {
    setAircraftOnWatch(aircraft, toggle.checked, toggle);
  });
  container.appendChild(toggle);
  // We keep the detailed info as tooltip only; the visual column shows just the checkbox.
  if (isDevMode && watch && watch.payload && Object.keys(watch.payload).length) {
    const debug = document.createElement("details");
    debug.className = "on-watch-debug";
    const summary = document.createElement("summary");
    summary.textContent = "Payload";
    debug.appendChild(summary);
    const pre = document.createElement("pre");
    pre.textContent = JSON.stringify(watch.payload, null, 2);
    debug.appendChild(pre);
    container.appendChild(debug);
  }
  return container;
}

function renderTamStatusCell(aircraft, model) {
  const container = createElement("div", "tam-stack");
  if (model.statusCheckFailed) {
    container.classList.add("tam-stack--error");
  }
  const header = createElement("div", "tam-stack__header");
  let headerHasContent = false;
  if (model.tamDisplay && model.tamDisplay !== "—") {
    const badge = createElement("span", "tam-chip", model.tamDisplay);
    const tamClass = getTamChipClass(model.tamDisplay);
    if (tamClass) {
      badge.classList.add(tamClass);
    }
    header.appendChild(badge);
    headerHasContent = true;
  } else {
    container.classList.add("tam-stack--empty");
  }
  if (model.statusCheckFailed) {
    const indicator = createElement("span", "tam-stack__error-indicator");
    indicator.title = "Status retrieval failed twice. Manual review required.";
    indicator.setAttribute("role", "img");
    indicator.setAttribute(
      "aria-label",
      "Status retrieval failed twice. Manual review required."
    );
    header.appendChild(indicator);
    headerHasContent = true;
  }
  if (headerHasContent) {
    container.appendChild(header);
  }
  if (model.storageHold) {
    container.appendChild(createElement("div", "tam-previous", "Held until next activity"));
  }
  if (model.tamUpdated) {
    const relative = formatRelative(model.tamUpdated);
    const absolute = formatDisplayTimestamp(model.tamUpdated);
    const detail = relative && absolute && relative !== absolute ? `${absolute} (${relative})` : absolute || relative;
    if (detail) {
      container.appendChild(createElement("div", "tam-meta", `Updated ${detail}`));
    }
  }
  if (model.statusCheckFailed) {
    container.appendChild(
      createElement("div", "tam-meta", "FlightAware fetch failed twice · manual review required.")
    );
  }
  if (model.expectedReleaseDate && isExpectedReleaseTamStatus(model.tamDisplay)) {
    const expectedLine = createElement("div", "tam-expected-release");
    expectedLine.classList.add(classifyExpectedReleaseDateForUi(model.expectedReleaseDate));
    expectedLine.textContent = `Expected release: ${model.expectedReleaseDate}`;
    expectedLine.setAttribute("title", "Expected return to service (maintenance / storage / incident)");
    container.appendChild(expectedLine);
  }
  return container;
}

function renderLinksCell(aircraft, model) {
  const container = createElement("div", "links-stack");
  const links = [];
  if (model.trackerUrl) {
    links.push({
      type: "anchor",
      url: model.trackerUrl,
      title: "xRM Asset datasheet",
      label: "xRM",
    });
  }
  if (model.flightawareUrl) {
    links.push({
      type: "anchor",
      url: model.flightawareUrl,
      title: "FlightAware",
      label: "FA",
    });
  }
  if (model.fr24Url) {
    links.push({
      type: "anchor",
      url: model.fr24Url,
      title: "Flightradar24",
      label: "FR24",
    });
  }
  links.push({ type: "button", title: "Refresh AC position", label: "⟳" });
  // Add Edit button for admin users
  if (isAdminUser()) {
    links.push({
      type: "button",
      title: "Edit aircraft",
      label: "✏️",
      action: () => openAircraftDialog(aircraft),
    });
  }
  if (state.sessionUser) {
    links.push({
      type: "button",
      title: "Operator status email (copy)",
      label: "✉️",
      action: () => openOperatorOutreachDialog(aircraft),
    });
  }

  // Lay links out in two rows to keep the column compact.
  const row1 = createElement("div", "links-row");
  const row2 = createElement("div", "links-row");

  links.forEach((item, index) => {
    const targetRow = index < 2 ? row1 : row2;
    if (item.type === "anchor" && item.url) {
      const anchor = createElement("a", "link-pill", item.label);
      anchor.href = item.url;
      anchor.target = "_blank";
      anchor.rel = "noopener";
      anchor.title = item.title;
      targetRow.appendChild(anchor);
    } else if (item.type === "button") {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "link-pill";
      button.textContent = item.label;
      button.title = item.title;
      button.setAttribute("aria-label", item.title);
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (item.action && typeof item.action === "function") {
          item.action();
        } else {
          refreshSingleStatus(aircraft, button);
        }
      });
      targetRow.appendChild(button);
    }
  });

  if (row1.children.length) container.appendChild(row1);
  if (row2.children.length) container.appendChild(row2);

  if (!container.children.length) {
    container.appendChild(createElement("div", "links-empty", "—"));
  }
  return container;
}
function getFlightawareDebugState(aircraftId) {
  const numeric = Number(aircraftId);
  if (Number.isFinite(numeric) && state.flightawareDebug.has(numeric)) {
    return state.flightawareDebug.get(numeric);
  }
  return { loading: false, records: [], error: null, ident: null, fetchedAt: null };
}

function renderCommentsCell(aircraft, model, existingContainer) {
  const container = existingContainer || createElement("div", "comment-stack");
  container.className = "comment-stack";
  container.innerHTML = "";

  const commentState = state.comments.get(aircraft.id) || {
    loading: !state.comments.has(aircraft.id),
    items: [],
  };

  const debugState = getFlightawareDebugState(aircraft.id);

  const textColumn = createElement("div", "comment-text-column");
  container.appendChild(textColumn);

  if (model.notes) {
    textColumn.appendChild(createElement("div", "note-entry", model.notes));
  }

  if (commentState.loading) {
    textColumn.appendChild(createElement("div", "comment-placeholder", "Loading comments"));
  } else if (commentState.error) {
    const error = createElement("div", "error-state");
    error.innerHTML = `<strong>Unable to load comments</strong><span>${commentState.error}</span>`;
    textColumn.appendChild(error);
  } else {
    const items = Array.isArray(commentState.items) ? commentState.items : [];
    if (items.length) {
      const list = createElement("div", "comments-list");
      const show = items.slice(0, 3);
      show.forEach((comment) => {
        const entry = createElement("div", "comment-entry");
        entry.appendChild(createElement("div", "comment-line", comment.body));
        const metaParts = [];
        const author = cleanText(comment.author);
        if (author) {
          metaParts.push(author);
        }
        if (comment.created_at) {
          const relative = formatRelative(comment.created_at);
          const absolute = formatDisplayTimestamp(comment.created_at);
          if (relative) {
            metaParts.push(relative);
          }
          if (absolute && absolute !== relative) {
            metaParts.push(absolute);
          }
        }
        if (metaParts.length) {
          entry.appendChild(createElement("div", "comment-meta", metaParts.join(" · ")));
        }
        list.appendChild(entry);
      });
      textColumn.appendChild(list);
      if (items.length > show.length) {
        textColumn.appendChild(
          createElement("div", "comment-meta", `… +${items.length - show.length} more`)
        );
      }
    }
  }

  const actions = createElement("div", "comment-actions");
  container.appendChild(actions);

  const toggleButton = document.createElement("button");
  toggleButton.type = "button";
  toggleButton.className = "link-pill comment-add";
  toggleButton.setAttribute("aria-label", "Add comment or update status");
  toggleButton.textContent = "+";
  toggleButton.disabled = Boolean(commentState.submitting);
  toggleButton.addEventListener("click", () => {
    openCommentDialog(aircraft, model);
  });
  actions.appendChild(toggleButton);

  if (devToolsEnabled) {
    const debugButton = document.createElement("button");
    debugButton.type = "button";
    debugButton.className = "comment-debug";
    if (debugState.loading) {
      debugButton.textContent = "Loading logs…";
      debugButton.setAttribute("aria-busy", "true");
    } else {
      debugButton.textContent = "FlightAware logs";
      debugButton.removeAttribute("aria-busy");
    }
    debugButton.addEventListener("click", () => {
      toggleFlightawareDebug(aircraft, model);
    });
    actions.appendChild(debugButton);
    if (debugState.loading) {
      const meta = createElement("div", "comment-meta", "Fetching FlightAware diagnostics…");
      meta.classList.add("comment-debug-meta");
      actions.appendChild(meta);
    } else if (debugState.error) {
      const meta = createElement("div", "comment-meta", `Logs error: ${debugState.error}`);
      meta.classList.add("comment-debug-meta");
      actions.appendChild(meta);
    } else if (debugState.fetchedAt) {
      const meta = createElement(
        "div",
        "comment-meta",
        `Logs updated ${formatRelative(debugState.fetchedAt)} (${formatDisplayTimestamp(debugState.fetchedAt)})`
      );
      meta.classList.add("comment-debug-meta");
      actions.appendChild(meta);
    }
  }

  return container;
}

function renderFlightawareDebugPanel(aircraft, debugState = {}) {
  const panel = createElement("div", "flightaware-debug");
  const identLabel = getStatusIdent(aircraft) || cleanText(aircraft.registration) || "Unknown";
  const headerParts = [`Ident ${identLabel}`];
  if (debugState.fetchedAt) {
    headerParts.push(
      `Updated ${formatRelative(debugState.fetchedAt)} (${formatDisplayTimestamp(debugState.fetchedAt)})`
    );
  }
  panel.appendChild(createElement("div", "flightaware-debug__header", headerParts.join(" · ")));

  if (debugState.loading) {
    panel.appendChild(createElement("div", "comment-placeholder", "Loading FlightAware logs…"));
    return panel;
  }

  if (debugState.error) {
    panel.appendChild(createElement("div", "flightaware-debug__error", debugState.error));
    return panel;
  }

  const records = Array.isArray(debugState.records) ? debugState.records : [];
  if (!records.length) {
    panel.appendChild(createElement("div", "flightaware-debug__empty", "No FlightAware queries recorded for this ident."));
    return panel;
  }

  records.forEach((record, index) => {
    const entry = createElement("div", "flightaware-debug__record");
    const statusParts = [];
    if (record.status !== undefined && record.status !== null) {
      statusParts.push(`HTTP ${record.status}`);
    }
    const code = cleanText(record.code);
    if (code) {
      statusParts.push(code);
    }
    const errorText = cleanText(record.error);
    if (errorText) {
      statusParts.push(errorText);
    }
    if (statusParts.length) {
      entry.appendChild(createElement("div", "comment-meta", statusParts.join(" · ")));
    }
    if (record.endpoint) {
      entry.appendChild(createElement("div", "comment-meta", record.endpoint));
    }
    if (record.queried_at) {
      entry.appendChild(
        createElement(
          "div",
          "comment-meta",
          `Queried ${formatRelative(record.queried_at)} (${formatDisplayTimestamp(record.queried_at)})`
        )
      );
    }
    if (record.requested_at) {
      entry.appendChild(
        createElement(
          "div",
          "comment-meta",
          `Requested ${formatRelative(record.requested_at)} (${formatDisplayTimestamp(record.requested_at)})`
        )
      );
    }
    if (!record.queried_at && !record.requested_at) {
      entry.appendChild(createElement("div", "comment-meta", `Record ${index + 1}`));
    }
    if (record.params) {
      entry.appendChild(createElement("div", "comment-meta", "Params"));
      const paramsPre = document.createElement("pre");
      paramsPre.textContent = stringifyForDebug(record.params, 500);
      entry.appendChild(paramsPre);
    }
    if (record.payload) {
      entry.appendChild(createElement("div", "comment-meta", "Response"));
      const payloadPre = document.createElement("pre");
      payloadPre.textContent = stringifyForDebug(record.payload, 1400);
      entry.appendChild(payloadPre);
    }
    panel.appendChild(entry);
  });

  return panel;
}

function renderDebugDialogContent(aircraft, model) {
  if (!dom.debugDialogContent) {
    return;
  }
  dom.debugDialogContent.innerHTML = "";
  const debugState = getFlightawareDebugState(aircraft.id);
  dom.debugDialogContent.appendChild(renderFlightawareDebugPanel(aircraft, debugState));
}

function openDebugDialog(aircraft, model) {
  if (!dom.debugDialog) {
    return;
  }
  const reg =
    cleanText(aircraft.registration) || cleanText(aircraft.tail) || cleanText(aircraft.ident) || "Unknown";
  if (dom.debugDialogTitle) {
    dom.debugDialogTitle.textContent = `FlightAware logs · ${reg}`;
  }
  renderDebugDialogContent(aircraft, model);
  dom.debugDialog.hidden = false;
  dom.debugDialog.setAttribute("aria-hidden", "false");
  document.body.classList.add("modal-open");
}

function closeDebugDialog() {
  if (dom.debugDialog) {
    dom.debugDialog.hidden = true;
    dom.debugDialog.setAttribute("aria-hidden", "true");
  }
  state.activeDebugDialog = null;
  const commentHidden = !dom.commentDialog || dom.commentDialog.hidden;
  const statusHidden = !dom.statusDialog || dom.statusDialog.hidden;
  if (commentHidden && statusHidden) {
    document.body.classList.remove("modal-open");
  }
  if (dom.debugDialogContent) {
    dom.debugDialogContent.innerHTML = "";
  }
}

function updateDebugDialogIfActive(aircraftId) {
  if (!state.activeDebugDialog || Number(state.activeDebugDialog.aircraftId) !== Number(aircraftId)) {
    return;
  }
  const latestAircraft =
    state.aircraft.find((item) => Number(item.id) === Number(aircraftId)) || state.activeDebugDialog.aircraft;
  if (!latestAircraft) {
    return;
  }
  const model = composeStatusModel(latestAircraft);
  state.activeDebugDialog.model = model;
  state.activeDebugDialog.aircraft = latestAircraft;
  renderDebugDialogContent(latestAircraft, model);
}

async function fetchFlightawareDebugLogs(aircraftId, ident, aircraft, model) {
  if (!state.sessionUser) {
    return;
  }
  const limit = isProdMode ? 3 : 5;
  try {
    const response = await fetchJson(
      apiUrl(`/api/flightaware/status/debug?ident=${encodeURIComponent(ident)}&limit=${limit}`)
    );
    const rows = Array.isArray(response.rows) ? response.rows : [];
    const existing = state.flightawareDebug.get(Number(aircraftId)) || {};
    state.flightawareDebug.set(Number(aircraftId), {
      ...existing,
      loading: false,
      error: response.error || null,
      records: rows,
      fetchedAt: new Date().toISOString(),
      ident,
    });
    logEvent("FlightAware debug loaded", {
      aircraftId,
      ident,
      records: rows.length,
    });
  } catch (error) {
    const existing = state.flightawareDebug.get(Number(aircraftId)) || {};
    state.flightawareDebug.set(Number(aircraftId), {
      ...existing,
      loading: false,
      error: error.message,
    });
    logEvent("FlightAware debug fetch failed", { aircraftId, ident, error: error.message });
  }
  updateCommentCell(aircraftId, aircraft, model);
  updateDebugDialogIfActive(aircraftId);
}

function toggleFlightawareDebug(aircraft, model) {
  if (!devToolsEnabled) {
    return;
  }
  if (!state.sessionUser) {
    handleUnauthenticated("Session expired. Please sign in again.");
    return;
  }
  const aircraftId = Number(aircraft.id);
  if (!Number.isFinite(aircraftId)) {
    return;
  }
  const ident = getStatusIdent(aircraft);
  const snapshot =
    state.aircraft.find((item) => Number(item.id) === aircraftId) || aircraft;
  const currentModel = model || composeStatusModel(snapshot);
  state.activeDebugDialog = { aircraftId, aircraft: snapshot, model: currentModel };
  openDebugDialog(snapshot, currentModel);
  if (!ident) {
    const existing = state.flightawareDebug.get(aircraftId) || {};
    state.flightawareDebug.set(aircraftId, {
      ...existing,
      loading: false,
      error: "No ident available for FlightAware lookup.",
    });
    updateDebugDialogIfActive(aircraftId);
    return;
  }
  const existing = state.flightawareDebug.get(aircraftId) || {};
  state.flightawareDebug.set(aircraftId, {
    ...existing,
    loading: true,
    error: null,
    ident,
  });
  renderDebugDialogContent(snapshot, currentModel);
  updateCommentCell(aircraftId, snapshot, currentModel);
  fetchFlightawareDebugLogs(aircraftId, ident, snapshot, currentModel);
}

function appendStatusSummaryItem(summaryList, label, value) {
  const term = createElement("dt", "status-detail__term", label);
  const description = createElement("dd", "status-detail__value", value ?? "—");
  summaryList.appendChild(term);
  summaryList.appendChild(description);
}

function renderStatusDetailContent(aircraft, model) {
  if (!dom.statusDialogContent) {
    return;
  }
  dom.statusDialogContent.innerHTML = "";

  const wrapper = createElement("div", "status-detail");

  const summary = document.createElement("dl");
  summary.className = "status-detail__summary";

  const registration =
    cleanText(aircraft.registration) || cleanText(aircraft.tail) || cleanText(aircraft.ident) || "—";
  appendStatusSummaryItem(summary, "Registration", registration);

  const ident = getStatusIdent(aircraft) || "—";
  appendStatusSummaryItem(summary, "Lookup ident", ident);

  appendStatusSummaryItem(summary, "Automatic status", model.categoryLabel || "—");
  appendStatusSummaryItem(summary, "Phase", model.phase || "—");
  appendStatusSummaryItem(summary, "Route", model.route || "—");
  appendStatusSummaryItem(summary, "Location DB source", model.locationSourceLabel || "FA");
  const timeSummaryLabel = model.timeSummaryLabel || (model.isAirborne ? "Time airborne" : "Time on ground");
  appendStatusSummaryItem(summary, timeSummaryLabel, model.timeLabel || "—");

  if (model.isAirborne) {
    const airborneMinutes = Number.isFinite(model.airborneMinutes)
      ? `${model.airborneMinutes} min`
      : "Airborne";
    appendStatusSummaryItem(summary, "Airborne minutes", airborneMinutes);
  } else {
    const groundedMinutes = Number.isFinite(model.groundedMinutes)
      ? `${model.groundedMinutes} min`
      : "—";
    appendStatusSummaryItem(summary, "Grounded minutes", groundedMinutes);
  }

  const contextLabel = model.timeContextLabel || (model.isAirborne ? "Departed" : "Last landing");
  const contextValue = model.since
    ? `${formatRelative(model.since)} (${formatDisplayTimestamp(model.since)})`
    : model.isAirborne
    ? "Airborne"
    : "Not recorded";
  appendStatusSummaryItem(summary, contextLabel, contextValue);

  const fetchedLabel = model.fetchedAt
    ? `${formatRelative(model.fetchedAt)} (${formatDisplayTimestamp(model.fetchedAt)})`
    : "Not fetched yet";
  appendStatusSummaryItem(summary, "Status fetched", fetchedLabel);

  const payloadUpdated = cleanText(model.raw?.updated_at);
  const payloadUpdatedLabel = payloadUpdated
    ? `${formatRelative(payloadUpdated)} (${formatDisplayTimestamp(payloadUpdated)})`
    : "Not provided";
  appendStatusSummaryItem(summary, "FlightAware updated", payloadUpdatedLabel);

  wrapper.appendChild(summary);

  if (Array.isArray(model.lookupKeys) && model.lookupKeys.length) {
    const lookupLine = createElement(
      "div",
      "status-detail__meta",
      `Lookup keys: ${model.lookupKeys.map((key) => key || "?").join(", ")}`
    );
    wrapper.appendChild(lookupLine);
  }

  if (model.timeline) {
    const entries = model.timeline.split(" · ").map((item) => item.trim()).filter(Boolean);
    if (entries.length) {
      const timelineSection = createElement("section", "status-detail__section");
      timelineSection.appendChild(createElement("h3", "status-detail__heading", "Timeline"));
      const list = document.createElement("ul");
      list.className = "status-detail__timeline";
      entries.forEach((entry) => {
        const item = document.createElement("li");
        item.textContent = entry;
        list.appendChild(item);
      });
      timelineSection.appendChild(list);
      wrapper.appendChild(timelineSection);
    }
  }

  if (model.statusError || model.raw?.error) {
    const errorText = model.statusError || model.raw?.error;
    if (errorText) {
      wrapper.appendChild(createElement("div", "status-detail__error", errorText));
    }
  }

  const payloadSection = createElement("section", "status-detail__section");
  payloadSection.appendChild(
    createElement("h3", "status-detail__heading", "FlightAware payload (normalised)")
  );
  const payloadPre = document.createElement("pre");
  payloadPre.textContent = stringifyForDebug(
    model.raw || { message: "No FlightAware status has been recorded yet." },
    2400
  );
  payloadSection.appendChild(payloadPre);
  wrapper.appendChild(payloadSection);

  dom.statusDialogContent.appendChild(wrapper);
}

function openStatusDetailDialog(aircraft, model) {
  if (!dom.statusDialog) {
    return;
  }
  const latestAircraft =
    state.aircraft.find((item) => Number(item.id) === Number(aircraft.id)) || aircraft;
  const latestModel = model || composeStatusModel(latestAircraft);
  const reg =
    cleanText(latestAircraft.registration) ||
    cleanText(latestAircraft.tail) ||
    cleanText(latestAircraft.ident) ||
    `Aircraft ${latestAircraft.id}`;
  if (dom.statusDialogTitle) {
    dom.statusDialogTitle.textContent = `Status details · ${reg}`;
  }
  state.activeStatusDetail = {
    aircraftId: Number(latestAircraft.id),
    aircraft: latestAircraft,
    model: latestModel,
  };
  renderStatusDetailContent(latestAircraft, latestModel);
  dom.statusDialog.hidden = false;
  dom.statusDialog.setAttribute("aria-hidden", "false");
  document.body.classList.add("modal-open");
}

function closeStatusDetailDialog() {
  if (dom.statusDialog) {
    dom.statusDialog.hidden = true;
    dom.statusDialog.setAttribute("aria-hidden", "true");
  }
  state.activeStatusDetail = null;
  if (dom.statusDialogContent) {
    dom.statusDialogContent.innerHTML = "";
  }
  const commentHidden = !dom.commentDialog || dom.commentDialog.hidden;
  const debugHidden = !dom.debugDialog || dom.debugDialog.hidden;
  if (commentHidden && debugHidden) {
    document.body.classList.remove("modal-open");
  }
}

function updateStatusDetailIfActive(aircraftId) {
  if (!state.activeStatusDetail) {
    return;
  }
  if (Number(state.activeStatusDetail.aircraftId) !== Number(aircraftId)) {
    return;
  }
  const latestAircraft =
    state.aircraft.find((item) => Number(item.id) === Number(aircraftId)) ||
    state.activeStatusDetail.aircraft;
  if (!latestAircraft) {
    return;
  }
  const latestModel = composeStatusModel(latestAircraft);
  state.activeStatusDetail = {
    aircraftId: Number(aircraftId),
    aircraft: latestAircraft,
    model: latestModel,
  };
  if (!dom.statusDialog || dom.statusDialog.hidden) {
    return;
  }
  const reg =
    cleanText(latestAircraft.registration) ||
    cleanText(latestAircraft.tail) ||
    cleanText(latestAircraft.ident) ||
    `Aircraft ${latestAircraft.id}`;
  if (dom.statusDialogTitle) {
    dom.statusDialogTitle.textContent = `Status details · ${reg}`;
  }
  renderStatusDetailContent(latestAircraft, latestModel);
}

function setCommentDialogError(message) {
  if (!dom.commentDialogError) return;
  const text = message || "";
  dom.commentDialogError.textContent = text;
  dom.commentDialogError.hidden = !text;
}

function normaliseManualLastFlightInput() {
  if (!dom.manualLastFlight) {
    return "";
  }
  const rawValue = dom.manualLastFlight.value || "";
  const trimmed = rawValue.trim();
  if (!trimmed) {
    return "";
  }
  // `<input type="date">` only accepts `YYYY-MM-DD`. Do not append `T00:00` — that is invalid
  // and browsers clear the field or fight typing, which looked like "deleting month/year".
  if (dom.manualLastFlight.type === "date") {
    const dateOnly = trimmed.match(/^(\d{4}-\d{2}-\d{2})$/);
    if (dateOnly) {
      return trimmed;
    }
    return trimmed;
  }
  const dateTrailingT = trimmed.match(/^\s*(\d{4}-\d{2}-\d{2})T\s*$/);
  if (dateTrailingT) {
    const normalised = `${dateTrailingT[1]}T00:00`;
    if (normalised !== rawValue) {
      dom.manualLastFlight.value = normalised;
      logEvent("Manual last flight time auto-filled", { value: normalised });
    }
    return normalised;
  }
  const match = trimmed.match(/^\s*(\d{4}-\d{2}-\d{2})(?:T(\d{2})(?::(\d{2}))?)?\s*$/);
  if (!match) {
    return rawValue;
  }
  const [, datePart, hourPart, minutePart] = match;
  const hours = hourPart !== undefined ? hourPart.padStart(2, "0") : "00";
  const minutes = minutePart !== undefined ? minutePart.padStart(2, "0") : "00";
  const normalised = `${datePart}T${hours}:${minutes}`;
  if (normalised !== rawValue) {
    dom.manualLastFlight.value = normalised;
    logEvent("Manual last flight time auto-filled", { value: normalised });
  }
  return normalised;
}

function updateManualStatusPreview() {
  if (!dom.manualStatusPreview) {
    return;
  }
  const context = state.activeCommentDialog;
  if (!context || !context.manualStatusEnabled) {
    dom.manualStatusPreview.textContent = "";
    return;
  }
  const normalisedInput = normaliseManualLastFlightInput();
  const iso = fromDatetimeLocalInput(normalisedInput);
  const computed = inferManualStatusFromLastFlight(iso);
  const fallback = context.manualStatusInitial?.text || "";
  const label = computed || fallback;
  context.manualStatusComputed = computed || "";
  if (computed) {
    dom.manualStatusPreview.textContent = `Override status will be set to ${computed}.`;
  } else if (label) {
    dom.manualStatusPreview.textContent = `Current override: ${label}. Update the last flight to recalculate.`;
  } else {
    dom.manualStatusPreview.textContent =
      "Enter the last known flight time to calculate the override automatically.";
  }
}

function updateCommentDialogHint() {
  if (!dom.commentDialogHint) return;
  const context = state.activeCommentDialog;
  if (!context) {
    dom.commentDialogHint.textContent = "";
    return;
  }
  const automatic = context.model?.categoryLabel || "Unknown";
  const tam = context.model?.tamDisplay && context.model.tamDisplay !== "—" ? context.model.tamDisplay : "No status";
  dom.commentDialogHint.textContent = `Automatic status: ${automatic}. Current TAM status: ${tam}.`;
}

function updateCommentDialogExpectedReleaseUi() {
  if (!dom.commentDialogExpectedReleaseField) {
    return;
  }
  const raw = dom.commentDialogStatus?.value || "";
  const show = isExpectedReleaseTamStatus(raw);
  dom.commentDialogExpectedReleaseField.hidden = !show;
  if (dom.commentDialogExpectedReleaseHint) {
    dom.commentDialogExpectedReleaseHint.textContent = show
      ? "Shown for maintenance, storage, and incident. Cleared automatically when TAM moves out of that group."
      : "";
  }
}

function setCommentDialogBusy(busy) {
  const disabled = Boolean(busy);
  if (dom.commentDialogSubmit) {
    dom.commentDialogSubmit.disabled = disabled;
  }
  if (dom.commentDialogCancel) {
    dom.commentDialogCancel.disabled = disabled;
  }
  if (dom.commentDialogClose) {
    dom.commentDialogClose.disabled = disabled;
  }
  if (dom.commentDialogStatus) {
    dom.commentDialogStatus.disabled = disabled;
  }
  if (dom.commentDialogComment) {
    dom.commentDialogComment.disabled = disabled;
  }
  if (dom.commentDialogExpectedRelease) {
    dom.commentDialogExpectedRelease.disabled = disabled;
  }
  if (dom.manualLastFlight) {
    dom.manualLastFlight.disabled = disabled;
  }
  if (dom.manualLastLocation) {
    dom.manualLastLocation.disabled = disabled;
  }
}

function closeCommentDialog() {
  if (dom.commentDialog) {
    dom.commentDialog.hidden = true;
    dom.commentDialog.setAttribute("aria-hidden", "true");
  }
  const debugHidden = !dom.debugDialog || dom.debugDialog.hidden;
  const statusHidden = !dom.statusDialog || dom.statusDialog.hidden;
  const outreachHidden = !dom.operatorOutreachDialog || dom.operatorOutreachDialog.hidden;
  const adminAircraftHidden = !dom.adminAircraftDialog || dom.adminAircraftDialog.hidden;
  const adminUserHidden = !dom.adminUserDialog || dom.adminUserDialog.hidden;
  const adminOpHidden = !dom.adminOperatorsDialog || dom.adminOperatorsDialog.hidden;
  if (debugHidden && statusHidden && outreachHidden && adminAircraftHidden && adminUserHidden && adminOpHidden) {
    document.body.classList.remove("modal-open");
  }
  state.activeCommentDialog = null;
  if (dom.commentDialogForm) {
    dom.commentDialogForm.reset();
  }
  if (dom.commentDialogStatus) {
    dom.commentDialogStatus.querySelectorAll("option[data-dynamic]").forEach((option) => option.remove());
    dom.commentDialogStatus.value = "";
  }
  if (dom.commentDialogOperator) {
    dom.commentDialogOperator.value = "";
  }
  if (dom.commentDialogExpectedRelease) {
    dom.commentDialogExpectedRelease.value = "";
  }
  if (dom.commentDialogExpectedReleaseField) {
    dom.commentDialogExpectedReleaseField.hidden = true;
  }
  if (dom.commentDialogExpectedReleaseHint) {
    dom.commentDialogExpectedReleaseHint.textContent = "";
  }
  handleOperatorSelectionChange();
  setCommentDialogBusy(false);
  setCommentDialogError("");
  updateCommentDialogHint();
  dom.commentDialogStatusHistoryList?.replaceChildren();
  dom.commentDialogCommentsHistoryList?.replaceChildren();
  if (dom.commentDialogStatusHistoryEmpty) {
    dom.commentDialogStatusHistoryEmpty.hidden = false;
    dom.commentDialogStatusHistoryEmpty.textContent = "No saved TAM updates yet.";
  }
  if (dom.commentDialogCommentsHistoryEmpty) {
    dom.commentDialogCommentsHistoryEmpty.hidden = false;
    dom.commentDialogCommentsHistoryEmpty.textContent = "No comments recorded yet.";
  }
  if (dom.commentDialogStatusHistoryMeta) {
    dom.commentDialogStatusHistoryMeta.textContent = "";
  }
  if (dom.commentDialogCommentsHistoryMeta) {
    dom.commentDialogCommentsHistoryMeta.textContent = "";
  }
  if (dom.manualStatusPreview) {
    dom.manualStatusPreview.textContent = "";
  }
}

let activeOperatorOutreachAircraft = null;

function isModalOpenExcludingOutreach() {
  return (
    (dom.commentDialog && !dom.commentDialog.hidden) ||
    (dom.debugDialog && !dom.debugDialog.hidden) ||
    (dom.statusDialog && !dom.statusDialog.hidden) ||
    (dom.adminAircraftDialog && !dom.adminAircraftDialog.hidden) ||
    (dom.adminUserDialog && !dom.adminUserDialog.hidden) ||
    (dom.adminOperatorsDialog && !dom.adminOperatorsDialog.hidden)
  );
}

function closeOperatorOutreachDialog() {
  if (dom.operatorOutreachDialog) {
    dom.operatorOutreachDialog.hidden = true;
    dom.operatorOutreachDialog.setAttribute("aria-hidden", "true");
  }
  if (dom.operatorOutreachError) {
    dom.operatorOutreachError.hidden = true;
    dom.operatorOutreachError.textContent = "";
  }
  if (dom.operatorOutreachMarkSent) {
    dom.operatorOutreachMarkSent.disabled = false;
  }
  activeOperatorOutreachAircraft = null;
  if (!isModalOpenExcludingOutreach()) {
    document.body.classList.remove("modal-open");
  }
}

async function refreshOperatorOutreachPreview() {
  if (!activeOperatorOutreachAircraft || !dom.operatorOutreachContact) {
    return;
  }
  const ac =
    state.aircraft.find((item) => Number(item.id) === Number(activeOperatorOutreachAircraft.id)) ||
    activeOperatorOutreachAircraft;
  const email = cleanText(dom.operatorOutreachContact.value);
  const opt = dom.operatorOutreachContact.selectedOptions?.[0];
  const contactName = cleanText(opt?.dataset?.contactName) || "Operator contact";
  if (!email) {
    if (dom.operatorOutreachSubject) {
      dom.operatorOutreachSubject.value = "";
    }
    if (dom.operatorOutreachBody) {
      dom.operatorOutreachBody.value = "";
    }
    return;
  }
  if (dom.operatorOutreachError) {
    dom.operatorOutreachError.hidden = true;
    dom.operatorOutreachError.textContent = "";
  }
  try {
    const response = await fetchJson(apiUrl(`/api/aircraft/${ac.id}/operator-outreach`), {
      method: "POST",
      body: JSON.stringify({ contactEmail: email, contactName, markSent: false }),
    });
    if (dom.operatorOutreachSubject) {
      dom.operatorOutreachSubject.value = cleanText(response?.subject) || "";
    }
    if (dom.operatorOutreachBody) {
      dom.operatorOutreachBody.value = cleanText(response?.body) || "";
    }
  } catch (error) {
    if (dom.operatorOutreachError) {
      dom.operatorOutreachError.textContent = error.message || "Unable to build message.";
      dom.operatorOutreachError.hidden = false;
    }
  }
}

async function openOperatorOutreachDialog(aircraft) {
  if (!dom.operatorOutreachDialog || !state.sessionUser || !aircraft?.id) {
    return;
  }
  if (!state.operatorDirectoryLoaded) {
    try {
      await loadOperatorDirectory();
    } catch (error) {
      logEvent("operator directory for outreach", { error: error.message });
    }
  }
  let ac =
    state.aircraft.find((item) => Number(item.id) === Number(aircraft.id)) || aircraft;
  try {
    const full = await fetchAircraftProfileById(aircraft.id, { skipRender: true, source: "outreach" });
    if (full) {
      ac = full;
    }
  } catch (error) {
    logEvent("outreach profile refresh failed", { error: error.message });
  }
  activeOperatorOutreachAircraft = ac;
  const opId = Number(ac.operator_id);
  const entry = Number.isFinite(opId) && opId > 0 ? getOperatorDirectoryEntry(opId) : null;
  const rawContacts = Array.isArray(entry?.contacts) && entry.contacts.length
    ? entry.contacts
    : Array.isArray(ac.operator_contacts)
    ? ac.operator_contacts
    : [];
  const contacts = rawContacts
    .map((c) => ({ name: cleanText(c?.name) || "Operator contact", email: cleanText(c?.email) || "" }))
    .filter((c) => c.email);
  if (dom.operatorOutreachContact) {
    dom.operatorOutreachContact.innerHTML = "";
    if (!contacts.length) {
      if (dom.operatorOutreachError) {
        dom.operatorOutreachError.textContent =
          "No operator or contacts on file. Set an operator on the aircraft and add contacts in Admin → Operators.";
        dom.operatorOutreachError.hidden = false;
      }
      if (dom.operatorOutreachMarkSent) {
        dom.operatorOutreachMarkSent.disabled = true;
      }
    } else {
      if (dom.operatorOutreachError) {
        dom.operatorOutreachError.hidden = true;
        dom.operatorOutreachError.textContent = "";
      }
      if (dom.operatorOutreachMarkSent) {
        dom.operatorOutreachMarkSent.disabled = false;
      }
      contacts.forEach((c) => {
        const option = document.createElement("option");
        option.value = c.email;
        option.dataset.contactName = c.name;
        option.textContent = `${c.name} · ${c.email}`;
        dom.operatorOutreachContact.appendChild(option);
      });
    }
  }
  dom.operatorOutreachDialog.hidden = false;
  dom.operatorOutreachDialog.setAttribute("aria-hidden", "false");
  document.body.classList.add("modal-open");
  if (contacts.length) {
    await refreshOperatorOutreachPreview();
  } else {
    if (dom.operatorOutreachSubject) {
      dom.operatorOutreachSubject.value = "";
    }
    if (dom.operatorOutreachBody) {
      dom.operatorOutreachBody.value = "";
    }
  }
}

async function handleOperatorOutreachMarkSent() {
  if (!activeOperatorOutreachAircraft || !dom.operatorOutreachContact) {
    return;
  }
  const email = cleanText(dom.operatorOutreachContact.value);
  const opt = dom.operatorOutreachContact.selectedOptions?.[0];
  const contactName = cleanText(opt?.dataset?.contactName) || "Operator contact";
  if (!email) {
    return;
  }
  if (dom.operatorOutreachMarkSent) {
    dom.operatorOutreachMarkSent.disabled = true;
  }
  if (dom.operatorOutreachError) {
    dom.operatorOutreachError.hidden = true;
    dom.operatorOutreachError.textContent = "";
  }
  try {
    const ac = activeOperatorOutreachAircraft;
    const response = await fetchJson(apiUrl(`/api/aircraft/${ac.id}/operator-outreach`), {
      method: "POST",
      body: JSON.stringify({ contactEmail: email, contactName, markSent: true }),
    });
    if (response?.watch) {
      updateWatchEntryInState(ac.id, response.watch);
    }
    closeOperatorOutreachDialog();
    await loadAircraft(true);
    const refreshed = state.aircraft.find((item) => Number(item.id) === Number(ac.id));
    if (refreshed) {
      await fetchStatusFor(refreshed);
    }
    await loadStatusHistory(ac.id, { force: true });
    renderTable();
    setRefreshIndicator("Operator email recorded · TAM set to Awaiting operator feedback");
  } catch (error) {
    if (dom.operatorOutreachError) {
      dom.operatorOutreachError.textContent = error.message || "Request failed.";
      dom.operatorOutreachError.hidden = false;
    }
    if (dom.operatorOutreachMarkSent) {
      dom.operatorOutreachMarkSent.disabled = false;
    }
  }
}

function setupOperatorOutreachDialog() {
  dom.operatorOutreachClose?.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    closeOperatorOutreachDialog();
  });
  dom.operatorOutreachCancel?.addEventListener("click", (event) => {
    event.preventDefault();
    closeOperatorOutreachDialog();
  });
  dom.operatorOutreachContact?.addEventListener("change", () => {
    refreshOperatorOutreachPreview().catch((error) => {
      logEvent("outreach preview failed", { error: error.message });
    });
  });
  dom.operatorOutreachMarkSent?.addEventListener("click", (event) => {
    event.preventDefault();
    handleOperatorOutreachMarkSent().catch((error) => {
      logEvent("outreach mark sent failed", { error: error.message });
    });
  });
  dom.operatorOutreachDialog?.addEventListener("click", (event) => {
    if (event.target === dom.operatorOutreachDialog) {
      closeOperatorOutreachDialog();
    }
  });
  document.addEventListener("keydown", (event) => {
    if (
      event.key === "Escape" &&
      dom.operatorOutreachDialog &&
      !dom.operatorOutreachDialog.hidden &&
      !dom.operatorOutreachMarkSent?.disabled
    ) {
      event.preventDefault();
      closeOperatorOutreachDialog();
    }
  });
}

/**
 * If the URL has ?aircraft=ID&open=comment, open the comment dialog for that aircraft
 * and clear the query params. Used by "Insert comment" links from the ME 4h email report.
 */
function tryOpenCommentFromUrl() {
  if (!state.sessionUser || !state.aircraft?.length) {
    return;
  }
  const params = new URLSearchParams(window.location.search);
  const open = params.get("open");
  const aircraftIdParam = params.get("aircraft");
  if (open !== "comment" || !aircraftIdParam) {
    return;
  }
  const id = Number(aircraftIdParam);
  if (!Number.isFinite(id) || id <= 0) {
    return;
  }
  const aircraft = state.aircraft.find((item) => Number(item.id) === id);
  if (!aircraft) {
    return;
  }
  const model = composeStatusModel(aircraft);
  openCommentDialog(aircraft, model);
  const cleanUrl = window.location.pathname + (window.location.hash || "");
  window.history.replaceState(null, "", cleanUrl);
}

async function openCommentDialog(aircraft, model) {
  if (!dom.commentDialog || !dom.commentDialogForm) {
    return;
  }
  if (!state.sessionUser) {
    handleUnauthenticated("Session expired. Please sign in again.");
    return;
  }
  let latestAircraft = state.aircraft.find((item) => item.id === aircraft.id) || aircraft;
  let latestModel = model || composeStatusModel(latestAircraft);
  const numericId = Number(aircraft?.id);
  if (Number.isFinite(numericId)) {
    try {
      const refreshed = await fetchAircraftProfileById(numericId, {
        skipRender: false,
        source: "comment-dialog",
      });
      if (refreshed) {
        latestAircraft = refreshed;
        latestModel = composeStatusModel(refreshed);
      }
    } catch (error) {
      logEvent("Aircraft profile refresh skipped", {
        aircraftId: numericId,
        error: error.message,
      });
    }
  }
  const cachedSnapshot = getAircraftSnapshot(latestAircraft) || getAircraftSnapshot(aircraft);
  const previousStatus = normaliseTamStatus(latestAircraft.tam_status);
  const statusIsUnknown = (latestModel.phase || "").toUpperCase().includes("UNKNOWN");
  const manualEnabled =
    isAdminUser() ||
    aircraftOwnerInSessionScope(latestAircraft, cachedSnapshot) ||
    statusIsUnknown ||
    latestModel.manualOverrideActive;
  const inferredManualStatus = inferManualStatusFromLastFlight(latestModel.manualLastFlightIso);
  const initialManualStatus = latestModel.manualStatusText || "";
  const initialRegistration =
    cleanText(latestAircraft.registration) ||
    cleanText(latestAircraft.tail) ||
    cleanText(cachedSnapshot?.registration) ||
    "";
  const initialMsn = cleanText(latestAircraft.msn) || cleanText(cachedSnapshot?.msn) || "";
  const initialOperatorId = Number.isFinite(Number(latestAircraft.operator_id))
    ? Number(latestAircraft.operator_id)
    : Number.isFinite(Number(cachedSnapshot?.operator_id))
    ? Number(cachedSnapshot.operator_id)
    : null;
  const initialOperatorName =
    cleanText(latestAircraft.operator) || cleanText(cachedSnapshot?.operator) || "";
  const initialExpectedRelease =
    cleanText(latestAircraft.expected_release_date) || cleanText(cachedSnapshot?.expected_release_date) || "";
  state.activeCommentDialog = {
    aircraft: latestAircraft,
    model: latestModel,
    previousStatus,
    manualStatusEnabled: manualEnabled,
    manualStatusInitial: {
      text: initialManualStatus,
      lastFlight: latestModel.manualLastFlightIso || "",
      location: latestModel.manualLastLocation || "",
    },
    manualStatusComputed: inferredManualStatus || "",
    profileInitial: {
      registration: initialRegistration,
      msn: initialMsn,
      operatorId: initialOperatorId,
      name: initialOperatorName,
    },
    initialExpectedRelease,
    initialStatusSelection: "",
    statusSelectionTouched: false,
  };
  if (!state.operatorDirectoryLoaded) {
    loadOperatorDirectory().catch((error) => {
      logEvent("operator directory lazy load failed", { error: error.message });
    });
  }
  if (dom.commentDialogStatus) {
    if (
      previousStatus &&
      !Array.from(dom.commentDialogStatus.options || []).some((option) => option.value === previousStatus)
    ) {
      const dynamicOption = document.createElement("option");
      dynamicOption.value = previousStatus;
      dynamicOption.textContent = previousStatus;
      dynamicOption.dataset.dynamic = "1";
      dom.commentDialogStatus.appendChild(dynamicOption);
    }
    dom.commentDialogStatus.value = previousStatus || "";
  }
  state.activeCommentDialog.initialStatusSelection = dom.commentDialogStatus?.value || "";
  state.activeCommentDialog.statusSelectionTouched = false;
  if (dom.commentDialogComment) {
    dom.commentDialogComment.value = "";
  }
  if (dom.commentDialogRegistration) {
    dom.commentDialogRegistration.value = initialRegistration;
  }
  if (dom.commentDialogMsn) {
    dom.commentDialogMsn.value = initialMsn;
  }
  populateOperatorSelector(state.activeCommentDialog.profileInitial);
  if (dom.commentDialogExpectedRelease) {
    dom.commentDialogExpectedRelease.value = initialExpectedRelease || "";
  }
  updateCommentDialogExpectedReleaseUi();
  if (dom.manualStatusPanel) {
    dom.manualStatusPanel.hidden = !manualEnabled;
  }
  if (manualEnabled) {
    if (dom.manualLastFlight) {
      dom.manualLastFlight.value = toDatetimeLocalInput(latestModel.manualLastFlightIso);
      if (!dom.manualLastFlight.value && dom.manualLastFlight.type !== "date") {
        dom.manualLastFlight.placeholder = "YYYY-MM-DDT00:00";
      }
      normaliseManualLastFlightInput();
    }
    if (dom.manualLastLocation) {
      dom.manualLastLocation.value = latestModel.manualLastLocation || "";
    }
    updateManualStatusPreview();
    if (dom.manualStatusHint) {
      if (latestModel.manualOverrideActive && latestModel.manualUpdatedAt) {
        const absolute = formatDisplayTimestamp(latestModel.manualUpdatedAt);
        const relative = formatRelative(latestModel.manualUpdatedAt);
        const userLabel = latestModel.manualUpdatedBy || getActiveUserLabel() || "ops";
        dom.manualStatusHint.textContent = `Last manual update by ${userLabel} ${relative} (${absolute}).`;
      } else {
        dom.manualStatusHint.textContent =
          "Set the last known flight date (time defaults to 12:00 AM) and location to update the override automatically.";
      }
    }
  } else {
    if (dom.manualLastFlight) {
      dom.manualLastFlight.value = "";
      if (dom.manualLastFlight.type !== "date") {
        dom.manualLastFlight.placeholder = "YYYY-MM-DDT00:00";
      }
    }
    if (dom.manualLastLocation) {
      dom.manualLastLocation.value = "";
    }
    if (dom.manualStatusPreview) {
      dom.manualStatusPreview.textContent = "";
    }
    if (dom.manualStatusHint) {
      dom.manualStatusHint.textContent = "";
    }
  }
  if (dom.commentDialogAircraft) {
    const reg =
      cleanText(latestAircraft.registration) ||
      cleanText(latestAircraft.tail) ||
      cleanText(latestAircraft.ident) ||
      "Unknown";
    const msn = cleanText(latestAircraft.msn) || "—";
    dom.commentDialogAircraft.textContent = `${reg} · MSN ${msn}`;
  }
  setCommentDialogError("");
  updateCommentDialogHint();
  updateCommentDialogHistorySections();
  fetchCommentsFor(latestAircraft.id, { limit: 50 });
  loadStatusHistory(latestAircraft.id, { force: true });
  dom.commentDialog.hidden = false;
  dom.commentDialog.setAttribute("aria-hidden", "false");
  document.body.classList.add("modal-open");
  setTimeout(() => {
    dom.commentDialogComment?.focus();
  }, 0);
}

async function handleCommentDialogSubmit(event) {
  event.preventDefault();
  if (!state.activeCommentDialog) {
    return;
  }
  if (!state.sessionUser) {
    handleUnauthenticated("Session expired. Please sign in again.");
    closeCommentDialog();
    return;
  }
  const context = state.activeCommentDialog;
  const { aircraft, previousStatus } = context;
  const trimmedComment = dom.commentDialogComment?.value?.trim() || "";
  const rawStatusSelection = dom.commentDialogStatus?.value || "";
  const statusSelected = Boolean(rawStatusSelection);
  const selectedStatus = normaliseTamStatus(rawStatusSelection);
  const statusChanged = Boolean(selectedStatus && selectedStatus !== previousStatus);
  const statusTouched = Boolean(
    context.statusSelectionTouched ||
      rawStatusSelection !== (context.initialStatusSelection ?? "")
  );
  const shouldSubmitStatus = statusSelected && (statusChanged || statusTouched);
  const hasComment = Boolean(trimmedComment);
  const initialProfile = context.profileInitial || {
    registration: "",
    msn: "",
    operatorId: null,
    name: "",
  };
  const profilePayload = {};
  if (dom.commentDialogRegistration) {
    const registrationInput = dom.commentDialogRegistration.value || "";
    const nextRegistration = normaliseRegistrationValue(registrationInput);
    const initialRegistration = normaliseRegistrationValue(initialProfile.registration);
    if (!registrationInput.trim() && initialRegistration) {
      profilePayload.registration = null;
    } else if (nextRegistration && nextRegistration !== initialRegistration) {
      profilePayload.registration = nextRegistration;
    }
  }
  if (dom.commentDialogMsn) {
    const msnInput = dom.commentDialogMsn.value || "";
    let nextMsn = normaliseMsnValue(msnInput);
    const initialMsn = normaliseMsnValue(initialProfile.msn);
    if (!nextMsn && initialMsn) {
      nextMsn = initialMsn;
      dom.commentDialogMsn.value = initialProfile.msn || initialMsn;
    }
    if (!nextMsn) {
      setCommentDialogError("Enter the MSN before saving.");
      return;
    }
    if (nextMsn !== initialMsn) {
      profilePayload.msn = nextMsn;
    }
  }
  const operatorSelection = dom.commentDialogOperator?.value || "";
  if (operatorSelection === "__none__") {
    if (initialProfile.operatorId != null) {
      profilePayload.operatorId = null;
    }
  } else if (operatorSelection) {
    const selectedId = Number(operatorSelection);
    if (Number.isFinite(selectedId) && selectedId !== initialProfile.operatorId) {
      profilePayload.operatorId = selectedId;
    }
  }
  const initialExpected = context.initialExpectedRelease || "";
  const currentExpected = dom.commentDialogExpectedRelease?.value?.trim() || "";
  const tamTargetForSave = (shouldSubmitStatus && selectedStatus) || previousStatus;
  const expectFieldActive = isExpectedReleaseTamStatus(tamTargetForSave);
  const expectedChangedForProfile =
    !shouldSubmitStatus &&
    expectFieldActive &&
    (currentExpected || "") !== (initialExpected || "");
  if (expectedChangedForProfile) {
    profilePayload.expected_release_date = currentExpected || null;
  }
  const hasProfileChange = Object.keys(profilePayload).length > 0;
  const manualEnabled = Boolean(state.activeCommentDialog.manualStatusEnabled);
  let manualPayload = null;
  if (manualEnabled) {
    const initial = state.activeCommentDialog.manualStatusInitial || {
      text: "",
      lastFlight: "",
      location: "",
    };
    const normalisedInput = normaliseManualLastFlightInput();
    const nextLastFlight = fromDatetimeLocalInput(normalisedInput);
    // date-only input is normalized to YYYY-MM-DDT00:00:00 (12:00 AM) by fromDatetimeLocalInput
    const nextLocation = normaliseManualLocationValue(dom.manualLastLocation?.value || "");
    const computedStatus = inferManualStatusFromLastFlight(nextLastFlight) || context.manualStatusComputed || "";
    const existingStatus = initial.text || "";
    const hasManualChange =
      computedStatus !== existingStatus ||
      (nextLastFlight || "") !== (initial.lastFlight || "") ||
      nextLocation !== normaliseManualLocationValue(initial.location || "");
    if (hasManualChange) {
      manualPayload = {
        statusText: computedStatus || null,
        lastFlightAt: nextLastFlight || null,
        lastLocation: nextLocation,
      };
    }
  }
  if (!hasComment && !shouldSubmitStatus && !manualPayload && !hasProfileChange) {
    setCommentDialogError("");
    logEvent("No changes to save", { aircraftId: aircraft.id });
    closeCommentDialog();
    setRefreshIndicator("No changes saved.");
    return;
  }
  setCommentDialogError("");
  setCommentDialogBusy(true);
  try {
    let profileUpdated = false;
    let latestAircraft = context.aircraft;
    const statusApplied = shouldSubmitStatus;
    if (hasProfileChange) {
      const profileResponse = await submitAircraftProfileUpdate(aircraft.id, profilePayload);
      profileUpdated = Boolean(profileResponse);
      if (profileResponse?.aircraft) {
        const normalisedAircraft = applyAircraftNormalisation(profileResponse.aircraft);
        latestAircraft = normalisedAircraft;
        context.aircraft = normalisedAircraft;
        context.profileInitial = {
          registration:
            cleanText(normalisedAircraft.registration) ||
            cleanText(normalisedAircraft.tail) ||
            "",
          msn: cleanText(normalisedAircraft.msn) || "",
          operatorId: Number.isFinite(Number(normalisedAircraft.operator_id))
            ? Number(normalisedAircraft.operator_id)
            : null,
          name: cleanText(normalisedAircraft.operator) || "",
        };
        if (Object.prototype.hasOwnProperty.call(profilePayload, "expected_release_date")) {
          context.initialExpectedRelease = cleanText(normalisedAircraft.expected_release_date) || "";
        }
      }
    }
    if (manualPayload) {
      await submitManualStatusOverride(latestAircraft, manualPayload);
    }
    if (statusApplied) {
      const tamStatusText = selectedStatus || previousStatus || "";
      const tamOptions = {
        skipReload: true,
        force: statusTouched && !statusChanged,
      };
      if (isExpectedReleaseTamStatus(tamStatusText)) {
        const cur = dom.commentDialogExpectedRelease?.value?.trim() || "";
        tamOptions.expectedReleaseDate = cur || null;
      }
      await submitTamStatus(latestAircraft, tamStatusText, tamOptions);
    }
    if (hasComment) {
      const success = await submitComment(aircraft.id, trimmedComment);
      if (!success) {
        throw new Error("Unable to save comment. Try again.");
      }
    }
    closeCommentDialog();
    if (statusApplied) {
      try {
        await loadAircraft(true);
        const refreshed = state.aircraft.find((item) => Number(item.id) === Number(aircraft.id));
        if (refreshed) {
          await fetchStatusFor(refreshed);
        }
        await loadStatusHistory(aircraft.id, { force: true });
        const stamp = new Date().toISOString();
        const statusLabel = statusChanged ? "TAM status updated" : "TAM status saved";
        setRefreshIndicator(`${statusLabel} ${formatDisplayTimestamp(stamp)}`);
      } catch (refreshError) {
        logEvent("post-update refresh failed", { error: refreshError.message });
        setRefreshIndicator(
          `TAM status saved. Refresh failed: ${refreshError.message}`
        );
      }
    } else if (manualPayload) {
      setRefreshIndicator("Manual status override saved.");
      renderTable();
    } else if (profileUpdated) {
      setRefreshIndicator("Aircraft profile updated.");
      renderTable();
    } else {
      setRefreshIndicator(`Comment added ${new Date().toLocaleTimeString()}`);
    }
  } catch (error) {
    setCommentDialogError(error.message || "Unable to save changes.");
  } finally {
    setCommentDialogBusy(false);
  }
}

function setupCommentDialog() {
  if (dom.commentDialogStatus && !dom.commentDialogStatus.dataset.initialised) {
    dom.commentDialogStatus.innerHTML = "";
    const keepOption = document.createElement("option");
    keepOption.value = "";
    keepOption.textContent = "Keep current status";
    dom.commentDialogStatus.appendChild(keepOption);
    TAM_STATUS_OPTIONS.forEach((option) => {
      const opt = document.createElement("option");
      opt.value = option;
      opt.textContent = option;
      dom.commentDialogStatus.appendChild(opt);
    });
    dom.commentDialogStatus.dataset.initialised = "1";
  }
  dom.commentDialogStatus?.addEventListener("change", () => {
    if (state.activeCommentDialog) {
      state.activeCommentDialog.statusSelectionTouched = true;
    }
    updateCommentDialogExpectedReleaseUi();
  });
  dom.commentDialogForm?.addEventListener("submit", handleCommentDialogSubmit);
  dom.commentDialogCancel?.addEventListener("click", (event) => {
    event.preventDefault();
    if (!dom.commentDialogCancel?.disabled) {
      closeCommentDialog();
    }
  });
  dom.commentDialogClose?.addEventListener("click", (event) => {
    event.preventDefault();
    if (!dom.commentDialogClose?.disabled) {
      closeCommentDialog();
    }
  });
  dom.commentDialogOperator?.addEventListener("change", () => handleOperatorSelectionChange());
  dom.commentDialog?.addEventListener("click", (event) => {
    if (event.target === dom.commentDialog && !dom.commentDialogSubmit?.disabled) {
      closeCommentDialog();
    }
  });
  if (dom.manualLastFlight) {
    if (dom.manualLastFlight.type !== "date") {
      dom.manualLastFlight.placeholder = "YYYY-MM-DDT00:00";
    }
    dom.manualLastFlight.addEventListener("input", updateManualStatusPreview);
    dom.manualLastFlight.addEventListener("change", updateManualStatusPreview);
    dom.manualLastFlight.addEventListener("blur", () => {
      normaliseManualLastFlightInput();
      updateManualStatusPreview();
    });
  }
  document.addEventListener("keydown", (event) => {
    if (
      event.key === "Escape" &&
      dom.commentDialog &&
      !dom.commentDialog.hidden &&
      !dom.commentDialogSubmit?.disabled
    ) {
      event.preventDefault();
      closeCommentDialog();
      return;
    }
    if (event.key === "Escape" && dom.statusDialog && !dom.statusDialog.hidden) {
      event.preventDefault();
      closeStatusDetailDialog();
      return;
    }
    if (event.key === "Escape" && dom.debugDialog && !dom.debugDialog.hidden) {
      event.preventDefault();
      closeDebugDialog();
    }
  });
}

function setupDebugDialog() {
  dom.debugDialogClose?.addEventListener("click", (event) => {
    event.preventDefault();
    closeDebugDialog();
  });
  dom.debugDialog?.addEventListener("click", (event) => {
    if (event.target === dom.debugDialog) {
      closeDebugDialog();
    }
  });
}

function setupStatusDialog() {
  dom.statusDialogClose?.addEventListener("click", (event) => {
    event.preventDefault();
    closeStatusDetailDialog();
  });
  dom.statusDialog?.addEventListener("click", (event) => {
    if (event.target === dom.statusDialog) {
      closeStatusDetailDialog();
    }
  });
}

function updateCommentCell(aircraftId, contextAircraft, contextModel) {
  if (!dom.tableBody) {
    return;
  }
  const selector = `td[data-aircraft-id="${aircraftId}"]`;
  const cell = dom.tableBody.querySelector(selector);
  if (!cell) {
    return;
  }
  const aircraft =
    contextAircraft || state.aircraft.find((item) => String(item.id) === String(aircraftId));
  if (!aircraft) {
    return;
  }
  const model = contextModel || composeStatusModel(aircraft);
  let container = cell.querySelector(".comment-stack");
  container = renderCommentsCell(aircraft, model, container || undefined);
  if (!cell.contains(container)) {
    cell.appendChild(container);
  }
}

function renderRegistrationCell(aircraft) {
  const container = createElement("div", "registration-stack");
  const main = cleanText(aircraft.registration) || cleanText(aircraft.tail) || cleanText(aircraft.ident) || "—";
  container.appendChild(createElement("div", "registration-main", main));
  const metaLines = [];
  if (cleanText(aircraft.ident) && cleanText(aircraft.ident) !== cleanText(aircraft.registration)) {
    metaLines.push(`Ident ${cleanText(aircraft.ident)}`);
  }
  if (cleanText(aircraft.base)) {
    metaLines.push(`Base ${cleanText(aircraft.base)}`);
  }
  if (metaLines.length) {
    container.appendChild(createElement("div", "registration-meta", metaLines.join(" · ")));
  }
  return container;
}

function renderStatusChangeAlert() {
  // Find or create alert container (before the table section)
  const mainContent = document.querySelector(".main-content");
  if (!mainContent) return;

  // Find the table section to insert before it
  const tableSection = document.querySelector("section.card--table");
  if (!tableSection) return;

  // Remove existing alert if it exists
  if (dom.statusChangeAlert) {
    dom.statusChangeAlert.remove();
    dom.statusChangeAlert = null;
  }

  // Filter out dismissed changes
  const visibleChanges = (state.statusChanges || []).filter(
    (change) => !state.statusChangesDismissed.has(change.id)
  );

  if (!visibleChanges.length) {
    return;
  }

  // Create alert banner
  const alert = document.createElement("div");
  alert.className = "status-change-alert";
  alert.setAttribute("role", "alert");
  alert.setAttribute("aria-live", "polite");

  const header = document.createElement("div");
  header.className = "status-change-alert__header";
  const title = document.createElement("h3");
  title.textContent = `🔄 Status Changes (${visibleChanges.length} aircraft with new activity)`;
  header.appendChild(title);

  const dismissBtn = document.createElement("button");
  dismissBtn.className = "status-change-alert__dismiss";
  dismissBtn.type = "button";
  dismissBtn.setAttribute("aria-label", "Dismiss alert");
  dismissBtn.textContent = "×";
  dismissBtn.onclick = () => {
    // Dismiss all visible changes
    visibleChanges.forEach((change) => {
      state.statusChangesDismissed.add(change.id);
    });
    renderStatusChangeAlert(); // Re-render (will hide if all dismissed)
  };
  header.appendChild(dismissBtn);

  alert.appendChild(header);

  const list = document.createElement("ul");
  list.className = "status-change-alert__list";

  visibleChanges.forEach((change) => {
    const item = document.createElement("li");
    item.className = "status-change-alert__item";

    const parts = [];
    if (change.msn) parts.push(`MSN ${change.msn}`);
    if (change.registration) parts.push(change.registration);
    if (change.operator) parts.push(`(${change.operator})`);

    const aircraftLabel = parts.length ? parts.join(" ") : `Aircraft ${change.aircraftId}`;
    const previousStatus = change.previousStatus || "MANUAL OVERRIDE";
    const newStatus = change.newStatus || "ACTIVE";
    const flightInfo = change.flightIdent ? `Flight ${change.flightIdent}` : "New activity";
    const flightDate = change.flightDate
      ? formatDisplayTimestamp(change.flightDate)
      : formatRelative(change.createdAt || new Date().toISOString());

    let text = `${aircraftLabel} changed status from **${previousStatus}** to **${newStatus}** as ${flightInfo} was recorded on ${flightDate}.`;
    if (change.watchRemoved) {
      text += " No longer on watch.";
    }

    // Simple text rendering (remove markdown-style bold)
    const textNode = document.createTextNode(text.replace(/\*\*/g, ""));
    item.appendChild(textNode);

    // Individual dismiss button
    const itemDismiss = document.createElement("button");
    itemDismiss.className = "status-change-alert__item-dismiss";
    itemDismiss.type = "button";
    itemDismiss.setAttribute("aria-label", `Dismiss ${aircraftLabel}`);
    itemDismiss.textContent = "×";
    itemDismiss.onclick = () => {
      state.statusChangesDismissed.add(change.id);
      renderStatusChangeAlert();
    };
    item.appendChild(itemDismiss);

    list.appendChild(item);
  });

  alert.appendChild(list);

  // Insert before table section
  mainContent.insertBefore(alert, tableSection);
  dom.statusChangeAlert = alert;

  // Auto-dismiss after 10 seconds (but keep in state)
  setTimeout(() => {
    if (dom.statusChangeAlert === alert) {
      visibleChanges.forEach((change) => {
        state.statusChangesDismissed.add(change.id);
      });
      renderStatusChangeAlert();
    }
  }, 10000);
}

function renderTable() {
  // Render status change alert first (before checking session)
  renderStatusChangeAlert();

  if (!dom.tableBody) return;
  if (!state.sessionUser) {
    renderHeatmap([], { reason: "signed-out" });
    if (dom.tableBody) {
      dom.tableBody.innerHTML = "";
      const row = document.createElement("tr");
      const cell = document.createElement("td");
      cell.colSpan = TABLE_LABELS.length;
      cell.textContent = "Sign in to view aircraft data.";
      row.appendChild(cell);
      dom.tableBody.appendChild(row);
    }
    return;
  }

  if (state.loadingAircraft) {
    renderHeatmap([], { loading: true });
    renderTableSkeleton(TABLE_LABELS);
    return;
  }

  const visibleRows = buildVisibleRows();

  const heatmapReason = visibleRows.length
    ? ""
    : state.aircraft.length
    ? "filtered"
    : "empty";
  renderHeatmap(visibleRows, { reason: heatmapReason });

  dom.tableBody.innerHTML = "";

  if (!visibleRows.length) {
    const row = document.createElement("tr");
    const cell = document.createElement("td");
    cell.colSpan = TABLE_LABELS.length;
    const stateEl = createElement(
      "div",
      "empty-state",
      state.aircraft.length
        ? "No aircraft match the current filters."
        : "No aircraft found. Add aircraft via the admin panel."
    );
    cell.appendChild(stateEl);
    row.appendChild(cell);
    dom.tableBody.appendChild(row);
    return;
  }

  visibleRows.forEach(({ aircraft, model }) => {
    const row = document.createElement("tr");

    const msnCell = document.createElement("td");
    msnCell.dataset.label = "MSN";
    msnCell.classList.add("col-msn");
    msnCell.style.textAlign = "left";
    const msnLine = createElement("div", "msn-line");
    const msnStrong = document.createElement("strong");
    msnStrong.textContent = cleanText(aircraft.msn) || "—";
    msnLine.appendChild(msnStrong);
    const registrationText =
      cleanText(aircraft.registration) ||
      cleanText(aircraft.tail) ||
      cleanText(aircraft.ident) ||
      "—";
    const regLine = createElement("div", "registration-line", registrationText);
    const baseType = cleanText(aircraft.aircraft_type) || "—";
    const engine = cleanText(aircraft.engine_model);
    const typeText = engine ? `${baseType} · ${engine}` : baseType;
    const typeLine = createElement("div", "type-line", typeText);
    msnCell.appendChild(msnLine);
    msnCell.appendChild(regLine);
    msnCell.appendChild(typeLine);
    row.appendChild(msnCell);

    const operatorCell = document.createElement("td");
    operatorCell.dataset.label = "Operator";
    operatorCell.classList.add("col-operator");
    operatorCell.textContent = cleanText(aircraft.operator) || "—";
    row.appendChild(operatorCell);

    const ownerCell = document.createElement("td");
    ownerCell.dataset.label = "Owner";
    ownerCell.classList.add("col-owner");
    ownerCell.textContent = cleanText(aircraft.owner) || "—";
    row.appendChild(ownerCell);

    const statusCell = document.createElement("td");
    statusCell.dataset.label = "Status";
    statusCell.classList.add("col-status");
    statusCell.appendChild(renderStatusCell(aircraft, model));
    row.appendChild(statusCell);

    const timeCell = document.createElement("td");
    timeCell.dataset.label = "Time on ground";
    timeCell.classList.add("col-time");
    timeCell.appendChild(renderTimeCell(model));
    row.appendChild(timeCell);

    const locationCell = document.createElement("td");
    locationCell.dataset.label = "Last location";
    locationCell.classList.add("col-location");
    locationCell.appendChild(renderLocationCell(model));
    row.appendChild(locationCell);

    const sourceCell = document.createElement("td");
    sourceCell.dataset.label = "Source";
    sourceCell.classList.add("col-location-source");
    sourceCell.appendChild(renderSourceCell(model));
    row.appendChild(sourceCell);

    const nextFlightCell = document.createElement("td");
    nextFlightCell.dataset.label = "Next flight scheduled";
    nextFlightCell.classList.add("col-next-flight");
    nextFlightCell.appendChild(renderNextFlightCell(model));
    row.appendChild(nextFlightCell);

    const tamCell = document.createElement("td");
    tamCell.dataset.label = "TAM status";
    tamCell.classList.add("col-tam");
    tamCell.appendChild(renderTamStatusCell(aircraft, model));
    row.appendChild(tamCell);

    const commentsCell = document.createElement("td");
    commentsCell.dataset.label = "Comments";
    commentsCell.dataset.aircraftId = String(aircraft.id);
    commentsCell.classList.add("col-comments");
    commentsCell.appendChild(renderCommentsCell(aircraft, model));
    row.appendChild(commentsCell);

    const onWatchCell = document.createElement("td");
    onWatchCell.dataset.label = "On watch";
    onWatchCell.classList.add("col-on-watch");
    onWatchCell.appendChild(renderOnWatchCell(aircraft, model));
    row.appendChild(onWatchCell);

    const linksCell = document.createElement("td");
    linksCell.dataset.label = "Links";
    linksCell.classList.add("col-links");
    linksCell.appendChild(renderLinksCell(aircraft, model));
    row.appendChild(linksCell);

    dom.tableBody.appendChild(row);

    if (!state.comments.has(aircraft.id)) {
      ensureCommentsFetched(aircraft.id, { aircraft, model });
    }
  });

  updateSortIndicators();

  if (state.activeStatusDetail) {
    updateStatusDetailIfActive(state.activeStatusDetail.aircraftId);
  }
}

function renderTableHeader() {
  const thead = document.querySelector(".table thead");
  if (!thead) return;
  thead.innerHTML = "";
  const tr = document.createElement("tr");
  TABLE_HEADERS.forEach(({ label, sortKey, className, title: headerTitle }) => {
    const th = document.createElement("th");
    th.scope = "col";
    th.dataset.sortKey = sortKey;
    th.className = className;
    th.textContent = label;
    if (headerTitle) {
      th.title = headerTitle;
    }
    tr.appendChild(th);
  });
  thead.appendChild(tr);
}

function updateSortIndicators() {
  const headers = document.querySelectorAll("th[data-sort-key]");
  headers.forEach((th) => {
    const key = th.dataset.sortKey;
    th.classList.remove("sorted-asc", "sorted-desc");
    th.removeAttribute("aria-sort");
    if (!key) return;
    if (key === state.sortKey) {
      const direction = state.sortDirection === "asc" ? "ascending" : "descending";
      th.setAttribute("aria-sort", direction);
      th.classList.add(state.sortDirection === "asc" ? "sorted-asc" : "sorted-desc");
    }
  });
}

function getOwnerFilterChoices() {
  const searchTerm = dom.searchInput?.value?.trim().toLowerCase() || "";
  const filtersDisabled = state.aircraftLimitActive;
  const operatorFilter = filtersDisabled ? "" : state.operatorFilter;
  const allowedOwners = getAllowedOwners();
  const matchingAircraft = getAircraftMatchingOperatorAndSearch(operatorFilter, searchTerm);
  const scoped = new Set();
  matchingAircraft.forEach((aircraft) => {
    const ownerValue = cleanText(aircraft.owner) || cleanText(aircraft.operator);
    if (!ownerValue) return;
    if (Array.isArray(allowedOwners) && allowedOwners.length) {
      if (!allowedOwners.some((o) => (o || "").toLowerCase() === ownerValue.toLowerCase())) return;
    }
    scoped.add(ownerValue);
  });
  return Array.from(scoped).sort((a, b) => a.localeCompare(b));
}

function populateOwnerFilter() {
  if (!dom.ownerFilter) return;
  ensureOwnerFilterIsValid();
  const allowed = getAllowedOwners();
  const selected = state.ownerFilter || "";
  const ownerChoices = getOwnerFilterChoices();
  dom.ownerFilter.innerHTML = "";
  const seen = new Set();
  if (!Array.isArray(allowed)) {
    const allOption = document.createElement("option");
    allOption.value = "";
    allOption.textContent = "All owners";
    dom.ownerFilter.appendChild(allOption);
    ownerChoices.forEach((owner) => {
      const option = document.createElement("option");
      option.value = owner;
      option.textContent = owner;
      dom.ownerFilter.appendChild(option);
      seen.add(owner);
    });
  } else {
    if (allowed.length > 1) {
      const allOption = document.createElement("option");
      allOption.value = "";
      allOption.textContent = "All assigned owners";
      dom.ownerFilter.appendChild(allOption);
    }
    const toShow = ownerChoices.length ? ownerChoices : (allowed.length ? allowed : state.ownerOptions);
    toShow.forEach((owner) => {
      const option = document.createElement("option");
      option.value = owner;
      option.textContent = owner;
      dom.ownerFilter.appendChild(option);
      seen.add(owner);
    });
  }
  if (selected && !seen.has(selected)) {
    const syntheticOption = document.createElement("option");
    syntheticOption.value = selected;
    syntheticOption.textContent = selected;
    syntheticOption.dataset.synthetic = "true";
    dom.ownerFilter.appendChild(syntheticOption);
  }
  dom.ownerFilter.value = selected;
  if (Array.isArray(allowed)) {
    if (!allowed.length || allowed.length === 1) {
      dom.ownerFilter.disabled = true;
      dom.ownerFilter.title = allowed.length
        ? "Owner filter locked to your assigned fleet."
        : "No owners assigned to this account.";
      return;
    }
  }
  if (state.aircraftLimitActive) {
    dom.ownerFilter.disabled = true;
    dom.ownerFilter.title = `Owner filter disabled while showing the first ${state.aircraftLimit} aircraft.`;
  } else {
    dom.ownerFilter.disabled = false;
    dom.ownerFilter.removeAttribute("title");
  }
}

function getAircraftMatchingOwnerAndSearch(ownerFilter, searchTerm) {
  return state.aircraft.filter((aircraft) => {
    if (ownerFilter) {
      const ownerValue = cleanText(aircraft.owner) || cleanText(aircraft.operator);
      if (ownerValue !== ownerFilter) return false;
    }
    if (!searchTerm) return true;
    const model = composeStatusModel(aircraft);
    const haystackValues = [
      aircraft.ident,
      aircraft.registration,
      aircraft.aircraft_type,
      aircraft.tail,
      aircraft.msn,
      aircraft.owner,
      aircraft.operator,
      aircraft.base,
      aircraft.notes,
      aircraft.last_known_location,
      model.categoryLabel,
      model.phase,
      model.tamDisplay,
      model.timeline,
      model.nextEventLabel,
      model.hasUpcomingFlight ? "yes" : "no",
      model.onWatch ? "on watch" : "",
      model.watchSummary,
      ...(Array.isArray(model.watch?.searchValues) ? model.watch.searchValues : []),
    ]
      .map((value) => expandSearchTokens(value))
      .flat();
    return haystackValues.some((value) => value.includes(searchTerm));
  });
}

function getAircraftMatchingOperatorAndSearch(operatorFilter, searchTerm) {
  return state.aircraft.filter((aircraft) => {
    if (operatorFilter) {
      const operatorValue = cleanText(aircraft.operator);
      if (operatorValue !== operatorFilter) return false;
    }
    if (!searchTerm) return true;
    const model = composeStatusModel(aircraft);
    const haystackValues = [
      aircraft.ident,
      aircraft.registration,
      aircraft.aircraft_type,
      aircraft.tail,
      aircraft.msn,
      aircraft.owner,
      aircraft.operator,
      aircraft.base,
      aircraft.notes,
      aircraft.last_known_location,
      model.categoryLabel,
      model.phase,
      model.tamDisplay,
      model.timeline,
      model.nextEventLabel,
      model.hasUpcomingFlight ? "yes" : "no",
      model.onWatch ? "on watch" : "",
      model.watchSummary,
      ...(Array.isArray(model.watch?.searchValues) ? model.watch.searchValues : []),
    ]
      .map((value) => expandSearchTokens(value))
      .flat();
    return haystackValues.some((value) => value.includes(searchTerm));
  });
}

function getOperatorFilterChoices() {
  const searchTerm = dom.searchInput?.value?.trim().toLowerCase() || "";
  const filtersDisabled = state.aircraftLimitActive;
  const ownerFilter = filtersDisabled ? "" : state.ownerFilter;
  const allowedOwners = getAllowedOwners();
  const matchingAircraft = getAircraftMatchingOwnerAndSearch(ownerFilter, searchTerm);
  const scoped = new Set();
  matchingAircraft.forEach((aircraft) => {
    if (Array.isArray(allowedOwners) && allowedOwners.length) {
      const ownerValue = cleanText(aircraft.owner) || cleanText(aircraft.operator);
      if (!ownerValue || !allowedOwners.some((o) => (o || "").toLowerCase() === ownerValue.toLowerCase())) {
        return;
      }
    }
    const operatorName = cleanText(aircraft.operator);
    if (operatorName) {
      scoped.add(operatorName);
    }
  });
  return Array.from(scoped).sort((a, b) => a.localeCompare(b));
}

function populateOperatorFilter() {
  if (!dom.operatorFilter) return;
  const previous = state.operatorFilter || "";
  const restricted = Array.isArray(getAllowedOwners());
  const choices = getOperatorFilterChoices();
  dom.operatorFilter.innerHTML = "";
  const allOption = document.createElement("option");
  allOption.value = "";
  allOption.textContent = restricted ? "All assigned operators" : "All operators";
  dom.operatorFilter.appendChild(allOption);
  const availableMap = new Map();
  choices.forEach((operator) => {
    const option = document.createElement("option");
    option.value = operator;
    option.textContent = operator;
    dom.operatorFilter.appendChild(option);
    availableMap.set(operator.toLowerCase(), operator);
  });
  let nextValue = "";
  if (previous) {
    const match = availableMap.get(previous.toLowerCase());
    if (match) {
      nextValue = match;
    }
  }
  dom.operatorFilter.value = nextValue;
  state.operatorFilter = nextValue;
  if (state.aircraftLimitActive) {
    dom.operatorFilter.disabled = true;
    dom.operatorFilter.title = `Operator filter disabled while showing the first ${state.aircraftLimit} aircraft.`;
  } else {
    dom.operatorFilter.disabled = false;
    dom.operatorFilter.removeAttribute("title");
  }
}

function getRegionFilterChoices() {
  const searchTerm = dom.searchInput?.value?.trim().toLowerCase() || "";
  const filtersDisabled = state.aircraftLimitActive;
  const ownerFilter = filtersDisabled ? "" : state.ownerFilter;
  const operatorFilter = filtersDisabled ? "" : state.operatorFilter;
  const tamFilter = filtersDisabled ? "" : state.tamFilter;
  const normalisedTamFilter = tamFilter ? normaliseTamStatus(tamFilter) : "";

  const scoped = new Set();
  state.aircraft.forEach((aircraft) => {
    if (operatorFilter) {
      const operatorValue = cleanText(aircraft.operator);
      if (operatorValue !== operatorFilter) return;
    }
    if (ownerFilter) {
      const ownerValue = cleanText(aircraft.owner) || cleanText(aircraft.operator);
      if (ownerValue !== ownerFilter) return;
    }
    const model = (searchTerm || normalisedTamFilter) ? composeStatusModel(aircraft) : null;
    if (normalisedTamFilter) {
      const rowTam = normaliseTamStatus(model?.tamDisplay || "");
      if (rowTam !== normalisedTamFilter) return;
    }
    if (searchTerm) {
      const haystackValues = [
        aircraft.ident,
        aircraft.registration,
        aircraft.aircraft_type,
        aircraft.tail,
        aircraft.msn,
        aircraft.owner,
        aircraft.operator,
        aircraft.base,
        aircraft.notes,
        aircraft.last_known_location,
        model?.categoryLabel,
        model?.phase,
        model?.tamDisplay,
        model?.timeline,
        model?.nextEventLabel,
      ]
        .map((value) => expandSearchTokens(value))
        .flat();
      if (!haystackValues.some((value) => value.includes(searchTerm))) return;
    }

    const regionValue = cleanText(aircraft.region);
    if (regionValue) scoped.add(regionValue);
  });

  return Array.from(scoped).sort((a, b) => a.localeCompare(b));
}

function populateRegionFilter() {
  if (!dom.regionFilter) return;
  const previous = state.regionFilter || "";
  const choices = getRegionFilterChoices();
  dom.regionFilter.innerHTML = "";
  const allOption = document.createElement("option");
  allOption.value = "";
  allOption.textContent = "All regions";
  dom.regionFilter.appendChild(allOption);
  const availableMap = new Map();
  choices.forEach((region) => {
    const option = document.createElement("option");
    option.value = region;
    option.textContent = region;
    dom.regionFilter.appendChild(option);
    availableMap.set(region.toLowerCase(), region);
  });
  let nextValue = "";
  if (previous) {
    const match = availableMap.get(previous.toLowerCase());
    if (match) nextValue = match;
  }
  dom.regionFilter.value = nextValue;
  state.regionFilter = nextValue;
  if (state.aircraftLimitActive) {
    dom.regionFilter.disabled = true;
    dom.regionFilter.title = `Region filter disabled while showing the first ${state.aircraftLimit} aircraft.`;
  } else {
    dom.regionFilter.disabled = false;
    dom.regionFilter.removeAttribute("title");
  }
}

function getTamFilterChoices() {
  const searchTerm = dom.searchInput?.value?.trim().toLowerCase() || "";
  const filtersDisabled = state.aircraftLimitActive;
  const ownerFilter = filtersDisabled ? "" : state.ownerFilter;
  const operatorFilter = filtersDisabled ? "" : state.operatorFilter;
  const regionFilter = filtersDisabled ? "" : state.regionFilter;

  const scoped = new Set();
  state.aircraft.forEach((aircraft) => {
    if (operatorFilter) {
      const operatorValue = cleanText(aircraft.operator);
      if (operatorValue !== operatorFilter) return;
    }
    if (ownerFilter) {
      const ownerValue = cleanText(aircraft.owner) || cleanText(aircraft.operator);
      if (ownerValue !== ownerFilter) return;
    }
    if (regionFilter) {
      const regionValue = cleanText(aircraft.region);
      if (regionValue !== regionFilter) return;
    }
    const model = composeStatusModel(aircraft);
    if (searchTerm) {
      const haystackValues = [
        aircraft.ident,
        aircraft.registration,
        aircraft.aircraft_type,
        aircraft.tail,
        aircraft.msn,
        aircraft.owner,
        aircraft.operator,
        aircraft.base,
        aircraft.notes,
        aircraft.last_known_location,
        model.categoryLabel,
        model.phase,
        model.tamDisplay,
        model.timeline,
        model.nextEventLabel,
      ]
        .map((value) => expandSearchTokens(value))
        .flat();
      if (!haystackValues.some((value) => value.includes(searchTerm))) return;
    }
    const normalised = normaliseTamStatus(model.tamDisplay || "");
    if (normalised) scoped.add(normalised);
  });

  return Array.from(scoped).sort((a, b) => a.localeCompare(b));
}

function populateTamFilter() {
  if (!dom.tamFilter) return;
  const previous = state.tamFilter || "";
  const choices = getTamFilterChoices();
  dom.tamFilter.innerHTML = "";
  const allOption = document.createElement("option");
  allOption.value = "";
  allOption.textContent = "All TAM statuses";
  dom.tamFilter.appendChild(allOption);
  const availableMap = new Map();
  choices.forEach((tam) => {
    const option = document.createElement("option");
    option.value = tam;
    option.textContent = tam;
    dom.tamFilter.appendChild(option);
    availableMap.set(tam.toLowerCase(), tam);
  });
  let nextValue = "";
  if (previous) {
    const match = availableMap.get(previous.toLowerCase());
    if (match) nextValue = match;
  }
  dom.tamFilter.value = nextValue;
  state.tamFilter = nextValue;
  if (state.aircraftLimitActive) {
    dom.tamFilter.disabled = true;
    dom.tamFilter.title = `TAM filter disabled while showing the first ${state.aircraftLimit} aircraft.`;
  } else {
    dom.tamFilter.disabled = false;
    dom.tamFilter.removeAttribute("title");
  }
}

function populateMeFleetFilter() {
  if (!dom.meFleetFilter) return;
  const nextValue = state.meFleetFilter || "";
  dom.meFleetFilter.value = nextValue;
  if (state.aircraftLimitActive) {
    dom.meFleetFilter.disabled = true;
    dom.meFleetFilter.title = `Fleet filter disabled while showing the first ${state.aircraftLimit} aircraft.`;
  } else {
    dom.meFleetFilter.disabled = false;
    dom.meFleetFilter.removeAttribute("title");
  }
}

function getOperatorDirectoryEntry(operatorId) {
  const numeric = Number(operatorId);
  if (!Number.isFinite(numeric)) {
    return null;
  }
  return state.operatorDirectory.find((entry) => Number(entry.id) === numeric) || null;
}

function populateOperatorSelector(currentProfile = null) {
  if (!dom.commentDialogOperator) {
    return;
  }
  const select = dom.commentDialogOperator;
  select.innerHTML = "";
  const current = currentProfile || state.activeCommentDialog?.profileInitial || null;

  const keepOption = document.createElement("option");
  keepOption.value = "";
  keepOption.dataset.mode = "keep";
  keepOption.textContent = current?.name ? `Keep ${current.name}` : "No operator change";
  select.appendChild(keepOption);

  const clearOption = document.createElement("option");
  clearOption.value = "__none__";
  clearOption.dataset.mode = "clear";
  clearOption.textContent = "Set to no operator";
  select.appendChild(clearOption);

  state.operatorDirectory.forEach((operator) => {
    const option = document.createElement("option");
    option.value = String(operator.id);
    option.textContent = operator.name;
    option.dataset.mode = "select";
    select.appendChild(option);
  });

  if (current?.id && getOperatorDirectoryEntry(current.id)) {
    select.value = String(current.id);
  } else if (current?.id === null) {
    select.value = "__none__";
  } else {
    select.value = "";
  }

  handleOperatorSelectionChange(current);
}

function handleOperatorSelectionChange(currentProfile = null) {
  if (!dom.commentDialogOperator) {
    return;
  }
}

async function hydrateStatusesFromCache(options = {}) {
  if (!state.sessionUser) {
    return { requested: 0, attempted: 0, loaded: 0, errors: 0 };
  }
  if (state.loadingCachedStatuses) {
    return { requested: 0, attempted: 0, loaded: 0, errors: 0 };
  }

  const force = Boolean(options.force);
  const rawIdents = state.aircraft
    .map((aircraft) => canonicalIdent(getStatusIdent(aircraft)))
    .filter(Boolean);
  if (!rawIdents.length) {
    return { requested: 0, attempted: 0, loaded: 0, errors: 0 };
  }

  const uniqueTargets = Array.from(new Set(rawIdents));
  // If force=true, refresh all. Otherwise, refresh if:
  // 1. No cached data exists, OR
  // 2. Cached data is older than 30 minutes (to pick up fresh cron data quickly)
  // Note: Reduced from 2 hours to 30 minutes to catch cron updates faster
  const now = Date.now();
  const STALE_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes
  const targets = force
    ? uniqueTargets
    : uniqueTargets.filter((ident) => {
        const cached = state.statuses.get(ident);
        const fetchedAt = state.statusFetchedAt.get(ident);
        // Refresh if no cache, or if cache is older than 2 hours
        if (!cached || !fetchedAt) return true;
        const age = now - fetchedAt;
        return age > STALE_THRESHOLD_MS;
      });

  if (!targets.length) {
    logEvent("Cached status preload skipped", { reason: "no targets", force });
    return { requested: uniqueTargets.length, attempted: 0, loaded: 0, errors: 0 };
  }

  state.loadingCachedStatuses = true;
  let loaded = 0;
  let errors = 0;
  let processed = 0;
  let latestFetched = 0;
  logEvent("Cached status preload started", { targets: targets.length, force });

  try {
    for (const batch of chunkArray(targets, CACHED_STATUS_BATCH_SIZE)) {
      try {
        const response = await fetchJson(apiUrl(`/api/flightaware/status/cache`), {
          method: "POST",
          body: JSON.stringify({ idents: batch }),
        });
        const entries = Array.isArray(response?.statuses) ? response.statuses : [];
        entries.forEach((entry) => {
          const identKey = canonicalIdent(entry.ident);
          if (!identKey) {
            return;
          }
          const statusPayload = entry.status && typeof entry.status === "object" ? entry.status : null;
          const fetchedStamp =
            Number.isFinite(Date.parse(entry.fetched_at))
              ? Date.parse(entry.fetched_at)
              : Number.isFinite(Date.parse(statusPayload?.updated_at))
              ? Date.parse(statusPayload.updated_at)
              : Date.now();
          if (Number.isFinite(fetchedStamp)) {
            latestFetched = Math.max(latestFetched, fetchedStamp);
          }
          const errorMessage = cleanText(entry.response_error);
          if (statusPayload) {
            applyStatusToAircraft(identKey, statusPayload, fetchedStamp);
            loaded += 1;
          } else if (errorMessage) {
            applyErrorStatusToAircraft(identKey, errorMessage, fetchedStamp);
          }
        });
        processed += batch.length;
        setRefreshIndicator(
          `Loaded stored FlightAware statuses (${Math.min(processed, targets.length)} of ${targets.length}).`
        );
      } catch (error) {
        errors += 1;
        logEvent("Cached status batch failed", { error: error.message, batch: batch.length });
      }
    }
  } finally {
    state.loadingCachedStatuses = false;
  }

  if (latestFetched) {
    state.lastRefreshTs = new Date(latestFetched);
  }
  if (loaded) {
    state.refreshErrors = 0;
  }
  renderTable();
  renderDataReport();
  updateLastUpdatedDisplay();
  logEvent("Cached status preload finished", {
    loaded,
    attempted: targets.length,
    errors,
    requested: uniqueTargets.length,
  });
  return { requested: uniqueTargets.length, attempted: targets.length, loaded, errors };
}

async function loadAircraft(force = false) {
  if (!state.sessionUser) {
    state.loadingAircraft = false;
    renderTable();
    return;
  }
  if (state.loadingAircraft) {
    if (force) {
      state.pendingAircraftReload = true;
    }
    return;
  }
  state.loadingAircraft = true;
  renderTable();
  try {
    const params = new URLSearchParams();
    // Admin users should see all aircraft - don't send owner filter
    const isAdmin = isAdminUser();
    const ownerQuery = isAdmin || state.aircraftLimitActive ? "" : cleanText(state.ownerFilter);
    if (devToolsEnabled && isAdmin) {
      logEvent("Admin aircraft load", { 
        isAdmin, 
        ownerFilter: state.ownerFilter, 
        ownerQuery,
        allowedOwners: state.sessionUser?.allowedOwners 
      });
    }
    if (ownerQuery) {
      params.set("owner", ownerQuery);
    }
    if (Number.isFinite(state.aircraftLimit) && state.aircraftLimit > 0) {
      params.set("limit", String(state.aircraftLimit));
    }
    const endpoint = `/api/aircraft${params.toString() ? `?${params.toString()}` : ""}`;
    if (devToolsEnabled && isAdmin) {
      logEvent("Admin fetching aircraft", { endpoint });
    }
    const data = await fetchJson(apiUrl(endpoint));
    const list = Array.isArray(data) ? data : Array.isArray(data?.aircraft) ? data.aircraft : [];
    const normalisedList = list.map((item) => applyAircraftNormalisation(item));
    let restoredFieldCount = 0;
    const mergedList = normalisedList.map((item) => {
      const { merged, restoredFields } = mergeAircraftWithSnapshot(item, { skipPersist: true });
      restoredFieldCount += restoredFields;
      return merged;
    });
    state.aircraft = mergedList;
    syncWatchState();
    renderAdminWatchSummary();
    if (isAdminUser()) {
      const reason = force ? "aircraft-reload" : "aircraft-load";
      loadAdminWatchSummary({ reason }).catch((error) => {
        logEvent("Watch summary refresh failed", { error: error.message, reason });
      });
    }
    if (restoredFieldCount > 0 && devToolsEnabled) {
      logEvent("Restored aircraft fields from snapshot", {
        restored: restoredFieldCount,
        tracked: state.aircraftSnapshot.size,
      });
    }
    scheduleAircraftSnapshotPersist();
    await loadOperatorDirectory();
    if (state.aircraftLimitActive) {
      logEvent("Development aircraft subset loaded", {
        limit: state.aircraftLimit,
        returned: state.aircraft.length,
      });
    }
    let ownerOptions = Array.from(
      new Set(normalisedList.map((aircraft) => cleanText(aircraft.owner)).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));
    const allowedOwnersForOptions = getAllowedOwners();
    if (Array.isArray(allowedOwnersForOptions)) {
      const allowedLower = new Set(allowedOwnersForOptions.map((owner) => owner.toLowerCase()));
      ownerOptions = ownerOptions.filter((owner) => allowedLower.has(owner.toLowerCase()));
      allowedOwnersForOptions.forEach((owner) => {
        if (!ownerOptions.some((existing) => existing.toLowerCase() === owner.toLowerCase())) {
          ownerOptions.push(owner);
        }
      });
    }
    state.ownerOptions = ownerOptions;
    ensureOwnerFilterIsValid();
    if (state.operatorDirectory.length) {
      state.operatorOptions = state.operatorDirectory.map((operator) => operator.name);
    } else {
      state.operatorOptions = Array.from(
        new Set(normalisedList.map((aircraft) => cleanText(aircraft.operator)).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b));
    }
    populateOwnerFilter();
    populateOperatorFilter();
    populateRegionFilter();
    populateTamFilter();
    populateMeFleetFilter();
    const nextStatuses = new Map();
    state.aircraft.forEach((aircraft) => {
      getLookupKeys(aircraft).forEach((key) => {
        if (state.statuses.has(key)) {
          nextStatuses.set(key, state.statuses.get(key));
        }
      });
    });
    state.statuses = nextStatuses;
    const validIdents = new Set(
      state.aircraft
        .map((item) => canonicalIdent(getStatusIdent(item)))
        .filter(Boolean)
    );
    state.statusFetchedAt = new Map(
      Array.from(state.statusFetchedAt.entries()).filter(([key]) => validIdents.has(key))
    );
    state.statusErrorStreak = new Map(
      Array.from(state.statusErrorStreak.entries()).filter(([key]) => validIdents.has(key))
    );
    const validIds = new Set(
      state.aircraft
        .map((item) => {
          const numeric = Number(item.id);
          return Number.isFinite(numeric) ? numeric : null;
        })
        .filter((value) => value !== null)
    );
    state.flightawareDebug = new Map(
      Array.from(state.flightawareDebug.entries()).filter(([key]) => {
        const numericKey = Number(key);
        return Number.isFinite(numericKey) && validIds.has(numericKey);
      })
    );
    state.aircraftLoadedAt = new Date();
    state.importReport = computeImportReport(state.aircraft);
    renderDataReport();
    logEvent("Aircraft import completed", {
      count: state.aircraft.length,
      ownerFilter: getOwnerFilterLabel() || "all",
      generatedAt: state.importReport.generatedAt,
    });
    setRefreshIndicator("Loading stored FlightAware statuses.");
    const preloadResult = await hydrateStatusesFromCache({ force: true });
    if (preloadResult.loaded > 0) {
      setRefreshIndicator(
        `Loaded ${preloadResult.loaded} cached FlightAware statuses. Manual Refresh to pull live updates.`
      );
    } else if (preloadResult.attempted > 0 && preloadResult.errors >= preloadResult.attempted) {
      setRefreshIndicator(
        "Aircraft list updated. Cached FlightAware statuses were unavailable; use Manual Refresh for live data."
      );
    } else {
      setRefreshIndicator("Aircraft list updated. Use Manual Refresh to pull FlightAware statuses.");
    }
    tryOpenCommentFromUrl();
  } catch (error) {
    logEvent("Failed to load aircraft", { error: error.message });
    state.aircraft = [];
    state.aircraftLoadedAt = null;
    state.importReport = null;
    state.onWatch.clear();
    scheduleOnWatchSnapshotPersist();
    renderAdminWatchSummary();
    renderDataReport();
    setRefreshIndicator(`Unable to load aircraft: ${error.message}`);
  } finally {
    state.loadingAircraft = false;
    renderTable();
    if (state.pendingAircraftReload) {
      state.pendingAircraftReload = false;
      loadAircraft();
    }
  }
}

function ensureCommentsFetched(aircraftId, context = {}) {
  if (!state.sessionUser) {
    return;
  }
  const existing = state.comments.get(aircraftId);
  if (existing?.loading || commentInFlight.has(aircraftId)) {
    updateCommentCell(aircraftId, context.aircraft, context.model);
    return;
  }
  if (commentQueue.includes(aircraftId)) {
    updateCommentCell(aircraftId, context.aircraft, context.model);
    return;
  }
  commentInFlight.add(aircraftId);
  state.comments.set(aircraftId, {
    ...existing,
    loading: true,
    items: Array.isArray(existing?.items) ? existing.items : [],
    error: null,
  });
  updateCommentCell(aircraftId, context.aircraft, context.model);
  commentQueue.push(aircraftId);
  processCommentQueue();
}

async function processCommentQueue() {
  if (!state.sessionUser) {
    commentQueue.length = 0;
    return;
  }
  if (activeCommentFetches >= MAX_COMMENT_CONCURRENCY) {
    return;
  }
  const nextId = commentQueue.shift();
  if (typeof nextId === "undefined") {
    return;
  }
  activeCommentFetches += 1;
  try {
    await fetchCommentsFor(nextId, { limit: 10, viaQueue: true });
  } finally {
    activeCommentFetches -= 1;
    commentInFlight.delete(nextId);
    if (commentQueue.length) {
      processCommentQueue();
    }
  }
}

async function fetchCommentsFor(aircraftId, options = {}) {
  if (!state.sessionUser) {
    return;
  }
  const limit = Number.isFinite(options.limit) && options.limit > 0 ? Math.min(Math.floor(options.limit), 100) : 10;
  const existing = state.comments.get(aircraftId) || { items: [] };
  state.comments.set(aircraftId, {
    ...existing,
    loading: true,
    error: null,
  });
  updateCommentCell(aircraftId);
  updateCommentDialogHistorySections();
  try {
    const data = await fetchJson(apiUrl(`/api/aircraft/${aircraftId}/comments?limit=${limit}`));
    const items = Array.isArray(data) ? data : Array.isArray(data?.comments) ? data.comments : [];
    const normalised = (items || [])
      .map((item) => ({
        ...item,
        author: cleanText(item.author) || "system",
      }))
      .sort((left, right) => {
        const leftTs = Date.parse(left.created_at || "");
        const rightTs = Date.parse(right.created_at || "");
        const safeLeft = Number.isNaN(leftTs) ? 0 : leftTs;
        const safeRight = Number.isNaN(rightTs) ? 0 : rightTs;
        return safeRight - safeLeft;
      });
    state.comments.set(aircraftId, { loading: false, items: normalised, error: null });
  } catch (error) {
    logEvent("Failed to load comments", { aircraftId, error: error.message });
    state.comments.set(aircraftId, {
      loading: false,
      items: existing.items || [],
      error: error.message,
    });
  }
  updateCommentCell(aircraftId);
  updateCommentDialogHistorySections();
}

function getStatusHistoryState(aircraftId) {
  const key = Number(aircraftId);
  if (!state.statusHistory.has(key)) {
    state.statusHistory.set(key, { loading: false, error: null, items: [] });
  }
  return state.statusHistory.get(key);
}

async function loadStatusHistory(aircraftId, { force = false } = {}) {
  if (!state.sessionUser) {
    return;
  }
  const key = Number(aircraftId);
  const existing = getStatusHistoryState(key);
  if (existing.loading && !force) {
    return;
  }
  state.statusHistory.set(key, { ...existing, loading: true, error: null });
  updateCommentDialogHistorySections();
  try {
    const data = await fetchJson(apiUrl(`/api/aircraft/${aircraftId}/status-history?limit=50`));
    const items = Array.isArray(data) ? data : Array.isArray(data?.rows) ? data.rows : [];
    const normalised = (items || [])
      .map((item) => ({
        ...item,
        status: cleanText(item.status) || null,
        source: cleanText(item.source) || null,
        user_name: cleanText(item.user_name) || null,
        updated_at: item.updated_at ? new Date(item.updated_at).toISOString() : null,
      }))
      .sort((left, right) => {
        const leftTs = Date.parse(left.updated_at || "");
        const rightTs = Date.parse(right.updated_at || "");
        const safeLeft = Number.isNaN(leftTs) ? 0 : leftTs;
        const safeRight = Number.isNaN(rightTs) ? 0 : rightTs;
        return safeLeft - safeRight;
      });
    state.statusHistory.set(key, { loading: false, error: null, items: normalised });
  } catch (error) {
    logEvent("Failed to load status history", { aircraftId, error: error.message });
    state.statusHistory.set(key, { ...existing, loading: false, error: error.message });
  }
  updateCommentDialogHistorySections();
}

function updateCommentDialogHistorySections() {
  const context = state.activeCommentDialog;
  if (!context) {
    return;
  }
  renderStatusHistorySection(context.aircraft.id);
  renderCommentHistorySection(context.aircraft.id);
}

function renderStatusHistorySection(aircraftId) {
  if (!dom.commentDialogStatusHistory || !state.activeCommentDialog) {
    return;
  }
  const key = Number(aircraftId);
  const entry = state.statusHistory.get(key) || { loading: false, error: null, items: [] };
  const list = dom.commentDialogStatusHistoryList;
  const empty = dom.commentDialogStatusHistoryEmpty;
  const meta = dom.commentDialogStatusHistoryMeta;
  if (!list || !empty || !meta) {
    return;
  }
  list.replaceChildren();
  const parts = [];
  if (entry.loading) {
    parts.push("Loading TAM history…");
  }
  if (entry.error) {
    parts.push(`Error: ${entry.error}`);
  }
  if (parts.length) {
    meta.textContent = parts.join(" · ");
  } else if (entry.items?.length) {
    meta.textContent = `${entry.items.length} record${entry.items.length === 1 ? "" : "s"}`;
  } else {
    meta.textContent = "";
  }

  if (entry.loading) {
    empty.hidden = false;
    empty.textContent = "Loading TAM history…";
    return;
  }

  if (entry.error) {
    empty.hidden = false;
    empty.textContent = `Unable to load TAM history: ${entry.error}`;
    return;
  }

  const items = Array.isArray(entry.items) ? entry.items : [];
  if (!items.length) {
    empty.hidden = false;
    empty.textContent = "No saved TAM updates yet.";
    return;
  }
  empty.hidden = true;

  items.forEach((item) => {
    const row = createElement("div", "history-row");
    const labelParts = [];
    if (item.status) {
      labelParts.push(item.status);
    }
    if (item.source) {
      labelParts.push(item.source);
    }
    row.appendChild(createElement("div", "history-row__primary", labelParts.join(" · ") || "Unknown status"));
    if (item.updated_at) {
      const relative = formatRelative(item.updated_at);
      const absolute = formatDisplayTimestamp(item.updated_at);
      const metaLine = relative && absolute && relative !== absolute ? `${absolute} (${relative})` : absolute || relative;
      if (metaLine) {
        row.appendChild(createElement("div", "history-row__meta", metaLine));
      }
    }
    if (item.id !== null && item.id !== undefined) {
      const deleteButton = document.createElement("button");
      deleteButton.type = "button";
      deleteButton.className = "history-row__delete";
      deleteButton.textContent = "×";
      deleteButton.title = "Delete TAM update";
      deleteButton.addEventListener("click", () => {
        handleStatusHistoryDelete(aircraftId, item.id, deleteButton);
      });
      row.appendChild(deleteButton);
    }
    list.appendChild(row);
  });
}

function renderCommentHistorySection(aircraftId) {
  if (!dom.commentDialogCommentsHistory) {
    return;
  }
  const entry = state.comments.get(aircraftId) || { items: [], loading: false, error: null };
  const list = dom.commentDialogCommentsHistoryList;
  const empty = dom.commentDialogCommentsHistoryEmpty;
  const meta = dom.commentDialogCommentsHistoryMeta;
  if (!list || !empty || !meta) {
    return;
  }
  list.replaceChildren();
  const info = [];
  if (entry.loading) {
    info.push("Loading comments…");
  }
  if (entry.error) {
    info.push(`Error: ${entry.error}`);
  }
  if (info.length) {
    meta.textContent = info.join(" · ");
  } else if (entry.items?.length) {
    meta.textContent = `${entry.items.length} comment${entry.items.length === 1 ? "" : "s"}`;
  } else {
    meta.textContent = "";
  }

  if (entry.loading) {
    empty.hidden = false;
    empty.textContent = "Loading comments…";
    return;
  }

  if (entry.error) {
    empty.hidden = false;
    empty.textContent = `Unable to load comments: ${entry.error}`;
    return;
  }

  const items = Array.isArray(entry.items) ? [...entry.items] : [];
  if (!items.length) {
    empty.hidden = false;
    empty.textContent = "No comments recorded yet.";
    return;
  }
  empty.hidden = true;

  items
    .slice()
    .sort((left, right) => {
      const leftTs = Date.parse(left.created_at || "");
      const rightTs = Date.parse(right.created_at || "");
      const safeLeft = Number.isNaN(leftTs) ? 0 : leftTs;
      const safeRight = Number.isNaN(rightTs) ? 0 : rightTs;
      return safeLeft - safeRight;
    })
    .forEach((comment) => {
      const row = createElement("div", "history-row");
      row.appendChild(createElement("div", "history-row__primary", comment.body || "(empty comment)"));
      const metaParts = [];
      if (comment.author) {
        metaParts.push(comment.author);
      }
      if (comment.created_at) {
        const relative = formatRelative(comment.created_at);
        const absolute = formatDisplayTimestamp(comment.created_at);
        if (absolute && relative && absolute !== relative) {
          metaParts.push(`${absolute} (${relative})`);
        } else if (absolute || relative) {
          metaParts.push(absolute || relative);
        }
      }
      if (metaParts.length) {
        row.appendChild(createElement("div", "history-row__meta", metaParts.join(" · ")));
      }
      const deleteButton = document.createElement("button");
      deleteButton.type = "button";
      deleteButton.className = "history-row__delete";
      deleteButton.textContent = "🗑";
      deleteButton.title = "Delete comment";
      deleteButton.addEventListener("click", () => {
        handleCommentHistoryDelete(aircraftId, comment.id, deleteButton);
      });
      row.appendChild(deleteButton);
      list.appendChild(row);
    });
}

async function handleStatusHistoryDelete(aircraftId, statusId, button) {
  button.disabled = true;
  try {
    await fetchJson(apiUrl(`/api/aircraft/${aircraftId}/status-history/${encodeURIComponent(statusId)}`), {
      method: "DELETE",
    });
    await loadStatusHistory(aircraftId, { force: true });
  } catch (error) {
    button.disabled = false;
    button.title = error.message || "Unable to delete TAM update";
    updateCommentDialogHistorySections();
  }
}

async function handleCommentHistoryDelete(aircraftId, commentId, button) {
  button.disabled = true;
  try {
    await fetchJson(apiUrl(`/api/aircraft/${aircraftId}/comments/${encodeURIComponent(commentId)}`), {
      method: "DELETE",
    });
    await fetchCommentsFor(aircraftId, { limit: 50 });
  } catch (error) {
    button.disabled = false;
    button.title = error.message || "Unable to delete comment";
    updateCommentDialogHistorySections();
  }
}

async function submitComment(aircraftId, text) {
  const trimmed = String(text || "").trim();
  if (!trimmed) return;
  if (!state.sessionUser) return;
  const commentState = state.comments.get(aircraftId) || { items: [] };
  const optimisticComment = {
    id: `tmp-${Date.now()}`,
    body: trimmed,
    author: getActiveUserLabel() || "system",
    created_at: new Date().toISOString(),
  };
  commentState.items = [optimisticComment, ...(commentState.items || [])];
  commentState.submitting = true;
  state.comments.set(aircraftId, commentState);
  updateCommentCell(aircraftId);
  try {
    const saved = await fetchJson(apiUrl(`/api/aircraft/${aircraftId}/comments`), {
      method: "POST",
      body: JSON.stringify({ body: trimmed }),
    });
    const updated = state.comments.get(aircraftId) || { items: [] };
    const normalisedSaved = {
      ...saved,
      author: cleanText(saved?.author) || getActiveUserLabel() || "system",
    };
    updated.items = [
      normalisedSaved,
      ...updated.items
        .filter((item) => item.id !== optimisticComment.id)
        .map((item) => ({
          ...item,
          author: cleanText(item.author) || getActiveUserLabel() || "system",
        })),
    ];
    updated.submitting = false;
    state.comments.set(aircraftId, updated);
    const target = state.aircraft.find((item) => item.id === aircraftId);
    if (target) {
      target.comment_count = Number(target.comment_count || 0) + 1;
      target.last_comment = saved;
      recordAircraftSnapshot(target);
    }
    updateCommentCell(aircraftId);
    if (state.sortKey === "comments") {
      renderTable();
    }
    return true;
  } catch (error) {
    logEvent("Failed to submit comment", { error: error.message });
    const updated = state.comments.get(aircraftId) || { items: [] };
    updated.items = updated.items.filter((item) => item.id !== optimisticComment.id);
    updated.submitting = false;
    updated.error = error.message;
    state.comments.set(aircraftId, updated);
    updateCommentCell(aircraftId);
    return false;
  }
}

function getAutoTamKey(aircraft) {
  if (!aircraft) {
    return null;
  }
  const numericId = Number(aircraft.id);
  if (Number.isFinite(numericId)) {
    return numericId;
  }
  const fallback =
    cleanText(aircraft.ident) ||
    cleanText(aircraft.registration) ||
    cleanText(aircraft.msn) ||
    cleanText(aircraft.tail);
  return fallback || null;
}

async function maybeAutoRestoreTamStatus(aircraft, model) {
  if (!aircraft || !model) {
    return;
  }
  const tamNormalised = normaliseTamStatus(aircraft.tam_status);
  if (tamNormalised === "IN SERVICE") {
    return;
  }
  const activeAgain = Boolean(model.isAirborne || model.hasRecentFlight);
  if (!activeAgain) {
    return;
  }
  const key = getAutoTamKey(aircraft);
  if (key === null) {
    return;
  }
  if (state.autoTamPending.has(key)) {
    return;
  }
  state.autoTamPending.add(key);
  try {
    await submitTamStatus(aircraft, "IN SERVICE", {
      auto: true,
      skipReload: true,
      source: "auto-return",
      userName: "system",
    });
    state.autoTamHistory.set(key, new Date().toISOString());
    logEvent("Auto TAM reset to IN SERVICE", { aircraftId: aircraft.id });
  } catch (error) {
    logEvent("Auto TAM update failed", { aircraftId: aircraft.id, error: error.message });
  } finally {
    state.autoTamPending.delete(key);
  }
}

async function submitTamStatus(aircraft, statusText, options = {}) {
  const trimmed = String(statusText || "").trim();
  if (!trimmed) return;
  const nextStatus = trimmed.toUpperCase();
  const auto = Boolean(options.auto);
  const skipReload = Boolean(options.skipReload);
  const force = Boolean(options.force);
  if (!state.sessionUser && !auto) {
    handleUnauthenticated("Session expired. Please sign in again.");
    return;
  }
  const resolvedSource =
    cleanText(options.source) ||
    (auto ? "system" : cleanText(DEFAULT_TAM_SOURCE)) ||
    "ui";
  const resolvedUser =
    cleanText(options.userName) ||
    (auto ? "system" : getActiveUserLabel() || cleanText(DEFAULT_TAM_USER) || "ops");
  const payload = {
    status: nextStatus,
    source: resolvedSource,
    userName: resolvedUser,
    auto,
  };
  if (Object.prototype.hasOwnProperty.call(options, "expectedReleaseDate")) {
    const er = options.expectedReleaseDate;
    if (er === null || er === undefined || er === "") {
      payload.expected_release_date = null;
    } else {
      payload.expected_release_date = String(er).trim();
    }
  }
  try {
    const response = await fetchJson(apiUrl(`/api/aircraft/${aircraft.id}/tam-status`), {
      method: "POST",
      body: JSON.stringify(payload),
    });
    logEvent("TAM status updated", {
      aircraftId: aircraft.id,
      status: nextStatus,
      automatic: auto,
      confirmed: force,
      watchEnabled: Boolean(response.watch?.onWatch),
    });
    if (response.watch) {
      updateWatchEntryInState(aircraft.id, response.watch);
    }
    const target = state.aircraft.find((item) => item.id === aircraft.id);
    if (target) {
      target.tam_status = nextStatus;
      target.tam_status_source = response.status?.source || payload.source;
      target.tam_status_user = response.status?.userName || payload.userName;
      target.tam_status_updated_at = response.status?.updatedAt || new Date().toISOString();
      recordAircraftSnapshot(target);
    }
    renderTable();
    renderDataReport();
    if (!auto && !skipReload) {
      await loadAircraft(true);
      await refreshStatuses({ force: true, aircraftIds: [aircraft.id] });
      setRefreshIndicator(`TAM status updated ${new Date().toLocaleTimeString()}`);
    }
    return response;
  } catch (error) {
    logEvent("Failed to update TAM status", { aircraftId: aircraft.id, error: error.message });
    throw error;
  }
}

async function submitManualStatusOverride(aircraft, override = {}) {
  if (!state.sessionUser) {
    handleUnauthenticated("Session expired. Please sign in again.");
    return;
  }
  const statusText = cleanText(override.statusText) || null;
  const lastFlightAt = override.lastFlightAt || null;
  const lastLocation = normaliseManualLocationValue(override.lastLocation);
  const payload = {
    statusText,
    lastFlightAt,
    lastLocation,
  };
  try {
    const response = await fetchJson(apiUrl(`/api/aircraft/${aircraft.id}/manual-status`), {
      method: "POST",
      body: JSON.stringify(payload),
    });
    logEvent("Manual status updated", { aircraftId: aircraft.id });
    const target = state.aircraft.find((item) => item.id === aircraft.id);
    if (target) {
      const overrideResponse = response?.override || {};
      const updatedByFallback = getActiveUserLabel() || "ui";
      const statusOverrideText = cleanText(
        overrideResponse.status_text ?? payload.statusText ?? null
      );
      target.manual_status_text = statusOverrideText ? statusOverrideText.toUpperCase() : null;
      target.manual_status_last_flight = overrideResponse.last_flight_at ?? payload.lastFlightAt ?? null;
      target.manual_status_location = normaliseManualLocationValue(
        overrideResponse.last_location ?? payload.lastLocation ?? null
      );
      target.manual_status_updated_at = overrideResponse.updated_at ?? new Date().toISOString();
      target.manual_status_updated_by =
        overrideResponse.updated_by ?? updatedByFallback;
      if (
        state.activeCommentDialog &&
        Number(state.activeCommentDialog.aircraft?.id) === Number(aircraft.id)
      ) {
        state.activeCommentDialog.manualStatusInitial = {
          text: target.manual_status_text || "",
          lastFlight: target.manual_status_last_flight || "",
          location: target.manual_status_location || "",
        };
        state.activeCommentDialog.aircraft = target;
      }
      recordAircraftSnapshot(target);
    }
    renderTable();
    return response;
  } catch (error) {
    logEvent("Failed to update manual status", { aircraftId: aircraft.id, error: error.message });
    throw error;
  }
}

function shouldRetryStatusFetch(error) {
  const statusCode = Number(error?.status ?? error?.response?.status);
  if (Number.isFinite(statusCode)) {
    if ([400, 401, 403, 404].includes(statusCode)) {
      return false;
    }
    if (statusCode === 429) {
      return false;
    }
  }
  return true;
}

async function fetchStatusFor(aircraft) {
  if (!state.sessionUser) return;
  const ident = getStatusIdent(aircraft);
  const lookupKey = canonicalIdent(ident);
  if (!ident || !lookupKey) return;
  const keys = getLookupKeys(aircraft);

  const attemptFetch = async (attempt = 1) => {
    try {
      // CRITICAL FIX: Pass registration explicitly to ensure registration-first lookup
      // This ensures we get the CURRENT flight for the aircraft, not an old flight number
      const registration = cleanText(aircraft.registration) || cleanText(aircraft.tail);
      const url = registration 
        ? apiUrl(`/api/flightaware/status?ident=${encodeURIComponent(ident)}&registration=${encodeURIComponent(registration)}`)
        : apiUrl(`/api/flightaware/status?ident=${encodeURIComponent(ident)}`);
      const status = await fetchJson(url);
      
      // BULLETPROOF FIX: Check if status is null/empty BEFORE applying
      // This handles cases where FlightAware returns successfully but with no data
      const hasValidStatus = status && typeof status === "object" && 
        (status.on || status.off || status.destination || status.origin || status.position || status.phase);
      
      if (!hasValidStatus) {
        // FlightAware returned null/empty - try to load last known data from cache
        let hasCachedData = false;
        try {
          const cacheResponse = await fetchJson(apiUrl(`/api/flightaware/status/cache`), {
            method: "POST",
            body: JSON.stringify({ idents: [ident] }),
          });
          const cachedEntries = Array.isArray(cacheResponse?.statuses) ? cacheResponse.statuses : [];
          if (cachedEntries.length > 0) {
            const cachedEntry = cachedEntries[0];
            const cachedStatus = cachedEntry.status && typeof cachedEntry.status === "object" ? cachedEntry.status : null;
            if (cachedStatus && (cachedStatus.on || cachedStatus.off || cachedStatus.destination || cachedStatus.origin || cachedStatus.position || cachedStatus.phase)) {
              // Use cached data instead of null/empty response
              logEvent("FlightAware returned empty data, using cached data as fallback", { ident, cachedAt: cachedEntry.fetched_at });
              const cachedFetchedStamp = Number.isFinite(Date.parse(cachedEntry.fetched_at))
                ? Date.parse(cachedEntry.fetched_at)
                : Number.isFinite(Date.parse(cachedStatus?.updated_at))
                ? Date.parse(cachedStatus.updated_at)
                : Date.now();
              keys.forEach((key) => {
                state.statuses.set(key, {
                  ...cachedStatus,
                  _fallback: true, // Mark as fallback data
                  _emptyResponse: true, // Mark that FlightAware returned empty
                });
              });
              if (Number.isFinite(cachedFetchedStamp)) {
                state.statusFetchedAt.set(lookupKey, cachedFetchedStamp);
              }
              hasCachedData = true;
              // Still persist the empty response to database for tracking
              await persistStatusSnapshot({
                ident,
                status: null,
                fetchedAt: new Date().toISOString(),
                responseStatus: 200,
                responseError: "FlightAware returned empty data, using cached fallback",
                source: "ui-manual-empty",
                aliases: keys,
              });
              // Use cached data for display
              const refreshedAircraft =
                state.aircraft.find((item) => Number(item.id) === Number(aircraft.id)) || aircraft;
              const refreshedModel = composeStatusModel(refreshedAircraft);
              await maybeAutoRestoreTamStatus(refreshedAircraft, refreshedModel);
              return cachedStatus; // Return cached data instead of null
            }
          }
        } catch (cacheError) {
          // Non-critical - if cache lookup fails, continue with null status
          logEvent("Cache fallback lookup failed for empty response", { ident, error: cacheError.message });
        }
        
        // If no cached data found, preserve existing status (don't overwrite with null)
        if (!hasCachedData) {
          const existing = state.statuses.get(keys[0]);
          if (existing) {
            logEvent("FlightAware returned empty, preserving existing status", { ident });
            // Don't overwrite - just persist the empty response for tracking
            await persistStatusSnapshot({
              ident,
              status: null,
              fetchedAt: new Date().toISOString(),
              responseStatus: 200,
              responseError: "FlightAware returned empty data, preserving existing status",
              source: "ui-manual-empty",
              aliases: keys,
            });
            return existing; // Return existing status instead of null
          }
        }
      }
      
      // Normal path: status has valid data
      await persistStatusSnapshot({
        ident,
        status,
        fetchedAt: status?.updated_at,
        responseStatus: 200,
        source: "ui-manual",
        aliases: keys,
      });
      const ts = Number.isFinite(Date.parse(status?.updated_at))
        ? Date.parse(status.updated_at)
        : Date.now();
      applyStatusToAircraft(lookupKey, status, ts);
      const refreshedAircraft =
        state.aircraft.find((item) => Number(item.id) === Number(aircraft.id)) || aircraft;
      const refreshedModel = composeStatusModel(refreshedAircraft);
      await maybeAutoRestoreTamStatus(refreshedAircraft, refreshedModel);
      return status;
    } catch (error) {
      await persistStatusSnapshot({
        ident,
        status: null,
        responseStatus: Number.isFinite(error?.status) ? Number(error.status) : null,
        responseError: error?.message || "unknown error",
        source: "ui-error",
        aliases: keys,
      });
      const attemptMeta = { ident, attempt, error: error.message };
      logEvent("Status fetch failed", attemptMeta);
      const errorStamp = new Date().toISOString();
      
      // CRITICAL FIX: Before showing "unknown", try to load last known data from database cache
      // This preserves the last recorded flight data when FlightAware fails
      let hasCachedData = false;
      const previous = state.statuses.get(keys[0]);
      if (!previous && error.status !== 429 && error.status !== 401) {
        try {
          // Try to load from database cache as fallback
          const cacheResponse = await fetchJson(apiUrl(`/api/flightaware/status/cache`), {
            method: "POST",
            body: JSON.stringify({ idents: [ident] }),
          });
          const cachedEntries = Array.isArray(cacheResponse?.statuses) ? cacheResponse.statuses : [];
          if (cachedEntries.length > 0) {
            const cachedEntry = cachedEntries[0];
            const cachedStatus = cachedEntry.status && typeof cachedEntry.status === "object" ? cachedEntry.status : null;
            if (cachedStatus) {
              const cachedFetchedStamp = Number.isFinite(Date.parse(cachedEntry.fetched_at))
                ? Date.parse(cachedEntry.fetched_at)
                : Number.isFinite(Date.parse(cachedStatus?.updated_at))
                ? Date.parse(cachedStatus.updated_at)
                : Date.now();
              // Use cached data instead of showing "unknown"
              keys.forEach((key) => {
                state.statuses.set(key, {
                  ...cachedStatus,
                  error: error.message,
                  last_error_at: errorStamp,
                  _fallback: true, // Mark as fallback data
                });
              });
              if (Number.isFinite(cachedFetchedStamp)) {
                state.statusFetchedAt.set(lookupKey, cachedFetchedStamp);
              }
              hasCachedData = true;
              logEvent("Status fetch failed, using cached data as fallback", { ident, cachedAt: cachedEntry.fetched_at });
            }
          }
        } catch (cacheError) {
          // Non-critical - if cache lookup fails, continue with error handling
          logEvent("Cache fallback lookup failed", { ident, error: cacheError.message });
        }
      }
      
      // Only set "unknown" if we don't have cached data to fall back to
      if (!hasCachedData) {
        keys.forEach((key) => {
          const previous = state.statuses.get(key);
          if (previous) {
            state.statuses.set(key, { ...previous, error: error.message, last_error_at: errorStamp });
          } else if (error.status !== 429 && error.status !== 401) {
            state.statuses.set(key, {
              phase: "unknown",
              error: error.message,
              updated_at: errorStamp,
            });
          }
        });
      }
      
      state.statusFetchedAt.set(lookupKey, Date.now());
      const nextStreak = (state.statusErrorStreak.get(lookupKey) || 0) + 1;
      state.statusErrorStreak.set(lookupKey, nextStreak);
      updateStatusDetailIfActive(aircraft.id);

      const canRetry = attempt < STATUS_FETCH_MAX_ATTEMPTS && shouldRetryStatusFetch(error);
      if (canRetry) {
        await new Promise((resolve) => setTimeout(resolve, STATUS_RETRY_DELAY_MS));
        logEvent("Retrying status fetch", { ident, attempt: attempt + 1 });
        return attemptFetch(attempt + 1);
      }

      if (!canRetry && nextStreak < STATUS_FETCH_MAX_ATTEMPTS) {
        state.statusErrorStreak.set(lookupKey, STATUS_FETCH_MAX_ATTEMPTS);
      }
      logEvent("Status fetch exhausted retries", { ident, attempts: attempt, error: error.message });
      throw error;
    }
  };

  return attemptFetch(1);
}

async function refreshSingleStatus(aircraft, triggerButton) {
  if (!state.sessionUser) {
    handleUnauthenticated("Session expired. Please sign in again.");
    return;
  }
  const label =
    cleanText(aircraft.registration) ||
    cleanText(aircraft.ident) ||
    cleanText(aircraft.tail) ||
    `Aircraft ${aircraft.id}`;

  if (triggerButton && triggerButton.isConnected) {
    triggerButton.disabled = true;
    if (!triggerButton.dataset.originalLabel) {
      triggerButton.dataset.originalLabel = triggerButton.textContent || "Refresh";
    }
    triggerButton.textContent = "Refreshing…";
    triggerButton.setAttribute("aria-busy", "true");
    triggerButton.removeAttribute("title");
    delete triggerButton.dataset.error;
  }

  let errorMessage = null;
  try {
    await fetchStatusFor(aircraft);
  } catch (error) {
    errorMessage = error?.message || "Unable to refresh status.";
  }

  if (triggerButton && triggerButton.isConnected) {
    triggerButton.disabled = false;
    triggerButton.removeAttribute("aria-busy");
    const originalLabel = triggerButton.dataset.originalLabel || "Refresh";
    triggerButton.textContent = errorMessage ? "Retry" : originalLabel;
    if (errorMessage) {
      triggerButton.title = errorMessage;
      triggerButton.dataset.error = errorMessage;
    } else {
      triggerButton.removeAttribute("title");
      delete triggerButton.dataset.error;
    }
  }

  renderTable();
  renderDataReport();

  if (errorMessage) {
    setRefreshIndicator(`Refresh failed for ${label}: ${errorMessage}`);
  } else {
    setRefreshIndicator(`Status refreshed for ${label} ${new Date().toLocaleTimeString()}`);
  }
}

async function refreshStatuses(forceOrOptions = {}) {
  if (!state.sessionUser) return;

  const options =
    typeof forceOrOptions === "object" && forceOrOptions !== null
      ? forceOrOptions
      : { force: Boolean(forceOrOptions) };
  const { force = false, visibleOnly = false, aircraftIds = [] } = options;
  const summaryReason = force ? "manual-refresh" : "auto-refresh";
  const shouldRefreshWatchSummary = force && isAdminUser();
  const refreshWatchSummary = () => {
    if (!shouldRefreshWatchSummary) {
      return;
    }
    loadAdminWatchSummary({ reason: summaryReason }).catch((error) => {
      logEvent("Watch summary refresh failed", { error: error.message, reason: summaryReason });
    });
  };

  if (!force && state.loadingStatuses) return;
  if (!state.aircraft.length) {
    refreshWatchSummary();
    return;
  }

  let targets = state.aircraft;
  if (Array.isArray(aircraftIds) && aircraftIds.length) {
    const idSet = new Set(
      aircraftIds
        .map((value) => {
          const numeric = Number(value);
          return Number.isFinite(numeric) ? numeric : null;
        })
        .filter((value) => value !== null)
    );
    targets = state.aircraft.filter((aircraft) => idSet.has(Number(aircraft.id)));
  } else if (visibleOnly) {
    const visibleRows = buildVisibleRows();
    const seen = new Set();
    targets = visibleRows
      .map((row) => row.aircraft)
      .filter((aircraft) => {
        const numericId = Number(aircraft.id);
        if (!Number.isFinite(numericId)) {
          return true;
        }
        if (seen.has(numericId)) {
          return false;
        }
        seen.add(numericId);
        return true;
      });
  }

  if (!targets.length) {
    refreshWatchSummary();
    return;
  }

  const now = Date.now();
  if (!force && state.flightawarePauseUntil && now < state.flightawarePauseUntil) {
    const resumeIso = new Date(state.flightawarePauseUntil).toISOString();
    setRefreshIndicator(`FlightAware pause active. Next retry ${formatRelative(resumeIso)}`);
    refreshWatchSummary();
    return;
  }

  state.loadingStatuses = true;
  const candidates = force
    ? targets
    : targets.filter((aircraft) => shouldRefreshStatusForAircraft(aircraft, now));
  if (!candidates.length) {
    state.loadingStatuses = false;
    state.lastRefreshTs = new Date();
    setRefreshIndicator(`Status updated ${formatRelative(state.lastRefreshTs)}`);
    updateLastUpdatedDisplay();
    renderTable();
    refreshWatchSummary();
    return;
  }

  let processed = 0;
  let failures = 0;
  let rateLimited = false;

  const limitedByFilter =
    (visibleOnly && targets.length < state.aircraft.length) ||
    (Array.isArray(aircraftIds) && aircraftIds.length);
  const scopeLabel = limitedByFilter ? "filtered" : "all";
  setRefreshIndicator(`Fetching FlightAware status (0 of ${candidates.length}, ${scopeLabel} view).`);

  for (const aircraft of candidates) {
    if (!state.sessionUser) {
      break;
    }
    try {
      await fetchStatusFor(aircraft);
      renderTable();
      renderDataReport();
    } catch (error) {
      failures += 1;
      if (error?.status === 429) {
        rateLimited = true;
        break;
      }
    } finally {
      processed += 1;
      setRefreshIndicator(
        `Fetching FlightAware status (${Math.min(processed, candidates.length)} of ${candidates.length}).`
      );
    }
    if (processed < candidates.length) {
      await new Promise((resolve) => setTimeout(resolve, STATUS_REQUEST_DELAY_MS));
    }
  }

  state.loadingStatuses = false;
  state.lastRefreshTs = new Date();
  renderTable();
  renderDataReport();
  updateLastUpdatedDisplay();

  if (rateLimited) {
    state.refreshErrors += 1;
    state.flightawarePauseUntil = Date.now() + RATE_LIMIT_BACKOFF_MS;
    const resumeIso = new Date(state.flightawarePauseUntil).toISOString();
    setRefreshIndicator(`FlightAware rate limit. Next retry ${formatRelative(resumeIso)}`);
    refreshWatchSummary();
    return;
  }

  state.flightawarePauseUntil = 0;

  if (failures) {
    state.refreshErrors += 1;
    setRefreshIndicator(
      `Status updated ${formatRelative(state.lastRefreshTs)} · ${failures} failed ${failures === 1 ? "request" : "requests"}.`
    );
  } else {
    state.refreshErrors = 0;
    setRefreshIndicator(`Status updated ${formatRelative(state.lastRefreshTs)} (manual trigger).`);
  }
  refreshWatchSummary();

  // Fetch status changes after refresh completes
  fetchStatusChanges();

  // ME verification (FR24 vs FlightAware) after manual refresh.
  // Runs server-side only (token stays on server). Admin-only endpoint.
  if (force && isAdminUser() && (cleanText(state.ownerFilter) || "").toUpperCase() === "ME") {
    fetchJson(apiUrl("/api/admin/fr24/verify-me"), { method: "POST" }).catch((error) => {
      logEvent("FR24 verify trigger failed", { error: error.message });
    });
  }
}

async function fetchStatusChanges() {
  if (!state.sessionUser) return;

  try {
    const since = state.statusChangesLastChecked || new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const response = await fetchJson(apiUrl(`/api/status-changes?since=${encodeURIComponent(since)}&limit=50`));

    if (response?.ok && Array.isArray(response.changes)) {
      // Merge new changes, avoiding duplicates
      const existingIds = new Set(state.statusChanges.map((c) => c.id));
      const newChanges = response.changes.filter((c) => !existingIds.has(c.id));
      if (newChanges.length > 0) {
        state.statusChanges = [...state.statusChanges, ...newChanges].sort(
          (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
        );
        state.statusChangesLastChecked = new Date().toISOString();
        renderTable(); // Re-render to show alert
      }
    }
  } catch (error) {
    // Silently fail - status changes are non-critical
    if (devToolsEnabled) {
      logEvent("Status changes fetch failed", { error: error.message });
    }
  }
}

async function fetchCronStatus() {
  if (!dom.autoRefreshNote) return;

  // If not authenticated, show static message
  if (!state.sessionUser) {
    dom.autoRefreshNote.textContent = "Daily refresh runs at 5:00 AM EST on weekdays";
    dom.autoRefreshNote.className = "auto-refresh-note";
    return;
  }

  try {
    const response = await fetchJson(apiUrl("/api/cron/status"));
    
    if (response?.ok) {
      const { cronFound, scheduleDescription, nextRunTime, logStatus, isHealthy } = response;
      
      let statusText = "";
      let statusClass = "";
      
      if (!cronFound) {
        statusText = "⚠️ Cron job not found in crontab";
        statusClass = "auto-refresh-note--warning";
      } else if (isHealthy) {
        statusText = `✓ Daily refresh runs ${scheduleDescription || "on schedule"}`;
        if (nextRunTime) {
          const nextRun = new Date(nextRunTime);
          const now = new Date();
          const hoursUntil = Math.round((nextRun - now) / (1000 * 60 * 60));
          if (hoursUntil < 24) {
            statusText += ` (next run in ~${hoursUntil}h)`;
          }
        }
        if (logStatus.lastModifiedAgo) {
          statusText += ` • Last run: ${logStatus.lastModifiedAgo}`;
        }
        statusClass = "auto-refresh-note--healthy";
      } else {
        statusText = `⚠️ Cron scheduled ${scheduleDescription || ""} but may not be running`;
        if (logStatus.lastModifiedAgo) {
          statusText += ` • Last activity: ${logStatus.lastModifiedAgo}`;
        } else {
          statusText += " • No log activity found";
        }
        statusClass = "auto-refresh-note--warning";
      }
      
      dom.autoRefreshNote.textContent = statusText;
      dom.autoRefreshNote.className = `auto-refresh-note ${statusClass}`;
      dom.autoRefreshNote.style.cursor = "pointer";
      dom.autoRefreshNote.style.textDecoration = "underline";
      dom.autoRefreshNote.title = "Click to view cron logs";
      
      // Show restart button for admin users
      if (isAdminUser() && dom.cronRestartButton) {
        dom.cronRestartButton.hidden = false;
      }
    } else {
      // API returned error
      dom.autoRefreshNote.textContent = `⚠️ Could not load cron status: ${response?.error || "Unknown error"}`;
      dom.autoRefreshNote.className = "auto-refresh-note auto-refresh-note--warning";
      if (devToolsEnabled) {
        logEvent("Cron status API error", { error: response?.error, response });
      }
      // Show restart button for admin users when there's an error
      if (isAdminUser() && dom.cronRestartButton) {
        dom.cronRestartButton.hidden = false;
      }
    }
  } catch (error) {
    // Network or other error - show fallback
    dom.autoRefreshNote.textContent = "Daily refresh runs at 5:00 AM EST on weekdays";
    dom.autoRefreshNote.className = "auto-refresh-note";
    if (devToolsEnabled) {
      logEvent("Cron status fetch failed", { error: error.message, status: error.status });
    }
    // Show restart button for admin users
    if (isAdminUser() && dom.cronRestartButton) {
      dom.cronRestartButton.hidden = false;
    }
  }
}

async function fetchCronLogs() {
  try {
    const response = await fetchJson(apiUrl("/api/cron/logs?limit=200"));
    
    if (response?.ok && dom.cronLogsDialogContent) {
      const { logs, totalLines, showingLast, downloadUrl } = response;
      
      let content = `<div style="margin-bottom: 16px; padding: 12px; background: #f5f5f5; border-radius: 8px; display: flex; justify-content: space-between; align-items: center;">`;
      content += `<div>`;
      content += `<strong>Total log lines:</strong> ${totalLines}<br>`;
      content += `<strong>Showing last:</strong> ${showingLast}`;
      content += `</div>`;
      if (downloadUrl && totalLines > showingLast) {
        content += `<a href="${apiUrl(downloadUrl)}" download style="padding: 8px 16px; background: #B81818; color: white; text-decoration: none; border-radius: 4px; font-size: 14px;">📥 Download Full Log</a>`;
      }
      content += `</div>`;
      
      if (logs.length === 0) {
        content += `<p style="color: #666;">Log file is empty or does not exist yet.</p>`;
      } else {
        content += `<pre style="background: #1e1e1e; color: #d4d4d4; padding: 16px; border-radius: 8px; overflow-x: auto; font-size: 11px; line-height: 1.4; max-height: 60vh; overflow-y: auto; font-family: 'Courier New', monospace;">`;
        logs.forEach((line) => {
          const escaped = line
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
          // Highlight timestamp if present
          const withTimestamp = escaped.replace(/^(\[[^\]]+\])/g, '<span style="color: #60a5fa;">$1</span>');
          content += withTimestamp + "\n";
        });
        content += `</pre>`;
      }
      
      dom.cronLogsDialogContent.innerHTML = content;
    } else {
      dom.cronLogsDialogContent.innerHTML = `<p style="color: #d32f2f;">Failed to load logs: ${response?.error || "Unknown error"}</p>`;
    }
  } catch (error) {
    if (dom.cronLogsDialogContent) {
      dom.cronLogsDialogContent.innerHTML = `<p style="color: #d32f2f;">Error loading logs: ${error.message}</p>`;
    }
  }
}

function openCronLogsDialog() {
  if (!dom.cronLogsDialog) return;
  
  dom.cronLogsDialog.hidden = false;
  dom.cronLogsDialog.setAttribute("aria-hidden", "false");
  document.body.classList.add("modal-open");
  
  fetchCronLogs();
}

function closeCronLogsDialog() {
  if (!dom.cronLogsDialog) return;
  
  dom.cronLogsDialog.hidden = true;
  dom.cronLogsDialog.setAttribute("aria-hidden", "true");
  document.body.classList.remove("modal-open");
}

function setupListeners() {
  dom.loginForm?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const username = dom.loginUser?.value || "";
    const password = dom.loginPassword?.value || "";
    if (dom.loginSubmit) {
      dom.loginSubmit.disabled = true;
    }
    const success = await signIn(username, password);
    if (!success && dom.loginPassword) {
      dom.loginPassword.value = "";
      dom.loginPassword.focus();
    }
    if (dom.loginSubmit) {
      dom.loginSubmit.disabled = false;
    }
  });

  dom.logoutButton?.addEventListener("click", (event) => {
    event.preventDefault();
    signOut();
  });

  dom.refreshButton?.addEventListener("click", () => {
    logEvent("Manual refresh triggered");
    refreshStatuses({ force: true, visibleOnly: true }).catch((error) =>
      logEvent("manual refresh failed", { error: error.message })
    );
  });
  dom.exportButton?.addEventListener("click", () => {
    exportVisibleDataToExcel().catch((error) => {
      logEvent("export failed", { error: error.message });
      setRefreshIndicator(`Export failed: ${error.message}`);
    });
  });
  dom.exportPdfButton?.addEventListener("click", () => {
    exportVisibleDataToPdf().catch((error) => {
      logEvent("export failed", { format: "pdf", error: error.message });
      setRefreshIndicator(`Export failed: ${error.message}`);
    });
  });
  dom.emailExportButton?.addEventListener("click", () => {
    emailVisibleDataReport().catch((error) => {
      logEvent("email export crashed", { error: error.message });
      setRefreshIndicator(`Email export failed: ${error.message}`);
    });
  });
  
  // Cron logs dialog
  // Cron restart button (admin only)
  dom.cronRestartButton?.addEventListener("click", async () => {
    if (!isAdminUser()) return;
    if (dom.cronRestartButton.disabled) return;
    
    dom.cronRestartButton.disabled = true;
    const originalText = dom.cronRestartButton.textContent;
    dom.cronRestartButton.textContent = "Running...";
    
    try {
      const response = await fetchJson(apiUrl("/api/admin/cron/run"), {
        method: "POST",
      });
      
      if (response?.ok) {
        setRefreshIndicator("Cron job triggered successfully. Check logs for progress.");
        // Refresh cron status after a delay
        setTimeout(() => {
          fetchCronStatus();
        }, 3000);
      } else {
        setRefreshIndicator(`Failed to trigger cron: ${response?.error || "Unknown error"}`);
      }
    } catch (error) {
      setRefreshIndicator(`Failed to trigger cron: ${error.message}`);
      logEvent("Cron restart failed", { error: error.message });
    } finally {
      dom.cronRestartButton.disabled = false;
      dom.cronRestartButton.textContent = originalText;
    }
  });
  
  dom.autoRefreshNote?.addEventListener("click", () => {
    if (!state.aircraftLimitActive) {
      openCronLogsDialog();
    }
  });
  
  dom.cronLogsDialogClose?.addEventListener("click", () => {
    closeCronLogsDialog();
  });
  dom.usageDialogClose?.addEventListener("click", () => {
    closeUsageDialog();
  });
  
  // Close dialog on backdrop click
  dom.cronLogsDialog?.addEventListener("click", (event) => {
    if (event.target === dom.cronLogsDialog) {
      closeCronLogsDialog();
    }
  });
  dom.usageDialog?.addEventListener("click", (event) => {
    if (event.target === dom.usageDialog) {
      closeUsageDialog();
    }
  });
  
  // Close dialog on Escape key
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && dom.cronLogsDialog && !dom.cronLogsDialog.hidden) {
      closeCronLogsDialog();
    }
    if (event.key === "Escape" && dom.usageDialog && !dom.usageDialog.hidden) {
      closeUsageDialog();
    }
  });
  dom.searchInput?.addEventListener("input", () => {
    populateOwnerFilter();
    populateOperatorFilter();
    populateRegionFilter();
    populateTamFilter();
    populateMeFleetFilter();
    renderTable();
  });
  dom.ownerFilter?.addEventListener("change", (event) => {
    if (state.aircraftLimitActive) {
      event.target.value = "";
      state.ownerFilter = "";
      renderDataReport();
      renderTable();
      logEvent("Owner filter ignored (development limit active)");
      return;
    }
    const nextValue = event.target.value || "";
    const allowedOwners = getAllowedOwners();
    if (Array.isArray(allowedOwners) && !allowedOwners.length) {
      state.ownerFilter = "";
      event.target.value = "";
      renderTable();
      return;
    }
    if (state.ownerFilter === nextValue) {
      renderTable();
      return;
    }
    state.ownerFilter = nextValue;
    logEvent("Owner filter changed", { owner: nextValue || "all" });
    renderDataReport();
    loadAircraft(true);
  });
  dom.operatorFilter?.addEventListener("change", (event) => {
    if (state.aircraftLimitActive) {
      event.target.value = "";
      state.operatorFilter = "";
      renderDataReport();
      renderTable();
      logEvent("Operator filter ignored (development limit active)");
      return;
    }
    state.operatorFilter = event.target.value || "";
    populateOwnerFilter();
    populateRegionFilter();
    populateTamFilter();
    populateMeFleetFilter();
    renderDataReport();
    renderTable();
  });

  dom.regionFilter?.addEventListener("change", (event) => {
    if (state.aircraftLimitActive) {
      event.target.value = "";
      state.regionFilter = "";
      renderDataReport();
      renderTable();
      logEvent("Region filter ignored (development limit active)");
      return;
    }
    state.regionFilter = event.target.value || "";
    populateOwnerFilter();
    populateOperatorFilter();
    populateTamFilter();
    populateMeFleetFilter();
    renderDataReport();
    renderTable();
  });

  dom.tamFilter?.addEventListener("change", (event) => {
    if (state.aircraftLimitActive) {
      event.target.value = "";
      state.tamFilter = "";
      renderDataReport();
      renderTable();
      logEvent("TAM filter ignored (development limit active)");
      return;
    }
    state.tamFilter = event.target.value || "";
    populateOwnerFilter();
    populateOperatorFilter();
    populateRegionFilter();
    populateMeFleetFilter();
    renderDataReport();
    renderTable();
  });

  dom.meFleetFilter?.addEventListener("change", (event) => {
    if (state.aircraftLimitActive) {
      event.target.value = "";
      state.meFleetFilter = "";
      renderDataReport();
      renderTable();
      logEvent("Fleet filter ignored (development limit active)");
      return;
    }
    state.meFleetFilter = event.target.value || "";
    populateOwnerFilter();
    populateOperatorFilter();
    populateRegionFilter();
    populateTamFilter();
    renderDataReport();
    renderTable();
  });
  renderTableHeader();
  document.querySelectorAll("th[data-sort-key]").forEach((th) => {
    th.classList.add("sortable");
    th.addEventListener("click", () => {
      const key = th.dataset.sortKey;
      if (!key) return;
      if (state.sortKey === key) {
        state.sortDirection = state.sortDirection === "asc" ? "desc" : "asc";
      } else {
        state.sortKey = key;
        state.sortDirection = key === "comments" ? "desc" : "asc";
      }
      renderTable();
    });
  });

  setupCommentDialog();
  setupOperatorOutreachDialog();
  if (devToolsEnabled) {
    setupDebugDialog();
  } else if (dom.debugDialog) {
    dom.debugDialog.remove();
    dom.debugDialog = null;
  }
  setupStatusDialog();

  if (devToolsEnabled) {
    dom.devTableClear?.addEventListener("click", (event) => {
      event.preventDefault();
      clearDbTableOutput();
    });
  }
  dom.adminBackupButton?.addEventListener("click", (event) => {
    event.preventDefault();
    triggerManualBackup();
  });
  dom.faUsageButton?.addEventListener("click", (event) => {
    event.preventDefault();
    fetchAndShowFlightawareUsage({ range: "all" }).catch((error) => {
      logEvent("FlightAware usage load failed", { error: error.message });
      openUsageDialog({
        title: "FlightAware costs",
        html: `<p style="color:#b91c1c;">Failed to load: ${escapeHtml(error.message)}</p>`,
        allowMtd: true,
        onMtd: () => fetchAndShowFlightawareUsage({ range: "mtd" }),
      });
    });
  });
  dom.fr24UsageButton?.addEventListener("click", (event) => {
    event.preventDefault();
    fetchAndShowFr24Usage().catch((error) => {
      logEvent("FR24 usage load failed", { error: error.message });
      openUsageDialog({
        title: "FR24 costs",
        html: `<p style="color:#b91c1c;">Failed to load: ${escapeHtml(error.message)}</p>`,
      });
    });
  });
  dom.adminRefreshLogs?.addEventListener("click", (event) => {
    event.preventDefault();
    loadAdminTaskLogs({ reason: "manual" }).catch((error) => {
      logEvent("Admin log refresh failed", { error: error.message });
      state.adminTaskLogError = error.message || "Unable to refresh task history.";
      renderAdminTaskLogs();
    });
  });
  dom.adminCronInstallButton?.addEventListener("click", (event) => {
    event.preventDefault();
    showCronInstallInstructions();
  });
  dom.adminCronRunButton?.addEventListener("click", (event) => {
    event.preventDefault();
    openCronRunDialog();
  });
  dom.adminCronRefreshButton?.addEventListener("click", async (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    
    // Double-check this is the right button - be very explicit
    const clickedButton = event.target.closest ? event.target.closest("#admin-cron-refresh-button") : event.target;
    if (!clickedButton || clickedButton.id !== "admin-cron-refresh-button") {
      console.warn("Cron refresh button click ignored - wrong target", event.target);
      return;
    }
    
    if (!isAdminUser()) {
      console.warn("Cron refresh button click ignored - not admin user");
      return;
    }
    
    if (!dom.adminCronRefreshButton || dom.adminCronRefreshButton.disabled) {
      console.warn("Cron refresh button click ignored - button disabled or missing");
      return;
    }
    
    console.log("Cron refresh button clicked - triggering cron run");
    
    dom.adminCronRefreshButton.disabled = true;
    const originalText = dom.adminCronRefreshButton.textContent;
    dom.adminCronRefreshButton.textContent = "Refreshing...";
    
    try {
      // First refresh the cron status
      await fetchCronStatus();
      
      // Then trigger a cron run (no options, just run it)
      const response = await fetchJson(apiUrl("/api/admin/cron/run"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      
      if (response?.ok) {
        setRefreshIndicator("Cron job refreshed and triggered successfully. Check logs for progress.");
        // Refresh cron status again after a delay
        setTimeout(() => {
          fetchCronStatus();
        }, 3000);
      } else {
        setRefreshIndicator(`Failed to trigger cron: ${response?.error || "Unknown error"}`);
      }
    } catch (error) {
      setRefreshIndicator(`Failed to refresh cron: ${error.message}`);
      logEvent("Cron refresh failed", { error: error.message });
    } finally {
      dom.adminCronRefreshButton.disabled = false;
      dom.adminCronRefreshButton.textContent = originalText;
    }
  });
  dom.adminCronRunDialogClose?.addEventListener("click", () => closeCronRunDialog());
  dom.adminCronRunDialogCancel?.addEventListener("click", () => closeCronRunDialog());
  dom.adminCronRunDialogForm?.addEventListener("submit", handleCronRunSubmit);
  dom.adminCronRunTestMode?.addEventListener("change", () => {
    if (dom.adminCronRunTestEmailField) {
      dom.adminCronRunTestEmailField.hidden = !dom.adminCronRunTestMode?.checked;
      // Set default test email if showing the field
      if (dom.adminCronRunTestMode?.checked && dom.adminCronRunTestEmail) {
        if (!dom.adminCronRunTestEmail.value) {
          dom.adminCronRunTestEmail.value = "claus.schmetkamp@dubaiaerospace.com";
        }
      }
    }
  });
  // Close dialog when clicking backdrop
  dom.adminCronRunDialog?.addEventListener("click", (e) => {
    if (e.target === dom.adminCronRunDialog) {
      closeCronRunDialog();
    }
  });
}

function showCronInstallInstructions() {
  const cronStatus = "⚠️ Cron installation requires root access on the server.\n\n" +
    "To install cron, SSH to your server and run:\n\n" +
    "  sudo /opt/actracker/scripts/install-cron.sh\n\n" +
    "To remove cron, run:\n\n" +
    "  sudo /opt/actracker/scripts/uninstall-cron.sh\n\n" +
    "The cron job will run weekdays at 5:00 AM EST (10:00 UTC).\n\n" +
    "Check cron status in the UI to see if it's installed.";
  alert(cronStatus);
}

function openCronRunDialog() {
  if (!dom.adminCronRunDialog || !isAdminUser()) return;
  
  // Ensure owners are loaded
  if (adminAircraftState.owners.length === 0) {
    loadAdminOwners().then(() => {
      openCronRunDialog();
    }).catch((error) => {
      logEvent("Failed to load owners for cron dialog", { error: error.message });
      alert("Failed to load owners. Please try again.");
    });
    return;
  }
  
  dom.adminCronRunDialog.hidden = false;
  dom.adminCronRunDialog.setAttribute("aria-hidden", "false");
  dom.adminCronRunDialogForm.reset();
  
  // Populate owner checkboxes
  if (dom.adminCronRunOwnersCheckboxes) {
    dom.adminCronRunOwnersCheckboxes.innerHTML = "";
    adminAircraftState.owners.forEach((owner) => {
      const label = document.createElement("label");
      label.style.cssText = "display: flex; align-items: center; gap: 8px; cursor: pointer;";
      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";
      checkbox.value = owner;
      checkbox.checked = false; // Default: none selected (process all)
      checkbox.name = "cron-owner";
      const text = document.createElement("span");
      text.textContent = owner;
      label.appendChild(checkbox);
      label.appendChild(text);
      dom.adminCronRunOwnersCheckboxes.appendChild(label);
    });
  }
  
  if (dom.adminCronRunTestEmail) {
    dom.adminCronRunTestEmail.value = "";
  }
  if (dom.adminCronRunTestEmailField) {
    dom.adminCronRunTestEmailField.hidden = true;
  }
  if (dom.adminCronRunSendEmail) {
    dom.adminCronRunSendEmail.checked = true;
  }
  if (dom.adminCronRunDialogError) {
    dom.adminCronRunDialogError.hidden = true;
    dom.adminCronRunDialogError.textContent = "";
  }
}

function closeCronRunDialog() {
  if (!dom.adminCronRunDialog) return;
  dom.adminCronRunDialog.hidden = true;
  dom.adminCronRunDialog.setAttribute("aria-hidden", "true");
  if (dom.adminCronRunDialogError) {
    dom.adminCronRunDialogError.hidden = true;
    dom.adminCronRunDialogError.textContent = "";
  }
}

async function handleCronRunSubmit(event) {
  event.preventDefault();
  if (!dom.adminCronRunDialogForm || !isAdminUser()) return;
  
  // Get selected owners
  const ownerCheckboxes = dom.adminCronRunOwnersCheckboxes?.querySelectorAll('input[name="cron-owner"]:checked');
  const selectedOwners = ownerCheckboxes ? Array.from(ownerCheckboxes).map(cb => cb.value) : [];
  
  // Get options
  const testMode = dom.adminCronRunTestMode?.checked === true;
  const sendEmail = dom.adminCronRunSendEmail?.checked !== false;
  const testEmail = cleanText(dom.adminCronRunTestEmail?.value) || null;
  
  // Validate test email if test mode is enabled
  if (testMode && !testEmail) {
    if (dom.adminCronRunDialogError) {
      dom.adminCronRunDialogError.textContent = "Test email address is required when test mode is enabled.";
      dom.adminCronRunDialogError.hidden = false;
    }
    return;
  }
  
  try {
    const response = await fetchJson(apiUrl("/api/admin/cron/run"), {
      method: "POST",
      body: JSON.stringify({
        owners: selectedOwners.length > 0 ? selectedOwners : null,
        testMode,
        sendEmail,
        testEmailTo: testEmail,
      }),
    });
    
    if (response?.ok) {
      closeCronRunDialog();
      setRefreshIndicator(`Cron job triggered successfully (PID: ${response.pid}). Check logs for progress.`);
      // Refresh cron status after a delay
      setTimeout(() => {
        fetchCronStatus();
      }, 3000);
    } else {
      throw new Error(response.error || "Failed to trigger cron job");
    }
  } catch (error) {
    if (dom.adminCronRunDialogError) {
      dom.adminCronRunDialogError.textContent = error.message || "Failed to trigger cron job.";
      dom.adminCronRunDialogError.hidden = false;
    }
    logEvent("Cron run failed", { error: error.message });
  }
}

// ========== Admin Users Management ==========

let adminUsersState = { users: [], loading: false };
let adminAircraftState = { aircraft: [], owners: [], operators: [], loading: false };
let adminOperatorsState = { operators: [], loading: false };
let editingUser = null;
let editingAircraft = null;
let editingOperatorContacts = null;

function setupAdminManagement() {
  if (!isAdminUser()) return;
  
  // Users management
  dom.adminUsersAdd?.addEventListener("click", () => openUserDialog());
  dom.adminUsersRefresh?.addEventListener("click", () => loadAdminUsers());
  dom.adminUserDialogClose?.addEventListener("click", () => closeUserDialog());
  dom.adminUserDialogCancel?.addEventListener("click", () => closeUserDialog());
  dom.adminUserDialogForm?.addEventListener("submit", handleUserSubmit);
  dom.adminUserIsAdmin?.addEventListener("change", () => {
    if (dom.adminUserIsAdmin?.checked) {
      dom.adminUserOwnerCodes.value = "";
      dom.adminUserOwnerCodes.disabled = true;
    } else {
      dom.adminUserOwnerCodes.disabled = false;
    }
  });
  
  // Aircraft management
  dom.adminAircraftAdd?.addEventListener("click", () => openAircraftDialog());
  dom.adminAircraftRefresh?.addEventListener("click", () => loadAdminAircraft());
  // Fix X button close handler - ensure it works even if button is inside form
  const closeButton = dom.adminAircraftDialogClose;
  if (closeButton) {
    closeButton.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      closeAircraftDialog();
    });
    // Also handle via onclick as fallback
    closeButton.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      closeAircraftDialog();
      return false;
    };
  }
  dom.adminAircraftDialogCancel?.addEventListener("click", () => closeAircraftDialog());
  dom.adminAircraftDialogForm?.addEventListener("submit", handleAircraftSubmit);
  
  // Close dialog when clicking backdrop
  dom.adminAircraftDialog?.addEventListener("click", (e) => {
    if (e.target === dom.adminAircraftDialog) {
      closeAircraftDialog();
    }
  });

  dom.adminOperatorsRefresh?.addEventListener("click", () => {
    loadAdminOperatorsPage().catch((error) => logEvent("admin operators refresh failed", { error: error.message }));
  });
  dom.adminOperatorsDialogClose?.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    closeAdminOperatorContactsDialog();
  });
  dom.adminOperatorsDialogCancel?.addEventListener("click", (event) => {
    event.preventDefault();
    closeAdminOperatorContactsDialog();
  });
  dom.adminOperatorsDialogForm?.addEventListener("submit", handleAdminOperatorContactsSubmit);
  dom.adminOperatorsAddContact?.addEventListener("click", (event) => {
    event.preventDefault();
    if (dom.adminOperatorsContacts) {
      dom.adminOperatorsContacts.appendChild(buildAdminOperatorContactRow({ name: "", email: "" }));
    }
  });
  dom.adminOperatorsDialog?.addEventListener("click", (event) => {
    if (event.target === dom.adminOperatorsDialog) {
      closeAdminOperatorContactsDialog();
    }
  });
  
  loadAdminUsers();
  loadAdminOwners();
  loadAdminOperators();
  loadAdminAircraft();
}

async function loadAdminUsers() {
  if (!isAdminUser() || !dom.adminUsersBody) return;
  adminUsersState.loading = true;
  try {
    const response = await fetchJson(apiUrl("/api/admin/users"));
    if (response.ok && Array.isArray(response.users)) {
      adminUsersState.users = response.users;
      renderAdminUsers();
    }
  } catch (error) {
    logEvent("Failed to load admin users", { error: error.message });
  } finally {
    adminUsersState.loading = false;
  }
}

function renderAdminUsers() {
  if (!dom.adminUsersBody) return;
  dom.adminUsersBody.innerHTML = "";
  adminUsersState.users.forEach((user) => {
    const row = document.createElement("tr");
    row.innerHTML = `
      <td>${cleanText(user.username) || ""}</td>
      <td>${cleanText(user.displayName) || ""}</td>
      <td>${cleanText(user.initials) || ""}</td>
      <td>${cleanText(user.email) || ""}</td>
      <td>${user.isAdmin ? "Yes" : "No"}</td>
      <td>${Array.isArray(user.ownerCodes) ? user.ownerCodes.join(", ") : ""}</td>
      <td>${user.active ? "Yes" : "No"}</td>
      <td>
        <button class="ghost" data-action="edit" data-username="${cleanText(user.username)}">Edit</button>
        <button class="ghost" data-action="reset-password" data-username="${cleanText(user.username)}">Reset Password</button>
        <button class="ghost" data-action="delete" data-username="${cleanText(user.username)}">Delete</button>
      </td>
    `;
    row.querySelector('[data-action="edit"]')?.addEventListener("click", () => openUserDialog(user));
    row.querySelector('[data-action="reset-password"]')?.addEventListener("click", () => resetUserPassword(user.username));
    row.querySelector('[data-action="delete"]')?.addEventListener("click", () => deleteUser(user.username));
    dom.adminUsersBody.appendChild(row);
  });
}

function openUserDialog(user = null) {
  editingUser = user;
  if (!dom.adminUserDialog) return;
  dom.adminUserDialog.hidden = false;
  dom.adminUserDialog.setAttribute("aria-hidden", "false");
  dom.adminUserDialogTitle.textContent = user ? "Edit User" : "Add User";
  dom.adminUserDialogForm.reset();
  if (user) {
    dom.adminUserUsername.value = user.username || "";
    dom.adminUserUsername.disabled = true;
    dom.adminUserDisplayName.value = user.displayName || "";
    dom.adminUserInitials.value = user.initials || "";
    dom.adminUserEmail.value = user.email || "";
    dom.adminUserPassword.value = "";
    dom.adminUserPassword.required = false;
    dom.adminUserPasswordHint.textContent = "(leave empty to keep current password)";
    dom.adminUserIsAdmin.checked = user.isAdmin || false;
    dom.adminUserOwnerCodes.value = Array.isArray(user.ownerCodes) ? user.ownerCodes.join(",") : "";
    dom.adminUserActive.checked = user.active !== false;
  } else {
    dom.adminUserUsername.disabled = false;
    dom.adminUserPassword.required = true;
    dom.adminUserPasswordHint.textContent = "(required for new users)";
    dom.adminUserIsAdmin.checked = false;
    dom.adminUserActive.checked = true;
  }
  if (dom.adminUserIsAdmin.checked) {
    dom.adminUserOwnerCodes.disabled = true;
  } else {
    dom.adminUserOwnerCodes.disabled = false;
  }
}

function closeUserDialog() {
  if (!dom.adminUserDialog) return;
  dom.adminUserDialog.hidden = true;
  dom.adminUserDialog.setAttribute("aria-hidden", "true");
  editingUser = null;
  dom.adminUserDialogError.hidden = true;
  dom.adminUserDialogError.textContent = "";
}

async function handleUserSubmit(event) {
  event.preventDefault();
  if (!dom.adminUserDialogForm) return;
  const formData = new FormData(dom.adminUserDialogForm);
  const username = cleanText(dom.adminUserUsername?.value);
  const displayName = cleanText(dom.adminUserDisplayName?.value);
  const initials = cleanText(dom.adminUserInitials?.value);
  const email = cleanText(dom.adminUserEmail?.value);
  const password = dom.adminUserPassword?.value || null;
  const isAdmin = dom.adminUserIsAdmin?.checked || false;
  const ownerCodes = cleanText(dom.adminUserOwnerCodes?.value)
    ? dom.adminUserOwnerCodes.value.split(",").map(s => cleanText(s)).filter(Boolean)
    : [];
  const active = dom.adminUserActive?.checked !== false;
  
  if (!username || !email) {
    dom.adminUserDialogError.textContent = "Username and email are required.";
    dom.adminUserDialogError.hidden = false;
    return;
  }
  
  if (!editingUser && !password) {
    dom.adminUserDialogError.textContent = "Password is required for new users.";
    dom.adminUserDialogError.hidden = false;
    return;
  }
  
  try {
    let response;
    if (editingUser) {
      // Update
      response = await fetchJson(apiUrl(`/api/admin/users/${encodeURIComponent(username)}`), {
        method: "PUT",
        body: JSON.stringify({ displayName, initials, email, isAdmin, ownerCodes, active }),
      });
      if (password) {
        await fetchJson(apiUrl(`/api/admin/users/${encodeURIComponent(username)}/reset-password`), {
          method: "POST",
          body: JSON.stringify({ password }),
        });
      }
    } else {
      // Create
      response = await fetchJson(apiUrl("/api/admin/users"), {
        method: "POST",
        body: JSON.stringify({ username, displayName, initials, email, password, isAdmin, ownerCodes, active }),
      });
    }
    if (response.ok) {
      closeUserDialog();
      await loadAdminUsers();
      setRefreshIndicator("User saved successfully.");
    } else {
      throw new Error(response.error || "Failed to save user");
    }
  } catch (error) {
    dom.adminUserDialogError.textContent = error.message || "Failed to save user.";
    dom.adminUserDialogError.hidden = false;
  }
}

async function resetUserPassword(username) {
  const password = prompt(`Enter new password for ${username}:`);
  if (!password) return;
  try {
    const response = await fetchJson(apiUrl(`/api/admin/users/${encodeURIComponent(username)}/reset-password`), {
      method: "POST",
      body: JSON.stringify({ password }),
    });
    if (response.ok) {
      setRefreshIndicator("Password reset successfully.");
    } else {
      throw new Error(response.error || "Failed to reset password");
    }
  } catch (error) {
    alert(`Failed to reset password: ${error.message}`);
  }
}

async function deleteUser(username) {
  if (!confirm(`Are you sure you want to delete user ${username}?`)) return;
  try {
    const response = await fetchJson(apiUrl(`/api/admin/users/${encodeURIComponent(username)}`), {
      method: "DELETE",
    });
    if (response.ok) {
      await loadAdminUsers();
      setRefreshIndicator("User deleted successfully.");
    } else {
      throw new Error(response.error || "Failed to delete user");
    }
  } catch (error) {
    alert(`Failed to delete user: ${error.message}`);
  }
}

// ========== Admin Aircraft Management ==========

async function loadAdminOwners() {
  if (!isAdminUser()) return;
  try {
    const response = await fetchJson(apiUrl("/api/admin/owners"));
    if (response.ok && Array.isArray(response.owners)) {
      adminAircraftState.owners = response.owners;
      if (dom.adminAircraftOwner) {
        dom.adminAircraftOwner.innerHTML = '<option value="">Select owner...</option>';
        response.owners.forEach((owner) => {
          const option = document.createElement("option");
          option.value = owner;
          option.textContent = owner;
          dom.adminAircraftOwner.appendChild(option);
        });
      }
    }
  } catch (error) {
    logEvent("Failed to load admin owners", { error: error.message });
  }
}

async function loadAdminOperators() {
  if (!isAdminUser()) return;
  try {
    const response = await fetchJson(apiUrl("/api/operators"));
    if (response.ok && Array.isArray(response.operators)) {
      adminAircraftState.operators = response.operators;
      if (dom.adminAircraftOperator) {
        dom.adminAircraftOperator.innerHTML = '<option value="">Select operator...</option>';
        response.operators.forEach((operator) => {
          const option = document.createElement("option");
          option.value = String(operator.id || "");
          option.textContent = operator.name || `Operator ${operator.id}`;
          dom.adminAircraftOperator.appendChild(option);
        });
      }
    }
  } catch (error) {
    logEvent("Failed to load admin operators", { error: error.message });
  }
}

async function loadAdminOperatorsPage() {
  if (!isAdminUser() || !dom.adminOperatorsBody) return;
  adminOperatorsState.loading = true;
  try {
    const response = await fetchJson(apiUrl("/api/operators"));
    if (response.ok && Array.isArray(response.operators)) {
      adminOperatorsState.operators = response.operators;
      response.operators.forEach((op) => upsertOperatorDirectory(op));
      renderAdminOperatorsPage();
    }
  } catch (error) {
    logEvent("Failed to load operators admin list", { error: error.message });
  } finally {
    adminOperatorsState.loading = false;
  }
}

function renderAdminOperatorsPage() {
  if (!dom.adminOperatorsBody) return;
  dom.adminOperatorsBody.innerHTML = "";
  adminOperatorsState.operators.forEach((operator) => {
    const row = document.createElement("tr");
    const contacts = Array.isArray(operator.contacts) ? operator.contacts : [];
    const primary = cleanText(operator.contact_email) || (contacts[0] ? contacts[0].email : "");
    const summary =
      contacts.length > 0
        ? contacts.map((c) => `${cleanText(c.name) || "Contact"} <${cleanText(c.email) || ""}>`).join("\n")
        : "—";
    row.innerHTML = `
      <td>${operator.id || ""}</td>
      <td>${cleanText(operator.name) || ""}</td>
      <td>${Number.isFinite(Number(operator.aircraft_count)) ? operator.aircraft_count : 0}</td>
      <td>${primary || "—"}</td>
      <td title="${htmlEscape(summary)}">${contacts.length ? `${contacts.length} contact(s)` : "—"}</td>
      <td>
        <button class="ghost" data-action="edit-contacts" data-id="${operator.id}">Edit contacts</button>
      </td>
    `;
    row.querySelector('[data-action="edit-contacts"]')?.addEventListener("click", () => {
      openAdminOperatorContactsDialog(operator);
    });
    dom.adminOperatorsBody.appendChild(row);
  });
}

function buildAdminOperatorContactRow({ name = "", email = "" } = {}) {
  const wrap = document.createElement("div");
  wrap.className = "dialog-grid dialog-grid--details";
  wrap.style.marginTop = "10px";
  wrap.innerHTML = `
    <div class="dialog-field">
      <label class="dialog-label">Contact name</label>
      <input class="admin-operator-contact-name" type="text" value="${htmlEscape(name)}" autocomplete="off" />
    </div>
    <div class="dialog-field">
      <label class="dialog-label">Email</label>
      <input class="admin-operator-contact-email" type="email" value="${htmlEscape(
        email
      )}" autocomplete="off" />
    </div>
  `;
  return wrap;
}

function readAdminOperatorContactsFromEditor(root) {
  if (!root) {
    return [];
  }
  const blocks = root.querySelectorAll(".dialog-grid");
  const out = [];
  blocks.forEach((block) => {
    const name = cleanText(block.querySelector(".admin-operator-contact-name")?.value) || "Operator contact";
    const email = cleanText(block.querySelector(".admin-operator-contact-email")?.value) || "";
    if (!email) {
      return;
    }
    out.push({ name, email });
  });
  return out;
}

function openAdminOperatorContactsDialog(operator) {
  editingOperatorContacts = operator;
  if (!dom.adminOperatorsDialog || !dom.adminOperatorsContacts) {
    return;
  }
  dom.adminOperatorsDialog.hidden = false;
  dom.adminOperatorsDialog.setAttribute("aria-hidden", "false");
  if (dom.adminOperatorsDialogTitle) {
    dom.adminOperatorsDialogTitle.textContent = `Contacts · ${cleanText(operator.name) || "Operator"}`;
  }
  if (dom.adminOperatorsDialogSummary) {
    dom.adminOperatorsDialogSummary.textContent = `Operator ID ${operator.id}${
      operator.slug ? ` · ${operator.slug}` : ""
    }`;
  }
  dom.adminOperatorsContacts.innerHTML = "";
  const contacts = Array.isArray(operator.contacts) && operator.contacts.length ? operator.contacts : [{ name: "", email: "" }];
  contacts.forEach((c) => {
    dom.adminOperatorsContacts.appendChild(
      buildAdminOperatorContactRow({ name: c.name, email: c.email })
    );
  });
  if (dom.adminOperatorsDialogError) {
    dom.adminOperatorsDialogError.hidden = true;
    dom.adminOperatorsDialogError.textContent = "";
  }
  document.body.classList.add("modal-open");
}

function closeAdminOperatorContactsDialog() {
  if (dom.adminOperatorsDialog) {
    dom.adminOperatorsDialog.hidden = true;
    dom.adminOperatorsDialog.setAttribute("aria-hidden", "true");
  }
  editingOperatorContacts = null;
  if (dom.adminOperatorsDialogError) {
    dom.adminOperatorsDialogError.hidden = true;
    dom.adminOperatorsDialogError.textContent = "";
  }
  const otherOpen =
    (dom.commentDialog && !dom.commentDialog.hidden) ||
    (dom.debugDialog && !dom.debugDialog.hidden) ||
    (dom.statusDialog && !dom.statusDialog.hidden) ||
    (dom.adminAircraftDialog && !dom.adminAircraftDialog.hidden) ||
    (dom.adminUserDialog && !dom.adminUserDialog.hidden) ||
    (dom.operatorOutreachDialog && !dom.operatorOutreachDialog.hidden);
  if (!otherOpen) {
    document.body.classList.remove("modal-open");
  }
}

async function handleAdminOperatorContactsSubmit(event) {
  event.preventDefault();
  if (!editingOperatorContacts) {
    return;
  }
  const contacts = readAdminOperatorContactsFromEditor(dom.adminOperatorsContacts);
  if (!contacts.length) {
    if (dom.adminOperatorsDialogError) {
      dom.adminOperatorsDialogError.textContent = "Add at least one contact with a valid email.";
      dom.adminOperatorsDialogError.hidden = false;
    }
    return;
  }
  if (dom.adminOperatorsDialogError) {
    dom.adminOperatorsDialogError.hidden = true;
    dom.adminOperatorsDialogError.textContent = "";
  }
  try {
    const response = await fetchJson(
      apiUrl(`/api/admin/operators/${encodeURIComponent(String(editingOperatorContacts.id))}`),
      {
        method: "PUT",
        body: JSON.stringify({ contacts }),
      }
    );
    if (!response?.ok) {
      throw new Error(response?.error || "Failed to save contacts");
    }
    if (response.operator) {
      upsertOperatorDirectory(response.operator);
    }
    closeAdminOperatorContactsDialog();
    await loadAdminOperatorsPage();
    await loadAdminOperators();
    setRefreshIndicator("Operator contacts saved.");
  } catch (error) {
    if (dom.adminOperatorsDialogError) {
      dom.adminOperatorsDialogError.textContent = error.message || "Failed to save contacts.";
      dom.adminOperatorsDialogError.hidden = false;
    }
  }
}

async function loadAdminAircraft() {
  if (!isAdminUser() || !dom.adminAircraftBody) return;
  adminAircraftState.loading = true;
  try {
    const response = await fetchJson(apiUrl("/api/admin/aircraft"));
    if (response.ok && Array.isArray(response.aircraft)) {
      adminAircraftState.aircraft = response.aircraft;
      renderAdminAircraft();
    }
  } catch (error) {
    logEvent("Failed to load admin aircraft", { error: error.message });
  } finally {
    adminAircraftState.loading = false;
  }
}

function renderAdminAircraft() {
  if (!dom.adminAircraftBody) return;
  dom.adminAircraftBody.innerHTML = "";
  adminAircraftState.aircraft.forEach((aircraft) => {
    const row = document.createElement("tr");
    row.innerHTML = `
      <td>${aircraft.id || ""}</td>
      <td>${cleanText(aircraft.ident) || ""}</td>
      <td>${cleanText(aircraft.registration) || ""}</td>
      <td>${cleanText(aircraft.msn) || ""}</td>
      <td>${cleanText(aircraft.tail) || ""}</td>
      <td>${cleanText(aircraft.operator) || ""}</td>
      <td>${cleanText(aircraft.owner) || ""}</td>
      <td>${aircraft.active !== false ? "Yes" : "No"}</td>
      <td>
        <button class="ghost" data-action="edit" data-id="${aircraft.id}">Edit</button>
        <button class="ghost" data-action="delete" data-id="${aircraft.id}">Delete</button>
      </td>
    `;
    row.querySelector('[data-action="edit"]')?.addEventListener("click", () => openAircraftDialog(aircraft));
    row.querySelector('[data-action="delete"]')?.addEventListener("click", () => deleteAircraft(aircraft.id));
    dom.adminAircraftBody.appendChild(row);
  });
}

async function openAircraftDialog(aircraft = null) {
  editingAircraft = aircraft;
  if (!dom.adminAircraftDialog) {
    return;
  }
  let profile = aircraft;
  if (aircraft?.id) {
    try {
      const full = await fetchAircraftProfileById(aircraft.id, { skipRender: true, source: "admin-aircraft-dialog" });
      if (full) {
        profile = full;
      }
    } catch (error) {
      logEvent("admin aircraft dialog profile load", { error: error.message });
    }
  }
  if (adminAircraftState.owners.length === 0 || adminAircraftState.operators.length === 0) {
    try {
      await Promise.all([
        adminAircraftState.owners.length === 0 ? loadAdminOwners() : Promise.resolve(),
        adminAircraftState.operators.length === 0 ? loadAdminOperators() : Promise.resolve(),
      ]);
    } catch (error) {
      logEvent("Failed to load data for dialog", { error: error.message });
    }
  }
  openAircraftDialogInternal(profile);
}

function openAircraftDialogInternal(aircraft = null) {
  if (!dom.adminAircraftDialog) return;
  dom.adminAircraftDialog.hidden = false;
  dom.adminAircraftDialog.setAttribute("aria-hidden", "false");
  dom.adminAircraftDialogTitle.textContent = aircraft ? "Edit Aircraft" : "Add Aircraft";
  dom.adminAircraftDialogForm.reset();
  
  // Populate owner dropdown
  if (dom.adminAircraftOwner && adminAircraftState.owners.length > 0) {
    dom.adminAircraftOwner.innerHTML = '<option value="">Select owner...</option>';
    adminAircraftState.owners.forEach((owner) => {
      const option = document.createElement("option");
      option.value = owner;
      option.textContent = owner;
      if (aircraft && aircraft.owner === owner) {
        option.selected = true;
      }
      dom.adminAircraftOwner.appendChild(option);
    });
  }
  
  // Populate operator dropdown
  if (dom.adminAircraftOperator && adminAircraftState.operators.length > 0) {
    dom.adminAircraftOperator.innerHTML = '<option value="">Select operator...</option>';
    adminAircraftState.operators.forEach((operator) => {
      const option = document.createElement("option");
      option.value = String(operator.id || "");
      option.textContent = operator.name || `Operator ${operator.id}`;
      if (aircraft && aircraft.operator_id && Number(aircraft.operator_id) === Number(operator.id)) {
        option.selected = true;
      }
      dom.adminAircraftOperator.appendChild(option);
    });
  }
  
  if (aircraft) {
    dom.adminAircraftIdent.value = aircraft.ident || "";
    dom.adminAircraftIdent.disabled = true;
    dom.adminAircraftRegistration.value = aircraft.registration || "";
    dom.adminAircraftType.value = aircraft.aircraft_type || "";
    dom.adminAircraftMsn.value = aircraft.msn || "";
    dom.adminAircraftTail.value = aircraft.tail || "";
    const operatorId = aircraft.operator_id ? String(aircraft.operator_id) : "";
    dom.adminAircraftOperator.value = operatorId;
    dom.adminAircraftOwner.value = aircraft.owner || "";
    dom.adminAircraftUrl.value = aircraft.tracker_url || aircraft.url || "";
    dom.adminAircraftRegion.value = aircraft.region || "";
    if (dom.adminAircraftFlightawareUrl) {
      dom.adminAircraftFlightawareUrl.value = cleanText(aircraft.flightaware_url) || "";
    }
    if (dom.adminAircraftNotes) {
      dom.adminAircraftNotes.value = cleanText(aircraft.notes) || "";
    }
    if (dom.adminAircraftExpectedRelease) {
      const ymd = cleanText(aircraft.expected_release_date) || "";
      dom.adminAircraftExpectedRelease.value = ymd && /^\d{4}-\d{2}-\d{2}/.test(ymd) ? ymd.slice(0, 10) : "";
    }
    dom.adminAircraftActive.checked = aircraft.active !== false;
  } else {
    dom.adminAircraftIdent.disabled = false;
    dom.adminAircraftActive.checked = true;
    if (dom.adminAircraftFlightawareUrl) {
      dom.adminAircraftFlightawareUrl.value = "";
    }
    if (dom.adminAircraftNotes) {
      dom.adminAircraftNotes.value = "";
    }
    if (dom.adminAircraftExpectedRelease) {
      dom.adminAircraftExpectedRelease.value = "";
    }
  }
  document.body.classList.add("modal-open");
}

function closeAircraftDialog() {
  if (!dom.adminAircraftDialog) return;
  dom.adminAircraftDialog.hidden = true;
  dom.adminAircraftDialog.setAttribute("aria-hidden", "true");
  editingAircraft = null;
  dom.adminAircraftDialogError.hidden = true;
  dom.adminAircraftDialogError.textContent = "";
  const otherOpen =
    (dom.commentDialog && !dom.commentDialog.hidden) ||
    (dom.debugDialog && !dom.debugDialog.hidden) ||
    (dom.statusDialog && !dom.statusDialog.hidden) ||
    (dom.adminUserDialog && !dom.adminUserDialog.hidden) ||
    (dom.adminOperatorsDialog && !dom.adminOperatorsDialog.hidden) ||
    (dom.operatorOutreachDialog && !dom.operatorOutreachDialog.hidden);
  if (!otherOpen) {
    document.body.classList.remove("modal-open");
  }
}

async function handleAircraftSubmit(event) {
  event.preventDefault();
  if (!dom.adminAircraftDialogForm) return;
  const ident = cleanText(dom.adminAircraftIdent?.value);
  const registration = cleanText(dom.adminAircraftRegistration?.value);
  const aircraftType = cleanText(dom.adminAircraftType?.value);
  const msn = cleanText(dom.adminAircraftMsn?.value);
  const tail = cleanText(dom.adminAircraftTail?.value);
  const operatorId = dom.adminAircraftOperator?.value ? parseInt(dom.adminAircraftOperator.value, 10) : null;
  const owner = cleanText(dom.adminAircraftOwner?.value);
  const url = cleanText(dom.adminAircraftUrl?.value);
  const region = cleanText(dom.adminAircraftRegion?.value);
  const active = dom.adminAircraftActive?.checked !== false;
  const flightawareUrl = cleanText(dom.adminAircraftFlightawareUrl?.value) || null;
  const notes = cleanText(dom.adminAircraftNotes?.value) || null;
  const expectedRelease = dom.adminAircraftExpectedRelease?.value?.trim() || null;
  
  if (!ident || !owner) {
    dom.adminAircraftDialogError.textContent = "Ident and owner are required.";
    dom.adminAircraftDialogError.hidden = false;
    return;
  }
  
  try {
    let response;
    const payload = { ident, registration, msn, tail, owner, active };
    if (operatorId && Number.isInteger(operatorId)) {
      payload.operator_id = operatorId;
    }
    if (aircraftType) {
      payload.aircraft_type = aircraftType;
    }
    if (url) {
      payload.url = url;
    }
    if (region) {
      payload.region = region;
    }
    payload.flightaware_url = flightawareUrl;
    payload.notes = notes;
    if (expectedRelease) {
      payload.expected_release_date = expectedRelease;
    } else if (editingAircraft) {
      payload.expected_release_date = null;
    }
    
    if (editingAircraft) {
      // Update
      response = await fetchJson(apiUrl(`/api/admin/aircraft/${editingAircraft.id}`), {
        method: "PUT",
        body: JSON.stringify(payload),
      });
    } else {
      // Create
      response = await fetchJson(apiUrl("/api/admin/aircraft"), {
        method: "POST",
        body: JSON.stringify(payload),
      });
    }
    if (response.ok) {
      closeAircraftDialog();
      await loadAdminAircraft();
      try {
        await loadAircraft(true);
      } catch (syncError) {
        logEvent("loadAircraft after admin save", { error: syncError.message });
      }
      setRefreshIndicator("Aircraft saved successfully.");
    } else {
      throw new Error(response.error || "Failed to save aircraft");
    }
  } catch (error) {
    dom.adminAircraftDialogError.textContent = error.message || "Failed to save aircraft.";
    dom.adminAircraftDialogError.hidden = false;
  }
}

async function deleteAircraft(id) {
  if (!confirm(`Are you sure you want to delete aircraft ${id}?`)) return;
  try {
    const response = await fetchJson(apiUrl(`/api/admin/aircraft/${id}`), {
      method: "DELETE",
    });
    if (response.ok) {
      await loadAdminAircraft();
      setRefreshIndicator("Aircraft deleted successfully.");
    } else {
      throw new Error(response.error || "Failed to delete aircraft");
    }
  } catch (error) {
    alert(`Failed to delete aircraft: ${error.message}`);
  }
}

function setupApiBaseControls() {
  updateApiBaseStatus();
}

async function bootstrap() {
  ensureAppShellVisible();
  setHeaderHeightVar();
  renderDataReport();
  updateLastUpdatedDisplay();
  populateOwnerFilter();
  populateOperatorFilter();
  populateRegionFilter();
  populateTamFilter();
  populateMeFleetFilter();
  setModeChip();
  const watchHydrated = tryHydrateOnWatchSnapshot();
  hydrateAircraftSnapshotFromStorage();
  if (dom.autoRefreshNote) {
    if (state.aircraftLimitActive) {
      dom.autoRefreshNote.textContent = `Development limit active: showing the first ${state.aircraftLimit} aircraft. Manual refresh still uses live FlightAware data.`;
      dom.autoRefreshNote.style.cursor = "default";
      dom.autoRefreshNote.style.textDecoration = "none";
    } else {
      // Show loading state - will be updated after authentication
      dom.autoRefreshNote.textContent = "Loading cron status...";
    }
  }
  if (!devToolsEnabled && dom.devConsole) {
    dom.devConsole.remove();
    dom.devConsole = null;
  } else if (devToolsEnabled && dom.devConsole) {
    dom.devConsole.hidden = false;
  }
  ensureSessionVisibility();
  setupApiBaseControls();
  setupListeners();
  if (dom.exportButton) {
    dom.exportButton.hidden = false;
  }
  if (dom.exportPdfButton) {
    dom.exportPdfButton.hidden = false;
  }
  if (dom.emailExportButton) {
    dom.emailExportButton.hidden = false;
  }
  updateEmailExportAvailability();
  if (state.aircraftLimitActive) {
    logEvent("Development aircraft limit enabled", { limit: state.aircraftLimit });
  }
  logEvent("UI bootstrap", {
    runtimeMode,
    visualMode,
    devToolsEnabled,
    apiBase: getActiveApiBase(),
    defaultApiBase: getDefaultApiBase(),
    fallbackApiBase: getFallbackApiBase(),
    apiBaseSource: state.apiBaseSource,
    watchHydrated,
  });
  if (devToolsEnabled) {
    loadDbTables();
    fetchJson(apiUrl("/api/health/flightaware/last"), { skipAuthHandling: true })
      .then((data) => {
        const summary = summariseLastFlightawareQuery(data);
        if (summary.skipped) {
          logEvent("Cron health", {
            note: "FlightAware query history unavailable while database access is disabled.",
          });
          return;
        }
        if (summary.timestamp) {
          const cronMeta = {
            lastRun: formatDisplayTimestamp(summary.timestamp) || summary.timestamp,
          };
          if (Number.isFinite(summary.rowCount)) {
            cronMeta.rowCount = summary.rowCount;
          }
          logEvent("Cron health", cronMeta);
        } else {
          logEvent("Cron health", { note: "No FlightAware queries recorded yet." });
        }
        if (summary.failure) {
          const failureMeta = {};
          if (summary.statusText) {
            failureMeta.status = summary.statusText;
          } else if (Number.isFinite(summary.statusCode)) {
            failureMeta.status = String(summary.statusCode);
          }
          if (summary.endpoint) {
            failureMeta.endpoint = summary.endpoint;
          }
          if (summary.ident) {
            failureMeta.ident = summary.ident;
          }
          failureMeta.error = summary.error || "Unknown error";
          if (summary.timestamp) {
            failureMeta.lastRun = formatDisplayTimestamp(summary.timestamp) || summary.timestamp;
          }
          if (Number.isFinite(summary.rowCount)) {
            failureMeta.rowCount = summary.rowCount;
          }
          logEvent("FlightAware last query failed", failureMeta);
        } else if (summary.timestamp) {
          const successMeta = {};
          if (summary.statusText) {
            successMeta.status = summary.statusText;
          } else if (Number.isFinite(summary.statusCode)) {
            successMeta.status = String(summary.statusCode);
          }
          if (summary.endpoint) {
            successMeta.endpoint = summary.endpoint;
          }
          if (summary.ident) {
            successMeta.ident = summary.ident;
          }
          successMeta.lastRun = formatDisplayTimestamp(summary.timestamp) || summary.timestamp;
          if (Number.isFinite(summary.rowCount)) {
            successMeta.rowCount = summary.rowCount;
          }
          logEvent("FlightAware last query succeeded", successMeta);
        }
      })
      .catch((error) => {
        logEvent("Cron health check failed", { error: error?.message || "unknown" });
      });
  }
  logEvent("Scheduled refresh", { note: "Server cron runs daily at 01:00 EST" });
  setRefreshIndicator("Checking saved session.");
  await refreshEmailTransport({ reason: "initial" });
  const hasSession = await loadSession();
  
  // Setup admin management if admin user
  if (isAdminUser()) {
    setupAdminManagement();
  }
  if (!hasSession) {
    setRefreshIndicator("Sign in to load data.");
    await refreshEmailTransport({ reason: "anonymous" });
    renderTable();
    // Still try to fetch cron status even if not authenticated (might work if endpoint allows)
    if (dom.autoRefreshNote && !state.aircraftLimitActive) {
      fetchCronStatus();
    }
    return;
  }
  await refreshEmailTransport({ reason: "session" });
  setRefreshIndicator("Loading aircraft data.");
  await loadAircraft();
  logTaskSummary();
  setRefreshIndicator("Aircraft loaded. Use Manual Refresh to pull FlightAware statuses.");
  
  // Fetch cron status after authentication
  if (dom.autoRefreshNote && !state.aircraftLimitActive) {
    fetchCronStatus();
  }

  // Auto-refresh from DB cache every 5 minutes so UI stays in sync with cron data
  // without requiring a page reload or manual refresh click.
  setInterval(async () => {
    try {
      await hydrateStatusesFromCache({ force: false });
    } catch (_) {
      // Silent — don't disrupt the UI on background refresh failure
    }
  }, 5 * 60 * 1000);
}

bootstrap().catch((error) => {
  logEvent("Bootstrap failed", { error: error.message });
  setRefreshIndicator(`Startup failed: ${error.message}`);
});
